import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Drawer, Dropdown, Modal, Select, Tooltip, message } from "antd";
import { useNavigate } from "react-router-dom";
import { CareShell, Panel } from "../../components/care/CareLayout";
import { useAuth } from "../../contexts/AuthContext";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import { useFileUpload } from "../../hooks/useFileUpload";
import { careService } from "../../services/careService";
import MedicationImportWorkspace from "../../components/care/MedicationImportWorkspace";
import ResidentInformationSummary from "../../components/care/ResidentInformationSummary";
import { preparePrescriptionFiles } from "../../components/care/prescriptionImport";
import {
  MedicationBodyPartPicker,
  medicationBodyPartLabel,
  medicationBodyPartNeedsOtherText,
  medicationBodyPartOptionsForRoute,
  medicationBodyPartOtherValue,
  medicationBodyPartPayload,
  medicationBodyPartValues,
  medicationRouteNeedsBodyPart,
} from "../../components/care/MedicationBodyPartPicker";

const pageItems = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const firstText = (...values) =>
  values.map((value) => String(value || "").trim()).find(Boolean) || "";

const apiErrorMessage = (error, fallback) => {
  const responseData = error?.response?.data;
  const candidates = [
    responseData?.message,
    typeof responseData === "string" ? responseData : "",
    error?.message,
  ];
  const messageText = candidates
    .map((value) => String(value || "").trim())
    .find((value) => value && !/<(?:!doctype|html|head|body)\b/i.test(value));
  return messageText || fallback;
};

const hasEnteredValue = (value) =>
  value !== undefined && value !== null && String(value).trim() !== "";

const numericDecimalValue = (value) => {
  const normalized = String(value || "").replace(/[^\d.]/g, "");
  const [whole = "", ...rest] = normalized.split(".");
  return rest.length ? `${whole}.${rest.join("")}` : whole;
};

const numericDosageValue = (value) =>
  numericDecimalValue(value);

const booleanValue = (value) =>
  value === true || String(value).toLowerCase() === "true";

const yesNoValue = (value) => {
  if (!hasEnteredValue(value)) return "";
  return booleanValue(value) ? "Yes" : "No";
};

const browserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Los_Angeles";
  } catch (err) {
    return "America/Los_Angeles";
  }
};

const joinName = (...values) =>
  values.map((value) => String(value || "").trim()).filter(Boolean).join(" ");

const residentId = (resident = {}) => resident.id || resident.residentId || "";

const residentName = (resident = {}) =>
  firstText(
    resident.name,
    resident.fullName,
    resident.residentName,
    joinName(resident.firstName, resident.middleName, resident.lastName),
    "Unnamed resident",
  );

const residentRoom = (resident = {}) =>
  firstText(resident.room, resident.roomNumber, resident.bed ? `Bed ${resident.bed}` : "");

const medicationName = (medication = {}) =>
  firstText(medication.medicationName, medication.name, medication.title, "Unnamed medication");

const medicationStrength = (medication = {}) =>
  firstText(
    [medication.strengthAmount, optionLabelFromStored(medicationStrengthUnitOptions, medication.strengthUnit)].filter(Boolean).join(" "),
    medication.strength,
    medication.medicationStrength,
  );

const medicationStrengthAmount = (medication = {}) => {
  const directValue = firstText(medication.strengthAmount, medication.medicationStrengthAmount);
  if (directValue) return numericDecimalValue(directValue);
  const match = firstText(medication.strength, medication.medicationStrength).match(/^\s*(\d+(?:\.\d+)?)/);
  return match ? match[1] : "";
};

const medicationStrengthUnit = (medication = {}) => {
  const directValue = firstText(medication.strengthUnit, medication.medicationStrengthUnit);
  if (directValue) return optionLabelFromStored(medicationStrengthUnitOptions, directValue);
  const strength = firstText(medication.strength, medication.medicationStrength);
  const amount = medicationStrengthAmount(medication);
  return amount ? strength.slice(strength.indexOf(amount) + amount.length).trim() : "";
};

const medicationPluralUnitLabels = {
  tablet: "Tablets",
  capsule: "Capsules",
  teaspoon: "Teaspoons",
  tablespoon: "Tablespoons",
  drop: "Drops",
  puff: "Puffs",
  spray: "Sprays",
  patch: "Patches",
  application: "Applications",
  packet: "Packets",
  scoop: "Scoops",
  suppository: "Suppositories",
  unit: "Units",
  vial: "Vials",
  injection: "Injections",
};

const medicationUnitForAmount = (amount, unit) => {
  const label = firstText(unit);
  if (!label) return "";
  const numericAmount = Number(amount);
  if (!Number.isFinite(numericAmount) || Math.abs(numericAmount) === 1) return label;
  const mapped = medicationPluralUnitLabels[label.toLowerCase()];
  if (mapped) return mapped;
  if (/^(mL|mg|mcg|g|mEq|IU|N\/A|%)$/i.test(label) || label.includes("/")) return label;
  if (/s$/i.test(label)) return label;
  return `${label}s`;
};

const medicationDoseUnit = (medication = {}) =>
  optionLabelFromStored(medicationDoseUnitOptions, firstText(medication.doseUnit, medication.dosageUnit, medication.doseUnitForm));

const medicationDoseUnitDisplay = (medication = {}) =>
  medicationUnitForAmount(medication.dosage, medicationDoseUnit(medication));

const medicationQuantityAmount = (medication = {}) => {
  const directValue = firstText(medication.quantityAmount, medication.medicationQuantityAmount);
  if (directValue) return numericDecimalValue(directValue);
  const match = firstText(medication.quantity).match(/^\s*(\d+(?:\.\d+)?)/);
  return match ? match[1] : "";
};

const medicationQuantityUnit = (medication = {}) => {
  const directValue = firstText(medication.quantityUnit, medication.medicationQuantityUnit);
  if (directValue) return optionLabelFromStored(medicationDoseUnitOptions, directValue);
  const quantity = firstText(medication.quantity);
  const amount = medicationQuantityAmount(medication);
  return amount ? quantity.slice(quantity.indexOf(amount) + amount.length).trim() : "";
};

const medicationQuantityUnitDisplay = (medication = {}) =>
  medicationUnitForAmount(medicationQuantityAmount(medication), medicationQuantityUnit(medication));

const medicationRemainingQuantityAmount = (medication = {}) => {
  const directValue = firstText(medication.remainingQuantity, medication.remainingQuantityAmount, medication.medicationRemainingQuantity);
  return directValue ? numericDecimalValue(directValue) : medicationQuantityAmount(medication);
};

const medicationRemainingQuantityDisplay = (medication = {}) =>
  [
    medicationRemainingQuantityAmount(medication),
    medicationUnitForAmount(medicationRemainingQuantityAmount(medication), medicationQuantityUnit(medication)),
  ].filter(Boolean).join(" ");

const medicationMaxDailyDoseAmount = (medication = {}) => {
  const directValue = firstText(medication.maxDailyDoseAmount, medication.medicationMaxDailyDoseAmount);
  if (directValue) return numericDecimalValue(directValue);
  const match = firstText(medication.maxDailyDose).match(/^\s*(\d+(?:\.\d+)?)/);
  return match ? match[1] : "";
};

const medicationMaxDailyDoseUnit = (medication = {}) => {
  const directValue = firstText(medication.maxDailyDoseUnit, medication.medicationMaxDailyDoseUnit);
  if (directValue) return optionLabelFromStored(medicationDoseUnitOptions, directValue);
  const maxDailyDose = firstText(medication.maxDailyDose);
  const amount = medicationMaxDailyDoseAmount(medication);
  return amount ? maxDailyDose.slice(maxDailyDose.indexOf(amount) + amount.length).trim() : "";
};

const medicationMaxDailyDoseUnitDisplay = (medication = {}) =>
  medicationUnitForAmount(medicationMaxDailyDoseAmount(medication), medicationMaxDailyDoseUnit(medication));

const medicationNumericAmount = (value) => {
  const parsed = Number(numericDecimalValue(value));
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizedMedicationUnit = (unit) =>
  String(unit || "").trim().toLowerCase().replace(/s$/, "");

const medicationPrnDailyDoseLimitStatus = (medication = {}, logs = []) => {
  const maxAmount = medicationNumericAmount(medicationMaxDailyDoseAmount(medication));
  const doseAmount = medicationNumericAmount(medication.dosage);
  const doseUnit = medicationDoseUnit(medication);
  const maxUnit = medicationMaxDailyDoseUnit(medication);
  if (!maxAmount || !doseAmount || !doseUnit || !maxUnit) return null;
  if (normalizedMedicationUnit(doseUnit) !== normalizedMedicationUnit(maxUnit)) return null;
  const administeredTotal = logs
    .filter((log) => String(log.adminAction || log.status || "").toUpperCase() === "ADMINISTERED")
    .reduce((total, log) => total + (medicationNumericAmount(log.doseGiven) || doseAmount), 0);
  const displayUnit = medicationUnitForAmount(maxAmount, maxUnit);
  const nextTotal = administeredTotal + doseAmount;
  if (administeredTotal >= maxAmount) {
    return {
      level: "reached",
      message: `Max daily dose reached: ${administeredTotal} of ${maxAmount} ${displayUnit} administered today.`,
    };
  }
  if (nextTotal > maxAmount) {
    return {
      level: "exceeds",
      message: `Administering this dose would exceed the max daily dose: ${nextTotal} of ${maxAmount} ${displayUnit}.`,
    };
  }
  return null;
};

const medicationUnitMismatchWarning = (form = {}) => {
  const units = [
    ["Dose", customSelectValue(form, "doseUnit", medicationDoseUnitOptions)],
    ["Quantity", customSelectValue(form, "quantityUnit", medicationDoseUnitOptions)],
    ["Max daily dose", customSelectValue(form, "maxDailyDoseUnit", medicationDoseUnitOptions)],
  ].filter(([, value]) => hasEnteredValue(value));
  if (units.length < 2) return "";
  const uniqueUnits = Array.from(new Set(units.map(([, value]) => String(value).trim().toLowerCase())));
  if (uniqueUnits.length <= 1) return "";
  return `Unit mismatch: ${units.map(([label, value]) => `${label} uses ${value}`).join(", ")}.`;
};

const medicationRxNumber = (medication = {}) =>
  firstText(medication.orderNumber, medication.rxNumber, medication.prescriptionNumber);

const medicationDoseSummary = (medication = {}) =>
  [[medication.dosage, medicationDoseUnitDisplay(medication)].filter(Boolean).join(" "), medicationStrength(medication), medication.route, medication.frequency]
    .filter(Boolean)
    .join(" | ");

const medicationRecordId = (medication = {}) =>
  firstText(medication.id, medication.medicationId, medication.residentMedicationId);

const refillRequestedMedication = (request = {}) =>
  request?.medicationSnapshot && !Array.isArray(request.medicationSnapshot) && typeof request.medicationSnapshot === "object"
    ? request.medicationSnapshot
    : request || {};

const medicationRefillBalance = (medication = {}) => {
  const total = Number.isInteger(medication.totalRefills) ? medication.totalRefills : null;
  const remaining = Number.isInteger(medication.remainingRefills) ? medication.remainingRefills : total;
  return { total, remaining };
};

const REFILL_FAX_DISPLAY = {
  NOT_SENT: { label: "Not faxed", tone: "neutral", retryable: true },
  NO_FAX_NUMBER: { label: "No pharmacy fax number", tone: "red", retryable: true },
  QUEUED: { label: "Faxing to pharmacy", tone: "low", retryable: false },
  SENT: { label: "Fax sent to pharmacy", tone: "green", retryable: false },
  DELIVERED: { label: "Fax delivered to pharmacy", tone: "green", retryable: false },
  FAILED: { label: "Fax to pharmacy failed", tone: "red", retryable: true },
};

const refillFaxDisplay = (request = {}) =>
  REFILL_FAX_DISPLAY[String(request?.faxStatus || "").toUpperCase()] || REFILL_FAX_DISPLAY.NOT_SENT;


const refillFaxReason = (request = {}) => {
  const reason = String(request?.faxFailureReason || "").trim();
  if (!reason) return "";
  const sent = request?.faxSentAt ? ` Last sent ${formatTimestamp(request.faxSentAt)}.` : "";
  return `The pharmacy has not been reached. ${reason}.${sent}`;
};


function RefillRowActions({ request, busy, onSendFax, onFulfill, onCancel, onPreview, onRefreshFax }) {
  const isRequested = String(request?.status || "").toUpperCase() === "REQUESTED";
  const fax = refillFaxDisplay(request);


  const hasDocument = Boolean(request?.faxDocumentAvailable);
  if (!isRequested && !hasDocument && !request?.faxId) {
    return <span className="medication-row-actions-empty">-</span>;
  }

  const menuItems = [
    {
      key: "preview",

      disabled: !hasDocument,
      icon: <i className="ti ti-file-text" />,
      label: "Preview fax document",
    },
    request?.faxId ? {
      key: "refreshFax", disabled: busy,
      icon: <i className="ti ti-refresh" />, label: "Refresh fax status",
    } : null,
    isRequested
      ? {
          key: "cancel",
          danger: true,
          icon: <i className="ti ti-x" />,
          label: "Cancel request",
        }
      : null,
  ].filter(Boolean);

  return (
    <div className="medication-row-actions">
      {isRequested ? (
        <button className="mini-btn success" type="button" onClick={onFulfill}>
          <i className="ti ti-package-import" /> Fulfill
        </button>
      ) : null}

      {isRequested && fax.retryable ? (
        <Tooltip title="Send this refill request to the pharmacy by fax">
          <button className="mini-btn" type="button" disabled={busy} onClick={onSendFax}>
            <i className={busy ? "ti ti-loader-2" : "ti ti-send"} /> {busy ? "Sending" : "Send fax"}
          </button>
        </Tooltip>
      ) : null}

      {menuItems.length ? (
        <Dropdown
          menu={{
            items: menuItems,
            onClick: ({ key, domEvent }) => {
              domEvent.stopPropagation();
              if (key === "preview") onPreview();
              if (key === "cancel") onCancel();
              if (key === "refreshFax") onRefreshFax();
            },
          }}
          overlayClassName="medication-row-actions-menu"
          placement="bottomRight"
          trigger={["click"]}
        >
          <button
            aria-label="More actions for this refill request"
            className="resident-icon-button medication-row-actions-trigger"
            type="button"
          >
            <i className="ti ti-dots-vertical" />
          </button>
        </Dropdown>
      ) : null}
    </div>
  );
}

const logMedicationId = (log = {}) => {
  const record = log || {};
  return firstText(record.medicationId, record.residentMedicationId, record.medication?.id);
};

const logScheduledDate = (log = {}) => {
  const record = log || {};
  return dateKeyOnly(record.scheduledFor || record.scheduledDate || record.date);
};

const logScheduledSlot = (log = {}) => {
  const record = log || {};
  const slot = String(record.scheduledSlot || record.slot || "").trim().toUpperCase();
  return slot.startsWith("PRN_") ? "PRN" : slot;
};

const logActorName = (log = {}) => {
  const record = log || {};
  return firstText(record.administeredByName, record.performedByName, record.recordedByName, record.createdByName);
};

const logHasCaregiverActor = (log = {}) =>
  Boolean(firstText(log.administeredById, log.administeredByName, log.performedById, log.performedByName, log.recordedById, log.recordedByName));

const isSystemMissedAdministrationLog = (log = {}) => {
  if (String(log.adminAction || log.status || "").toUpperCase() !== "MISSED") return false;
  const actorName = logActorName(log).toLowerCase();
  return !logHasCaregiverActor(log) || actorName === "system" || actorName.includes("system generated") || actorName.includes("auto");
};

const pharmacyName = (pharmacy = {}) =>
  firstText(pharmacy.name, pharmacy.pharmacy?.name, "Unnamed pharmacy");

const pharmacyAddress = (pharmacy = {}) => {
  const record = pharmacy.pharmacy || pharmacy;
  return [
    record.addressLine1,
    record.addressLine2,
    record.city,
    record.state,
    record.postalCode,
    record.country,
  ].map((value) => String(value || "").trim()).filter(Boolean).join(", ");
};

const userId = (user = {}) => user.id || user.userId || user.uid || user.sub || "";

const userName = (user = {}) =>
  firstText(
    user.name,
    user.fullName,
    joinName(user.firstName, user.middleName, user.lastName),
    user.email,
    "Care staff",
  );

const userInitials = (name) => {
  const text = firstText(name);
  if (!text) return "-";
  const parts = text.split(/\s+/).filter(Boolean);
  if (!parts.length) return "-";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
};

const displayValue = (value, fallback = "Not added") => firstText(value, fallback);

const shouldShowMedicationDetailValue = (value) => {
  const text = firstText(value);
  return Boolean(text) && !["Not added", "Not set"].includes(text);
};

const APPROVAL_MEDICATION_STATUSES = new Set([
  "PENDING_OLIVEEB_APPROVAL",
  "PENDING_ADF_APPROVAL",
  "MEDICATION_PENDING_OLIVEEB_APPROVAL",
  "MEDICATION_PENDING_ADF_APPROVAL",
]);

const OLIVEEB_APPROVAL_MEDICATION_STATUSES = new Set([
  "PENDING_OLIVEEB_APPROVAL",
  "MEDICATION_PENDING_OLIVEEB_APPROVAL",
]);

const ADF_APPROVAL_MEDICATION_STATUSES = new Set([
  "PENDING_ADF_APPROVAL",
  "MEDICATION_PENDING_ADF_APPROVAL",
]);

const medicationStatus = (medication) =>
  String(medication?.status || "").trim().toUpperCase();

const isMedicationListVisible = (medication = {}) =>
  !APPROVAL_MEDICATION_STATUSES.has(medicationStatus(medication));

const isOliveebApprovalMedication = (medication = {}) =>
  OLIVEEB_APPROVAL_MEDICATION_STATUSES.has(medicationStatus(medication));

const isAdfApprovalMedication = (medication = {}) =>
  ADF_APPROVAL_MEDICATION_STATUSES.has(medicationStatus(medication));

const isMedicationApprovalVisible = (medication = {}) =>
  APPROVAL_MEDICATION_STATUSES.has(medicationStatus(medication));

const NEW_MEDICATION_STATUS_FILTER_OPTIONS = [
  { value: "PENDING_APPROVAL", label: "Pending approval" },
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Rejected" },
  { value: "ALL", label: "All statuses" },
];

const newMedicationStatusLabel = (medication = {}) => {
  const status = medicationStatus(medication);
  if (status === "ACTIVE") return "Approved";
  if (status === "REJECTED") return "Rejected";
  if (isMedicationApprovalVisible(medication)) return "Pending approval";
  return displayValue(medication?.status);
};

const newMedicationStatusPillClass = (medication = {}) => {
  const status = medicationStatus(medication);
  if (status === "REJECTED") return "high";
  if (status === "ACTIVE") return "low";
  return "medium";
};

const newMedicationMatchesStatusFilter = (medication = {}, filter = "PENDING_APPROVAL") => {
  const status = medicationStatus(medication);
  if (filter === "ALL") return true;
  if (filter === "APPROVED") return status === "ACTIVE";
  if (filter === "REJECTED") return status === "REJECTED";
  return isMedicationApprovalVisible(medication);
};

const MEDICATION_LIST_TABS = new Set(["admin", "adminLog", "list", "pharmacy", "alerts"]);
const MEDICATION_DAY_TABS = new Set(["admin", "adminLog", "alerts"]);

const formatFileSize = (size) => {
  const bytes = Number(size || 0);
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const medicationDocumentUrl = (document = {}) =>
  firstText(document?.localPreviewUrl, document?.previewUrl, document?.signedUrl, document?.downloadUrl, document?.url, document?.fileUrl);

const medicationDocumentName = (document = {}, index = 0) =>
  firstText(document?.fileName, document?.originalFileName, document?.name, `Medication document ${index + 1}`);

const medicationDocumentKey = (document = {}, index = 0) =>
  firstText(document?.id, document?.fileId, document?.storageFileId, document?.fileName ? `${document.fileName}-${index}` : "", `document-${index}`);

const medicationDocumentIsImage = (document = {}) => {
  const contentType = String(document?.contentType || document?.mimeType || document?.type || "").toLowerCase();
  const fileName = String(document?.fileName || document?.originalFileName || document?.name || "").toLowerCase();
  return contentType.startsWith("image/") || /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(fileName);
};

const medicationDocumentIsPdf = (document = {}) => {
  const contentType = String(document?.contentType || document?.mimeType || document?.type || "").toLowerCase();
  const fileName = String(document?.fileName || document?.originalFileName || document?.name || "").toLowerCase();
  return contentType === "application/pdf" || fileName.endsWith(".pdf");
};

const medicationDocumentsFromPayload = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.documents)) return payload.documents;
  if (Array.isArray(payload.files)) return payload.files;
  return [payload];
};

const newMedicationReviewFormFromRecord = (record = {}) => {
  const extracted = record.extractedMedicationDetails && typeof record.extractedMedicationDetails === "object" && !Array.isArray(record.extractedMedicationDetails)
    ? record.extractedMedicationDetails
    : {};
  return {
    ...medicationFormFromRecord({
      ...extracted,
      ...record,
      medicationName: firstText(record.medicationName, extracted.medicationName),
      sourceContactName: firstText(record.receivedFromName, record.sourceContactName, extracted.sourceContactName),
      sourceContactPhone: firstText(record.receivedFromFaxNumber, record.sourceContactPhone, extracted.sourceContactPhone),
      documents: medicationDocumentsFromPayload(record.prescriptionDocument || record.documents || extracted.documents),
    }),
    efaxNumber: firstText(record.efaxNumber, extracted.efaxNumber),
  };
};

const selectedOptionLabel = (options, value) =>
  options.find(([optionValue]) => optionValue === value)?.[1] || value;

const customSelectValue = (form, field, options, customField = `${field}Other`) => {
  const value = form[field];
  if (!value) return "";
  if (Array.isArray(value)) return customMultiSelectValue(form, field, options, customField);
  if (["OTHER", "CUSTOM", "SPECIFIC_TIME"].includes(value)) {
    return firstText(form[customField], selectedOptionLabel(options, value));
  }
  return selectedOptionLabel(options, value);
};

const customMultiSelectValue = (form, field, options, customField = `${field}Other`) => {
  const values = Array.isArray(form[field]) ? form[field] : [form[field]].filter(Boolean);
  return values
    .filter(Boolean)
    .map((value) => {
      if (["OTHER", "CUSTOM", "SPECIFIC_TIME"].includes(value)) {
        return firstText(form[customField], selectedOptionLabel(options, value));
      }
      return selectedOptionLabel(options, value);
    })
    .filter(Boolean)
    .join(", ");
};

const optionValueFromStored = (options, storedValue, otherValue = "OTHER") => {
  const normalized = String(storedValue || "").trim();
  if (!normalized) return "";
  const match = options.find(([value, label]) =>
    String(value || "").toLowerCase() === normalized.toLowerCase()
    || String(label || "").toLowerCase() === normalized.toLowerCase(),
  );
  return match ? match[0] : otherValue;
};

const optionLabelFromStored = (options, storedValue) => {
  const normalized = String(storedValue || "").trim();
  if (!normalized) return "";
  const match = options.find(([value, label]) =>
    String(value || "").toLowerCase() === normalized.toLowerCase()
    || String(label || "").toLowerCase() === normalized.toLowerCase(),
  );
  return match ? match[1] : normalized;
};

const optionValuesFromStored = (options, storedValue, otherValue = "OTHER") => {
  const normalized = String(storedValue || "").trim();
  if (!normalized) return [];
  const pieces = normalized
    .split(/\s*(?:,|;|\|)\s*/)
    .map((value) => value.trim())
    .filter(Boolean);
  const sourceValues = pieces.length ? pieces : [normalized];
  const optionEntries = options.filter(([value]) => value);
  const selected = [];
  const unmatched = [];
  sourceValues.forEach((piece) => {
    const match = optionEntries.find(([value, label]) =>
      String(value || "").toLowerCase() === piece.toLowerCase()
      || String(label || "").toLowerCase() === piece.toLowerCase(),
    );
    if (match) selected.push(match[0]);
    else unmatched.push(piece);
  });
  if (unmatched.length) selected.push(otherValue);
  return Array.from(new Set(selected));
};

const multiSelectOptions = (options) =>
  options
    .filter(([value]) => value)
    .map(([value, label]) => ({ value, label }));

const FieldLabel = ({ children, info }) => (
  <span className="medication-field-label">
    {children}
    {info ? (
      <Tooltip title={info} placement="top">
        <i className="ti ti-info-circle" aria-label={`${children} guidance`} tabIndex={0} />
      </Tooltip>
    ) : null}
  </span>
);

const ScaleSlider = ({ label = "Value", max = 10, onChange, value }) => {
  const hasValue = hasEnteredValue(value);
  const sliderValue = hasValue ? String(value) : "0";
  return (
    <div className="vital-scale-slider">
      <input
        aria-label={label}
        max={max}
        min="0"
        step="1"
        type="range"
        value={sliderValue}
        onChange={(event) => onChange(event.target.value)}
      />
      <div className="vital-scale-slider-meta">
        <span>0</span>
        <strong>{hasValue ? `${sliderValue}/${max}` : "Not set"}</strong>
        <span>{max}</span>
      </div>
    </div>
  );
};

const pad = (value) => String(value).padStart(2, "0");

const dateKey = (value = new Date()) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const dateKeyOnly = (value) => {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
  return dateKey(value);
};

const parseDateKey = (value) => {
  const [year, month, day] = String(value || "").split("-").map(Number);
  return year && month && day ? new Date(year, month - 1, day) : new Date();
};

const addDays = (date, days) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);

const dateKeysInRange = (startValue, endValue, limit = 62) => {
  const start = parseDateKey(startValue);
  const end = parseDateKey(endValue);
  if (!startValue || !endValue || start > end) return [];
  const keys = [];
  for (let cursor = start; cursor <= end && keys.length < limit; cursor = addDays(cursor, 1)) {
    keys.push(dateKey(cursor));
  }
  return keys;
};

const formatMedicationSlotTime = (slot = {}) => {
  if (!Number.isFinite(slot.time?.hours) || !Number.isFinite(slot.time?.minutes)) return "Scheduled";
  return new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" })
    .format(new Date(2020, 0, 1, slot.time.hours, slot.time.minutes));
};

const todayDate = () => new Date();

const localDateTimeInputValue = (value = new Date()) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const medicationLateAdministrationEarliestDate = () => {
  const date = todayDate();
  date.setMonth(date.getMonth() - 6);
  date.setHours(0, 0, 0, 0);
  return date;
};

const lateMedicationAdministrationDateAllowed = (value) => {
  const candidate = parseDateKey(dateKeyOnly(value));
  const today = parseDateKey(dateKey(todayDate()));
  return !Number.isNaN(candidate.getTime())
    && candidate <= today
    && candidate >= parseDateKey(dateKey(medicationLateAdministrationEarliestDate()));
};

const prnAdministrationDateAllowed = (value) => {
  const candidate = dateKeyOnly(value);
  return Boolean(candidate) && candidate <= dateKey(todayDate());
};

const monthStartKey = (monthDate) => dateKey(new Date(monthDate.getFullYear(), monthDate.getMonth(), 1));

const monthEndKey = (monthDate) => dateKey(new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0));

const formatDate = (value) => {
  if (!value) return "Not set";
  const date = typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value) ? parseDateKey(value) : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric" }).format(date);
};

const formatTimestamp = (value) => {
  if (!value) return "Not set";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

const formatTimeOnly = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" }).format(date);
};

const escapeHtml = (value) =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const downloadTextBlob = (content, fileName, type) => {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const monthLabel = (date) =>
  new Intl.DateTimeFormat(undefined, { month: "long", year: "numeric" }).format(date);

const monthOptions = Array.from({ length: 12 }, (_, index) => ({
  value: index,
  label: new Intl.DateTimeFormat(undefined, { month: "long" }).format(new Date(2026, index, 1)),
}));

const addMonths = (date, delta) => new Date(date.getFullYear(), date.getMonth() + delta, 1);

const buildCalendarDays = (monthDate) => {
  const first = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay());
  return Array.from({ length: 42 }, (_, index) => {
    const day = new Date(start);
    day.setDate(start.getDate() + index);
    return {
      date: day,
      key: dateKey(day),
      currentMonth: day.getMonth() === monthDate.getMonth(),
    };
  });
};

const medicationExpectedOn = (medication = {}, key) => {
  const start = dateKeyOnly(medication.startDate || medication.effectiveDate);
  const end = dateKeyOnly(medication.endDate || medication.discontinuedDate);
  const target = dateKeyOnly(key);
  const status = String(medication.status || "").trim().toUpperCase();
  const active = !status || !["DISCONTINUED", "STOPPED", "HELD", "INACTIVE"].includes(status);
  return active && Boolean(target) && (!start || start <= target) && (!end || end >= target);
};

const truthyMedicationFlag = (value) => {
  if (typeof value === "boolean") return value;
  const normalized = String(value || "").trim().toLowerCase();
  return ["true", "yes", "y", "1"].includes(normalized);
};

const isMedicationPrn = (medication = {}) => {
  const frequency = String(medication.frequency || "").trim().toLowerCase();
  const scheduleFrequency = String(medication.administrationSchedule?.frequency || "").trim().toLowerCase();
  const administrationTime = String(medication.administrationTime || "").trim().toLowerCase();
  const prnReason = String(medication.prnReason || "").trim();
  const selectedTimes = Array.isArray(medication.administrationSchedule?.selectedTimes)
    ? medication.administrationSchedule.selectedTimes.map((time) => String(time || "").trim().toUpperCase())
    : [];
  return truthyMedicationFlag(medication.prn)
    || frequency.includes("as needed")
    || frequency.includes("prn")
    || scheduleFrequency.includes("as needed")
    || scheduleFrequency.includes("prn")
    || administrationTime.includes("as needed")
    || administrationTime.includes("prn")
    || Boolean(prnReason)
    || selectedTimes.includes("AS_NEEDED");
};

const isMedicationWithheld = (medication = {}) =>
  booleanValue(firstText(medication.withheld, medication.isWithheld, medication.medicationWithheld));

const medicationWithholdingRecords = (medication = {}) =>
  Array.isArray(medication.withholdingRecords) ? medication.withholdingRecords : [];

const medicationExpectedScheduledOn = (medication = {}, key) =>
  medicationExpectedOn(medication, key) && !isMedicationPrn(medication) && !isMedicationWithheld(medication);

const medicationPeriod = (medication = {}) => {
  const start = medication.startDate || medication.effectiveDate;
  const end = medication.endDate || medication.discontinuedDate;
  if (!start && !end) return "-";
  return `${formatDate(start)} - ${end ? formatDate(end) : "Current"}`;
};

const logAdministrationAction = (log = {}) => {
  const record = log || {};
  return String(record.adminAction || record.status || "").toUpperCase();
};

const medicationMissedEvaluationApplies = (key) => Boolean(key) && key <= dateKey(todayDate());

const parseMedicationLocalTime = (value) => {
  const text = String(value || "").trim().toLowerCase();
  if (!text) return null;
  const match = text.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)?$/i);
  if (!match) return null;
  let hours = Number(match[1]);
  const minutes = Number(match[2] || 0);
  if (hours > 23 || minutes > 59) return null;
  const period = match[3];
  if (period === "pm" && hours < 12) hours += 12;
  if (period === "am" && hours === 12) hours = 0;
  return { hours, minutes };
};

const medicationAdministrationTimedSlots = (medication = {}) => {
  const schedule = medication.administrationSchedule;
  if (!schedule || typeof schedule !== "object" || Array.isArray(schedule)) return [];
  const entries = Array.isArray(schedule.entries) ? schedule.entries : [];
  const slots = entries
    .map((entry, index) => {
      const parsedTime = parseMedicationLocalTime(entry?.time);
      if (!parsedTime) return null;
      return {
        scheduledSlot: normalizeAdministrationSlot(entry?.value) || `DOSE_${index + 1}`,
        time: parsedTime,
      };
    })
    .filter(Boolean);
  const specificTime = parseMedicationLocalTime(schedule.specificTime);
  if (specificTime) {
    slots.push({
      scheduledSlot: "SPECIFIC_TIME",
      time: specificTime,
    });
  }
  return slots.sort((a, b) => medicationSlotSortValue(a) - medicationSlotSortValue(b));
};

const medicationSlotSortValue = (slot = {}) => {
  const hours = slot.time?.hours;
  const minutes = slot.time?.minutes;
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return Number.POSITIVE_INFINITY;
  return hours * 60 + minutes;
};

const medicationFirstAdministrationSortValue = (medication = {}) => {
  const [firstSlot] = medicationAdministrationTimedSlots(medication);
  return firstSlot ? medicationSlotSortValue(firstSlot) : Number.POSITIVE_INFINITY;
};

const compareSortValues = (firstValue, secondValue) => {
  if (firstValue === secondValue) return 0;
  if (!Number.isFinite(firstValue) && !Number.isFinite(secondValue)) return 0;
  return firstValue - secondValue;
};

const compareMedicationsByAdministrationTime = (firstMedication, secondMedication) => {
  return compareSortValues(
    medicationFirstAdministrationSortValue(firstMedication),
    medicationFirstAdministrationSortValue(secondMedication),
  );
};

const logAdministrationSortValue = (log = {}) => {
  const scheduledTime = parseMedicationLocalTime(log.scheduledTime || log.administrationTime);
  if (scheduledTime) return scheduledTime.hours * 60 + scheduledTime.minutes;
  const actualDate = new Date(log.administrationAt || log.createdAt || 0);
  if (Number.isNaN(actualDate.getTime())) return Number.POSITIVE_INFINITY;
  return actualDate.getHours() * 60 + actualDate.getMinutes();
};

const logTimingStatus = (log = {}) => {
  if (logScheduledSlot(log) === "PRN") return { label: "PRN", level: "prn" };
  const scheduledTime = parseMedicationLocalTime(log.scheduledTime || log.administrationTime);
  const scheduledDateKey = logScheduledDate(log);
  const actualDate = new Date(log.administrationAt || log.createdAt || 0);
  if (!scheduledTime || !scheduledDateKey || Number.isNaN(actualDate.getTime())) {
    return { label: "Not scheduled", level: "neutral" };
  }
  const scheduledDate = parseDateKey(scheduledDateKey);
  scheduledDate.setHours(scheduledTime.hours, scheduledTime.minutes, 0, 0);
  const late = actualDate.getTime() > scheduledDate.getTime() + 60 * 60 * 1000;
  return {
    label: late ? "Late" : "On time",
    level: late ? "late" : "on-time",
  };
};

const administrationCorrectionOutsideExpectedRange = (log = {}, value) => {
  const scheduledTime = parseMedicationLocalTime(log.scheduledTime);
  const scheduledDateKey = logScheduledDate(log);
  const actualDate = new Date(value || 0);
  if (!scheduledTime || !scheduledDateKey || Number.isNaN(actualDate.getTime())) return false;
  const scheduledDate = parseDateKey(scheduledDateKey);
  scheduledDate.setHours(scheduledTime.hours, scheduledTime.minutes, 0, 0);
  return actualDate.getTime() < scheduledDate.getTime() - 60 * 60 * 1000
    || actualDate.getTime() > scheduledDate.getTime() + 60 * 60 * 1000;
};

const medicationAdministrationCardsForDay = (medication = {}, key) => {
  const slots = medicationAdministrationTimedSlots(medication);
  if (!slots.length) {
    return [{
      medication,
      administrationSlot: null,
      sortValue: Number.POSITIVE_INFINITY,
      key: `${medicationRecordId(medication) || medicationName(medication)}:unscheduled`,
    }];
  }
  return slots.map((slot) => ({
    medication,
    administrationSlot: {
      scheduledSlot: slot.scheduledSlot,
      scheduledLabel: administrationTimeOptionLabel(slot.scheduledSlot),
      scheduledTime: `${pad(slot.time.hours)}:${pad(slot.time.minutes)}`,
      ...medicationSlotAdministrationWindow(key, slot, slots),
    },
    sortValue: medicationSlotSortValue(slot),
    key: `${medicationRecordId(medication) || medicationName(medication)}:${slot.scheduledSlot}`,
  }));
};

const medicationSlotOverdue = (key, slot, now = todayDate()) => {
  const date = parseDateKey(key);
  date.setHours(slot.time.hours, slot.time.minutes, 0, 0);
  return now.getTime() >= date.getTime() + 60 * 60 * 1000;
};

const medicationSlotDate = (key, slot) => {
  if (!Number.isFinite(slot?.time?.hours) || !Number.isFinite(slot?.time?.minutes)) return null;
  const date = parseDateKey(key);
  date.setHours(slot.time.hours, slot.time.minutes, 0, 0);
  return date;
};

const medicationSlotNextDate = (key, slot, slots = []) => {
  const sortedSlots = slots.filter((entry) =>
    Number.isFinite(entry?.time?.hours) && Number.isFinite(entry?.time?.minutes))
    .sort((a, b) =>
    a.time.hours === b.time.hours ? a.time.minutes - b.time.minutes : a.time.hours - b.time.hours,
  );
  const currentDate = medicationSlotDate(key, slot);
  if (!currentDate) return null;
  const nextSlot = sortedSlots.find((entry) => medicationSlotDate(key, entry).getTime() > currentDate.getTime());
  if (nextSlot) return medicationSlotDate(key, nextSlot);
  if (!sortedSlots.length) return null;
  const nextDateKey = dateKey(addDays(parseDateKey(key), 1));
  return medicationSlotDate(nextDateKey, sortedSlots[0]);
};

const medicationSlotAdministrationWindow = (key, slot, slots = []) => {
  const scheduledDate = medicationSlotDate(key, slot);
  if (!scheduledDate) return null;
  const scheduledAt = scheduledDate.getTime();
  const oneHourBefore = scheduledAt - 60 * 60 * 1000;
  const nextAdministrationDate = medicationSlotNextDate(key, slot, slots);
  const endsAt = nextAdministrationDate ? nextAdministrationDate.getTime() : scheduledAt + 24 * 60 * 60 * 1000;
  return {
    startsAt: oneHourBefore,
    scheduledAt,
    scheduledDate,
    nextAdministrationDate,
    endsAt,
  };
};

const medicationSlotAdministrationWindowOpen = (key, slot, slots = [], now = todayDate()) => {
  const window = medicationSlotAdministrationWindow(key, slot, slots);
  if (!window) return false;
  const currentTime = now.getTime();
  return currentTime >= window.startsAt && currentTime < window.endsAt;
};

const administrationSlotWindowOpen = (administrationSlot, now = todayDate()) => {
  const currentTime = now.getTime();
  return Number.isFinite(administrationSlot?.startsAt)
    && Number.isFinite(administrationSlot?.endsAt)
    && currentTime >= administrationSlot.startsAt
    && currentTime < administrationSlot.endsAt;
};

const medicationSlotAdministrationLate = (administrationSlot, now = todayDate()) => {
  const scheduledAt = administrationSlot?.scheduledAt;
  return Number.isFinite(scheduledAt) && now.getTime() > scheduledAt + 60 * 60 * 1000;
};

const daysBetween = (fromKey, toKey) => {
  const from = parseDateKey(fromKey);
  const to = parseDateKey(toKey);
  return Math.round((to.getTime() - from.getTime()) / 86400000);
};

const alertSeverityRank = {
  high: 0,
  medium: 1,
  low: 2,
};

const numericRefills = (value) => {
  const match = String(value || "").match(/\d+/);
  return match ? Number(match[0]) : null;
};

const addMedicationAlert = (alerts, severity, title, medication, detail, action) => {
  alerts.push({
    id: `${severity}-${medication?.id || medicationName(medication)}-${title}`,
    severity,
    title,
    medicationName: medicationName(medication),
    detail,
    action,
  });
};

const adminOptions = [
  ["ADMINISTERED", "Administered", "ti-check"],
  ["REFUSED", "Refused", "ti-ban"],
  ["HELD", "Held", "ti-player-pause"],
  ["MISSED", "Missed", "ti-alert-triangle"],
];

const prnAdminOptions = [
  ["ADMINISTERED", "Administer", "ti-check"],
  ["REFUSED", "Refuse", "ti-ban"],
];

const gracePeriodAdminActions = new Set(["REFUSED", "MISSED"]);

const adminOptionsForAdministrationSlot = (administrationSlot) => {
  const gracePeriodPassed = medicationSlotAdministrationLate(administrationSlot);
  return adminOptions.filter(([status]) => gracePeriodPassed || !gracePeriodAdminActions.has(status));
};

const adminActionLabel = (status) =>
  adminOptions.find(([value]) => value === status)?.[1] || (status === "NOTE" ? "Add note" : displayValue(status, "Record"));

const medicationAdminLogActionLabel = (status) => {
  const normalized = String(status || "").toUpperCase();
  if (normalized === "HELD" || normalized === "WITHHELD") return "Withheld";
  return adminActionLabel(normalized);
};

const medicationAdminLogActionLevel = (status) => {
  const normalized = String(status || "").toUpperCase();
  if (normalized === "ADMINISTERED") return "administered";
  if (normalized === "MISSED") return "missed";
  if (normalized === "HELD" || normalized === "WITHHELD") return "withheld";
  if (normalized === "REFUSED") return "refused";
  return "neutral";
};

const medicationMedLogStatus = (status) => {
  const level = medicationAdminLogActionLevel(status);
  const presentations = {
    administered: { icon: "ti-check", label: "Given" },
    missed: { icon: "ti-clock-x", label: "Missed" },
    refused: { icon: "ti-ban", label: "Refused" },
    withheld: { icon: "ti-player-pause", label: "Withheld" },
    neutral: { icon: "ti-minus", label: medicationAdminLogActionLabel(status) },
  };
  return { level, ...presentations[level] };
};

const adminActionsRequiringReason = new Set(["REFUSED", "HELD", "MISSED"]);

const adminActionNoteLabel = (status) => {
  if (status === "ADMINISTERED") return "Notes";
  if (status === "HELD") return "Reason";
  return "Explanation";
};

const adminActionNotePlaceholder = (status) => {
  if (status === "ADMINISTERED") return "Optional notes about the dose given.";
  if (status === "HELD") return "Reason this dose is being held.";
  if (status === "REFUSED") return "Explain why the medication was refused.";
  if (status === "MISSED") return "Explain why the dose was missed.";
  return "Add notes.";
};

const medicationExpectedBodyPart = (medication = {}) => {
  if (Array.isArray(medication.expectedBodyParts) && medication.expectedBodyParts.length) {
    return medicationBodyPartPayload(medication.expectedBodyParts, medication.expectedBodyPartOther);
  }
  return firstText(medication.expectedBodyPart, medication.bodyPart, medication.administrationSite, medication.injectionSite, medication.topicalSite);
};

const medicationBodyPartNote = (expectedBodyPart, confirmedBodyPart, routeValue) => {
  const expectedLabel = medicationBodyPartLabel(expectedBodyPart, routeValue);
  const confirmedLabel = medicationBodyPartLabel(confirmedBodyPart, routeValue);
  if (!expectedLabel && !confirmedLabel) return "";
  if (expectedLabel && confirmedLabel) return `Expected site: ${expectedLabel}; confirmed site: ${confirmedLabel}`;
  if (confirmedLabel) return `Confirmed site: ${confirmedLabel}`;
  return `Expected site: ${expectedLabel}`;
};

const medicationTabs = [
  ["admin", "Med Admin", "ti-calendar-event"],
  ["list", "Med List", "ti-pill"],
  ["adminLog", "Admin Log", "ti-clipboard-list"],
  ["refills", "Refills & Orders", "ti-prescription"],
  ["new", "Med Approvals", "ti-circle-plus"],
  ["import", "Med Import", "ti-file-import"],
  ["logs", "Resident Log", "ti-history"],
  ["alerts", "Alerts", "ti-alert-triangle"],
  ["pharmacy", "Pharmacy", "ti-building-store"],
];

const medicationTabHash = {
  admin: "med-admin",
  adminLog: "med-admin-log",
  list: "med-list",
  refills: "refills-orders",
  pharmacy: "pharmacy",
  new: "new-meds",
  import: "med-import",
  logs: "resident-log",
  alerts: "medication-alerts",
};

const medicationHashTab = Object.fromEntries(
  Object.entries(medicationTabHash).map(([tab, hash]) => [hash, tab]),
);

const validDateKey = (value) => (/^\d{4}-\d{2}-\d{2}$/.test(String(value || "")) ? value : "");

const medicationTabFromHash = () => {
  const hash = window.location.hash.replace("#", "");
  return medicationHashTab[hash] || (medicationTabs.some(([tab]) => tab === hash) ? hash : "");
};

const medicationUrlParam = (name) => new URLSearchParams(window.location.search).get(name) || "";

const initialMedicationResidentId = () => medicationUrlParam("residentId");

const initialMedicationAdminLogView = () => medicationUrlParam("logView") === "list" ? "table" : "matrix";

const initialMedicationAdminView = () => medicationUrlParam("adminView") === "calendar" ? "calendar" : "chart";

const residentListRequests = new Map();
const loadMedicationResidents = (key, request) => {
  if (residentListRequests.has(key)) return residentListRequests.get(key);
  const pending = request();
  residentListRequests.set(key, pending);
  const clear = () => {
    if (residentListRequests.get(key) === pending) residentListRequests.delete(key);
  };
  pending.then(clear, clear);
  return pending;
};

const initialMedicationDate = () => validDateKey(medicationUrlParam("date")) || dateKey(todayDate());

const initialMedicationMonth = () => {
  const month = medicationUrlParam("month");
  if (/^\d{4}-\d{2}$/.test(month)) {
    const [year, monthNumber] = month.split("-").map(Number);
    return new Date(year, monthNumber - 1, 1);
  }
  return new Date(parseDateKey(initialMedicationDate()).getFullYear(), parseDateKey(initialMedicationDate()).getMonth(), 1);
};

const syncMedicationBookmarkUrl = ({ residentId: nextResidentId, tab, selectedDate: nextSelectedDate, monthDate, adminLogView, medAdminView }) => {
  const url = new URL(window.location.href);
  if (nextResidentId) {
    url.searchParams.set("residentId", nextResidentId);
  } else {
    url.searchParams.delete("residentId");
    url.searchParams.delete("date");
    url.searchParams.delete("month");
    url.searchParams.delete("logView");
    url.searchParams.delete("adminView");
    url.hash = "";
    window.history.replaceState(null, "", `${url.pathname}${url.search}`);
    return;
  }
  if (tab === "admin" && nextSelectedDate) {
    url.searchParams.set("date", nextSelectedDate);
  } else {
    url.searchParams.delete("date");
  }
  if ((tab === "admin" || tab === "adminLog") && monthDate instanceof Date && !Number.isNaN(monthDate.getTime())) {
    url.searchParams.set("month", `${monthDate.getFullYear()}-${pad(monthDate.getMonth() + 1)}`);
  } else {
    url.searchParams.delete("month");
  }
  if (tab === "adminLog") {
    url.searchParams.set("logView", adminLogView === "table" ? "list" : "chart");
  } else {
    url.searchParams.delete("logView");
  }
  if (tab === "admin") {
    url.searchParams.set("adminView", medAdminView === "calendar" ? "calendar" : "chart");
  } else {
    url.searchParams.delete("adminView");
  }
  url.hash = tab ? medicationTabHash[tab] || tab : "";
  window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
};

const medicationStatusOptions = [
  ["ACTIVE", "Active"],
  ["HELD", "Held"],
  ["DISCONTINUED", "Discontinued"],
  ["INACTIVE", "Inactive"],
];

const medicationFormOptions = [
  ["", "Select form"],
  ["TABLET", "Tablet"],
  ["CAPSULE", "Capsule"],
  ["LIQUID", "Liquid"],
  ["INJECTION", "Injection"],
  ["TOPICAL", "Topical"],
  ["DROPS", "Drops"],
  ["INHALER", "Inhaler"],
  ["PATCH", "Patch"],
  ["SUPPOSITORY", "Suppository"],
  ["OTHER", "Other"],
];

const medicationDoseUnitOptions = [
  ["", "Select dose unit/form"],
  ["TABLET", "Tablet"],
  ["CAPSULE", "Capsule"],
  ["ML", "mL"],
  ["TEASPOON", "Teaspoon"],
  ["TABLESPOON", "Tablespoon"],
  ["DROP", "Drop"],
  ["PUFF", "Puff"],
  ["SPRAY", "Spray"],
  ["PATCH", "Patch"],
  ["APPLICATION", "Application"],
  ["PACKET", "Packet"],
  ["SCOOP", "Scoop"],
  ["SUPPOSITORY", "Suppository"],
  ["UNIT", "Unit"],
  ["VIAL", "Vial"],
  ["INJECTION", "Injection"],
  ["OTHER", "Other"],
];

const medicationStrengthUnitOptions = [
  ["", "Select strength unit"],
  ["MG", "mg"],
  ["MCG", "mcg"],
  ["G", "g"],
  ["MEQ", "mEq"],
  ["UNIT", "unit"],
  ["UNITS_ML", "units/mL"],
  ["MG_ML", "mg/mL"],
  ["MCG_ML", "mcg/mL"],
  ["MG_5_ML", "mg/5 mL"],
  ["PERCENT", "%"],
  ["IU", "IU"],
  ["OTHER", "Other"],
  ["N_A", "N/A"],
];

const medicationClassificationOptions = [
  ["", "Select classification"],
  ["ANALGESIC", "Analgesic"],
  ["ANTACID", "Antacid"],
  ["ANTIARRHYTHMIC", "Antiarrhythmic"],
  ["ANTIBIOTIC", "Antibiotic"],
  ["ANTICOAGULANT", "Anticoagulant"],
  ["ANTICONVULSANT", "Anticonvulsant"],
  ["ANTIDEPRESSANT", "Antidepressant"],
  ["ANTIEMETIC", "Antiemetic"],
  ["ANTIFUNGAL", "Antifungal"],
  ["ANTIHISTAMINE", "Antihistamine"],
  ["ANTIHYPERTENSIVE", "Antihypertensive"],
  ["ANTI_INFLAMMATORY", "Anti-inflammatory"],
  ["ANTIPLATELET", "Antiplatelet"],
  ["ANTIPSYCHOTIC", "Antipsychotic"],
  ["ANTIVIRAL", "Antiviral"],
  ["ANXIOLYTIC", "Anxiolytic"],
  ["BETA_BLOCKER", "Beta blocker"],
  ["BRONCHODILATOR", "Bronchodilator"],
  ["CALCIUM_CHANNEL_BLOCKER", "Calcium channel blocker"],
  ["CORTICOSTEROID", "Corticosteroid"],
  ["DECONGESTANT", "Decongestant"],
  ["DIABETES_MEDICATION", "Diabetes medication"],
  ["DIURETIC", "Diuretic"],
  ["GASTROINTESTINAL", "Gastrointestinal"],
  ["HORMONE", "Hormone"],
  ["LAXATIVE", "Laxative"],
  ["LIPID_LOWERING", "Lipid lowering"],
  ["MOOD_STABILIZER", "Mood stabilizer"],
  ["NSAID", "NSAID"],
  ["OPIOID", "Opioid"],
  ["PROTON_PUMP_INHIBITOR", "Proton pump inhibitor"],
  ["SEDATIVE_HYPNOTIC", "Sedative / hypnotic"],
  ["SUPPLEMENT", "Supplement"],
  ["THYROID_MEDICATION", "Thyroid medication"],
  ["VACCINE", "Vaccine"],
  ["VITAMIN_MINERAL", "Vitamin / mineral"],
  ["OTHER", "Other"],
];

const medicationRouteOptions = [
  ["", "Select route"],
  ["ORAL", "Oral"],
  ["TOPICAL", "Topical"],
  ["BODY_AREA", "Body area / affected area"],
  ["INJECTION", "Injection"],
  ["SUBCUTANEOUS", "Subcutaneous"],
  ["INTRAMUSCULAR", "Intramuscular"],
  ["INTRAVENOUS", "Intravenous"],
  ["INHALATION", "Inhalation"],
  ["OPHTHALMIC", "Ophthalmic"],
  ["OTIC", "Otic"],
  ["NASAL", "Nasal"],
  ["RECTAL", "Rectal"],
  ["OTHER", "Other"],
];

const medicationFrequencyOptions = [
  ["", "Select frequency"],
  ["ONE_TIME", "One time only"],
  ["ONCE_DAILY", "Once daily"],
  ["TWICE_DAILY", "Twice daily"],
  ["THREE_TIMES_DAILY", "Three times daily"],
  ["FOUR_TIMES_DAILY", "Four times daily"],
  ["EVERY_MORNING", "Every morning"],
  ["EVERY_AFTERNOON", "Every afternoon"],
  ["EVERY_EVENING", "Every evening"],
  ["BEDTIME", "At bedtime"],
  ["EVERY_4_HOURS", "Every 4 hours"],
  ["EVERY_6_HOURS", "Every 6 hours"],
  ["EVERY_8_HOURS", "Every 8 hours"],
  ["EVERY_12_HOURS", "Every 12 hours"],
  ["EVERY_OTHER_DAY", "Every other day"],
  ["EVERY_2_DAYS", "Every 2 days"],
  ["EVERY_3_DAYS", "Every 3 days"],
  ["WEEKLY", "Weekly"],
  ["TWICE_WEEKLY", "Twice weekly"],
  ["MONTHLY", "Monthly"],
  ["AS_NEEDED", "As needed"],
];

const medicationTimeOptions = [
  ["", "Select time"],
  ["MORNING", "Morning"],
  ["NOON", "Noon"],
  ["AFTERNOON", "Afternoon"],
  ["EVENING", "Evening"],
  ["NIGHT", "Night"],
  ["BEDTIME", "Bedtime"],
  ["WITH_MEALS", "With meals"],
  ["BEFORE_MEALS", "Before meals"],
  ["AFTER_MEALS", "After meals"],
  ["SPECIFIC_TIME", "Specific time in instructions"],
  ["AS_NEEDED", "As needed"],
];

const scheduledAdministrationTimeOptions = medicationTimeOptions.filter(([value]) => value !== "AS_NEEDED");

const defaultAdministrationTimeDetails = {
  MORNING: "08:00",
  NOON: "12:00",
  AFTERNOON: "13:00",
  EVENING: "17:00",
  NIGHT: "23:00",
  BEDTIME: "20:00",
};

const administrationTimeDetailFor = (time, currentDetails = {}) =>
  String(currentDetails?.[time] || defaultAdministrationTimeDetails[time] || "").trim();

const administrationTimeDetailsFor = (times = [], currentDetails = {}) =>
  Object.fromEntries(
    times
      .filter((time) => time !== "AS_NEEDED" && time !== "SPECIFIC_TIME")
      .map((time) => [time, administrationTimeDetailFor(time, currentDetails)]),
  );

const defaultAdministrationTimesForFrequency = (frequencyValue) => {
  if (frequencyValue === "EVERY_MORNING") return ["MORNING"];
  if (frequencyValue === "EVERY_AFTERNOON") return ["AFTERNOON"];
  if (frequencyValue === "EVERY_EVENING") return ["EVENING"];
  if (frequencyValue === "BEDTIME") return ["BEDTIME"];
  return [];
};

const administrationTimeLimitForFrequency = (frequencyValue, customFrequency = "") => {
  const storedValue = String(frequencyValue || "").trim();
  const frequency = String(
    storedValue === "CUSTOM"
      ? customFrequency
      : selectedOptionLabel(medicationFrequencyOptions, storedValue),
  ).trim().toLowerCase();
  if (!frequency) return 0;
  if (frequency.includes("as needed") || frequency === "prn") return 0;
  if (frequency.includes("four") || frequency.includes("qid") || frequency.includes("4 times")) return 4;
  if (frequency.includes("three") || frequency.includes("tid") || frequency.includes("3 times")) return 3;
  if ((frequency.includes("twice") && !frequency.includes("weekly")) || frequency.includes("bid") || frequency.includes("2 times")) return 2;
  if (
    frequency.includes("one time")
    || frequency.includes("once")
    || frequency.includes("daily")
    || frequency.includes("morning")
    || frequency.includes("afternoon")
    || frequency.includes("evening")
    || frequency.includes("bedtime")
    || frequency.includes("weekly")
    || frequency.includes("monthly")
    || frequency.includes("other day")
  ) {
    return 1;
  }
  const everyHours = frequency.match(/every\s+(\d+)\s*(?:hour|hr|h)/i);
  if (everyHours) {
    const hours = Number(everyHours[1]);
    if (hours > 0) return Math.max(1, Math.floor(24 / hours));
  }
  const everyDays = frequency.match(/every\s+(\d+)\s*days?/i);
  if (everyDays) return 1;
  const timesPerDay = frequency.match(/(\d+)\s*(?:x|times?)\s*(?:\/|per)?\s*day/i);
  if (timesPerDay) return Math.max(1, Number(timesPerDay[1]));
  return 0;
};

const administrationTimeLimitLabel = (limit) => {
  if (!limit) return "Select at least 1 administration time unless this medication is as needed.";
  if (limit === 1) return "Select 1 administration time.";
  return `Select ${limit} unique administration times.`;
};

const administrationTimePresetOptions = (limit) => {
  if (limit === 1) {
    return [
      ["Morning", ["MORNING"]],
      ["Afternoon", ["AFTERNOON"]],
      ["Evening", ["EVENING"]],
      ["Night", ["NIGHT"]],
      ["Bedtime", ["BEDTIME"]],
    ];
  }
  if (limit === 2) {
    return [
      ["Morning and evening", ["MORNING", "EVENING"]],
      ["Morning and night", ["MORNING", "NIGHT"]],
      ["Afternoon and bedtime", ["AFTERNOON", "BEDTIME"]],
    ];
  }
  if (limit === 3) {
    return [
      ["Morning, afternoon, evening", ["MORNING", "AFTERNOON", "EVENING"]],
      ["Morning, noon, night", ["MORNING", "NOON", "NIGHT"]],
      ["Morning, evening, bedtime", ["MORNING", "EVENING", "BEDTIME"]],
    ];
  }
  if (limit === 4) {
    return [
      ["Morning, noon, evening, bedtime", ["MORNING", "NOON", "EVENING", "BEDTIME"]],
      ["Morning, noon, afternoon, night", ["MORNING", "NOON", "AFTERNOON", "NIGHT"]],
    ];
  }
  return [];
};

const administrationTimeOptionLabel = (value) =>
  selectedOptionLabel(medicationTimeOptions, value);

const normalizeAdministrationSlot = (value) =>
  String(value || "").trim().toUpperCase().replace(/[^A-Z0-9]+/g, "_").replace(/^_+|_+$/g, "");

const parseAdministrationTimeFromStored = (storedValue) => {
  const normalized = String(storedValue || "").trim();
  if (!normalized) return { values: [], details: {}, other: "" };
  const optionEntries = medicationTimeOptions.filter(([value]) => value);
  const values = [];
  const details = {};
  const unmatched = [];

  normalized
    .split(/\s*(?:,|;|\|)\s*/)
    .map((value) => value.trim())
    .filter(Boolean)
    .forEach((piece) => {
      const match = optionEntries.find(([value, label]) => {
        const lowerPiece = piece.toLowerCase();
        return lowerPiece === String(value).toLowerCase()
          || lowerPiece === String(label).toLowerCase()
          || lowerPiece.startsWith(`${String(label).toLowerCase()} - `)
          || lowerPiece.startsWith(`${String(label).toLowerCase()} at `);
      });
      if (!match) {
        unmatched.push(piece);
        return;
      }
      const [value, label] = match;
      values.push(value);
      const detail = piece
        .replace(new RegExp(`^${label}\\s*(?:-|at)?\\s*`, "i"), "")
        .trim();
      if (detail && detail.toLowerCase() !== label.toLowerCase()) {
        details[value] = detail;
      }
    });

  if (unmatched.length) values.push("SPECIFIC_TIME");
  return {
    values: Array.from(new Set(values)),
    details,
    other: unmatched.join(", "),
  };
};

const parseAdministrationScheduleFromRecord = (medication = {}) => {
  const schedule = medication.administrationSchedule;
  if (schedule && typeof schedule === "object" && !Array.isArray(schedule)) {
    const selectedTimes = Array.isArray(schedule.selectedTimes) ? schedule.selectedTimes : [];
    const entries = Array.isArray(schedule.entries) ? schedule.entries : [];
    const details = entries.reduce((result, entry) => {
      if (entry?.value && entry?.time) result[entry.value] = entry.time;
      return result;
    }, { ...(schedule.timeDetails || {}) });
    return {
      values: selectedTimes,
      details,
      other: schedule.specificTime || "",
    };
  }
  return parseAdministrationTimeFromStored(medication.administrationTime);
};

const administrationTimePayloadValue = (form) => {
  if (form.prn) return "As needed";
  return (form.administrationTime || [])
    .map((value) => {
      if (value === "AS_NEEDED") return "As needed";
      if (value === "SPECIFIC_TIME") return firstText(form.administrationTimeOther, "Specific time");
      const label = administrationTimeOptionLabel(value);
      const detail = String(form.administrationTimeDetails?.[value] || "").trim();
      return detail ? `${label} - ${detail}` : label;
    })
    .filter(Boolean)
    .join(", ");
};

const administrationSchedulePayloadValue = (form) => {
  if (form.prn) {
    return {
      frequency: customSelectValue(form, "frequency", medicationFrequencyOptions) || "As needed",
      timeZone: form.medicationTimeZone || browserTimeZone(),
      selectedTimes: ["AS_NEEDED"],
      entries: [],
      specificTime: "",
    };
  }
  return {
    frequency: customSelectValue(form, "frequency", medicationFrequencyOptions),
    timeZone: form.medicationTimeZone || browserTimeZone(),
    selectedTimes: form.administrationTime || [],
    entries: (form.administrationTime || [])
      .filter((value) => !["AS_NEEDED", "SPECIFIC_TIME"].includes(value))
      .map((value) => ({
        value,
        label: administrationTimeOptionLabel(value),
        time: String(form.administrationTimeDetails?.[value] || "").trim(),
      })),
    specificTime: form.administrationTime?.includes("SPECIFIC_TIME") ? form.administrationTimeOther || "" : "",
  };
};

const validateAdministrationScheduleForm = (form, limit) => {
  const selectedTimes = Array.isArray(form.administrationTime) ? form.administrationTime : [];
  const isPrn = booleanValue(form.prn) || form.frequency === "AS_NEEDED";
  if (isPrn) return true;
  if (!form.frequency) {
    message.error("Frequency and administration schedule times are required before submission.");
    return false;
  }
  const scheduledTimes = selectedTimes.filter((time) => time !== "AS_NEEDED");
  if (limit && scheduledTimes.length !== limit) {
    message.error(administrationTimeLimitLabel(limit));
    return false;
  }
  if (!limit && !scheduledTimes.length) {
    message.error("Administration schedule times are required for non-PRN medications before submission.");
    return false;
  }
  const missingTime = scheduledTimes.find((time) => {
    if (time === "SPECIFIC_TIME") return !String(form.administrationTimeOther || "").trim();
    return !String(form.administrationTimeDetails?.[time] || "").trim();
  });
  if (missingTime) {
    message.error("Enter an administration time for every selected schedule before submission.");
    return false;
  }
  return true;
};

const administrationScheduleDisplay = (medication = {}) => {
  const schedule = medication.administrationSchedule;
  if (!schedule || typeof schedule !== "object" || Array.isArray(schedule)) {
    return displayValue(medication.administrationTime);
  }
  const lines = [];
  if (schedule.frequency) {
    lines.push(`Frequency: ${schedule.frequency}`);
  }
  const entries = Array.isArray(schedule.entries) ? schedule.entries : [];
  entries.forEach((entry) => {
    const label = firstText(entry?.label, administrationTimeOptionLabel(entry?.value), entry?.value);
    if (!label) return;
    const time = String(entry?.time || "").trim();
    lines.push(time ? `${label}: ${time}` : label);
  });
  if (schedule.specificTime) {
    lines.push(`Specific time: ${schedule.specificTime}`);
  }
  const timeZone = firstText(medication.medicationTimeZone, schedule.timeZone);
  if (timeZone) {
    lines.push(`Timezone: ${timeZone}`);
  }
  return lines.length ? lines.join("\n") : displayValue(medication.administrationTime);
};

const medicationFoodInstructionOptions = [
  ["", "Select food instructions"],
  ["WITH_FOOD", "With food"],
  ["WITHOUT_FOOD", "Without food"],
  ["BEFORE_MEALS", "Before meals"],
  ["AFTER_MEALS", "After meals"],
  ["WITH_WATER", "With water"],
  ["CUSTOM", "Custom instructions"],
];

const medicationPrnReasonOptions = [
  ["", "Select PRN reason"],
  ["PAIN", "Pain"],
  ["FEVER", "Fever"],
  ["NAUSEA", "Nausea"],
  ["VOMITING", "Vomiting"],
  ["CONSTIPATION", "Constipation"],
  ["DIARRHEA", "Diarrhea"],
  ["ANXIETY", "Anxiety"],
  ["AGITATION", "Agitation"],
  ["INSOMNIA", "Insomnia"],
  ["COUGH", "Cough"],
  ["SHORTNESS_OF_BREATH", "Shortness of breath"],
  ["ALLERGY_SYMPTOMS", "Allergy symptoms"],
  ["ITCHING", "Itching"],
  ["HEADACHE", "Headache"],
  ["OTHER", "Other"],
];

const medicationRefillOptions = [
  ["", "Select refills"],
  ["0", "0 refills"],
  ["1", "1 refill"],
  ["2", "2 refills"],
  ["3", "3 refills"],
  ["4", "4 refills"],
  ["5_PLUS", "5+ refills"],
  ["PRN", "PRN / as needed refills"],
  ["NONE", "None"],
  ["PROVIDER_APPROVAL_REQUIRED", "Provider approval required"],
  ["EXPIRED_PROVIDER_APPROVAL_REQUIRED", "Expired, provider approval required"],
  ["OTHER", "Other"],
];

const medicationSourceTypeOptions = [
  ["", "Select source type"],
  ["HOSPITAL_DISCHARGE", "Hospital discharge"],
  ["PRIMARY_CARE", "Primary care clinic"],
  ["SPECIALIST", "Specialist visit"],
  ["PHARMACY_LIST", "Pharmacy medication list"],
  ["MAR", "Current MAR"],
  ["E_PRESCRIPTION", "Electronic prescription"],
  ["PAPER_ORDER", "Paper prescription/order"],
  ["FAMILY_PROVIDED", "Family-provided list"],
  ["RESIDENT_PROVIDED", "Resident-provided list"],
  ["OTHER", "Other"],
];

const medicationIndicationOptions = [
  ["", "Select indication"],
  ["PAIN", "Pain"],
  ["FEVER", "Fever"],
  ["INFECTION", "Infection"],
  ["HYPERTENSION", "Hypertension"],
  ["DIABETES", "Diabetes"],
  ["HEART_CONDITION", "Heart condition"],
  ["BLOOD_CLOT_PREVENTION", "Blood clot prevention"],
  ["SEIZURE_CONTROL", "Seizure control"],
  ["ANXIETY", "Anxiety"],
  ["DEPRESSION", "Depression"],
  ["MOOD_OR_BEHAVIOR", "Mood or behavior symptoms"],
  ["SLEEP", "Sleep"],
  ["CONSTIPATION", "Constipation"],
  ["NAUSEA", "Nausea"],
  ["ALLERGY", "Allergy"],
  ["RESPIRATORY_SYMPTOMS", "Respiratory symptoms"],
  ["SKIN_CONDITION", "Skin condition"],
  ["VITAMIN_OR_SUPPLEMENT", "Vitamin or supplement"],
  ["OTHER", "Other"],
];

const medicationStorageOptions = [
  ["", "Select storage"],
  ["ROOM_TEMPERATURE", "Room temperature"],
  ["REFRIGERATED", "Refrigerated"],
  ["PROTECT_FROM_LIGHT", "Protect from light"],
  ["CONTROLLED_LOCKED_STORAGE", "Controlled locked storage"],
  ["ORIGINAL_CONTAINER", "Original container"],
  ["CUSTOM", "Custom storage instructions"],
];

const medicationSpecialHandlingOptions = [
  ["", "Select handling"],
  ["NONE", "None"],
  ["SHAKE_WELL", "Shake well"],
  ["DO_NOT_CRUSH", "Do not crush"],
  ["SHOULD_CRUSH", "Should crush"],
  ["WEAR_GLOVES", "Wear gloves"],
  ["MEASURE_DOSE", "Measure dose carefully"],
  ["HAZARDOUS_PRECAUTIONS", "Hazardous medication precautions"],
  ["CUSTOM", "Custom handling instructions"],
];

const defaultMedicationForm = () => ({
  medicationName: "",
  genericName: "",
  brandName: "",
  medicationForm: "",
  medicationFormOther: "",
  classification: "",
  classificationOther: "",
  dosage: "",
  doseUnit: "",
  doseUnitOther: "",
  strength: "",
  strengthAmount: "",
  strengthUnit: "",
  strengthUnitOther: "",
  route: "",
  routeOther: "",
  expectedBodyPart: [],
  expectedBodyPartOther: "",
  frequency: "",
  frequencyOther: "",
  status: "ACTIVE",
  prescribedBy: "",
  prescriberPhone: "",
  prescriberEmail: "",
  pharmacy: "",
  pharmacyId: "",
  hospitalSource: "",
  sourceType: "",
  sourceTypeOther: "",
  sourceDocumentName: "",
  sourceDocumentDate: "",
  sourceReferenceNumber: "",
  sourceContactName: "",
  sourceContactPhone: "",
  sourceContactEmail: "",
  sourceNotes: "",
  orderNumber: "",
  orderDate: "",
  startDate: "",
  endDate: "",
  indication: "",
  indicationOther: "",
  quantity: "",
  quantityAmount: "",
  initialQuantityAmount: "",
  remainingQuantity: "",
  quantityUnit: "",
  quantityUnitOther: "",
  refills: "",
  refillsOther: "",
  controlledSubstance: false,
  highAlertMedication: false,
  requiresVitalsCheck: false,
  requiresBloodSugarCheck: false,
  foodInstructions: [],
  foodInstructionsOther: "",
  medicationTimeZone: browserTimeZone(),
  administrationTime: [],
  administrationTimeDetails: {},
  administrationTimeOther: "",
  prn: false,
  withheld: false,
  withheldReason: "",
  resumeReason: "",
  prnReason: [],
  prnReasonOther: "",
  maxDailyDose: "",
  maxDailyDoseAmount: "",
  maxDailyDoseUnit: "",
  maxDailyDoseUnitOther: "",
  monitoringInstructions: "",
  sideEffectsToWatch: "",
  contraindications: "",
  storageInstructions: [],
  storageInstructionsOther: "",
  specialHandling: [],
  specialHandlingOther: "",
  missedDoseInstructions: "",
  refusalInstructions: "",
  holdParameters: "",
  instructions: "",
  notes: "",
  documents: [],
});

const currentTimeInput = () => {
  const date = todayDate();
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const defaultVitalSignsForm = () => ({
  recordedDate: dateKey(todayDate()),
  recordedTime: currentTimeInput(),
  systolicBloodPressure: "",
  diastolicBloodPressure: "",
  pulse: "",
  temperature: "",
  temperatureUnit: "F",
  respiratoryRate: "",
  oxygenSaturation: "",
  bloodSugar: "",
  weight: "",
  painLevel: "",
  bowelBladderEpisodes: "",
  bowelBladderConcernType: "",
  intakeOutputBalance: "",
  intakeOutputConcernLevel: "",
  skinWoundSeverity: "",
  skinWoundDrainage: "",
  edemaGrade: "",
  edemaLocationCount: "",
  sleepHours: "",
  fatigueLevel: "",
  sleepAwakenings: "",
  mentalStatusScore: "",
  moodScore: "",
  mentalStatusChangeType: "",
  fallMobilityRisk: "",
  fallOccurred: "",
  medicationSideEffectSeverity: "",
  medicationSideEffectType: "",
  position: "",
  site: "",
  notes: "",
});

const vitalSignChecklist = [
  ["bloodPressure", "Blood pressure", "Digital cuff or manual systolic/diastolic reading", "ti-activity-heartbeat"],
  ["pulse", "Heart rate / pulse", "Manual or monitor reading in beats per minute", "ti-heart-rate-monitor"],
  ["temperature", "Body temperature", "Oral, temporal, or tympanic temperature", "ti-temperature"],
  ["respiratoryRate", "Respiratory rate", "Breaths per minute with depth and effort observation", "ti-lungs"],
  ["oxygenSaturation", "SpO2", "Fingertip pulse oximeter oxygen saturation", "ti-wind"],
  ["weight", "Weight monitoring", "Weekly or monthly weight trend", "ti-scale"],
  ["bloodSugar", "Blood glucose", "Accu-Chek or glucometer reading before meals or bedtime", "ti-droplet"],
  ["painLevel", "Pain level tracking", "Numeric or visual pain scale assessment", "ti-mood-sad"],
  ["bowelBladder", "Bowel & bladder function", "Frequency, consistency, UTI signs, or constipation", "ti-toilet-paper"],
  ["intakeOutput", "Fluid intake & output", "Fluid consumption and urine volume tracking", "ti-cup"],
  ["skinWound", "Skin integrity & wound care", "Pressure areas, skin breakdown, or wound changes", "ti-bandage"],
  ["edema", "Edema / swelling checks", "Leg, ankle, or foot swelling observation", "ti-body-scan"],
  ["sleepFatigue", "Sleep & fatigue patterns", "Night awakenings or daily stamina changes", "ti-moon"],
  ["mentalStatusMood", "Mental status & mood", "Confusion, sundowning, depression, or anxiety changes", "ti-brain"],
  ["fallMobility", "Fall & mobility checks", "Balance, gait, mobility, or post-fall observations", "ti-walk"],
  ["medicationSideEffects", "Medication side effects", "Appetite, dizziness, sedation, or medication reaction monitoring", "ti-alert-circle"],
];

const vitalCarePlanTabs = [
  ["thresholds", "Vital Sign Thresholds", "ti-alert-triangle"],
  ["activity", "Care Activity Log", "ti-notes"],
];

const vitalCarePlanTabHash = {
  thresholds: "vital-sign-thresholds",
  activity: "care-activity-log",
};

const vitalCarePlanHashTab = Object.fromEntries(
  Object.entries(vitalCarePlanTabHash).map(([tab, hash]) => [hash, tab]),
);
vitalCarePlanHashTab["health-tracking-log"] = "activity";

const carePlanTabFromHash = () => {
  const hash = window.location.hash.replace("#", "");
  return vitalCarePlanHashTab[hash] || (vitalCarePlanTabs.some(([tab]) => tab === hash) ? hash : "thresholds");
};

const booleanThresholdOptions = [["false", "No"], ["true", "Yes"]];
const multipleThresholdField = { multiple: true };
const fivePointThresholdScale = { scale: "Scale: 0-5" };
const respiratoryThresholdOptions = [
  ["LABORED_BREATHING", "Labored breathing"],
  ["SHALLOW_BREATHING", "Shallow breathing"],
  ["SHORTNESS_OF_BREATH", "Shortness of breath"],
  ["CHANGED_EFFORT", "Changed effort"],
];
const painThresholdOptions = [
  ["NEW_SEVERE_PAIN", "New severe pain"],
  ["NOT_RELIEVED_BY_INTERVENTION", "Pain not relieved by ordered intervention"],
  ["CHEST_PAIN", "Chest pain"],
  ["PAIN_WITH_SWELLING", "Pain with swelling"],
];
const bowelBladderThresholdOptions = [
  ["DIARRHEA", "Diarrhea"],
  ["URINARY_PAIN", "Urinary pain"],
  ["BLOOD_PRESENT", "Blood present"],
  ["UTI_SIGNS", "UTI signs"],
  ["CONSTIPATION", "Constipation"],
];

const vitalAlertThresholds = [
  {
    key: "bloodPressure",
    title: "Blood Pressure",
    icon: "ti-activity-heartbeat",
    summary: "Notify RN/MD if Systolic < 90 or > 160 | Diastolic < 60 or > 90",
    fields: [
      ["systolicLow", "Systolic low", "90", "mmHg"],
      ["systolicHigh", "Systolic high", "160", "mmHg"],
      ["diastolicLow", "Diastolic low", "60", "mmHg"],
      ["diastolicHigh", "Diastolic high", "90", "mmHg"],
    ],
  },
  {
    key: "pulse",
    title: "Pulse / Heart Rate",
    icon: "ti-heart-rate-monitor",
    summary: "Notify RN/MD if < 60 bpm or > 100 bpm",
    fields: [["low", "Low", "60", "bpm"], ["high", "High", "100", "bpm"]],
  },
  {
    key: "temperature",
    title: "Temperature",
    icon: "ti-temperature",
    summary: "Notify RN/MD if < 96 F or > 100.4 F",
    fields: [["low", "Low", "96", "F"], ["high", "High", "100.4", "F"]],
  },
  {
    key: "respiratoryRate",
    title: "Respiratory Rate",
    icon: "ti-lungs",
    summary: "Notify RN/MD if < 12/min or > 24/min, or with labored breathing",
    fields: [["low", "Low", "12", "/min"], ["high", "High", "24", "/min"], ["instruction", "Condition", ["LABORED_BREATHING"], "", respiratoryThresholdOptions, multipleThresholdField]],
  },
  {
    key: "oxygenSaturation",
    title: "Oxygen Saturation (SpO2)",
    icon: "ti-wind",
    summary: "Notify RN/MD if < 90% on room air",
    fields: [
      ["low", "Low", "90", "%"],
      ["condition", "Condition", ["ROOM_AIR"], "", [["ROOM_AIR", "Room air"], ["OXYGEN_ORDER", "On ordered oxygen"], ["AFTER_REST", "After rest"], ["WITH_SYMPTOMS", "With symptoms"]], multipleThresholdField],
    ],
  },
  {
    key: "weight",
    title: "Weight Monitoring",
    icon: "ti-scale",
    summary: "Notify RN/MD for rapid gain/loss, edema-related increase, or unexplained weight loss",
    fields: [["gainPounds", "Weight gain", "3", "lb"], ["lossPounds", "Weight loss", "5", "lb"], ["days", "Within", "7", "days"]],
  },
  {
    key: "bloodSugar",
    title: "Blood Sugar (Accu-Chek)",
    icon: "ti-droplet",
    summary: "Notify RN/MD if < 70 mg/dL or > 250 mg/dL",
    fields: [["low", "Low", "70", "mg/dL"], ["high", "High", "250", "mg/dL"]],
  },
  {
    key: "painLevel",
    title: "Pain Level Tracking",
    icon: "ti-mood-sad",
    summary: "Notify RN/MD for pain > 7/10, new severe pain, or pain not relieved by ordered intervention",
    fields: [["high", "High", "7", "/10"], ["instruction", "Condition", ["NEW_SEVERE_PAIN"], "", painThresholdOptions, multipleThresholdField]],
  },
  {
    key: "bowelBladder",
    title: "Bowel & Bladder Function",
    icon: "ti-toilet-paper",
    summary: "Notify RN/MD for no bowel movement in 3 days, diarrhea, urinary pain, blood, or UTI signs",
    fields: [
      ["noBowelMovementDays", "No bowel movement", "3", "days"],
      ["instruction", "Condition", ["DIARRHEA"], "", bowelBladderThresholdOptions, multipleThresholdField],
    ],
  },
  {
    key: "intakeOutput",
    title: "Fluid Intake & Output",
    icon: "ti-cup",
    summary: "Notify RN/MD for low intake, low urine output, dehydration signs, or ordered I&O outside parameters",
    fields: [["lowIntakeMl", "Low intake", "1000", "mL"], ["lowOutputMl", "Low urine output", "500", "mL"], ["periodHours", "Period", "24", "hours"]],
  },
  {
    key: "skinWound",
    title: "Skin Integrity & Wound Care",
    icon: "ti-bandage",
    summary: "Notify RN/MD for new open area, worsening redness, drainage, odor, or wound severity increase",
    fields: [["severityHigh", "Severity at/above", "3", "/5"], ["severityIncrease", "Severity increase", "1", "point", null, fivePointThresholdScale], ["drainageFlag", "Drainage present", true, "", booleanThresholdOptions]],
  },
  {
    key: "edema",
    title: "Edema / Swelling Checks",
    icon: "ti-body-scan",
    summary: "Notify RN/MD for new swelling, one-sided swelling, pain, shortness of breath, or grade increase",
    fields: [["gradeHigh", "Grade at/above", "2", "+"], ["gradeIncrease", "Grade increase", "1", "point"], ["oneSidedFlag", "One-sided swelling", true, "", booleanThresholdOptions]],
  },
  {
    key: "sleepFatigue",
    title: "Sleep & Fatigue Patterns",
    icon: "ti-moon",
    summary: "Notify RN/MD for major sleep change, unusual daytime fatigue, or fatigue impacting ADLs",
    fields: [["sleepLowHours", "Sleep below", "4", "hours"], ["fatigueHigh", "Fatigue at/above", "7", "/10"], ["awakeningsHigh", "Night awakenings", "3", "times"]],
  },
  {
    key: "mentalStatusMood",
    title: "Mental Status & Mood",
    icon: "ti-brain",
    summary: "Notify RN/MD for sudden confusion, mood change, agitation, depression concern, or safety risk",
    fields: [["statusChangeHigh", "Status change at/above", "5", "/10"], ["moodConcernHigh", "Mood concern at/above", "7", "/10"], ["safetyRiskFlag", "Safety risk present", true, "", booleanThresholdOptions]],
  },
  {
    key: "fallMobility",
    title: "Fall & Mobility Checks",
    icon: "ti-walk",
    summary: "Notify RN/MD after a fall, new weakness, gait change, dizziness, or increased mobility risk",
    fields: [["riskHigh", "Risk at/above", "7", "/10"], ["fallOccurredFlag", "Fall occurred", true, "", booleanThresholdOptions], ["dizzinessFlag", "Dizziness present", true, "", booleanThresholdOptions]],
  },
  {
    key: "medicationSideEffects",
    title: "Medication Side Effects",
    icon: "ti-alert-circle",
    summary: "Notify RN/MD for dizziness, sedation, appetite change, rash, breathing change, or other adverse reaction",
    fields: [["severityHigh", "Severity at/above", "5", "/10"], ["sedationHigh", "Sedation at/above", "5", "/10"], ["breathingChangeFlag", "Breathing change", true, "", booleanThresholdOptions]],
  },
];

const vitalThresholdMap = Object.fromEntries(vitalAlertThresholds.map((threshold) => [threshold.key, threshold]));

const thresholdValuesFromDefinition = (threshold = {}) =>
  Object.fromEntries((threshold.fields || []).map(([field, , value]) => [field, value]));

const thresholdValuesForResident = (threshold = {}, residentOverrides = {}) => ({
  ...thresholdValuesFromDefinition(threshold),
  ...(residentOverrides[threshold.key] || {}),
});

const vitalThresholdOverridesFromApi = (rows = []) =>
  pageItems(rows).reduce((overrides, row) => {
    const key = row?.thresholdKey || row?.key;
    if (!key) return overrides;
    const threshold = vitalThresholdMap[key];
    const values = row?.values || row?.thresholdValues || {};
    return {
      ...overrides,
      [key]: threshold ? normalizeThresholdValues(threshold, values) : values,
    };
  }, {});

const normalizeThresholdValues = (threshold = {}, values = {}) =>
  Object.fromEntries((threshold.fields || []).map((fieldDefinition) => {
    const [field, , defaultValue, , options, settings] = fieldDefinition;
    const value = values[field] === undefined ? defaultValue : values[field];
    if (options === booleanThresholdOptions) {
      return [field, value === true || String(value).toLowerCase() === "true"];
    }
    if (settings?.multiple) {
      return [field, (Array.isArray(value) ? value : [value]).filter((entry) => hasEnteredValue(entry))];
    }
    return [field, value];
  }));

const thresholdOptionLabel = (threshold = {}, fieldKey = "", value = "") => {
  const field = (threshold.fields || []).find(([key]) => key === fieldKey);
  const options = field?.[4] || [];
  const labelForValue = (entry) =>
    options.find(([optionValue]) => String(optionValue) === String(entry))?.[1] || displayValue(entry, "");
  if (Array.isArray(value)) {
    return value.map(labelForValue).filter(Boolean).join(", ");
  }
  return labelForValue(value);
};

const thresholdFieldScale = (fieldDefinition = []) => {
  const [, , , unit, , settings] = fieldDefinition;
  if (settings?.scale) return settings.scale;
  const scaleMatch = String(unit || "").match(/^\/(\d+)$/);
  return scaleMatch ? `Scale: 0-${scaleMatch[1]}` : "";
};

const thresholdFieldScaleMax = (fieldDefinition = []) => {
  const [, , , unit, , settings] = fieldDefinition;
  if (settings?.scale) {
    const settingsMatch = String(settings.scale).match(/0-(\d+)/);
    return settingsMatch ? Number(settingsMatch[1]) : null;
  }
  const unitMatch = String(unit || "").match(/^\/(\d+)$/);
  return unitMatch ? Number(unitMatch[1]) : null;
};

const thresholdFieldInfo = (thresholdKey = "", fieldDefinition = []) => {
  const [, label, , unit, options, settings] = fieldDefinition;
  if (options?.length && settings?.multiple) return `Select every ${label.toLowerCase()} that should trigger notification.`;
  if (options === booleanThresholdOptions) return "Choose Yes when this condition should trigger notification.";
  const scale = thresholdFieldScale(fieldDefinition);
  if (scale) return `${label} is entered as a number. ${scale}.`;
  if (unit) return `${label} is entered as a number in ${unit}.`;
  if (thresholdKey === "bloodPressure") return "Blood pressure threshold values are entered in mmHg.";
  return "";
};

const thresholdFieldExpectsNumber = (fieldDefinition = []) => {
  const [field, , , , options] = fieldDefinition;
  return field !== "instruction" && !options?.length;
};

const numericInputValue = (value) => {
  const text = String(value || "");
  return /^\d*\.?\d*$/.test(text) ? text : null;
};

const invalidThresholdNumberField = (threshold = {}, values = {}) =>
  (threshold.fields || []).find((fieldDefinition) => {
    if (!thresholdFieldExpectsNumber(fieldDefinition)) return false;
    const [field] = fieldDefinition;
    const value = values[field];
    return !hasEnteredValue(value) || Number.isNaN(Number(value));
  });

const thresholdSummaryFromValues = (threshold = {}, values = {}) => {
  if (threshold.key === "bloodPressure") {
    return `Notify RN/MD if Systolic < ${values.systolicLow} or > ${values.systolicHigh} | Diastolic < ${values.diastolicLow} or > ${values.diastolicHigh}`;
  }
  if (threshold.key === "pulse") return `Notify RN/MD if < ${values.low} bpm or > ${values.high} bpm`;
  if (threshold.key === "temperature") return `Notify RN/MD if < ${values.low} F or > ${values.high} F`;
  if (threshold.key === "respiratoryRate") return `Notify RN/MD if < ${values.low}/min or > ${values.high}/min, or with ${thresholdOptionLabel(threshold, "instruction", values.instruction).toLowerCase()}`;
  if (threshold.key === "oxygenSaturation") return `Notify RN/MD if < ${values.low}% ${thresholdOptionLabel(threshold, "condition", values.condition).toLowerCase()}`;
  if (threshold.key === "weight") return `Notify RN/MD for gain > ${values.gainPounds} lb or loss > ${values.lossPounds} lb within ${values.days} days`;
  if (threshold.key === "bloodSugar") return `Notify RN/MD if < ${values.low} mg/dL or > ${values.high} mg/dL`;
  if (threshold.key === "painLevel") return `Notify RN/MD for pain > ${values.high}/10, ${thresholdOptionLabel(threshold, "instruction", values.instruction).toLowerCase()}`;
  if (threshold.key === "bowelBladder") return `Notify RN/MD for no bowel movement in ${values.noBowelMovementDays} days, or ${thresholdOptionLabel(threshold, "instruction", values.instruction).toLowerCase()}`;
  if (threshold.key === "intakeOutput") return `Notify RN/MD if intake < ${values.lowIntakeMl} mL or output < ${values.lowOutputMl} mL over ${values.periodHours} hours`;
  if (threshold.key === "skinWound") return `Notify RN/MD if severity >= ${values.severityHigh}/5, increases by ${values.severityIncrease}, or drainage present is ${thresholdOptionLabel(threshold, "drainageFlag", values.drainageFlag)}`;
  if (threshold.key === "edema") return `Notify RN/MD if grade >= ${values.gradeHigh}+, increases by ${values.gradeIncrease}, or one-sided swelling is ${thresholdOptionLabel(threshold, "oneSidedFlag", values.oneSidedFlag)}`;
  if (threshold.key === "sleepFatigue") return `Notify RN/MD if sleep < ${values.sleepLowHours} hours, fatigue >= ${values.fatigueHigh}/10, or awakenings >= ${values.awakeningsHigh}`;
  if (threshold.key === "mentalStatusMood") return `Notify RN/MD if status change >= ${values.statusChangeHigh}/10, mood concern >= ${values.moodConcernHigh}/10, or safety risk present is ${thresholdOptionLabel(threshold, "safetyRiskFlag", values.safetyRiskFlag)}`;
  if (threshold.key === "fallMobility") return `Notify RN/MD if mobility risk >= ${values.riskHigh}/10, fall occurred is ${thresholdOptionLabel(threshold, "fallOccurredFlag", values.fallOccurredFlag)}, or dizziness present is ${thresholdOptionLabel(threshold, "dizzinessFlag", values.dizzinessFlag)}`;
  if (threshold.key === "medicationSideEffects") return `Notify RN/MD if side effect severity >= ${values.severityHigh}/10, sedation >= ${values.sedationHigh}/10, or breathing change is ${thresholdOptionLabel(threshold, "breathingChangeFlag", values.breathingChangeFlag)}`;
  return `Notify RN/MD for ${values.instruction || threshold.summary.replace(/^Notify RN\/MD for\s*/i, "")}`;
};

const healthTrackingLogCategories = [
  ["careActivity", "Care activity"],
  ["medicationCareInstructions", "Medication administering"],
  ["monitoringInstructions", "Vital signs capturing"],
  ["adls", "Planned activities / ADLs"],
  ["residentNeeds", "Resident needs"],
  ["interventions", "Interventions"],
  ["goals", "Goals"],
];

const vitalSignChecklistMap = Object.fromEntries(
  vitalSignChecklist.map(([key, label, detail, icon]) => [key, { key, label, detail, icon }]),
);

const vitalSignValueFields = {
  bloodPressure: ["systolicBloodPressure", "diastolicBloodPressure"],
  pulse: ["pulse"],
  temperature: ["temperature"],
  respiratoryRate: ["respiratoryRate"],
  oxygenSaturation: ["oxygenSaturation"],
  weight: ["weight"],
  bloodSugar: ["bloodSugar"],
  painLevel: ["painLevel"],
  bowelBladder: ["bowelBladderEpisodes", "bowelBladderConcernType"],
  intakeOutput: ["intakeOutputBalance", "intakeOutputConcernLevel"],
  skinWound: ["skinWoundSeverity", "skinWoundDrainage"],
  edema: ["edemaGrade", "edemaLocationCount"],
  sleepFatigue: ["sleepHours", "fatigueLevel", "sleepAwakenings"],
  mentalStatusMood: ["mentalStatusScore", "moodScore", "mentalStatusChangeType"],
  fallMobility: ["fallMobilityRisk", "fallOccurred"],
  medicationSideEffects: ["medicationSideEffectSeverity", "medicationSideEffectType"],
};

const vitalSignsRequiringPosition = new Set(["bloodPressure", "oxygenSaturation"]);
const vitalSignsRequiringSite = new Set(["bloodPressure", "temperature", "skinWound", "edema"]);

const vitalCaptureInfo = {
  bloodPressure: "Enter systolic over diastolic pressure in mmHg, such as 120 over 80.",
  pulse: "Enter heart rate as beats per minute.",
  temperature: "Enter the measured temperature and confirm whether it is Fahrenheit or Celsius.",
  respiratoryRate: "Enter breaths per minute after observing the resident at rest when possible.",
  oxygenSaturation: "Enter pulse oximeter percentage and note oxygen use, symptoms, or room air status when relevant.",
  bloodSugar: "Enter glucometer value in mg/dL.",
  weight: "Enter weight in pounds using the resident's ordered monitoring schedule.",
  painLevel: "Use the resident's reported pain score from 0 to 10.",
  bowelBladder: "Enter the count or measurable occurrence and select the related concern type.",
  intakeOutput: "Enter fluid balance in mL and select concern level from 0 to 10.",
  skinWound: "Record severity on a 0 to 5 scale and whether drainage is present.",
  edema: "Record edema grade from 0 to 4+ and the number of swollen areas.",
  sleepFatigue: "Capture sleep hours, fatigue level from 0 to 10, and awakenings.",
  mentalStatusMood: "Capture status and mood concern levels from 0 to 10, plus the change type.",
  fallMobility: "Capture mobility risk from 0 to 10 and whether a fall occurred.",
  medicationSideEffects: "Capture side effect severity from 0 to 10 and the observed reaction type.",
};

const vitalRecordedAt = (record = {}) =>
  firstText(record.recordedAt, record.takenAt, record.vitalSignAt, record.createdAt);

const careActivityRecordedAt = (record = {}) =>
  firstText(record.recordedAt, record.activityAt, record.performedAt, record.createdAt);

const readableCode = (value, fallback = "") => {
  const text = firstText(value, fallback);
  return text
    ? text.toLowerCase().replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())
    : "";
};

const residentSummaryText = (value, fallback = "Not added") => {
  const source = Array.isArray(value) ? value.join(", ") : value;
  const text = String(source || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/\s+/g, " ")
    .trim();
  return text || fallback;
};

const careActivityCategoryLabel = (record = {}) => {
  const category = firstText(record.category, record.activityType, record.sourceType, record.type);
  return healthTrackingLogCategories.find(([value]) => value === category)?.[1]
    || readableCode(category)
    || "Care activity";
};

const careActivitySummary = (record = {}) =>
  firstText(
    record.careActivity,
    record.activitySummary,
    record.summary,
    record.description,
    record.title,
    record.medicationName ? `${record.medicationName} ${readableCode(record.status || record.adminAction)}` : "",
    record.vitalSignLabel ? `${record.vitalSignLabel}: ${firstText(record.value, record.measurementValue)}` : "",
    "Care activity recorded",
  );

const vitalBloodPressure = (record = {}) => {
  const systolic = firstText(record.systolicBloodPressure, record.systolic, record.bloodPressureSystolic);
  const diastolic = firstText(record.diastolicBloodPressure, record.diastolic, record.bloodPressureDiastolic);
  return systolic && diastolic ? `${systolic}/${diastolic}` : firstText(record.bloodPressure, "");
};

const vitalValue = (record = {}, fields = [], suffix = "") => {
  const value = fields
    .map((field) => record[field])
    .find((entry) => hasEnteredValue(entry));
  return hasEnteredValue(value) ? `${value}${suffix}` : "Not added";
};

const vitalDisplayValue = (value, fallback = "Not added") =>
  value === 0 || value === "0" ? "0" : displayValue(value, fallback);

const vitalPairValue = (leftValue, leftSuffix, rightValue, rightSuffix) => {
  const parts = [];
  if (hasEnteredValue(leftValue)) parts.push(`${leftValue}${leftSuffix}`);
  if (hasEnteredValue(rightValue)) parts.push(`${rightValue}${rightSuffix}`);
  return parts.length ? parts.join(" | ") : "Not added";
};

const vitalMultiValue = (...values) => {
  const parts = values
    .map(([value, suffix]) => (hasEnteredValue(value) ? `${value}${suffix}` : ""))
    .filter(Boolean);
  return parts.length ? parts.join(" | ") : "Not added";
};

const vitalRecordMatchesType = (record = {}, vitalSignKey = "") => {
  if (record.vitalSignType) return String(record.vitalSignType) === vitalSignKey;
  const fields = vitalSignValueFields[vitalSignKey] || [];
  if (fields.some((field) => record[field] !== undefined && record[field] !== null && String(record[field]).trim() !== "")) return true;
  if (vitalSignKey === "bloodPressure" && vitalBloodPressure(record)) return true;
  return false;
};

const vitalRecordValueForType = (record = {}, vitalSignKey = "") => {
  if (vitalSignKey === "bloodPressure") return displayValue(vitalBloodPressure(record));
  if (vitalSignKey === "pulse") return vitalValue(record, ["pulse", "heartRate"], " bpm");
  if (vitalSignKey === "temperature") return vitalValue(record, ["temperature"], record.temperatureUnit ? ` ${record.temperatureUnit}` : "");
  if (vitalSignKey === "respiratoryRate") return vitalValue(record, ["respiratoryRate"], "/min");
  if (vitalSignKey === "oxygenSaturation") return vitalValue(record, ["oxygenSaturation", "spo2"], "%");
  if (vitalSignKey === "bloodSugar") return vitalValue(record, ["bloodSugar", "bloodGlucose"], " mg/dL");
  if (vitalSignKey === "weight") return vitalValue(record, ["weight"], " lb");
  if (vitalSignKey === "painLevel") return vitalDisplayValue(record.painLevel);
  if (vitalSignKey === "bowelBladder") return vitalPairValue(record.bowelBladderEpisodes, " episodes", record.bowelBladderConcernType, " concern type");
  if (vitalSignKey === "intakeOutput") return vitalPairValue(record.intakeOutputBalance, " mL", record.intakeOutputConcernLevel, "/10 concern");
  if (vitalSignKey === "skinWound") return vitalPairValue(record.skinWoundSeverity, "/5", yesNoValue(record.skinWoundDrainage), " drainage present");
  if (vitalSignKey === "edema") return vitalPairValue(record.edemaGrade, "+", record.edemaLocationCount, " areas");
  if (vitalSignKey === "sleepFatigue") return vitalMultiValue([record.sleepHours, " hrs"], [record.fatigueLevel, "/10 fatigue"], [record.sleepAwakenings, " awakenings"]);
  if (vitalSignKey === "mentalStatusMood") return vitalMultiValue([record.mentalStatusScore, "/10 status"], [record.moodScore, "/10 mood"], [record.mentalStatusChangeType, " type"]);
  if (vitalSignKey === "fallMobility") return vitalPairValue(record.fallMobilityRisk, "/10 risk", yesNoValue(record.fallOccurred), " fall occurred");
  if (vitalSignKey === "medicationSideEffects") return vitalPairValue(record.medicationSideEffectSeverity, "/10", record.medicationSideEffectType, " type");
  return displayValue(record.notes);
};

const numericVitalValueForType = (record = {}, vitalSignKey = "") => {
  const numericFrom = (...fields) => {
    const value = fields
      .map((field) => record[field])
      .find((entry) => hasEnteredValue(entry));
    if (typeof value === "boolean") return value ? 1 : 0;
    const numeric = Number.parseFloat(String(value).replace(/[^\d.-]/g, ""));
    return Number.isFinite(numeric) ? numeric : null;
  };
  if (vitalSignKey === "bloodPressure") return numericFrom("systolicBloodPressure", "systolic", "bloodPressureSystolic");
  if (vitalSignKey === "pulse") return numericFrom("pulse", "heartRate");
  if (vitalSignKey === "temperature") return numericFrom("temperature");
  if (vitalSignKey === "respiratoryRate") return numericFrom("respiratoryRate");
  if (vitalSignKey === "oxygenSaturation") return numericFrom("oxygenSaturation", "spo2");
  if (vitalSignKey === "bloodSugar") return numericFrom("bloodSugar", "bloodGlucose");
  if (vitalSignKey === "weight") return numericFrom("weight");
  if (vitalSignKey === "painLevel") return numericFrom("painLevel");
  if (vitalSignKey === "bowelBladder") return numericFrom("bowelBladderEpisodes");
  if (vitalSignKey === "intakeOutput") return numericFrom("intakeOutputBalance", "intakeOutputConcernLevel");
  if (vitalSignKey === "skinWound") return numericFrom("skinWoundSeverity", "skinWoundDrainage");
  if (vitalSignKey === "edema") return numericFrom("edemaGrade", "edemaLocationCount");
  if (vitalSignKey === "sleepFatigue") return numericFrom("sleepHours", "fatigueLevel", "sleepAwakenings");
  if (vitalSignKey === "mentalStatusMood") return numericFrom("mentalStatusScore", "moodScore");
  if (vitalSignKey === "fallMobility") return numericFrom("fallMobilityRisk", "fallOccurred");
  if (vitalSignKey === "medicationSideEffects") return numericFrom("medicationSideEffectSeverity");
  return null;
};

const medicationFormFromRecord = (medication = {}) => {
  const medicationForm = optionValueFromStored(medicationFormOptions, medication.medicationForm);
  const classification = optionValueFromStored(medicationClassificationOptions, medication.classification);
  const doseUnitText = medicationDoseUnit(medication);
  const doseUnit = optionValueFromStored(medicationDoseUnitOptions, doseUnitText);
  const strengthUnitText = medicationStrengthUnit(medication);
  const strengthUnit = optionValueFromStored(medicationStrengthUnitOptions, strengthUnitText);
  const route = optionValueFromStored(medicationRouteOptions, medication.route);
  const frequency = optionValueFromStored(medicationFrequencyOptions, medication.frequency, "CUSTOM");
  const sourceType = optionValueFromStored(medicationSourceTypeOptions, medication.sourceType);
  const indication = optionValueFromStored(medicationIndicationOptions, medication.indication);
  const refills = optionValueFromStored(medicationRefillOptions, medication.refills);
  const foodInstructions = optionValuesFromStored(medicationFoodInstructionOptions, medication.foodInstructions, "CUSTOM");
  const administrationTime = parseAdministrationScheduleFromRecord(medication);
  const storageInstructions = optionValuesFromStored(medicationStorageOptions, medication.storageInstructions, "CUSTOM");
  const specialHandling = optionValuesFromStored(medicationSpecialHandlingOptions, medication.specialHandling, "CUSTOM");
  const prnReason = optionValuesFromStored(medicationPrnReasonOptions, medication.prnReason);
  const expectedBodyPartSource = Array.isArray(medication.expectedBodyParts) && medication.expectedBodyParts.length
    ? medication.expectedBodyParts
    : firstText(medication.expectedBodyPart, medication.bodyPart, medication.administrationSite, medication.injectionSite, medication.topicalSite);

  return {
    ...defaultMedicationForm(),
    medicationName: medication.medicationName || "",
    genericName: medication.genericName || "",
    brandName: medication.brandName || "",
    medicationForm,
    medicationFormOther: medicationForm === "OTHER" ? medication.medicationForm || "" : "",
    classification,
    classificationOther: classification === "OTHER" ? medication.classification || "" : "",
    dosage: numericDosageValue(medication.dosage),
    doseUnit,
    doseUnitOther: doseUnit === "OTHER" ? doseUnitText : "",
    strength: medicationStrength(medication),
    strengthAmount: medicationStrengthAmount(medication),
    strengthUnit,
    strengthUnitOther: strengthUnit === "OTHER" ? strengthUnitText : "",
    route,
    routeOther: route === "OTHER" ? medication.route || "" : "",
    expectedBodyPart: medicationBodyPartValues(expectedBodyPartSource),
    expectedBodyPartOther: firstText(medication.expectedBodyPartOther, medicationBodyPartOtherValue(expectedBodyPartSource)),
    frequency,
    frequencyOther: frequency === "CUSTOM" ? medication.frequency || "" : "",
    status: medication.status || "ACTIVE",
    prescribedBy: medication.prescribedBy || "",
    prescriberPhone: medication.prescriberPhone || "",
    prescriberEmail: medication.prescriberEmail || "",
    pharmacy: medication.pharmacy || "",
    pharmacyId: medication.pharmacyId || "",
    hospitalSource: medication.hospitalSource || "",
    sourceType,
    sourceTypeOther: sourceType === "OTHER" ? medication.sourceType || "" : "",
    sourceDocumentName: medication.sourceDocumentName || "",
    sourceDocumentDate: medication.sourceDocumentDate || "",
    sourceReferenceNumber: medication.sourceReferenceNumber || "",
    sourceContactName: medication.sourceContactName || "",
    sourceContactPhone: medication.sourceContactPhone || "",
    sourceContactEmail: medication.sourceContactEmail || "",
    sourceNotes: medication.sourceNotes || "",
    orderNumber: medication.orderNumber || "",
    orderDate: medication.orderDate || "",
    startDate: medication.startDate || "",
    endDate: medication.endDate || "",
    indication,
    indicationOther: indication === "OTHER" ? medication.indication || "" : "",
    quantity: medication.quantity || "",
    quantityAmount: medicationQuantityAmount(medication),
    initialQuantityAmount: medicationQuantityAmount(medication),
    remainingQuantity: medicationRemainingQuantityAmount(medication),
    quantityUnit: optionValueFromStored(medicationDoseUnitOptions, medicationQuantityUnit(medication)),
    quantityUnitOther: optionValueFromStored(medicationDoseUnitOptions, medicationQuantityUnit(medication)) === "OTHER" ? medicationQuantityUnit(medication) : "",
    refills,
    refillsOther: refills === "OTHER" ? medication.refills || "" : "",
    controlledSubstance: Boolean(medication.controlledSubstance),
    highAlertMedication: Boolean(medication.highAlertMedication),
    requiresVitalsCheck: Boolean(medication.requiresVitalsCheck),
    requiresBloodSugarCheck: Boolean(medication.requiresBloodSugarCheck),
    foodInstructions,
    foodInstructionsOther: foodInstructions.includes("CUSTOM") ? medication.foodInstructions || "" : "",
    medicationTimeZone: medication.medicationTimeZone || medication.administrationSchedule?.timeZone || browserTimeZone(),
    administrationTime: administrationTime.values,
    administrationTimeDetails: administrationTimeDetailsFor(administrationTime.values, administrationTime.details),
    administrationTimeOther: administrationTime.other,
    prn: isMedicationPrn(medication),
    withheld: isMedicationWithheld(medication),
    withheldReason: medication.withheldReason || "",
    resumeReason: medication.resumeReason || "",
    prnReason,
    prnReasonOther: prnReason.includes("OTHER") ? medication.prnReason || "" : "",
    maxDailyDose: medication.maxDailyDose || "",
    maxDailyDoseAmount: medicationMaxDailyDoseAmount(medication),
    maxDailyDoseUnit: optionValueFromStored(medicationDoseUnitOptions, medicationMaxDailyDoseUnit(medication)),
    maxDailyDoseUnitOther: optionValueFromStored(medicationDoseUnitOptions, medicationMaxDailyDoseUnit(medication)) === "OTHER" ? medicationMaxDailyDoseUnit(medication) : "",
    monitoringInstructions: medication.monitoringInstructions || "",
    sideEffectsToWatch: medication.sideEffectsToWatch || "",
    contraindications: medication.contraindications || "",
    storageInstructions,
    storageInstructionsOther: storageInstructions.includes("CUSTOM") ? medication.storageInstructions || "" : "",
    specialHandling,
    specialHandlingOther: specialHandling.includes("CUSTOM") ? medication.specialHandling || "" : "",
    missedDoseInstructions: medication.missedDoseInstructions || "",
    refusalInstructions: medication.refusalInstructions || "",
    holdParameters: medication.holdParameters || "",
    instructions: medication.instructions || "",
    notes: medication.notes || "",
    documents: Array.isArray(medication.documents) ? medication.documents : [],
  };
};

export default function ResidentMedicationsPage({ view = "medications" }) {
  const vitalSignsOnly = view === "vitals";
  const carePlansOnly = view === "carePlans";
  const standaloneResidentCareView = vitalSignsOnly || carePlansOnly;
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedWorkspaceId, workspaceListReady } = useWorkspace();
  const actorId = user?.id || user?.uid || user?.userId || "";
  const [residents, setResidents] = useState([]);
  const [workspacePharmacies, setWorkspacePharmacies] = useState([]);
  const [residentPharmacyLinks, setResidentPharmacyLinks] = useState([]);
  const [selectedResidentDetails, setSelectedResidentDetails] = useState(null);
  const [selectedResidentId, setSelectedResidentId] = useState(() => initialMedicationResidentId());
  const [medications, setMedications] = useState([]);
  const [newMedications, setNewMedications] = useState([]);
  const [newMedicationStatusFilter, setNewMedicationStatusFilter] = useState("PENDING_APPROVAL");
  const [refillRequests, setRefillRequests] = useState([]);
  const [residentAuditLogs, setResidentAuditLogs] = useState([]);
  const [vitalSigns, setVitalSigns] = useState([]);
  const [expectedMedications, setExpectedMedications] = useState([]);
  const [administrationLogs, setAdministrationLogs] = useState([]);
  const [selectedDate, setSelectedDate] = useState(() => initialMedicationDate());
  const [calendarMonth, setCalendarMonth] = useState(() => initialMedicationMonth());
  const [adminLogRangeStart, setAdminLogRangeStart] = useState(() => monthStartKey(initialMedicationMonth()));
  const [adminLogRangeEnd, setAdminLogRangeEnd] = useState(() => monthEndKey(initialMedicationMonth()));
  const [adminLogView, setAdminLogView] = useState(() => initialMedicationAdminLogView());
  const [medAdminView, setMedAdminView] = useState(() => initialMedicationAdminView());
  const [adminLogMedicationMode, setAdminLogMedicationMode] = useState("scheduled");
  const [adminLogFullscreen, setAdminLogFullscreen] = useState(false);
  const [residentSearch, setResidentSearch] = useState("");
  const [medicationSearch, setMedicationSearch] = useState("");
  const [vitalSignsSearch, setVitalSignsSearch] = useState("");
  const [vitalThresholdSearch, setVitalThresholdSearch] = useState("");
  const [activeVitalCarePlanTab, setActiveVitalCarePlanTab] = useState(() => carePlanTabFromHash());
  const [careActivityLogs, setCareActivityLogs] = useState([]);
  const [loadingCareActivityLogs, setLoadingCareActivityLogs] = useState(false);
  const [careActivityPage, setCareActivityPage] = useState(1);
  const [careActivityPageSize, setCareActivityPageSize] = useState(10);
  const [vitalThresholdOverridesByResident, setVitalThresholdOverridesByResident] = useState({});
  const [loadingVitalThresholds, setLoadingVitalThresholds] = useState(false);
  const [vitalThresholdSaving, setVitalThresholdSaving] = useState(false);
  const [selectedVitalThresholdKey, setSelectedVitalThresholdKey] = useState("");
  const [vitalThresholdDrawerOpen, setVitalThresholdDrawerOpen] = useState(false);
  const [vitalThresholdEditing, setVitalThresholdEditing] = useState(false);
  const [vitalThresholdForm, setVitalThresholdForm] = useState({});
  const [activeMedicationTab, setActiveMedicationTab] = useState(() => {
    if (vitalSignsOnly) return "vitals";
    if (carePlansOnly) return "carePlans";
    return medicationTabFromHash();
  });
  const [adminDrawerOpen, setAdminDrawerOpen] = useState(false);
  const [prnAdminDrawerOpen, setPrnAdminDrawerOpen] = useState(false);
  const [focusedAdministrationCell, setFocusedAdministrationCell] = useState(null);
  const [adminSavingKey, setAdminSavingKey] = useState("");
  const [adminCancelSaving, setAdminCancelSaving] = useState(false);
  const [adminActionDialog, setAdminActionDialog] = useState({
    open: false,
    medication: null,
    status: "",
    notes: "",
    confirmedBodyPart: "",
    confirmedBodyPartOther: "",
    requiresLateReason: false,
    administrationAt: "",
    administrationSlot: null,
    existingRecords: [],
    doseGiven: "",
    originalDoseGiven: "",
    doseEditing: false,
  });
  const [adminCancelDialog, setAdminCancelDialog] = useState({
    open: false,
    log: null,
    reason: "",
  });
  const [medicationDrawerOpen, setMedicationDrawerOpen] = useState(false);
  const [medicationDrawerMode, setMedicationDrawerMode] = useState("medication");
  const [medicationDetailsOpen, setMedicationDetailsOpen] = useState(false);
  const [selectedMedication, setSelectedMedication] = useState(null);
  const [adminLogDetailsOpen, setAdminLogDetailsOpen] = useState(false);
  const [selectedAdminLog, setSelectedAdminLog] = useState(null);
  const [adminEditDialog, setAdminEditDialog] = useState({ open: false, log: null, administrationAt: "", adminAction: "", notes: "", changeReason: "", lateAdministrationReason: "" });
  const [adminEditSaving, setAdminEditSaving] = useState(false);
  const [medicationDocumentPreview, setMedicationDocumentPreview] = useState({ open: false, document: null, index: 0 });
  const [selectedNewMedication, setSelectedNewMedication] = useState(null);
  const [newMedicationReviewForm, setNewMedicationReviewForm] = useState(() => newMedicationReviewFormFromRecord());
  const [newMedicationReviewOpen, setNewMedicationReviewOpen] = useState(false);
  const [newMedicationReviewRotation, setNewMedicationReviewRotation] = useState(0);
  const [newMedicationRejectDialog, setNewMedicationRejectDialog] = useState({ open: false, reason: "" });
  const workspaceParams = useMemo(
    () => ({ wid: selectedWorkspaceId, workspaceId: selectedWorkspaceId }),
    [selectedWorkspaceId],
  );
  useEffect(() => setNewMedicationReviewRotation(0), [selectedNewMedication?.id]);

  useEffect(() => {
    let active = true;
    if (!selectedResidentId) {
      setSelectedResidentDetails(null);
      return undefined;
    }
    setSelectedResidentDetails(null);
    careService.residents.getInformationSummary(selectedResidentId, workspaceParams)
      .then((payload) => {
        if (active) setSelectedResidentDetails(payload || null);
      })
      .catch(() => {
        if (active) setSelectedResidentDetails(null);
      });
    return () => {
      active = false;
    };
  }, [selectedResidentId, workspaceParams]);

  useEffect(() => {
    if (!adminLogFullscreen) return undefined;
    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setAdminLogFullscreen(false);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [adminLogFullscreen]);
  const [medicationImportReviewRef, setMedicationImportReviewRef] = useState(null);
  const [medicationImportRefresh, setMedicationImportRefresh] = useState(0);
  const [newMedicationReviewSaving, setNewMedicationReviewSaving] = useState("");
  const [extractedMedicationDataOpen, setExtractedMedicationDataOpen] = useState(false);
  const [selectedPharmacy, setSelectedPharmacy] = useState(null);
  const [pharmacyDetailsOpen, setPharmacyDetailsOpen] = useState(false);
  const [editingMedicationId, setEditingMedicationId] = useState("");
  const [refillRequestDialog, setRefillRequestDialog] = useState({ open: false, medication: null, pharmacyId: "", notes: "" });
  const [refillRequestSaving, setRefillRequestSaving] = useState(false);
  const [refillFulfillmentDialog, setRefillFulfillmentDialog] = useState({ open: false, request: null, quantityAmount: "", quantityUnit: "", notes: "" });
  const [refillFulfillmentSaving, setRefillFulfillmentSaving] = useState(false);
  const [refillCancellationDialog, setRefillCancellationDialog] = useState({ open: false, request: null, cancelReason: "" });
  const [refillCancellationSaving, setRefillCancellationSaving] = useState(false);
  const [refillFaxSendingId, setRefillFaxSendingId] = useState(null);
  const [refillFaxPreview, setRefillFaxPreview] = useState({ open: false, loading: false, url: "", fileName: "", pharmacyName: "", faxNumber: "", sentAt: null, status: "", error: "" });
  const refillPreviewGeneration = useRef(0);
  const [vitalSignsDrawerOpen, setVitalSignsDrawerOpen] = useState(false);
  const [selectedVitalSignKey, setSelectedVitalSignKey] = useState("bloodPressure");
  const [medicationForm, setMedicationForm] = useState(() => defaultMedicationForm());
  const [vitalSignsForm, setVitalSignsForm] = useState(() => defaultVitalSignsForm());
  const [selectedMedicationFormDocumentKey, setSelectedMedicationFormDocumentKey] = useState("");
  const [medicationSaving, setMedicationSaving] = useState(false);
  const [vitalSignsSaving, setVitalSignsSaving] = useState(false);
  const [medicationDeletingId, setMedicationDeletingId] = useState("");
  const [medicationWithholdingId, setMedicationWithholdingId] = useState("");
  const [medicationWithholdDialog, setMedicationWithholdDialog] = useState({ open: false, medication: null, nextWithheld: false, reason: "" });
  const [loadingResidents, setLoadingResidents] = useState(true);
  const [loadingMedications, setLoadingMedications] = useState(false);
  const [loadingNewMedications, setLoadingNewMedications] = useState(false);
  const [loadingRefills, setLoadingRefills] = useState(false);
  const [loadingResidentAuditLogs, setLoadingResidentAuditLogs] = useState(false);
  const [loadingVitalSigns, setLoadingVitalSigns] = useState(false);
  const [loadingPharmacies, setLoadingPharmacies] = useState(false);
  const [loadingDay, setLoadingDay] = useState(false);
  const [residentPharmacyForm, setResidentPharmacyForm] = useState({ pharmacyId: "", preferred: false, notes: "" });
  const [addingResidentPharmacy, setAddingResidentPharmacy] = useState(false);
  const [removingResidentPharmacyId, setRemovingResidentPharmacyId] = useState("");
  const [pharmacyRemoveDialog, setPharmacyRemoveDialog] = useState({ open: false, pharmacy: null });
  const [error, setError] = useState("");
  const [medicationError, setMedicationError] = useState("");
  const { uploadFile, uploading: documentUploading } = useFileUpload();

  const shouldLoadMedicationList = !standaloneResidentCareView && MEDICATION_LIST_TABS.has(activeMedicationTab);
  const shouldLoadDayData = !standaloneResidentCareView && MEDICATION_DAY_TABS.has(activeMedicationTab);
  const shouldLoadRefillRequests = !standaloneResidentCareView && activeMedicationTab === "refills";
  const shouldLoadResidentAuditLogs = !standaloneResidentCareView && activeMedicationTab === "logs";
  const shouldLoadVitalSigns = vitalSignsOnly || (!standaloneResidentCareView && activeMedicationTab === "vitals");
  const shouldLoadNewMedicationRecords = !standaloneResidentCareView && activeMedicationTab === "new";
  const shouldLoadVitalThresholds = carePlansOnly;
  const shouldLoadCareActivityLogs = carePlansOnly && activeVitalCarePlanTab === "activity";
  const shouldLoadPharmacyData = !standaloneResidentCareView && (
    activeMedicationTab === "pharmacy"
    || medicationDrawerOpen
    || medicationDetailsOpen
    || refillRequestDialog.open
  );

  const refreshCareActivityLogs = useCallback(async ({ silent = true } = {}) => {
    if (!selectedResidentId || !selectedWorkspaceId || !shouldLoadCareActivityLogs) return;
    if (!silent) setLoadingCareActivityLogs(true);
    try {
      const data = await careService.careActivityLogs.listForResident(selectedResidentId, { ...workspaceParams, size: 500 });
      setCareActivityLogs(pageItems(data));
    } catch (err) {
      setCareActivityLogs([]);
      if (!silent) {
        setMedicationError(apiErrorMessage(err, "Unable to load care activity logs."));
      }
    } finally {
      if (!silent) setLoadingCareActivityLogs(false);
    }
  }, [selectedResidentId, selectedWorkspaceId, shouldLoadCareActivityLogs, workspaceParams]);

  const reloadMedications = useCallback(async ({ force = false, silent = true } = {}) => {
    if (!selectedResidentId || !selectedWorkspaceId || (!force && !shouldLoadMedicationList)) {
      setMedications([]);
      setLoadingMedications(false);
      return;
    }
    if (!silent) setLoadingMedications(true);
    try {
      const data = await careService.residentMedications.listForResident(selectedResidentId, workspaceParams);
      setMedications(pageItems(data).filter(isMedicationListVisible));
      setMedicationError("");
    } catch (err) {
      setMedications([]);
      setMedicationError(apiErrorMessage(err, "Unable to load resident medications."));
    } finally {
      if (!silent) setLoadingMedications(false);
    }
  }, [selectedResidentId, selectedWorkspaceId, shouldLoadMedicationList, workspaceParams]);

  useEffect(() => {
    if (!workspaceListReady || !selectedWorkspaceId) {
      setLoadingResidents(false);
      return;
    }
    let active = true;
    setLoadingResidents(true);
    setError("");
    const requestKey = `${selectedWorkspaceId}:${actorId || "anonymous"}:100`;
    loadMedicationResidents(requestKey, () => careService.residents.listMini({ ...workspaceParams, size: 100 }))
      .then((data) => {
        if (!active) return;
        const rows = pageItems(data);
        setResidents(rows);
      })
      .catch((err) => {
        if (!active) return;
        setResidents([]);
        setError(apiErrorMessage(err, "Unable to load residents."));
      })
      .finally(() => active && setLoadingResidents(false));
    return () => {
      active = false;
    };
  }, [actorId, selectedWorkspaceId, workspaceListReady, workspaceParams]);

  useEffect(() => {
    let active = true;
    reloadMedications({ silent: false }).finally(() => {
      if (!active) return;
    });
    return () => {
      active = false;
    };
  }, [reloadMedications]);

  useEffect(() => {
    if (!selectedResidentId || !selectedWorkspaceId) {
      setRefillRequests([]);
      setLoadingRefills(false);
      return;
    }
    if (!shouldLoadRefillRequests) {
      setLoadingRefills(false);
      return;
    }
    let active = true;
    setLoadingRefills(true);
    careService.residentMedications.listRefillRequests(selectedResidentId, workspaceParams)
      .then((data) => {
        if (!active) return;
        setRefillRequests(pageItems(data));
      })
      .catch(() => {
        if (!active) return;
        setRefillRequests([]);
      })
      .finally(() => active && setLoadingRefills(false));
    return () => {
      active = false;
    };
  }, [selectedResidentId, selectedWorkspaceId, shouldLoadRefillRequests, workspaceParams]);

  useEffect(() => {
    if (!selectedResidentId || !selectedWorkspaceId) {
      setResidentAuditLogs([]);
      setLoadingResidentAuditLogs(false);
      return;
    }
    if (!shouldLoadResidentAuditLogs) {
      setLoadingResidentAuditLogs(false);
      return;
    }
    let active = true;
    setLoadingResidentAuditLogs(true);
    setMedicationError("");
    careService.residents.listAuditLogs(selectedResidentId, { ...workspaceParams, actorId, size: 100 })
      .then((data) => {
        if (!active) return;
        setResidentAuditLogs(pageItems(data));
      })
      .catch((err) => {
        if (!active) return;
        setResidentAuditLogs([]);
        setMedicationError(apiErrorMessage(err, "Unable to load resident audit logs."));
      })
      .finally(() => active && setLoadingResidentAuditLogs(false));
    return () => {
      active = false;
    };
  }, [actorId, selectedResidentId, selectedWorkspaceId, shouldLoadResidentAuditLogs, workspaceParams]);

  useEffect(() => {
    if (!selectedResidentId || !selectedWorkspaceId) {
      setVitalSigns([]);
      setLoadingVitalSigns(false);
      return;
    }
    if (!shouldLoadVitalSigns) {
      setLoadingVitalSigns(false);
      return;
    }
    let active = true;
    setLoadingVitalSigns(true);
    setMedicationError("");
    careService.residentVitalSigns.listForResident(selectedResidentId, { ...workspaceParams, size: 500 })
      .then((data) => {
        if (!active) return;
        setVitalSigns(pageItems(data));
      })
      .catch((err) => {
        if (!active) return;
        setVitalSigns([]);
        setMedicationError(apiErrorMessage(err, "Unable to load vital signs."));
      })
      .finally(() => active && setLoadingVitalSigns(false));
    return () => {
      active = false;
    };
  }, [selectedResidentId, selectedWorkspaceId, shouldLoadVitalSigns, workspaceParams]);

  useEffect(() => {
    if (!selectedResidentId || !selectedWorkspaceId || !shouldLoadVitalThresholds) {
      setLoadingVitalThresholds(false);
      return;
    }
    let active = true;
    setLoadingVitalThresholds(true);
    setMedicationError("");
    careService.residentVitalSignThresholds.listForResident(selectedResidentId, { ...workspaceParams, size: 500 })
      .then((data) => {
        if (!active) return;
        const overrides = vitalThresholdOverridesFromApi(data);
        setVitalThresholdOverridesByResident((current) => ({
          ...current,
          [selectedResidentId]: overrides,
        }));
      })
      .catch((err) => {
        if (!active) return;
        setMedicationError(apiErrorMessage(err, "Unable to load vital sign thresholds."));
      })
      .finally(() => active && setLoadingVitalThresholds(false));
    return () => {
      active = false;
    };
  }, [selectedResidentId, selectedWorkspaceId, shouldLoadVitalThresholds, workspaceParams]);

  useEffect(() => {
    if (!selectedResidentId || !selectedWorkspaceId || !shouldLoadCareActivityLogs) {
      setCareActivityLogs([]);
      setLoadingCareActivityLogs(false);
      return;
    }
    let active = true;
    setLoadingCareActivityLogs(true);
    setMedicationError("");
    careService.careActivityLogs.listForResident(selectedResidentId, { ...workspaceParams, size: 500 })
      .then((data) => {
        if (!active) return;
        setCareActivityLogs(pageItems(data));
      })
      .catch((err) => {
        if (!active) return;
        setCareActivityLogs([]);
        setMedicationError(apiErrorMessage(err, "Unable to load care activity logs."));
      })
      .finally(() => active && setLoadingCareActivityLogs(false));
    return () => {
      active = false;
    };
  }, [selectedResidentId, selectedWorkspaceId, shouldLoadCareActivityLogs, workspaceParams]);

  useEffect(() => {
    if (!selectedResidentId || !selectedWorkspaceId) {
      setNewMedications([]);
      setLoadingNewMedications(false);
      return;
    }
    if (!shouldLoadNewMedicationRecords) {
      setLoadingNewMedications(false);
      return;
    }
    let active = true;
    setLoadingNewMedications(true);
    setMedicationError("");
    careService.residentMedications.listNewMedications(selectedResidentId, {
      ...workspaceParams,
      status: newMedicationStatusFilter,
    })
      .then((data) => {
        if (!active) return;
        setNewMedications(pageItems(data).filter((record) =>
          newMedicationMatchesStatusFilter(record, newMedicationStatusFilter)));
      })
      .catch((err) => {
        if (!active) return;
        setNewMedications([]);
        setMedicationError(apiErrorMessage(err, "Unable to load new medication approvals."));
      })
      .finally(() => active && setLoadingNewMedications(false));
    return () => {
      active = false;
    };
  }, [newMedicationStatusFilter, selectedResidentId, selectedWorkspaceId, shouldLoadNewMedicationRecords, workspaceParams]);

  useEffect(() => {
    if (!workspaceListReady || !selectedWorkspaceId) {
      setWorkspacePharmacies([]);
      setLoadingPharmacies(false);
      return;
    }
    if (!shouldLoadPharmacyData) {
      setLoadingPharmacies(false);
      return;
    }
    let active = true;
    setLoadingPharmacies(true);
    careService.pharmacies.list(workspaceParams)
      .then((data) => {
        if (!active) return;
        setWorkspacePharmacies(pageItems(data));
      })
      .catch(() => {
        if (!active) return;
        setWorkspacePharmacies([]);
      })
      .finally(() => active && setLoadingPharmacies(false));
    return () => {
      active = false;
    };
  }, [selectedWorkspaceId, shouldLoadPharmacyData, workspaceListReady, workspaceParams]);

  useEffect(() => {
    if (!selectedResidentId || !selectedWorkspaceId) {
      setResidentPharmacyLinks([]);
      setLoadingPharmacies(false);
      return;
    }
    if (!shouldLoadPharmacyData) {
      setLoadingPharmacies(false);
      return;
    }
    let active = true;
    setLoadingPharmacies(true);
    careService.pharmacies.listForResident(selectedResidentId, workspaceParams)
      .then((data) => {
        if (!active) return;
        setResidentPharmacyLinks(pageItems(data));
      })
      .catch(() => {
        if (!active) return;
        setResidentPharmacyLinks([]);
      })
      .finally(() => active && setLoadingPharmacies(false));
    return () => {
      active = false;
    };
  }, [selectedResidentId, selectedWorkspaceId, shouldLoadPharmacyData, workspaceParams]);

  useEffect(() => {
    if (!selectedResidentId || !selectedWorkspaceId) {
      setExpectedMedications([]);
      setAdministrationLogs([]);
      setLoadingDay(false);
      return;
    }
    if (!shouldLoadDayData) {
      setLoadingDay(false);
      return;
    }
    let active = true;
    const logStartDate = activeMedicationTab === "adminLog" ? adminLogRangeStart : monthStartKey(calendarMonth);
    const logEndDate = activeMedicationTab === "adminLog" ? adminLogRangeEnd : monthEndKey(calendarMonth);
    setLoadingDay(true);
    setMedicationError("");
    Promise.all([
      careService.residentMedications.expectedForResident(selectedResidentId, { ...workspaceParams, date: selectedDate }),
      careService.residentMedications.listAdministrationLogs(selectedResidentId, {
        ...workspaceParams,
        startDate: logStartDate,
        endDate: logEndDate,
      }),
    ])
      .then(([expected, logs]) => {
        if (!active) return;
        setExpectedMedications(pageItems(expected));
        setAdministrationLogs(pageItems(logs));
      })
      .catch((err) => {
        if (!active) return;
        setExpectedMedications([]);
        setAdministrationLogs([]);
        setMedicationError(apiErrorMessage(err, "Unable to load medication calendar."));
      })
      .finally(() => active && setLoadingDay(false));
    return () => {
      active = false;
    };
  }, [activeMedicationTab, adminLogRangeEnd, adminLogRangeStart, calendarMonth, selectedDate, selectedResidentId, selectedWorkspaceId, shouldLoadDayData, workspaceParams]);

  useEffect(() => {
    if (standaloneResidentCareView) {
      setActiveMedicationTab(vitalSignsOnly ? "vitals" : "carePlans");
      if (carePlansOnly) {
        const handleHashChange = () => {
          if (window.location.hash === "#adls") {
            navigate(`/resident-adls${selectedResidentId ? `?residentId=${encodeURIComponent(selectedResidentId)}` : ""}`, { replace: true });
            return;
          }
          setActiveVitalCarePlanTab(carePlanTabFromHash());
        };
        handleHashChange();
        window.addEventListener("hashchange", handleHashChange);
        return () => window.removeEventListener("hashchange", handleHashChange);
      }
      return undefined;
    }
    const handleHashChange = () => setActiveMedicationTab(medicationTabFromHash());
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, [carePlansOnly, navigate, selectedResidentId, standaloneResidentCareView, vitalSignsOnly]);

  useEffect(() => {
    if (standaloneResidentCareView) {
      const url = new URL(window.location.href);
      if (selectedResidentId) {
        url.searchParams.set("residentId", selectedResidentId);
      } else {
        url.searchParams.delete("residentId");
      }
      url.searchParams.delete("date");
      url.searchParams.delete("month");
      url.searchParams.delete("logView");
      url.searchParams.delete("adminView");
      url.hash = carePlansOnly ? vitalCarePlanTabHash[activeVitalCarePlanTab] || vitalCarePlanTabHash.thresholds : "";
      window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
      return;
    }
    syncMedicationBookmarkUrl({
      residentId: selectedResidentId,
      tab: activeMedicationTab,
      selectedDate,
      monthDate: calendarMonth,
      adminLogView,
      medAdminView,
    });
  }, [activeMedicationTab, activeVitalCarePlanTab, adminLogView, calendarMonth, carePlansOnly, medAdminView, selectedDate, selectedResidentId, standaloneResidentCareView]);

  const filteredResidents = useMemo(() => {
    const term = residentSearch.trim().toLowerCase();
    if (!term) return residents;
    return residents.filter((resident) => {
      const haystack = [
        residentName(resident),
        residentRoom(resident),
        resident.status,
        resident.primaryContactEmail,
      ].join(" ").toLowerCase();
      return haystack.includes(term);
    });
  }, [residentSearch, residents]);

  const selectedResidentListItem = residents.find((resident) => residentId(resident) === selectedResidentId) || null;
  const selectedResident = selectedResidentListItem
    ? { ...selectedResidentListItem, ...(selectedResidentDetails || {}) }
    : selectedResidentDetails;
  const calendarDays = useMemo(() => buildCalendarDays(calendarMonth), [calendarMonth]);
  const yearOptions = useMemo(() => {
    const currentYear = todayDate().getFullYear();
    return Array.from({ length: 16 }, (_, index) => currentYear - 12 + index);
  }, []);
  const activeMedications = useMemo(
    () => medications
      .filter((medication) => medicationExpectedScheduledOn(medication, selectedDate))
      .sort(compareMedicationsByAdministrationTime),
    [medications, selectedDate],
  );
  const prnMedicationCount = useMemo(
    () => medications.filter((medication) => isMedicationPrn(medication)).length,
    [medications],
  );
  const scheduledMedicationCount = useMemo(
    () => medications.filter((medication) => !isMedicationPrn(medication)).length,
    [medications],
  );
  const medicationUnitWarning = useMemo(() => medicationUnitMismatchWarning(medicationForm), [medicationForm]);
  const newMedicationReviewUnitWarning = useMemo(
    () => medicationUnitMismatchWarning(newMedicationReviewForm),
    [newMedicationReviewForm],
  );
  const filteredMedicationList = useMemo(() => {
    const term = medicationSearch.trim().toLowerCase();
    if (!term) return medications;
    return medications.filter((medication) => {
      const haystack = [
        medicationName(medication),
        medication.genericName,
        medication.brandName,
        medication.dosage,
        medicationDoseUnit(medication),
        medicationDoseUnitDisplay(medication),
        medicationStrengthAmount(medication),
        medicationStrengthUnit(medication),
        medicationStrength(medication),
        medicationQuantityAmount(medication),
        medicationQuantityUnit(medication),
        medicationQuantityUnitDisplay(medication),
        medicationRemainingQuantityAmount(medication),
        medicationRemainingQuantityDisplay(medication),
        medication.quantity,
        medicationMaxDailyDoseAmount(medication),
        medicationMaxDailyDoseUnit(medication),
        medicationMaxDailyDoseUnitDisplay(medication),
        medication.maxDailyDose,
        medication.route,
        medication.frequency,
        medication.status,
        isMedicationWithheld(medication) ? "withheld" : "",
        isMedicationPrn(medication) ? "prn" : "",
        medication.prescribedBy,
        medication.pharmacy,
        medication.indication,
        medication.instructions,
        medication.notes,
      ].map((value) => String(value || "")).join(" ").toLowerCase();
      return haystack.includes(term);
    });
  }, [medicationSearch, medications]);
  const drawerExpectedMedications = useMemo(() => {
    const filteredMedications = medications.filter((medication) => medicationExpectedScheduledOn(medication, selectedDate));
    const sourceMedications = filteredMedications.length || !loadingMedications
      ? filteredMedications
      : expectedMedications.filter((medication) => medicationExpectedScheduledOn(medication, selectedDate));
    return [...sourceMedications].sort(compareMedicationsByAdministrationTime);
  }, [expectedMedications, loadingMedications, medications, selectedDate]);
  const drawerPrnMedications = useMemo(() =>
    medications
      .filter((medication) => medicationExpectedOn(medication, selectedDate) && isMedicationPrn(medication) && !isMedicationWithheld(medication))
      .sort((firstMedication, secondMedication) => medicationName(firstMedication).localeCompare(medicationName(secondMedication))),
  [medications, selectedDate]);
  const activeAdministrationLogs = useMemo(
    () => administrationLogs.filter((log) => !log.canceled && !log.overridden),
    [administrationLogs],
  );
  const administrationLogsForMedicationDay = useCallback((medicationId, key) =>
    activeAdministrationLogs.filter((log) => logMedicationId(log) === medicationId && logScheduledDate(log) === key), [activeAdministrationLogs]);
  const adminActionConflictRecords = useMemo(() => {
    const medication = adminActionDialog.medication;
    const administrationSlot = adminActionDialog.administrationSlot;
    if (!medication || !administrationSlot) return [];
    const medicationId = medicationRecordId(medication);
    const actualDate = new Date(adminActionDialog.administrationAt || 0);
    if (isMedicationPrn(medication)) {
      if (Number.isNaN(actualDate.getTime())) return [];
      const actualDateKey = dateKey(actualDate);
      return activeAdministrationLogs.filter((log) => {
        if (logMedicationId(log) !== medicationId || logScheduledSlot(log) !== "PRN") return false;
        const existingDate = new Date(log.administrationAt || log.createdAt || 0);
        return !Number.isNaN(existingDate.getTime())
          && dateKey(existingDate) === actualDateKey
          && Math.abs(existingDate.getTime() - actualDate.getTime()) <= 60 * 60 * 1000;
      });
    }
    return administrationLogsForMedicationDay(medicationId, selectedDate)
      .filter((log) => logScheduledSlot(log) === administrationSlot.scheduledSlot);
  }, [activeAdministrationLogs, adminActionDialog.administrationAt, adminActionDialog.administrationSlot, adminActionDialog.medication, administrationLogsForMedicationDay, selectedDate]);
  const adminActionDayPrnRecords = useMemo(() => {
    const medication = adminActionDialog.medication;
    if (!medication || !isMedicationPrn(medication)) return [];
    const actualDate = new Date(adminActionDialog.administrationAt || 0);
    if (Number.isNaN(actualDate.getTime())) return [];
    const targetDate = dateKey(actualDate);
    return activeAdministrationLogs
      .filter((log) => logMedicationId(log) === medicationRecordId(medication)
        && logScheduledSlot(log) === "PRN"
        && dateKey(new Date(log.administrationAt || log.createdAt || 0)) === targetDate)
      .sort((first, second) => Date.parse(first.administrationAt || first.createdAt || 0) - Date.parse(second.administrationAt || second.createdAt || 0));
  }, [activeAdministrationLogs, adminActionDialog.administrationAt, adminActionDialog.medication]);
  const adminActionRequiresLateReason = adminActionDialog.status === "ADMINISTERED"
    && medicationSlotAdministrationLate(adminActionDialog.administrationSlot, new Date(adminActionDialog.administrationAt || 0));
  const adminActionDoseChanged = adminActionDialog.status === "ADMINISTERED"
    && String(adminActionDialog.doseGiven || "").trim() !== String(adminActionDialog.originalDoseGiven || "").trim();
  const adminActionPrnDoseLimitStatus = useMemo(() => {
    const medication = adminActionDialog.medication;
    if (!medication || !isMedicationPrn(medication)) return null;
    const logs = administrationLogsForMedicationDay(medicationRecordId(medication), selectedDate)
      .filter((log) => logScheduledSlot(log) === "PRN");
    return medicationPrnDailyDoseLimitStatus(medication, logs);
  }, [adminActionDialog.medication, administrationLogsForMedicationDay, selectedDate]);
  const drawerExpectedMedicationAdministrations = useMemo(() => drawerExpectedMedications
    .filter((medication) => !isMedicationPrn(medication))
    .flatMap((medication) => medicationAdministrationCardsForDay(medication, selectedDate))
    .sort((firstCard, secondCard) => compareSortValues(firstCard.sortValue, secondCard.sortValue)),
  [drawerExpectedMedications, selectedDate]);
  const visibleDrawerAdministrations = useMemo(() => {
    if (!focusedAdministrationCell) return drawerExpectedMedicationAdministrations;
    return drawerExpectedMedicationAdministrations.filter(({ medication, administrationSlot }) =>
      medicationRecordId(medication) === focusedAdministrationCell.medicationId
      && (!focusedAdministrationCell.scheduledSlot
        || administrationSlot?.scheduledSlot === focusedAdministrationCell.scheduledSlot));
  }, [drawerExpectedMedicationAdministrations, focusedAdministrationCell]);
  const visibleDrawerPrnMedications = useMemo(() => {
    if (!focusedAdministrationCell) return drawerPrnMedications;
    return drawerPrnMedications.filter((medication) => medicationRecordId(medication) === focusedAdministrationCell.medicationId);
  }, [drawerPrnMedications, focusedAdministrationCell]);
  const focusedAdministrationMedication = focusedAdministrationCell
    ? medications.find((medication) => medicationRecordId(medication) === focusedAdministrationCell.medicationId) || null
    : null;
  const computedMissedCountForMedicationDay = useCallback((medication, key) => {
    if (!medicationMissedEvaluationApplies(key)) return 0;
    if (!medicationExpectedScheduledOn(medication, key)) return 0;
    const overdueSlots = medicationAdministrationTimedSlots(medication)
      .filter((slot) => medicationSlotOverdue(key, slot));
    if (!overdueSlots.length) return 0;
    const logs = administrationLogsForMedicationDay(medicationRecordId(medication), key);
    const recordedMissedCount = logs.filter((log) => logAdministrationAction(log) === "MISSED").length;
    const loggedSlots = new Set(
      logs
        .filter((log) => logAdministrationAction(log) !== "MISSED")
        .map(logScheduledSlot)
        .filter(Boolean),
    );
    const computedMissedCount = overdueSlots.filter((slot) => !loggedSlots.has(slot.scheduledSlot)).length;
    return Math.max(recordedMissedCount, computedMissedCount);
  }, [administrationLogsForMedicationDay]);
  const residentPharmacies = useMemo(() => {
    return residentPharmacyLinks.map((link) => {
      const linkedId = link.pharmacyId || link.pharmacy?.id;
      const workspaceRecord = linkedId ? workspacePharmacies.find((pharmacy) => pharmacy.id === linkedId) : null;
      const record = link.pharmacy || workspaceRecord || link;
      const id = link.pharmacyId || record.id;
      const name = pharmacyName(record);
      const linkedMedicationNames = medications
        .filter((medication) => {
          const medicationPharmacyId = medication.pharmacyId;
          if (id && medicationPharmacyId) return medicationPharmacyId === id;
          return firstText(medication.pharmacy) === name;
        })
        .map((medication) => medicationName(medication));
      const activeCount = medications.filter((medication) => {
        const medicationPharmacyId = medication.pharmacyId;
        const matchesPharmacy = id && medicationPharmacyId
          ? medicationPharmacyId === id
          : firstText(medication.pharmacy) === name;
        return matchesPharmacy && medicationExpectedOn(medication, selectedDate);
      }).length;
      return {
        linkId: link.id,
        id,
        name,
        record,
        selected: true,
        preferred: Boolean(link.preferred),
        notes: link.notes || "",
        medicationNames: linkedMedicationNames,
        activeCount,
      };
    }).filter((pharmacy) => pharmacy.id || pharmacy.name);
  }, [medications, residentPharmacyLinks, selectedDate, workspacePharmacies]);
  const residentPharmacyNames = [...residentPharmacies]
    .sort((first, second) => Number(second.preferred) - Number(first.preferred))
    .map((pharmacy) => pharmacy.name)
    .filter(Boolean);
  const pharmacyOptions = useMemo(() => {
    const selectedIds = new Set(residentPharmacyLinks.map((link) => link.pharmacyId || link.pharmacy?.id).filter(Boolean));
    return workspacePharmacies
      .filter((pharmacy) => selectedIds.has(pharmacy.id))
      .map((pharmacy) => ({
        id: pharmacy.id,
        name: pharmacyName(pharmacy),
        detail: firstText(pharmacyAddress(pharmacy), pharmacy.phone, pharmacy.email),
      }));
  }, [residentPharmacyLinks, workspacePharmacies]);
  const availableResidentPharmacyOptions = useMemo(() => {
    const selectedIds = new Set(residentPharmacyLinks.map((link) => link.pharmacyId || link.pharmacy?.id).filter(Boolean));
    return workspacePharmacies
      .filter((pharmacy) => pharmacy.id && !selectedIds.has(pharmacy.id))
      .map((pharmacy) => ({
        ...pharmacy,
        displayName: pharmacyName(pharmacy),
        displayAddress: firstText(pharmacyAddress(pharmacy), "No address added"),
      }));
  }, [residentPharmacyLinks, workspacePharmacies]);
  const selectedMedicationPharmacy = useMemo(() => {
    if (!selectedMedication) return null;
    const medicationPharmacyId = selectedMedication.pharmacyId;
    const medicationPharmacyName = firstText(selectedMedication.pharmacy);
    return residentPharmacies.find((pharmacy) => {
      if (medicationPharmacyId && pharmacy.id) return pharmacy.id === medicationPharmacyId;
      return medicationPharmacyName && pharmacyName(pharmacy) === medicationPharmacyName;
    }) || null;
  }, [residentPharmacies, selectedMedication]);
  const refillRequestPharmacy = useMemo(() =>
    residentPharmacies.find((pharmacy) => pharmacy.id === refillRequestDialog.pharmacyId) || null,
  [refillRequestDialog.pharmacyId, residentPharmacies]);
  const administrationTimeLimit = useMemo(
    () => administrationTimeLimitForFrequency(medicationForm.frequency, medicationForm.frequencyOther),
    [medicationForm.frequency, medicationForm.frequencyOther],
  );
  const administrationTimeGuidance = useMemo(() => {
    const updateReminder = "Medication time should be updated.";
    if (!medicationForm.frequency) return `Select frequency first, then choose administration time slots. ${updateReminder}`;
    if (administrationTimeLimit) return `${administrationTimeLimitLabel(administrationTimeLimit)} ${updateReminder}`;
    if (medicationForm.frequency === "AS_NEEDED") return "As-needed medications do not require scheduled administration time slots.";
    return `Select administration time slots if the custom order specifies them. ${updateReminder}`;
  }, [administrationTimeLimit, medicationForm.frequency]);
  const administrationTimeOptions = useMemo(() => {
    if (administrationTimeLimit) return multiSelectOptions(scheduledAdministrationTimeOptions);
    if (medicationForm.frequency === "AS_NEEDED") return [{ value: "AS_NEEDED", label: "As needed" }];
    return multiSelectOptions(medicationTimeOptions);
  }, [administrationTimeLimit, medicationForm.frequency]);
  const administrationTimePresets = useMemo(
    () => administrationTimePresetOptions(administrationTimeLimit),
    [administrationTimeLimit],
  );
  const newMedicationReviewAdministrationTimeLimit = useMemo(
    () => administrationTimeLimitForFrequency(newMedicationReviewForm.frequency, newMedicationReviewForm.frequencyOther),
    [newMedicationReviewForm.frequency, newMedicationReviewForm.frequencyOther],
  );
  const newMedicationReviewAdministrationTimeGuidance = useMemo(() => {
    const updateReminder = "Medication time should be updated.";
    if (!newMedicationReviewForm.frequency) return `Select frequency first, then choose administration time slots. ${updateReminder}`;
    if (newMedicationReviewAdministrationTimeLimit) return `${administrationTimeLimitLabel(newMedicationReviewAdministrationTimeLimit)} ${updateReminder}`;
    if (newMedicationReviewForm.frequency === "AS_NEEDED") return "As-needed medications do not require scheduled administration time slots.";
    return `Select administration time slots if the custom order specifies them. ${updateReminder}`;
  }, [newMedicationReviewAdministrationTimeLimit, newMedicationReviewForm.frequency]);
  const newMedicationReviewAdministrationTimeOptions = useMemo(() => {
    if (newMedicationReviewAdministrationTimeLimit) return multiSelectOptions(scheduledAdministrationTimeOptions);
    if (newMedicationReviewForm.frequency === "AS_NEEDED") return [{ value: "AS_NEEDED", label: "As needed" }];
    return multiSelectOptions(medicationTimeOptions);
  }, [newMedicationReviewAdministrationTimeLimit, newMedicationReviewForm.frequency]);
  const newMedicationReviewAdministrationTimePresets = useMemo(
    () => administrationTimePresetOptions(newMedicationReviewAdministrationTimeLimit),
    [newMedicationReviewAdministrationTimeLimit],
  );
  const dayLogs = useMemo(
    () => activeAdministrationLogs
      .filter((log) => logScheduledDate(log) === selectedDate)
      .sort((firstLog, secondLog) => compareSortValues(logAdministrationSortValue(firstLog), logAdministrationSortValue(secondLog))),
    [activeAdministrationLogs, selectedDate],
  );
  const visibleDrawerDayLogs = focusedAdministrationCell
    ? dayLogs.filter((log) => logMedicationId(log) === focusedAdministrationCell.medicationId)
    : dayLogs;
  const medicationAdminLogRows = useMemo(
    () => [...activeAdministrationLogs]
      .sort((firstLog, secondLog) =>
        (Date.parse(secondLog.administrationAt || secondLog.createdAt || secondLog.scheduledFor || "") || 0)
        - (Date.parse(firstLog.administrationAt || firstLog.createdAt || firstLog.scheduledFor || "") || 0)),
    [activeAdministrationLogs],
  );
  const medicationAdminLogDisplayRows = useMemo(
    () => medicationAdminLogRows.map((log) => {
      const medication = medications.find((item) => medicationRecordId(item) === logMedicationId(log)) || log.medication || {};
      const medicationDose = [medication.dosage, medicationDoseUnitDisplay(medication)].filter(Boolean).join(" ");
      const quantity = firstText(log.doseGiven, medicationDose, "-");
      const administeredBy = displayValue(log.administeredByName || log.performedByName || log.createdByName, "-");
      const timing = logTimingStatus(log);
      const action = logAdministrationAction(log);
      return {
        log,
        medication,
        medicationName: medicationName(medication),
        strength: displayValue(medicationStrength(medication), ""),
        isPrn: isMedicationPrn(medication) || logScheduledSlot(log) === "PRN",
        action: medicationAdminLogActionLabel(action),
        actionLevel: medicationAdminLogActionLevel(action),
        quantity,
        time: formatTimestamp(log.administrationAt || log.createdAt || log.scheduledFor),
        initials: userInitials(administeredBy),
        administeredBy,
        route: displayValue(medication.route, "-"),
        notes: displayValue(log.notes, ""),
        timing,
      };
    }),
    [medicationAdminLogRows, medications],
  );
  const medicationAdminLogRangeLabel = `${formatDate(adminLogRangeStart)} - ${formatDate(adminLogRangeEnd)}`;
  const medicationChartRangeStart = activeMedicationTab === "admin" ? monthStartKey(calendarMonth) : adminLogRangeStart;
  const medicationChartRangeEnd = activeMedicationTab === "admin" ? monthEndKey(calendarMonth) : adminLogRangeEnd;
  const medicationChartMonth = new Date(`${medicationChartRangeStart}T12:00:00`);
  const medicationChartRangeLabel = `${formatDate(medicationChartRangeStart)} - ${formatDate(medicationChartRangeEnd)}`;
  const medicationAdminLogDays = useMemo(
    () => dateKeysInRange(medicationChartRangeStart, medicationChartRangeEnd),
    [medicationChartRangeEnd, medicationChartRangeStart],
  );
  const medicationAdminLogMatrixRows = useMemo(() => medications
    .filter((medication) => adminLogMedicationMode === "both"
      || (adminLogMedicationMode === "prn") === isMedicationPrn(medication))
    .sort((firstMedication, secondMedication) =>
      Number(isMedicationPrn(firstMedication)) - Number(isMedicationPrn(secondMedication))
      || compareMedicationsByAdministrationTime(firstMedication, secondMedication))
    .map((medication) => {
      const medicationId = medicationRecordId(medication);
      const prn = isMedicationPrn(medication);
      const medicationLogs = medicationAdminLogRows.filter((log) => logMedicationId(log) === medicationId);
      const slots = prn
        ? [{ scheduledSlot: "PRN", time: null }]
        : medicationAdministrationTimedSlots(medication);
      return {
        medication,
        medicationId,
        isPrn: prn,
        slots: slots.length ? slots : [{ scheduledSlot: "SCHEDULED", time: null }],
        logsByDay: medicationLogs.reduce((result, log) => {
          const key = logScheduledDate(log);
          if (key) (result[key] ||= []).push(log);
          return result;
        }, {}),
      };
    }), [adminLogMedicationMode, medicationAdminLogRows, medications]);
  const selectedAdminLogDisplay = useMemo(() => {
    if (!selectedAdminLog) return null;
    const medication = medications.find((item) => medicationRecordId(item) === logMedicationId(selectedAdminLog)) || selectedAdminLog.medication || {};
    const medicationDose = [medication.dosage, medicationDoseUnitDisplay(medication)].filter(Boolean).join(" ");
    const administeredBy = displayValue(selectedAdminLog.administeredByName || selectedAdminLog.performedByName || selectedAdminLog.createdByName, "-");
    const timing = logTimingStatus(selectedAdminLog);
    const action = logAdministrationAction(selectedAdminLog);
    return {
      log: selectedAdminLog,
      medication,
      medicationName: medicationName(medication),
      strength: displayValue(medicationStrength(medication), "-"),
      isPrn: isMedicationPrn(medication) || logScheduledSlot(selectedAdminLog) === "PRN",
      action: medicationAdminLogActionLabel(action),
      actionLevel: medicationAdminLogActionLevel(action),
      quantity: firstText(selectedAdminLog.doseGiven, medicationDose, "-"),
      time: formatTimestamp(selectedAdminLog.administrationAt || selectedAdminLog.createdAt || selectedAdminLog.scheduledFor),
      initials: userInitials(administeredBy),
      administeredBy,
      route: displayValue(medication.route, "-"),
      notes: displayValue(selectedAdminLog.notes, ""),
      timing,
      bodyPart: selectedAdminLog.confirmedBodyPart ? medicationBodyPartLabel(selectedAdminLog.confirmedBodyPart, medication.route) : "",
    };
  }, [medications, selectedAdminLog]);
  const medicationAlerts = useMemo(() => {
    const alerts = [];
    const today = dateKey(todayDate());
    const targetDate = selectedDate || today;

    medications
      .filter((medication) => medicationExpectedScheduledOn(medication, targetDate))
      .forEach((medication) => {
        const missedCount = computedMissedCountForMedicationDay(medication, targetDate);
        if (missedCount > 0) {
          addMedicationAlert(
            alerts,
            "high",
            "Missed medication",
            medication,
            `${formatDate(targetDate)} has ${missedCount} missed ${missedCount === 1 ? "dose" : "doses"}.`,
            "Open the Med Admin calendar day and record or review the administration outcome.",
          );
        }
      });

    medications.forEach((medication) => {
      const start = medication.startDate;
      const end = medication.endDate;
      if (start) {
        const startsIn = daysBetween(today, start);
        if (startsIn >= 0 && startsIn <= 7) {
          addMedicationAlert(
            alerts,
            "low",
            "Upcoming medication start",
            medication,
            `Starts ${formatDate(start)}.`,
            "Confirm medication supply, pharmacy, and administration instructions.",
          );
        }
      }
      if (end) {
        const endsIn = daysBetween(today, end);
        if (endsIn < 0) {
          addMedicationAlert(
            alerts,
            "medium",
            "Medication order ended",
            medication,
            `Ended ${formatDate(end)}.`,
            "Confirm whether this medication should be discontinued or renewed.",
          );
        } else if (endsIn <= 14) {
          addMedicationAlert(
            alerts,
            "medium",
            "Medication expiring soon",
            medication,
            `Ends ${formatDate(end)}.`,
            "Follow up with the prescriber or pharmacy before the end date.",
          );
        }
      }
      const refillCount = numericRefills(medication.refills);
      const refillText = String(medication.refills || "").toLowerCase();
      if (refillCount === 0 || refillText.includes("none") || refillText.includes("provider") || refillText.includes("expired")) {
        addMedicationAlert(
          alerts,
          refillCount === 0 || refillText.includes("expired") ? "medium" : "low",
          "Refill attention needed",
          medication,
          medication.refills ? `Refills: ${medication.refills}.` : "Refill details are not added.",
          "Review refill status and request a new order if needed.",
        );
      }
    });

    return alerts.sort((a, b) => (alertSeverityRank[a.severity] ?? 9) - (alertSeverityRank[b.severity] ?? 9));
  }, [computedMissedCountForMedicationDay, medications, selectedDate]);

  const selectMedicationResident = (id) => {
    setSelectedResidentId(id);
    setVitalSignsForm(defaultVitalSignsForm());
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const target = vitalSignsOnly
          ? document.getElementById("vital-signs")
          : carePlansOnly
            ? document.getElementById("resident-care-plans")
            : document.querySelector(".resident-medication-main > .medication-tabs");
        target?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });
    });
  };

  const selectMedicationTab = (tab) => {
    setActiveMedicationTab(tab);
  };

  const openVitalThreshold = (thresholdKey) => {
    const threshold = vitalThresholdMap[thresholdKey];
    if (!threshold) return;
    const residentOverrides = selectedResidentId ? vitalThresholdOverridesByResident[selectedResidentId] || {} : {};
    const nextValues = thresholdValuesForResident(threshold, residentOverrides);
    setSelectedVitalThresholdKey(thresholdKey);
    setVitalThresholdForm(nextValues);
    setVitalThresholdEditing(false);
    setVitalThresholdDrawerOpen(true);
  };

  const updateVitalThresholdForm = (field, value) => {
    setVitalThresholdForm((current) => ({ ...current, [field]: value }));
  };

  const saveVitalThreshold = async () => {
    if (!selectedVitalThresholdKey || vitalThresholdSaving) return;
    if (!selectedResidentId) {
      message.warning("Select a resident before updating thresholds.");
      return;
    }
    if (!selectedWorkspaceId) {
      message.warning("Select a workspace before updating thresholds.");
      return;
    }
    const threshold = vitalThresholdMap[selectedVitalThresholdKey];
    const nextValues = normalizeThresholdValues(threshold, vitalThresholdForm);
    const invalidNumberField = invalidThresholdNumberField(threshold, nextValues);
    if (invalidNumberField) {
      message.warning(`${invalidNumberField[1]} must be a number.`);
      return;
    }
    setVitalThresholdSaving(true);
    try {
      const saved = await careService.residentVitalSignThresholds.saveForResident(
        selectedResidentId,
        selectedVitalThresholdKey,
        {
          residentId: selectedResidentId,
          workspaceId: selectedWorkspaceId,
          thresholdKey: selectedVitalThresholdKey,
          title: threshold.title,
          values: nextValues,
        },
        workspaceParams,
      );
      const savedValues = normalizeThresholdValues(threshold, saved?.values || nextValues);
      setVitalThresholdOverridesByResident((current) => ({
        ...current,
        [selectedResidentId]: {
          ...(current[selectedResidentId] || {}),
          [selectedVitalThresholdKey]: savedValues,
        },
      }));
      setVitalThresholdForm(savedValues);
      setVitalThresholdEditing(false);
      message.success("Vital sign threshold updated.");
    } catch (err) {
      message.error(apiErrorMessage(err, "Unable to save vital sign threshold."));
    } finally {
      setVitalThresholdSaving(false);
    }
  };

  const openMedicationDetails = (medication) => {
    setSelectedMedication(medication);
    setMedicationDetailsOpen(true);
  };

  const openAdminLogDetails = (log) => {
    setSelectedAdminLog(log);
    setAdminLogDetailsOpen(true);
  };

  const openEditAdministrationLog = (log) => {
    if (!log?.id || log.canceled || log.overridden) return;
    setAdminLogDetailsOpen(false);
    setAdminEditDialog({
      open: true,
      log,
      administrationAt: localDateTimeInputValue(new Date(log.administrationAt || log.createdAt)),
      adminAction: logAdministrationAction(log),
      notes: log.notes || "",
      changeReason: "",
      lateAdministrationReason: "",
    });
  };

  const submitEditAdministrationLog = async () => {
    const { log, administrationAt, adminAction, notes, changeReason, lateAdministrationReason } = adminEditDialog;
    if (!log?.id) return;
    const actualDate = new Date(administrationAt);
    if (!administrationAt || Number.isNaN(actualDate.getTime())) {
      message.warning("Enter the actual administration date and time.");
      return;
    }
    if (!changeReason.trim()) {
      message.warning("Explain why this administration record is being changed.");
      return;
    }
    const outsideExpectedRange = administrationCorrectionOutsideExpectedRange(log, administrationAt);
    if (outsideExpectedRange && !lateAdministrationReason.trim()) {
      message.warning("Add a late medication administration reason because the corrected time is outside the expected range.");
      return;
    }
    setAdminEditSaving(true);
    try {
      const updatedLog = await careService.residentMedications.updateAdministrationLog(log.id, {
        administrationAt: actualDate.toISOString(),
        adminAction,
        notes: notes.trim(),
        changeReason: changeReason.trim(),
        lateAdministrationReason: outsideExpectedRange ? lateAdministrationReason.trim() : null,
      }, workspaceParams);
      setAdministrationLogs((current) => [updatedLog, ...current.filter((item) => item.id !== log.id && item.id !== updatedLog.id)]);
      setSelectedAdminLog(updatedLog);
      const logStartDate = activeMedicationTab === "adminLog" ? adminLogRangeStart : monthStartKey(calendarMonth);
      const logEndDate = activeMedicationTab === "adminLog" ? adminLogRangeEnd : monthEndKey(calendarMonth);
      try {
        const refreshedLogs = await careService.residentMedications.listAdministrationLogs(selectedResidentId, {
          ...workspaceParams,
          startDate: logStartDate,
          endDate: logEndDate,
        });
        setAdministrationLogs(pageItems(refreshedLogs));
      } catch {
        // Keep the successfully updated record already applied to local state.
      }
      setAdminEditDialog({ open: false, log: null, administrationAt: "", adminAction: "", notes: "", changeReason: "", lateAdministrationReason: "" });
      message.success("Medication administration record updated.");
    } catch (err) {
      message.error(apiErrorMessage(err, "Unable to update medication administration record."));
    } finally {
      setAdminEditSaving(false);
    }
  };

  const medicationAdminLogReportHtml = () => {
    const rows = medicationAdminLogDisplayRows.map((row) => `
      <tr>
        <td>${escapeHtml(row.medicationName)}${row.isPrn ? " <strong>PRN</strong>" : ""}</td>
        <td>${escapeHtml(row.action)}</td>
        <td>${escapeHtml(row.quantity)}</td>
        <td>${escapeHtml(row.time)}</td>
        <td>${escapeHtml(row.timing.label)}</td>
        <td>${escapeHtml(row.initials)}</td>
        <td>${escapeHtml(row.administeredBy)}</td>
      </tr>
    `).join("");
    return `
      <!doctype html>
      <html>
        <head>
          <title>Medication Admin Log</title>
          <style>
            body { color: #173324; font-family: Arial, sans-serif; margin: 32px; }
            h1 { color: #177c38; font-size: 24px; margin: 0 0 8px; }
            p { color: #475569; margin: 0 0 18px; }
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid #d8e3dc; font-size: 12px; padding: 8px; text-align: left; vertical-align: top; }
            th { background: #f0fbf1; color: #17452b; }
          </style>
        </head>
        <body>
          <h1>Medication Admin Log</h1>
          <p>${escapeHtml(selectedResident ? residentName(selectedResident) : "Selected resident")} | ${escapeHtml(medicationAdminLogRangeLabel)}</p>
          <table>
            <thead><tr><th>Medication</th><th>Action</th><th>Quantity</th><th>Time</th><th>Timing</th><th>Initials</th><th>Administered by</th></tr></thead>
            <tbody>${rows || '<tr><td colspan="7">No medication administration records found.</td></tr>'}</tbody>
          </table>
        </body>
      </html>
    `;
  };

  const printMedicationAdminLog = ({ pdf = false } = {}) => {
    const printWindow = window.open("", "_blank", "width=1100,height=760");
    if (!printWindow) return;
    printWindow.document.write(medicationAdminLogReportHtml());
    printWindow.document.close();
    printWindow.focus();
    if (pdf) {
      message.info("Use Save as PDF in the print dialog to export the medication admin log.");
    }
    window.setTimeout(() => printWindow.print(), 180);
  };

  const printMedicationAdminLogChart = () => {
    const chart = document.querySelector(".medication-med-log");
    if (!chart) {
      message.warning("The medication chart is not available to print.");
      return;
    }
    const printWindow = window.open("", "_blank", "width=1400,height=900");
    if (!printWindow) {
      message.warning("Allow pop-ups to print the medication chart.");
      return;
    }
    const chartCopy = chart.cloneNode(true);
    chartCopy.classList.remove("is-fullscreen");
    chartCopy.classList.add("is-print-copy");
    const styles = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
      .map((node) => node.outerHTML)
      .join("");
    printWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <title>${escapeHtml(selectedResident ? residentName(selectedResident) : "Resident")} Medication Chart</title>
          ${styles}
          <style>
            @page { size: landscape; margin: 0.3in; }
            html, body { background: #fff !important; margin: 0; padding: 0; }
            body { color-adjust: exact; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .medication-med-log.is-print-copy { border: 1px solid #b8d2c0; border-radius: 6px; display: block; overflow: visible; }
            .medication-med-log.is-print-copy .medication-med-log-head { border: 0; }
            .medication-med-log.is-print-copy .medication-med-log-scroll { overflow: visible; }
            .medication-med-log.is-print-copy .medication-med-log-head-actions button { display: none !important; }
            .medication-med-log.is-print-copy .medication-med-log-table { font-size: 7px; min-width: 0; table-layout: fixed; width: 100%; }
            .medication-med-log.is-print-copy .medication-med-log-table th,
            .medication-med-log.is-print-copy .medication-med-log-table td { height: 34px; min-width: 0; padding: 2px; position: static; }
            .medication-med-log.is-print-copy .medication-med-log-table .medication-med-log-medication { min-width: 0; padding: 4px; width: 15%; }
            .medication-med-log.is-print-copy .medication-med-log-table .medication-med-log-time { min-width: 0; width: 5%; }
            .medication-med-log.is-print-copy .medication-med-log-status { transform: scale(0.82); }
            .medication-med-log.is-print-copy .medication-med-log-administer,
            .medication-med-log.is-print-copy .medication-med-log-empty { visibility: hidden; }
          </style>
        </head>
        <body>${chartCopy.outerHTML}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    window.setTimeout(() => printWindow.print(), 500);
  };

  const exportMedicationAdminLogExcel = () => {
    const rows = [
      ["Medication", "PRN", "Action", "Quantity", "Time", "Timing", "Initials", "Administered by"],
      ...medicationAdminLogDisplayRows.map((row) => [
        row.medicationName,
        row.isPrn ? "PRN" : "",
        row.action,
        row.quantity,
        row.time,
        row.timing.label,
        row.initials,
        row.administeredBy,
      ]),
    ];
    const htmlRows = rows.map((row) => `<tr>${row.map((cell) => `<td>${escapeHtml(cell)}</td>`).join("")}</tr>`).join("");
    downloadTextBlob(
      `<table>${htmlRows}</table>`,
      `medication-admin-log-${adminLogRangeStart}-to-${adminLogRangeEnd}.xls`,
      "application/vnd.ms-excel;charset=utf-8",
    );
  };

  const openMedicationDocumentPreview = (document, index = 0) => {
    if (!medicationDocumentUrl(document)) return;
    setMedicationDocumentPreview({ open: true, document, index });
  };

  const closeMedicationDocumentPreview = () => {
    setMedicationDocumentPreview({ open: false, document: null, index: 0 });
  };

  const updateVitalSignsForm = (field, value) => {
    setVitalSignsForm((current) => ({ ...current, [field]: value }));
  };

  const openVitalSignsCapture = (vitalSignKey = "bloodPressure") => {
    setSelectedVitalSignKey(vitalSignChecklistMap[vitalSignKey] ? vitalSignKey : "bloodPressure");
    setVitalSignsForm(defaultVitalSignsForm());
    setVitalSignsDrawerOpen(true);
  };

  const submitVitalSigns = async (event) => {
    event.preventDefault();
    if (!selectedResidentId || vitalSignsSaving) return;
    const captureFields = vitalSignValueFields[selectedVitalSignKey] || ["notes"];
    const hasVitalValue = captureFields.some((field) => hasEnteredValue(vitalSignsForm[field]))
      || hasEnteredValue(vitalSignsForm.notes);
    if (!hasVitalValue) {
      message.warning(`Enter ${selectedVitalSign.label.toLowerCase()} details.`);
      return;
    }
    const localRecordedAt = `${vitalSignsForm.recordedDate || dateKey(todayDate())}T${vitalSignsForm.recordedTime || currentTimeInput()}:00`;
    const recordedAtDate = new Date(localRecordedAt);
    if (Number.isNaN(recordedAtDate.getTime())) {
      message.warning("Enter a valid recorded date and time.");
      return;
    }
    const recordedAt = recordedAtDate.toISOString();
    const payload = {
      residentId: selectedResidentId,
      vitalSignType: selectedVitalSign.key,
      vitalSignLabel: selectedVitalSign.label,
      recordedAt,
      systolicBloodPressure: vitalSignsForm.systolicBloodPressure,
      diastolicBloodPressure: vitalSignsForm.diastolicBloodPressure,
      bloodPressure: vitalSignsForm.systolicBloodPressure && vitalSignsForm.diastolicBloodPressure
        ? `${vitalSignsForm.systolicBloodPressure}/${vitalSignsForm.diastolicBloodPressure}`
        : "",
      pulse: vitalSignsForm.pulse,
      heartRate: vitalSignsForm.pulse,
      temperature: vitalSignsForm.temperature,
      temperatureUnit: vitalSignsForm.temperatureUnit,
      respiratoryRate: vitalSignsForm.respiratoryRate,
      oxygenSaturation: vitalSignsForm.oxygenSaturation,
      bloodSugar: vitalSignsForm.bloodSugar,
      weight: vitalSignsForm.weight,
      painLevel: vitalSignsForm.painLevel,
      bowelBladderEpisodes: vitalSignsForm.bowelBladderEpisodes,
      bowelBladderConcernType: vitalSignsForm.bowelBladderConcernType,
      intakeOutputBalance: vitalSignsForm.intakeOutputBalance,
      intakeOutputConcernLevel: vitalSignsForm.intakeOutputConcernLevel,
      skinWoundSeverity: vitalSignsForm.skinWoundSeverity,
      skinWoundDrainage: hasEnteredValue(vitalSignsForm.skinWoundDrainage) ? booleanValue(vitalSignsForm.skinWoundDrainage) : null,
      edemaGrade: vitalSignsForm.edemaGrade,
      edemaLocationCount: vitalSignsForm.edemaLocationCount,
      sleepHours: vitalSignsForm.sleepHours,
      fatigueLevel: vitalSignsForm.fatigueLevel,
      sleepAwakenings: vitalSignsForm.sleepAwakenings,
      mentalStatusScore: vitalSignsForm.mentalStatusScore,
      moodScore: vitalSignsForm.moodScore,
      mentalStatusChangeType: vitalSignsForm.mentalStatusChangeType,
      fallMobilityRisk: vitalSignsForm.fallMobilityRisk,
      fallOccurred: hasEnteredValue(vitalSignsForm.fallOccurred) ? booleanValue(vitalSignsForm.fallOccurred) : null,
      medicationSideEffectSeverity: vitalSignsForm.medicationSideEffectSeverity,
      medicationSideEffectType: vitalSignsForm.medicationSideEffectType,
      measurementValue: numericVitalValueForType(vitalSignsForm, selectedVitalSign.key),
      position: vitalSignsForm.position,
      site: vitalSignsForm.site,
      notes: vitalSignsForm.notes,
      recordedById: userId(user) || null,
      recordedByName: userName(user),
    };
    setVitalSignsSaving(true);
    try {
      const saved = await careService.residentVitalSigns.createForResident(selectedResidentId, payload, workspaceParams);
      setVitalSigns((current) => [saved, ...current.filter((item) => item.id !== saved.id)]);
      await refreshCareActivityLogs();
      setVitalSignsForm(defaultVitalSignsForm());
      message.success("Vital signs saved.");
    } catch (err) {
      message.error(apiErrorMessage(err, "Unable to save vital signs."));
    } finally {
      setVitalSignsSaving(false);
    }
  };

  const openNewMedicationReview = (record, importReference = null) => {
    setMedicationImportReviewRef(importReference);
    setSelectedNewMedication(record);
    setNewMedicationReviewForm(newMedicationReviewFormFromRecord(record));
    setNewMedicationReviewOpen(true);
  };

  const updateNewMedicationReviewForm = (field, value) => {
    setNewMedicationReviewForm((current) => applyMedicationFormUpdate(current, field, value));
  };

  const newMedicationReviewPayload = () => {
    const extracted = selectedNewMedication?.extractedMedicationDetails
      && typeof selectedNewMedication.extractedMedicationDetails === "object"
      && !Array.isArray(selectedNewMedication.extractedMedicationDetails)
      ? selectedNewMedication.extractedMedicationDetails
      : {};
    const quantityAmount = numericDecimalValue(newMedicationReviewForm.quantityAmount);
    const quantityChanged = quantityAmount !== numericDecimalValue(newMedicationReviewForm.initialQuantityAmount);
    return {
      residentId: selectedNewMedication?.residentId || selectedResidentId,
      medicationName: newMedicationReviewForm.medicationName,
      genericName: newMedicationReviewForm.genericName,
      brandName: newMedicationReviewForm.brandName,
      medicationForm: customSelectValue(newMedicationReviewForm, "medicationForm", medicationFormOptions),
      classification: customSelectValue(newMedicationReviewForm, "classification", medicationClassificationOptions),
      dosage: numericDosageValue(newMedicationReviewForm.dosage),
      doseUnit: customSelectValue(newMedicationReviewForm, "doseUnit", medicationDoseUnitOptions),
      strengthAmount: numericDecimalValue(newMedicationReviewForm.strengthAmount),
      strengthUnit: customSelectValue(newMedicationReviewForm, "strengthUnit", medicationStrengthUnitOptions),
      strength: [numericDecimalValue(newMedicationReviewForm.strengthAmount), customSelectValue(newMedicationReviewForm, "strengthUnit", medicationStrengthUnitOptions)].filter(Boolean).join(" "),
      route: customSelectValue(newMedicationReviewForm, "route", medicationRouteOptions),
      frequency: customSelectValue(newMedicationReviewForm, "frequency", medicationFrequencyOptions),
      status: newMedicationReviewForm.status,
      prescribedBy: newMedicationReviewForm.prescribedBy,
      prescriberPhone: newMedicationReviewForm.prescriberPhone,
      prescriberEmail: newMedicationReviewForm.prescriberEmail,
      pharmacy: newMedicationReviewForm.pharmacy,
      pharmacyId: newMedicationReviewForm.pharmacyId || null,
      hospitalSource: newMedicationReviewForm.hospitalSource,
      sourceType: customSelectValue(newMedicationReviewForm, "sourceType", medicationSourceTypeOptions),
      sourceDocumentName: newMedicationReviewForm.sourceDocumentName,
      sourceDocumentDate: newMedicationReviewForm.sourceDocumentDate || null,
      sourceReferenceNumber: newMedicationReviewForm.sourceReferenceNumber,
      sourceContactName: newMedicationReviewForm.sourceContactName,
      sourceContactPhone: newMedicationReviewForm.sourceContactPhone,
      sourceContactEmail: newMedicationReviewForm.sourceContactEmail,
      sourceNotes: newMedicationReviewForm.sourceNotes,
      orderNumber: newMedicationReviewForm.orderNumber,
      orderDate: newMedicationReviewForm.orderDate || null,
      startDate: newMedicationReviewForm.startDate || null,
      endDate: newMedicationReviewForm.endDate || null,
      indication: customSelectValue(newMedicationReviewForm, "indication", medicationIndicationOptions),
      quantityAmount,
      remainingQuantity: quantityChanged ? quantityAmount : numericDecimalValue(newMedicationReviewForm.remainingQuantity || newMedicationReviewForm.quantityAmount),
      quantityUnit: customSelectValue(newMedicationReviewForm, "quantityUnit", medicationDoseUnitOptions),
      quantity: [quantityAmount, customSelectValue(newMedicationReviewForm, "quantityUnit", medicationDoseUnitOptions)].filter(Boolean).join(" "),
      refills: customSelectValue(newMedicationReviewForm, "refills", medicationRefillOptions),
      controlledSubstance: newMedicationReviewForm.controlledSubstance,
      highAlertMedication: newMedicationReviewForm.highAlertMedication,
      requiresVitalsCheck: newMedicationReviewForm.requiresVitalsCheck,
      requiresBloodSugarCheck: newMedicationReviewForm.requiresBloodSugarCheck,
      foodInstructions: customSelectValue(newMedicationReviewForm, "foodInstructions", medicationFoodInstructionOptions),
      medicationTimeZone: newMedicationReviewForm.medicationTimeZone || browserTimeZone(),
      administrationTime: administrationTimePayloadValue(newMedicationReviewForm),
      administrationSchedule: administrationSchedulePayloadValue(newMedicationReviewForm),
      prn: booleanValue(newMedicationReviewForm.prn) || newMedicationReviewForm.frequency === "AS_NEEDED" || Boolean(customSelectValue(newMedicationReviewForm, "prnReason", medicationPrnReasonOptions)),
      withheld: booleanValue(newMedicationReviewForm.withheld),
      prnReason: customSelectValue(newMedicationReviewForm, "prnReason", medicationPrnReasonOptions),
      maxDailyDoseAmount: numericDecimalValue(newMedicationReviewForm.maxDailyDoseAmount),
      maxDailyDoseUnit: customSelectValue(newMedicationReviewForm, "maxDailyDoseUnit", medicationDoseUnitOptions),
      maxDailyDose: [numericDecimalValue(newMedicationReviewForm.maxDailyDoseAmount), customSelectValue(newMedicationReviewForm, "maxDailyDoseUnit", medicationDoseUnitOptions)].filter(Boolean).join(" "),
      monitoringInstructions: newMedicationReviewForm.monitoringInstructions,
      sideEffectsToWatch: newMedicationReviewForm.sideEffectsToWatch,
      contraindications: newMedicationReviewForm.contraindications,
      storageInstructions: customSelectValue(newMedicationReviewForm, "storageInstructions", medicationStorageOptions),
      specialHandling: customSelectValue(newMedicationReviewForm, "specialHandling", medicationSpecialHandlingOptions),
      missedDoseInstructions: newMedicationReviewForm.missedDoseInstructions,
      refusalInstructions: newMedicationReviewForm.refusalInstructions,
      holdParameters: newMedicationReviewForm.holdParameters,
      instructions: newMedicationReviewForm.instructions,
      notes: newMedicationReviewForm.notes,
      receivedFromName: newMedicationReviewForm.sourceContactName,
      receivedFromFaxNumber: newMedicationReviewForm.sourceContactPhone,
      efaxNumber: newMedicationReviewForm.efaxNumber,
      receivedAt: selectedNewMedication?.receivedAt || selectedNewMedication?.createdAt || new Date().toISOString(),
      prescriptionDocument: newMedicationReviewForm.documents?.length ? newMedicationReviewForm.documents : selectedNewMedication?.prescriptionDocument || null,
      extractedMedicationDetails: {
        ...extracted,
        ...newMedicationReviewForm,
      },
    };
  };

  const saveNewMedicationReview = async (action = "save") => {
    const reviewingMedicationImport = Boolean(
      medicationImportReviewRef?.importId && Number.isInteger(medicationImportReviewRef.index),
    );
    if ((!selectedNewMedication?.id && !reviewingMedicationImport) || newMedicationReviewSaving) return;
    if (action !== "save" && ["medicationName", "dosage", "doseUnit", "route", "frequency"].some((field) => !String(newMedicationReviewForm[field] || "").trim())) {
      message.error("Verify medication name, dose, dose unit, route and frequency before approval.");
      return;
    }
    if (!validateAdministrationScheduleForm(newMedicationReviewForm, newMedicationReviewAdministrationTimeLimit)) {
      return;
    }
    setNewMedicationReviewSaving(action);
    try {
      const medicationRecord = selectedNewMedication?.id
        ? selectedNewMedication
        : await careService.residentMedications.createImportMedicationDraft(
          medicationImportReviewRef.importId,
          medicationImportReviewRef.index,
          workspaceParams,
          userId(user),
        );
      if (!selectedNewMedication?.id) {
        setSelectedNewMedication(medicationRecord);
      }
      const saved = await careService.residentMedications.updateNewMedication(
        medicationRecord.id,
        newMedicationReviewPayload(),
        workspaceParams,
      );
      const finalRecord = action === "adf"
        ? await careService.residentMedications.approveNewMedicationForAdf(saved.id, workspaceParams)
        : action === "approve"
          ? await careService.residentMedications.activateNewMedication(saved.id, workspaceParams)
          : saved;
      setSelectedNewMedication(finalRecord);
      setNewMedicationReviewForm(newMedicationReviewFormFromRecord(finalRecord));
      if (action === "approve") {
        setNewMedications((current) => {
          const remaining = current.filter((item) => item.id !== saved.id && item.id !== finalRecord.id);
          return newMedicationStatusFilter === "ALL" ? [finalRecord, ...remaining] : remaining;
        });
        if (isMedicationListVisible(finalRecord)) {
          setMedications((current) => [finalRecord, ...current.filter((item) => item.id !== finalRecord.id)]);
          if (medicationExpectedScheduledOn(finalRecord, selectedDate)) {
            setExpectedMedications((current) => [finalRecord, ...current.filter((item) => item.id !== finalRecord.id)]);
          }
        }
        setNewMedicationReviewOpen(false);
        if (!newMedications.some((item) => item.id !== saved.id && item.id !== finalRecord.id)) selectMedicationTab("list");
        message.success("New medication approved and activated.");
        if (medicationImportReviewRef?.importId && Number.isInteger(medicationImportReviewRef.index)) {
          try {
            await careService.residentMedications.markImportMedicationReviewed(
              medicationImportReviewRef.importId,
              medicationImportReviewRef.index,
              { status: "APPROVED", medicationId: finalRecord.id },
              workspaceParams,
              userId(user),
            );
            setMedicationImportReviewRef(null);
            setMedicationImportRefresh((current) => current + 1);
          } catch (importError) {
            message.warning(apiErrorMessage(importError, "Medication was approved, but the import progress could not be updated."));
          }
        }
      } else {
        setNewMedications((current) => {
          const remaining = current.filter((item) => item.id !== finalRecord.id);
          return isMedicationApprovalVisible(finalRecord) ? [finalRecord, ...remaining] : remaining;
        });
        if (medicationImportReviewRef?.importId && Number.isInteger(medicationImportReviewRef.index)) {
          try {
            await careService.residentMedications.markImportMedicationReviewed(
              medicationImportReviewRef.importId,
              medicationImportReviewRef.index,
              { status: "WAITING_APPROVAL", medicationId: finalRecord.id },
              workspaceParams,
              userId(user),
            );
            setMedicationImportReviewRef(null);
            setMedicationImportRefresh((current) => current + 1);
          } catch (importError) {
            message.warning(apiErrorMessage(importError, "Medication review was saved, but the import progress could not be updated."));
          }
        }
        message.success(action === "adf"
          ? reviewingMedicationImport
            ? "Medication saved to ADF approval."
            : "Medication approved."
          : "New medication review saved.");
        if (action === "adf") {
          setNewMedicationReviewOpen(false);
        }
      }
    } catch (err) {
      message.error(apiErrorMessage(err, "Unable to save new medication review."));
    } finally {
      setNewMedicationReviewSaving("");
    }
  };

  const confirmNewMedicationApproval = (action) => {
    const saveToAdf = action === "adf" && Boolean(medicationImportReviewRef);
    const medicationLabel = selectedNewMedication ? medicationName(selectedNewMedication) : "this medication";
    Modal.confirm({
      title: saveToAdf ? "Save medication to ADF approval?" : "Approve medication?",
      content: saveToAdf
        ? `Confirm that ${medicationLabel} has been reviewed and is ready for ADF approval.`
        : `Confirm that ${medicationLabel} has been reviewed and can be approved.`,
      okText: saveToAdf ? "Save to ADF Approval" : "Approve",
      cancelText: "Go back",
      zIndex: 2200,
      onOk: () => saveNewMedicationReview(action),
    });
  };

  const rejectNewMedicationReview = async () => {
    const reason = String(newMedicationRejectDialog.reason || "").trim();
    if (!selectedNewMedication?.id || newMedicationReviewSaving) return;
    if (!reason) {
      message.error("Enter a reason for rejecting this medication.");
      return;
    }
    setNewMedicationReviewSaving("reject");
    try {
      const rejected = await careService.residentMedications.rejectNewMedication(
        selectedNewMedication.id,
        { reason },
        workspaceParams,
        userId(user),
      );
      setNewMedications((current) => newMedicationStatusFilter === "ALL"
        ? current.map((item) => item.id === selectedNewMedication.id ? rejected : item)
        : current.filter((item) => item.id !== selectedNewMedication.id));
      if (medicationImportReviewRef?.importId && Number.isInteger(medicationImportReviewRef.index)) {
        try {
          await careService.residentMedications.markImportMedicationReviewed(
            medicationImportReviewRef.importId,
            medicationImportReviewRef.index,
            { status: "REJECTED", medicationId: rejected.medicationId },
            workspaceParams,
            userId(user),
          );
          setMedicationImportReviewRef(null);
          setMedicationImportRefresh((current) => current + 1);
        } catch (importError) {
          message.warning(apiErrorMessage(importError, "Medication was rejected, but the import progress could not be updated."));
        }
      }
      setNewMedicationRejectDialog({ open: false, reason: "" });
      setNewMedicationReviewOpen(false);
      setSelectedNewMedication(null);
      message.success("Medication rejected.");
    } catch (err) {
      message.error(apiErrorMessage(err, "Unable to reject this medication."));
    } finally {
      setNewMedicationReviewSaving("");
    }
  };

  const openMedicationEdit = (medication) => {
    const nextForm = medicationFormFromRecord(medication);
    setSelectedMedication(medication);
    setEditingMedicationId(medication?.id || "");
    setMedicationForm(nextForm);
    setSelectedMedicationFormDocumentKey(nextForm.documents?.length ? medicationDocumentKey(nextForm.documents[0], 0) : "");
    setMedicationDrawerMode("medication");
    setMedicationDetailsOpen(false);
    setMedicationDrawerOpen(true);
  };

  const openRefillRequestDialog = (medication) => {
    if (!medication) return;
    const { remaining } = medicationRefillBalance(medication);
    const pendingRequests = refillRequests.filter((request) => (
      request.medicationId === medicationRecordId(medication)
      && String(request.status || "").toUpperCase() === "REQUESTED"
    )).length;
    if (remaining !== null && remaining <= 0) {
      message.error("No refills remain for this medication.");
      return;
    }
    if (remaining !== null && pendingRequests >= remaining) {
      message.error("All remaining refills already have pending requests.");
      return;
    }
    const medicationPharmacyId = medication.pharmacyId || "";
    const medicationPharmacyName = firstText(medication.pharmacy);
    const linkedPharmacy = residentPharmacies.find((pharmacy) => {
      if (medicationPharmacyId && pharmacy.id) return pharmacy.id === medicationPharmacyId;
      return medicationPharmacyName && pharmacyName(pharmacy) === medicationPharmacyName;
    });
    setRefillRequestDialog({ open: true, medication, pharmacyId: linkedPharmacy?.id || medicationPharmacyId, notes: "" });
  };

  const closeRefillRequestDialog = () => {
    setRefillRequestDialog({ open: false, medication: null, pharmacyId: "", notes: "" });
  };

  const submitRefillRequest = async () => {
    const medication = refillRequestDialog.medication;
    const medicationId = medicationRecordId(medication);
    if (!selectedResidentId || !medicationId || refillRequestSaving) return;
    const selectedPharmacy = residentPharmacies.find((pharmacy) => pharmacy.id === refillRequestDialog.pharmacyId);
    setRefillRequestSaving(true);
    try {
      const saved = await careService.residentMedications.createRefillRequest(
        selectedResidentId,
        {
          medicationId,
          pharmacyId: refillRequestDialog.pharmacyId || null,
          pharmacyName: selectedPharmacy ? pharmacyName(selectedPharmacy.record || selectedPharmacy) : firstText(medication.pharmacy),
          notes: refillRequestDialog.notes,
          requestedById: userId(user) || null,
          requestedByName: userName(user),
        },
        workspaceParams,
      );
      setRefillRequests((current) => [saved, ...current.filter((item) => item.id !== saved.id)]);
      const faxState = refillFaxDisplay(saved);
      if (faxState.retryable) {
        message.warning(`Refill request saved. ${faxState.label}.${saved.faxFailureReason ? ` ${saved.faxFailureReason}` : ""}`);
      } else {
        message.success(`Refill request saved. ${faxState.label}.`);
      }
      setSelectedMedication(medication);
      setMedicationDetailsOpen(false);
      closeRefillRequestDialog();
      selectMedicationTab("refills");
    } catch (err) {
      message.error(apiErrorMessage(err, "Unable to request refill."));
    } finally {
      setRefillRequestSaving(false);
    }
  };

  const openRefillFulfillmentDialog = (request) => {
    const requestedMedication = refillRequestedMedication(request);
    setRefillFulfillmentDialog({
      open: true,
      request,
      quantityAmount: "",
      quantityUnit: medicationQuantityUnit(requestedMedication),
      notes: "",
    });
  };

  const closeRefillFulfillmentDialog = () => {
    if (refillFulfillmentSaving) return;
    setRefillFulfillmentDialog({ open: false, request: null, quantityAmount: "", quantityUnit: "", notes: "" });
  };

  const submitRefillFulfillment = async () => {
    const request = refillFulfillmentDialog.request;
    const quantityAmount = numericDecimalValue(refillFulfillmentDialog.quantityAmount);
    if (!request?.id || refillFulfillmentSaving) return;
    if (!quantityAmount || Number(quantityAmount) <= 0) {
      message.error("Enter the quantity received from the refill.");
      return;
    }
    setRefillFulfillmentSaving(true);
    try {
      const saved = await careService.residentMedications.fulfillRefillRequest(
        request.id,
        { quantityAmount, quantityUnit: refillFulfillmentDialog.quantityUnit, notes: refillFulfillmentDialog.notes },
        workspaceParams,
        userId(user),
      );
      setRefillRequests((current) => current.map((item) => item.id === saved.id ? saved : item));
      await reloadMedications({ force: true });
      message.success(`Refill fulfilled for ${saved.medicationName || "medication"}.`);
      setRefillFulfillmentDialog({ open: false, request: null, quantityAmount: "", quantityUnit: "", notes: "" });
    } catch (err) {
      message.error(apiErrorMessage(err, "Unable to fulfill refill request."));
    } finally {
      setRefillFulfillmentSaving(false);
    }
  };

  const openRefillCancellationDialog = (request) => {
    setRefillCancellationDialog({ open: true, request, cancelReason: "" });
  };

  const sendRefillRequestFax = async (request) => {
    if (!request?.id || refillFaxSendingId) return;
    setRefillFaxSendingId(request.id);
    try {
      const saved = await careService.residentMedications.sendRefillRequestFax(
        request.id,
        workspaceParams,
        userId(user),
      );
      setRefillRequests((current) => current.map((item) => item.id === saved.id ? saved : item));
      const state = refillFaxDisplay(saved);
      if (state.retryable) {
        message.warning(`${state.label}.${saved.faxFailureReason ? ` ${saved.faxFailureReason}` : " The pharmacy has not been reached."}`);
      } else {
        message.success(`${state.label}${saved.pharmacyFaxNumber ? ` (${saved.pharmacyFaxNumber})` : ""}.`);
      }
    } catch (err) {
      message.error(apiErrorMessage(err, "Unable to fax the refill request to the pharmacy."));
    } finally {
      setRefillFaxSendingId(null);
    }
  };

  const closeRefillCancellationDialog = () => {
    if (refillCancellationSaving) return;
    setRefillCancellationDialog({ open: false, request: null, cancelReason: "" });
  };

  const refreshRefillFaxStatus = async (request) => {
    if (!request?.id || refillFaxSendingId) return;
    setRefillFaxSendingId(request.id);
    try {
      const saved = await careService.residentMedications.refreshRefillRequestFax(request.id, workspaceParams, userId(user));
      setRefillRequests(current => current.map(item => item.id === saved.id ? saved : item));
      message.info(refillFaxDisplay(saved).label);
    } catch (err) {
      message.error(apiErrorMessage(err, "Unable to refresh the pharmacy fax status."));
    } finally { setRefillFaxSendingId(null); }
  };

  const submitRefillCancellation = async () => {
    const request = refillCancellationDialog.request;
    const cancelReason = String(refillCancellationDialog.cancelReason || "").trim();
    if (!request?.id || refillCancellationSaving) return;
    if (!cancelReason) {
      message.error("Enter a reason for canceling this refill request.");
      return;
    }
    setRefillCancellationSaving(true);
    try {
      const saved = await careService.residentMedications.cancelRefillRequest(
        request.id,
        { cancelReason },
        workspaceParams,
        userId(user),
      );
      setRefillRequests((current) => current.map((item) => item.id === saved.id ? saved : item));
      message.success(`Refill request canceled for ${saved.medicationName || "medication"}.`);
      setRefillCancellationDialog({ open: false, request: null, cancelReason: "" });
    } catch (err) {
      message.error(apiErrorMessage(err, "Unable to cancel refill request."));
    } finally {
      setRefillCancellationSaving(false);
    }
  };

  const closeRefillFaxPreview = () => {
    refillPreviewGeneration.current++;
    setRefillFaxPreview({ open: false, loading: false, url: "", fileName: "", pharmacyName: "", faxNumber: "", sentAt: null, status: "", error: "" });
  };

  const openRefillFaxPreview = async (request) => {
    if (!request?.id) return;
    const generation = ++refillPreviewGeneration.current;
    const recipient = {
      pharmacyName: request.pharmacyName || "Pharmacy not recorded",
      faxNumber: request.pharmacyFaxNumber || "",
      sentAt: request.faxSentAt || null,
      status: refillFaxDisplay(request).label,
    };
    setRefillFaxPreview({ open: true, loading: true, url: "", fileName: "", ...recipient, error: "" });
    try {
      const preview = await careService.residentMedications.getRefillRequestFaxPreview(
        request.id,
        workspaceParams,
        userId(user),
      );
      if (generation !== refillPreviewGeneration.current) return;
      setRefillFaxPreview({
        open: true,
        loading: false,
        url: preview?.signedUrl || "",
        fileName: preview?.fileName || "",
        ...recipient,
        faxNumber: preview?.pharmacyFaxNumber || recipient.faxNumber,
        sentAt: preview?.faxSentAt || recipient.sentAt,
        error: preview?.signedUrl ? "" : "The fax document could not be opened. Try again in a moment.",
      });
    } catch (err) {
      if (generation !== refillPreviewGeneration.current) return;
      setRefillFaxPreview({
        open: true,
        loading: false,
        url: "",
        fileName: "",
        ...recipient,
        error: apiErrorMessage(err, "Unable to open the fax document."),
      });
    }
  };

  const moveCalendarMonth = (delta) => {
    const next = addMonths(calendarMonth, delta);
    setCalendarMonth(next);
    setSelectedDate(monthStartKey(next));
  };

  const jumpCalendarMonth = (month, year) => {
    const next = new Date(year, month, 1);
    setCalendarMonth(next);
    setSelectedDate(monthStartKey(next));
  };

  const jumpMedicationChartMonth = (month, year) => {
    const next = new Date(year, month, 1);
    if (activeMedicationTab === "admin") {
      jumpCalendarMonth(month, year);
      return;
    }
    setAdminLogRangeStart(monthStartKey(next));
    setAdminLogRangeEnd(monthEndKey(next));
  };

  const selectCalendarDate = (day, openDrawer = false) => {
    setSelectedDate(day.key);
    if (!day.currentMonth) {
      setCalendarMonth(new Date(day.date.getFullYear(), day.date.getMonth(), 1));
    }
    if (openDrawer) {
      setFocusedAdministrationCell(null);
      setAdminDrawerOpen(true);
    }
  };

  const saveAdministrationLog = async (medication, status, notes = "", selectedAdministrationSlot = null, confirmedBodyPart = "", confirmedBodyPartOther = "", administrationAt = "", doseGiven = "") => {
    const medicationId = medicationRecordId(medication);
    if (!selectedResidentId || !medicationId) return;
    if (isMedicationPrn(medication) && !prnAdministrationDateAllowed(selectedDate)) {
      message.warning("PRN administration can only be recorded for today or a past date.");
      return;
    }
    const administrationSlot = isMedicationPrn(medication)
      ? {
          scheduledSlot: "PRN",
          scheduledLabel: "PRN",
          scheduledTime: "",
          medicationTimeZone: medication?.medicationTimeZone || medication?.administrationSchedule?.timeZone || browserTimeZone(),
        }
      : administrationSlotForMedicationAction(medication, selectedAdministrationSlot);
    if (!administrationSlot) {
      message.warning("Administration actions are available from 1 hour before the scheduled medication time until the next scheduled administration time.");
      return;
    }
    const lateAdministering = status === "ADMINISTERED" && medicationSlotAdministrationLate(
      administrationSlot,
      administrationAt ? new Date(administrationAt) : todayDate(),
    );
    const resolvedDoseGiven = String(doseGiven || medication.dosage || "").trim();
    const doseChanged = resolvedDoseGiven !== String(medication.dosage || "").trim();
    if (lateAdministering && !String(notes || "").trim()) {
      message.warning("Reason is required when administering more than 1 hour after the scheduled time.");
      return;
    }
    const doseLimitStatus = status === "ADMINISTERED" && isMedicationPrn(medication)
      ? medicationPrnDailyDoseLimitStatus(
        medication,
        administrationLogsForMedicationDay(medicationId, selectedDate)
          .filter((log) => logScheduledSlot(log) === "PRN"),
      )
      : null;
    const expectedBodyPart = medicationExpectedBodyPart(medication);
    const requiresBodyPartConfirmation = medicationRouteNeedsBodyPart(optionValueFromStored(medicationRouteOptions, medication.route)) && status === "ADMINISTERED";
    if (requiresBodyPartConfirmation && !medicationBodyPartValues(confirmedBodyPart).length) {
      message.warning("Confirm the body part or administration site used.");
      return;
    }
    if (requiresBodyPartConfirmation && medicationBodyPartNeedsOtherText(confirmedBodyPart) && !String(confirmedBodyPartOther || "").trim()) {
      message.warning("Specify the other body part or administration site.");
      return;
    }
    const confirmedBodyPartPayload = medicationBodyPartPayload(confirmedBodyPart, confirmedBodyPartOther);
    const bodyPartNote = medicationBodyPartNote(expectedBodyPart, confirmedBodyPartPayload, medication.route);
    const logNotes = [
      lateAdministering ? `Late administration reason: ${notes}` : "",
      doseChanged ? `Dose amount change reason: ${notes}` : "",
      doseLimitStatus ? `Max daily dose override reason: ${notes}` : "",
      !lateAdministering && !doseChanged && !doseLimitStatus ? notes : "",
      bodyPartNote,
    ]
      .map((value) => String(value || "").trim())
      .filter(Boolean)
      .join("\n");
    const savingKey = `${medicationId}:${administrationSlot.scheduledSlot}:${status}`;
    setAdminSavingKey(savingKey);
    try {
      const log = await careService.residentMedications.createAdministrationLog(
        selectedResidentId,
        {
          residentId: selectedResidentId,
          medicationId,
          scheduledFor: selectedDate,
          scheduledSlot: administrationSlot.scheduledSlot,
          scheduledTime: administrationSlot.scheduledTime || null,
          medicationTimeZone: administrationSlot.medicationTimeZone,
          administrationAt: administrationAt ? new Date(administrationAt).toISOString() : new Date().toISOString(),
          status,
          adminAction: status,
          doseGiven: resolvedDoseGiven,
          expectedBodyPart,
          expectedBodyParts: medicationBodyPartValues(expectedBodyPart),
          expectedBodyPartOther: medicationBodyPartOtherValue(expectedBodyPart),
          confirmedBodyPart: confirmedBodyPartPayload || null,
          confirmedBodyParts: medicationBodyPartValues(confirmedBodyPart),
          confirmedBodyPartOther: confirmedBodyPartOther || null,
          administeredById: userId(user) || null,
          administeredByName: userName(user),
          notes: logNotes,
        },
        workspaceParams,
      );
      setAdministrationLogs((current) => [log, ...current.filter((item) => item.id !== log.id)]);
      const logStartDate = activeMedicationTab === "adminLog" ? adminLogRangeStart : monthStartKey(calendarMonth);
      const logEndDate = activeMedicationTab === "adminLog" ? adminLogRangeEnd : monthEndKey(calendarMonth);
      try {
        const refreshedLogs = await careService.residentMedications.listAdministrationLogs(selectedResidentId, {
          ...workspaceParams,
          startDate: logStartDate,
          endDate: logEndDate,
        });
        setAdministrationLogs(pageItems(refreshedLogs));
      } catch {
        // Keep the newly saved log visible if the follow-up refresh is unavailable.
      }
      await reloadMedications({ force: true });
      await refreshCareActivityLogs();
      message.success("Medication administration log saved.");
      setAdminActionDialog({ open: false, medication: null, status: "", notes: "", confirmedBodyPart: "", confirmedBodyPartOther: "", requiresLateReason: false, administrationAt: "", administrationSlot: null, existingRecords: [], doseGiven: "", originalDoseGiven: "", doseEditing: false });
    } catch (err) {
      message.error(apiErrorMessage(err, "Unable to save medication administration log."));
    } finally {
      setAdminSavingKey("");
    }
  };

  const openAdministrationActionDialog = (medication, status, selectedAdministrationSlot = null, targetDate = selectedDate) => {
    if (!selectedResidentId || !medicationRecordId(medication) || adminSavingKey) return;
    if (isMedicationPrn(medication) && !prnAdministrationDateAllowed(targetDate)) {
      message.warning("PRN administration cannot be recorded for a future date.");
      return;
    }
    const administrationSlot = isMedicationPrn(medication)
      ? {
          scheduledSlot: "PRN",
          scheduledLabel: "PRN",
          scheduledTime: "",
          medicationTimeZone: medication?.medicationTimeZone || medication?.administrationSchedule?.timeZone || browserTimeZone(),
        }
      : administrationSlotForMedicationAction(medication, selectedAdministrationSlot, targetDate);
    if (!administrationSlot) {
      message.warning("Administration actions are available from 1 hour before the scheduled medication time until the next scheduled administration time.");
      return;
    }
    const requiresLateReason = status === "ADMINISTERED" && medicationSlotAdministrationLate(administrationSlot);
    const existingRecords = requiresLateReason
      ? administrationLogsForMedicationDay(medicationRecordId(medication), targetDate)
        .filter((log) => logScheduledSlot(log) === administrationSlot.scheduledSlot)
      : [];
    const targetAdministrationDate = parseDateKey(targetDate);
    const currentTime = todayDate();
    targetAdministrationDate.setHours(currentTime.getHours(), currentTime.getMinutes(), 0, 0);
    const defaultAdministrationAt = administrationSlot.scheduledDate
      ? localDateTimeInputValue(administrationSlot.scheduledDate)
      : localDateTimeInputValue(targetAdministrationDate);
    setSelectedDate(targetDate);
    setAdminActionDialog({
      open: true,
      medication,
      status,
      notes: "",
      confirmedBodyPart: medicationBodyPartValues(medicationExpectedBodyPart(medication)),
      confirmedBodyPartOther: medicationBodyPartOtherValue(medicationExpectedBodyPart(medication)),
      requiresLateReason,
      administrationAt: defaultAdministrationAt,
      administrationSlot,
      existingRecords,
      doseGiven: String(medication.dosage || ""),
      originalDoseGiven: String(medication.dosage || ""),
      doseEditing: false,
    });
  };

  const openMedicationAdministrationWindow = (medication) => {
    if (!selectedResidentId || !medicationRecordId(medication)) return;
    setMedicationDetailsOpen(false);
    setPrnAdminDrawerOpen(false);
    setAdminDrawerOpen(true);
  };

  const submitAdministrationAction = () => {
    const { medication, status, notes, administrationSlot, confirmedBodyPart, confirmedBodyPartOther, administrationAt, doseGiven, originalDoseGiven } = adminActionDialog;
    const trimmedNotes = notes.trim();
    const doseChanged = status === "ADMINISTERED" && String(doseGiven || "").trim() !== String(originalDoseGiven || "").trim();
    const requiresDoseLimitReason = status === "ADMINISTERED" && Boolean(adminActionPrnDoseLimitStatus);
    if ((adminActionsRequiringReason.has(status) || adminActionRequiresLateReason || requiresDoseLimitReason || doseChanged) && !trimmedNotes) {
      message.warning(requiresDoseLimitReason
        ? "Reason is required when administering after the max daily dose has been reached."
        : doseChanged
        ? "Reason is required when changing the administered dose amount."
        : adminActionRequiresLateReason
        ? "Reason is required when administering more than 1 hour after the scheduled time."
        : `${adminActionLabel(status)} requires a ${adminActionNoteLabel(status).toLowerCase()}.`);
      return;
    }
    if (adminActionDialog.administrationAt) {
      const actualDate = new Date(administrationAt);
      if (!administrationAt || Number.isNaN(actualDate.getTime())) {
        message.warning("Enter the actual administration date and time.");
        return;
      }
      if (actualDate > new Date()) {
        message.warning("Actual administration date and time cannot be in the future.");
        return;
      }
      if (actualDate < medicationLateAdministrationEarliestDate()) {
        message.warning("Late medication administration can only be recorded up to 6 months in the past.");
        return;
      }
    }
    if (medicationRouteNeedsBodyPart(optionValueFromStored(medicationRouteOptions, medication?.route)) && status === "ADMINISTERED" && !medicationBodyPartValues(confirmedBodyPart).length) {
      message.warning("Confirm the body part or administration site used.");
      return;
    }
    if (medicationRouteNeedsBodyPart(optionValueFromStored(medicationRouteOptions, medication?.route)) && status === "ADMINISTERED" && medicationBodyPartNeedsOtherText(confirmedBodyPart) && !String(confirmedBodyPartOther || "").trim()) {
      message.warning("Specify the other body part or administration site.");
      return;
    }
    const saveAction = () => saveAdministrationLog(
      medication,
      status,
      trimmedNotes,
      administrationSlot,
      confirmedBodyPart,
      confirmedBodyPartOther,
      administrationAt,
      doseGiven,
    );
    if (adminActionConflictRecords.length) {
      const hasStaffRecord = adminActionConflictRecords.some((log) => !isSystemMissedAdministrationLog(log));
      Modal.confirm({
        title: hasStaffRecord ? "Overwrite existing administration record?" : "Replace system-generated missed record?",
        content: hasStaffRecord
          ? "A record conflicts with this administration date and time. Continuing will mark the existing record as overridden and retain it in the audit history."
          : "A system-generated missed record conflicts with this administration date and time. Continuing will permanently remove that record and save this administration.",
        okText: hasStaffRecord ? "Overwrite record" : "Replace record",
        cancelText: "Do not overwrite",
        okButtonProps: { danger: true },
        centered: true,
        zIndex: 2600,
        onOk: saveAction,
      });
      return;
    }
    saveAction();
  };

  const openCancelAdministrationLogDialog = (log) => {
    if (!log?.id || adminCancelSaving) return;
    setAdminCancelDialog({ open: true, log, reason: "" });
  };

  const submitCancelAdministrationLog = async () => {
    const trimmedReason = adminCancelDialog.reason.trim();
    if (!adminCancelDialog.log?.id) return;
    if (!trimmedReason) {
      message.warning("Delete reason is required.");
      return;
    }
    setAdminCancelSaving(true);
    try {
      const updatedLog = await careService.residentMedications.cancelAdministrationLog(
        adminCancelDialog.log.id,
        { cancelReason: trimmedReason },
        workspaceParams,
      );
      setAdministrationLogs((current) => adminCancelDialog.log.systemGenerated
        ? current.filter((log) => log.id !== updatedLog.id)
        : [updatedLog, ...current.filter((log) => log.id !== updatedLog.id)]);
      await reloadMedications({ force: true });
      setAdminCancelDialog({ open: false, log: null, reason: "" });
      message.success("Medication administration record deleted.");
    } catch (err) {
      message.error(apiErrorMessage(err, "Unable to delete medication administration record."));
    } finally {
      setAdminCancelSaving(false);
    }
  };

  const applyMedicationFormUpdate = (current, field, value) => {
      if (field === "pharmacyId") {
        const pharmacy = pharmacyOptions.find((option) => option.id === value);
        return { ...current, pharmacyId: value, pharmacy: pharmacy?.name || "" };
      }
      if (field === "prn") {
        if (value) {
          return {
            ...current,
            prn: true,
            frequency: current.frequency || "AS_NEEDED",
            administrationTime: ["AS_NEEDED"],
            administrationTimeDetails: {},
            administrationTimeOther: "",
          };
        }
        return {
          ...current,
          prn: false,
          frequency: current.frequency === "AS_NEEDED" ? "" : current.frequency,
          administrationTime: (current.administrationTime || []).filter((time) => time !== "AS_NEEDED"),
          administrationTimeDetails: administrationTimeDetailsFor(
            (current.administrationTime || []).filter((time) => time !== "AS_NEEDED"),
            current.administrationTimeDetails,
          ),
        };
      }
      if (field === "frequency") {
        const limit = administrationTimeLimitForFrequency(value, current.frequencyOther);
        const impliedAdministrationTime = defaultAdministrationTimesForFrequency(value);
        const currentScheduledTimes = (current.administrationTime || []).filter((time) => time !== "AS_NEEDED");
        const administrationTime = limit
          ? (currentScheduledTimes.length ? currentScheduledTimes : impliedAdministrationTime).slice(0, limit)
          : value === "AS_NEEDED" ? ["AS_NEEDED"] : [];
        const administrationTimeDetails = administrationTimeDetailsFor(administrationTime, current.administrationTimeDetails);
        return { ...current, frequency: value, prn: value === "AS_NEEDED" ? true : current.prn, administrationTime, administrationTimeDetails };
      }
      if (field === "frequencyOther") {
        const limit = administrationTimeLimitForFrequency(current.frequency, value);
        const administrationTime = limit
          ? (current.administrationTime || []).filter((time) => time !== "AS_NEEDED").slice(0, limit)
          : current.frequency === "AS_NEEDED" ? ["AS_NEEDED"] : current.administrationTime;
        const administrationTimeDetails = administrationTimeDetailsFor(administrationTime, current.administrationTimeDetails);
        return { ...current, frequencyOther: value, administrationTime, administrationTimeDetails };
      }
      if (field === "doseUnit") {
        const shouldSyncQuantityUnit = !current.quantityUnit || current.quantityUnit === current.doseUnit;
        const shouldSyncMaxDailyDoseUnit = !current.maxDailyDoseUnit || current.maxDailyDoseUnit === current.doseUnit;
        return {
          ...current,
          doseUnit: value,
          doseUnitOther: value === "OTHER" ? current.doseUnitOther : "",
          quantityUnit: shouldSyncQuantityUnit ? value : current.quantityUnit,
          quantityUnitOther: shouldSyncQuantityUnit ? value === "OTHER" ? current.doseUnitOther : "" : current.quantityUnitOther,
          maxDailyDoseUnit: shouldSyncMaxDailyDoseUnit ? value : current.maxDailyDoseUnit,
          maxDailyDoseUnitOther: shouldSyncMaxDailyDoseUnit ? value === "OTHER" ? current.doseUnitOther : "" : current.maxDailyDoseUnitOther,
        };
      }
      if (field === "strengthUnit") {
        return { ...current, strengthUnit: value, strengthUnitOther: value === "OTHER" ? current.strengthUnitOther : "" };
      }
      if (field === "quantityUnit") {
        return { ...current, quantityUnit: value, quantityUnitOther: value === "OTHER" ? current.quantityUnitOther : "" };
      }
      if (field === "maxDailyDoseUnit") {
        return { ...current, maxDailyDoseUnit: value, maxDailyDoseUnitOther: value === "OTHER" ? current.maxDailyDoseUnitOther : "" };
      }
      if (field === "route") {
        return {
          ...current,
          route: value,
          expectedBodyPart: medicationRouteNeedsBodyPart(value) ? current.expectedBodyPart : [],
          expectedBodyPartOther: medicationRouteNeedsBodyPart(value) ? current.expectedBodyPartOther : "",
        };
      }
      if (field === "administrationTime") {
        const limit = administrationTimeLimitForFrequency(current.frequency, current.frequencyOther);
        const values = Array.isArray(value) ? value : [];
        if (!limit) {
          const administrationTime = values.includes("AS_NEEDED") ? ["AS_NEEDED"] : values;
          const administrationTimeDetails = administrationTimeDetailsFor(administrationTime, current.administrationTimeDetails);
          return { ...current, administrationTime, administrationTimeDetails };
        }
        const scheduledValues = values.filter((time) => time !== "AS_NEEDED");
        if (scheduledValues.length > limit) {
          message.warning(administrationTimeLimitLabel(limit));
        }
        const administrationTime = scheduledValues.slice(0, limit);
        const administrationTimeDetails = administrationTimeDetailsFor(administrationTime, current.administrationTimeDetails);
        return { ...current, administrationTime, administrationTimeDetails };
      }
      if (field === "administrationTimeDetails") {
        return {
          ...current,
          administrationTimeDetails: {
            ...(current.administrationTimeDetails || {}),
            ...(value || {}),
          },
        };
      }
      return { ...current, [field]: value };
  };

  const updateMedicationForm = (field, value) => {
    setMedicationForm((current) => applyMedicationFormUpdate(current, field, value));
  };

  const applyAdministrationTimePreset = (times) => {
    updateMedicationForm("administrationTime", times);
  };

  const applyNewMedicationReviewAdministrationTimePreset = (times) => {
    updateNewMedicationReviewForm("administrationTime", times);
  };

  const administrationSlotsForMedicationAction = (medication, targetDate = selectedDate) => {
    const schedule = medication?.administrationSchedule || {};
    const medicationSlots = medicationAdministrationTimedSlots(medication);
    const scheduledEntries = medicationSlots
      .filter((entry) => medicationSlotAdministrationWindowOpen(targetDate, entry, medicationSlots)
        || lateMedicationAdministrationDateAllowed(targetDate))
      .map((entry) => ({
        scheduledSlot: entry.scheduledSlot,
        scheduledLabel: administrationTimeOptionLabel(entry.scheduledSlot),
        scheduledTime: `${pad(entry.time.hours)}:${pad(entry.time.minutes)}`,
        medicationTimeZone: medication?.medicationTimeZone || schedule.timeZone || browserTimeZone(),
        ...medicationSlotAdministrationWindow(targetDate, entry, medicationSlots),
      }))
      .filter(Boolean);
    return scheduledEntries;
  };

  const administrationSlotForMedicationAction = (medication, requestedSlot = null, targetDate = selectedDate) => {
    const scheduledEntries = administrationSlotsForMedicationAction(medication, targetDate);
    if (requestedSlot?.scheduledSlot) {
      const scheduledEntry = scheduledEntries.find((entry) => entry.scheduledSlot === requestedSlot.scheduledSlot);
      if (scheduledEntry) return scheduledEntry;
      if (administrationSlotWindowOpen(requestedSlot) || lateMedicationAdministrationDateAllowed(targetDate)) {
        return requestedSlot;
      }
      return null;
    }
    return scheduledEntries[0] || null;
  };

  const addPharmacyToResident = async (event) => {
    event.preventDefault();
    if (!selectedResidentId || !residentPharmacyForm.pharmacyId || addingResidentPharmacy) return;
    setAddingResidentPharmacy(true);
    try {
      const saved = await careService.pharmacies.addToResident(
        selectedResidentId,
        {
          pharmacyId: residentPharmacyForm.pharmacyId,
          preferred: residentPharmacyForm.preferred,
          notes: residentPharmacyForm.notes,
        },
        workspaceParams,
      );
      setResidentPharmacyLinks((current) => [saved, ...current.filter((item) => item.id !== saved.id)]);
      setResidentPharmacyForm({ pharmacyId: "", preferred: false, notes: "" });
      message.success("Pharmacy added to resident.");
    } catch (err) {
      message.error(apiErrorMessage(err, "Unable to add pharmacy to resident."));
    } finally {
      setAddingResidentPharmacy(false);
    }
  };

  const requestRemovePharmacyFromResident = (pharmacy) => {
    if (!selectedResidentId || !pharmacy?.id || removingResidentPharmacyId) return;
    setPharmacyRemoveDialog({ open: true, pharmacy });
  };

  const closePharmacyRemoveDialog = () => {
    if (removingResidentPharmacyId) return;
    setPharmacyRemoveDialog({ open: false, pharmacy: null });
  };

  const removePharmacyFromResident = async () => {
    const pharmacy = pharmacyRemoveDialog.pharmacy;
    const pharmacyId = pharmacy?.id;
    if (!selectedResidentId || !pharmacyId || removingResidentPharmacyId) return;
    setRemovingResidentPharmacyId(pharmacyId);
    try {
      await careService.pharmacies.removeFromResident(selectedResidentId, pharmacyId, workspaceParams);
      setResidentPharmacyLinks((current) =>
        current.filter((link) => (link.pharmacyId || link.pharmacy?.id) !== pharmacyId),
      );
      setPharmacyRemoveDialog({ open: false, pharmacy: null });
      message.success("Pharmacy removed from resident.");
    } catch (err) {
      message.error(apiErrorMessage(err, "Unable to remove pharmacy from resident."));
    } finally {
      setRemovingResidentPharmacyId("");
    }
  };

  const openPharmacyDetails = (pharmacy) => {
    setSelectedPharmacy(pharmacy);
    setPharmacyDetailsOpen(true);
  };

  const addMedicationDocuments = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;
    let preparedFiles;
    try {
      preparedFiles = await preparePrescriptionFiles(files);
    } catch (error) {
      message.error(error?.message || "Unable to prepare the selected prescription file.");
      return;
    }
    const newDocuments = preparedFiles.map((file) => ({
      id: `${file.name}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
      file,
      fileName: file.name,
      originalFileName: file.name,
      localPreviewUrl: URL.createObjectURL(file),
      contentType: file.type || "application/octet-stream",
      type: file.type || "DOCUMENT",
      size: file.size,
      description: "",
      uploaded: false,
      needsUpload: true,
    }));
    const insertionIndex = (medicationForm.documents || []).length;
    setMedicationForm((current) => ({
      ...current,
      documents: [
        ...(current.documents || []),
        ...newDocuments,
      ],
    }));
    setSelectedMedicationFormDocumentKey(medicationDocumentKey(newDocuments[0], insertionIndex));
  };

  const updateMedicationDocument = (documentId, field, value) => {
    setMedicationForm((current) => ({
      ...current,
      documents: (current.documents || []).map((document) =>
        document.id === documentId ? { ...document, [field]: value } : document,
      ),
    }));
  };

  const removeMedicationDocument = (documentId, documentIndex = -1) => {
    const documents = medicationForm.documents || [];
    const removedIndex = documents.findIndex((document, index) => (
      document.id ? document.id === documentId : index === documentIndex
    ));
    const removedDocument = removedIndex >= 0 ? documents[removedIndex] : null;
    const removedKey = removedDocument ? medicationDocumentKey(removedDocument, removedIndex) : "";
    const remainingDocuments = documents.filter((document) => document.id !== documentId);

    if (removedDocument?.localPreviewUrl) {
      URL.revokeObjectURL(removedDocument.localPreviewUrl);
    }

    setMedicationForm((current) => ({
      ...current,
      documents: (current.documents || []).filter((document, index) => (
        document.id ? document.id !== documentId : index !== documentIndex
      )),
    }));
    setSelectedMedicationFormDocumentKey((currentKey) => {
      if (currentKey && currentKey !== removedKey) return currentKey;
      const nextIndex = Math.min(Math.max(removedIndex, 0), remainingDocuments.length - 1);
      return remainingDocuments.length ? medicationDocumentKey(remainingDocuments[nextIndex], nextIndex) : "";
    });
  };

  const uploadMedicationDocuments = async () => {
    const uploadedDocuments = [];
    for (const document of medicationForm.documents || []) {
      if (document.fileId || !document.file) {
        const documentPayload = { ...document };
        delete documentPayload.file;
        delete documentPayload.localPreviewUrl;
        uploadedDocuments.push({
          ...documentPayload,
          uploaded: Boolean(documentPayload.uploaded || documentPayload.fileId),
        });
        continue;
      }
      const upload = await uploadFile(null, document.file, {
        confirmUpload: false,
        fileType: document.type || document.contentType || "DOCUMENT",
        includeUserId: false,
        preserveOriginal: true,
        purpose: "OTHER",
        skipUserValidation: true,
      });
      uploadedDocuments.push({
        id: document.id,
        fileId: upload?.fileId || upload?.file?.id || null,
        storageFileId: upload?.fileId || upload?.file?.id || null,
        fileName: document.fileName,
        originalFileName: document.originalFileName || document.fileName,
        description: document.description,
        contentType: document.contentType,
        type: document.type,
        size: document.size,
        confirmed: false,
        storageStatus: "UPLOADED_NOT_CONFIRMED",
        uploaded: true,
        uploadedAt: new Date().toISOString(),
      });
    }
    setMedicationForm((current) => ({ ...current, documents: uploadedDocuments }));
    return uploadedDocuments;
  };

  const openMedicationDrawer = (mode = "medication") => {
    setMedicationForm(defaultMedicationForm());
    setSelectedMedicationFormDocumentKey("");
    setEditingMedicationId("");
    setMedicationDrawerMode(mode);
    setMedicationDrawerOpen(true);
  };

  const deleteMedication = async (medication) => {
    if (!medication?.id || medicationDeletingId) return;
    const confirmed = window.confirm(`Delete ${medicationName(medication)} from this resident's medication list?`);
    if (!confirmed) return;
    setMedicationDeletingId(medication.id);
    try {
      await careService.residentMedications.delete(medication.id, workspaceParams);
      setMedications((current) => current.filter((item) => item.id !== medication.id));
      setExpectedMedications((current) => current.filter((item) => item.id !== medication.id));
      if (selectedMedication?.id === medication.id) {
        setSelectedMedication(null);
        setMedicationDetailsOpen(false);
      }
      message.success("Medication deleted.");
    } catch (err) {
      message.error(apiErrorMessage(err, "Unable to delete medication."));
    } finally {
      setMedicationDeletingId("");
    }
  };

  const openMedicationWithholdDialog = (medication) => {
    if (!medication?.id || medicationWithholdingId) return;
    const nextWithheld = !isMedicationWithheld(medication);
    setMedicationWithholdDialog({
      open: true,
      medication,
      nextWithheld,
      reason: "",
    });
  };

  const closeMedicationWithholdDialog = () => {
    if (medicationWithholdingId) return;
    setMedicationWithholdDialog({ open: false, medication: null, nextWithheld: false, reason: "" });
  };

  const submitMedicationWithholdDialog = async () => {
    const medication = medicationWithholdDialog.medication;
    if (!medication?.id || medicationWithholdingId) return;
    const reason = medicationWithholdDialog.reason.trim();
    if (!reason) {
      message.error(medicationWithholdDialog.nextWithheld ? "Withhold reason is required." : "Resume reason is required.");
      return;
    }
    const nextWithheld = medicationWithholdDialog.nextWithheld;
    setMedicationWithholdingId(medication.id);
    try {
      const saved = await careService.residentMedications.update(
        medication.id,
        {
          ...medication,
          withheld: nextWithheld,
          withheldReason: nextWithheld ? reason : medication.withheldReason || "",
          resumeReason: nextWithheld ? medication.resumeReason || "" : reason,
        },
        workspaceParams,
      );
      const updatedMedication = {
        ...medication,
        ...saved,
        withheld: nextWithheld,
        withheldReason: nextWithheld ? reason : saved.withheldReason || medication.withheldReason || "",
        resumeReason: nextWithheld ? saved.resumeReason || medication.resumeReason || "" : reason,
      };
      setMedications((current) => current.map((item) => (item.id === medication.id ? updatedMedication : item)));
      setExpectedMedications((current) => {
        const withoutMedication = current.filter((item) => item.id !== medication.id);
        return medicationExpectedScheduledOn(updatedMedication, selectedDate)
          ? [updatedMedication, ...withoutMedication]
          : withoutMedication;
      });
      setSelectedMedication((current) => (current?.id === medication.id ? updatedMedication : current));
      setMedicationWithholdDialog({ open: false, medication: null, nextWithheld: false, reason: "" });
      message.success(nextWithheld ? "Medication marked as withheld." : "Medication withholding removed.");
    } catch (err) {
      message.error(apiErrorMessage(err, "Unable to update medication withholding."));
    } finally {
      setMedicationWithholdingId("");
    }
  };

  const saveMedication = async (event) => {
    event.preventDefault();
    if (!selectedResidentId || medicationSaving) return;
    if (medicationDrawerMode !== "new" && !medicationForm.medicationName.trim()) {
      message.error("Medication name is required.");
      return;
    }
    if (!validateAdministrationScheduleForm(medicationForm, administrationTimeLimit)) {
      return;
    }
    if (medicationRouteNeedsBodyPart(medicationForm.route) && !medicationBodyPartValues(medicationForm.expectedBodyPart).length) {
      message.error("Select the expected body part or administration site for this route.");
      return;
    }
    if (medicationRouteNeedsBodyPart(medicationForm.route) && medicationBodyPartNeedsOtherText(medicationForm.expectedBodyPart) && !String(medicationForm.expectedBodyPartOther || "").trim()) {
      message.error("Specify the other body part or administration site.");
      return;
    }
    setMedicationSaving(true);
    try {
      const prnReason = customSelectValue(medicationForm, "prnReason", medicationPrnReasonOptions);
      const uploadedDocuments = await uploadMedicationDocuments();
      const quantityAmount = numericDecimalValue(medicationForm.quantityAmount);
      const quantityChanged = quantityAmount !== numericDecimalValue(medicationForm.initialQuantityAmount);
      const medicationPayload = {
        residentId: selectedResidentId,
        medicationName: medicationForm.medicationName.trim(),
        genericName: medicationForm.genericName,
        brandName: medicationForm.brandName,
        medicationForm: customSelectValue(medicationForm, "medicationForm", medicationFormOptions),
        classification: customSelectValue(medicationForm, "classification", medicationClassificationOptions),
        dosage: numericDosageValue(medicationForm.dosage),
        doseUnit: customSelectValue(medicationForm, "doseUnit", medicationDoseUnitOptions),
        strengthAmount: numericDecimalValue(medicationForm.strengthAmount),
        strengthUnit: customSelectValue(medicationForm, "strengthUnit", medicationStrengthUnitOptions),
        strength: [numericDecimalValue(medicationForm.strengthAmount), customSelectValue(medicationForm, "strengthUnit", medicationStrengthUnitOptions)].filter(Boolean).join(" "),
        route: customSelectValue(medicationForm, "route", medicationRouteOptions),
        expectedBodyPart: medicationBodyPartPayload(medicationForm.expectedBodyPart, medicationForm.expectedBodyPartOther),
        expectedBodyParts: medicationBodyPartValues(medicationForm.expectedBodyPart),
        expectedBodyPartOther: medicationForm.expectedBodyPartOther || null,
        frequency: customSelectValue(medicationForm, "frequency", medicationFrequencyOptions),
        status: medicationForm.status,
        prescribedBy: medicationForm.prescribedBy,
        prescriberPhone: medicationForm.prescriberPhone,
        prescriberEmail: medicationForm.prescriberEmail,
        pharmacy: medicationForm.pharmacy,
        pharmacyId: medicationForm.pharmacyId || null,
        hospitalSource: medicationForm.hospitalSource,
        sourceType: customSelectValue(medicationForm, "sourceType", medicationSourceTypeOptions),
        sourceDocumentName: medicationForm.sourceDocumentName,
        sourceDocumentDate: medicationForm.sourceDocumentDate || null,
        sourceReferenceNumber: medicationForm.sourceReferenceNumber,
        sourceContactName: medicationForm.sourceContactName,
        sourceContactPhone: medicationForm.sourceContactPhone,
        sourceContactEmail: medicationForm.sourceContactEmail,
        sourceNotes: medicationForm.sourceNotes,
        orderNumber: medicationForm.orderNumber,
        orderDate: medicationForm.orderDate || null,
        startDate: medicationForm.startDate || null,
        endDate: medicationForm.endDate || null,
        indication: customSelectValue(medicationForm, "indication", medicationIndicationOptions),
        ...(!editingMedicationId ? {
          quantityAmount,
          remainingQuantity: quantityChanged ? quantityAmount : numericDecimalValue(medicationForm.remainingQuantity || medicationForm.quantityAmount),
          quantityUnit: customSelectValue(medicationForm, "quantityUnit", medicationDoseUnitOptions),
          quantity: [quantityAmount, customSelectValue(medicationForm, "quantityUnit", medicationDoseUnitOptions)].filter(Boolean).join(" "),
        } : {}),
        ...(!editingMedicationId ? { refills: customSelectValue(medicationForm, "refills", medicationRefillOptions) } : {}),
        controlledSubstance: medicationForm.controlledSubstance,
        highAlertMedication: medicationForm.highAlertMedication,
        requiresVitalsCheck: medicationForm.requiresVitalsCheck,
        requiresBloodSugarCheck: medicationForm.requiresBloodSugarCheck,
        foodInstructions: customSelectValue(medicationForm, "foodInstructions", medicationFoodInstructionOptions),
        medicationTimeZone: medicationForm.medicationTimeZone || browserTimeZone(),
        administrationTime: administrationTimePayloadValue(medicationForm),
        administrationSchedule: administrationSchedulePayloadValue(medicationForm),
        prn: booleanValue(medicationForm.prn) || medicationForm.frequency === "AS_NEEDED" || Boolean(prnReason),
        withheld: booleanValue(medicationForm.withheld),
        withheldReason: medicationForm.withheldReason,
        resumeReason: medicationForm.resumeReason,
        prnReason,
        maxDailyDoseAmount: numericDecimalValue(medicationForm.maxDailyDoseAmount),
        maxDailyDoseUnit: customSelectValue(medicationForm, "maxDailyDoseUnit", medicationDoseUnitOptions),
        maxDailyDose: [numericDecimalValue(medicationForm.maxDailyDoseAmount), customSelectValue(medicationForm, "maxDailyDoseUnit", medicationDoseUnitOptions)].filter(Boolean).join(" "),
        monitoringInstructions: medicationForm.monitoringInstructions,
        sideEffectsToWatch: medicationForm.sideEffectsToWatch,
        contraindications: medicationForm.contraindications,
        storageInstructions: customSelectValue(medicationForm, "storageInstructions", medicationStorageOptions),
        specialHandling: customSelectValue(medicationForm, "specialHandling", medicationSpecialHandlingOptions),
        missedDoseInstructions: medicationForm.missedDoseInstructions,
        refusalInstructions: medicationForm.refusalInstructions,
        holdParameters: medicationForm.holdParameters,
        instructions: medicationForm.instructions,
        notes: medicationForm.notes,
        documents: uploadedDocuments,
      };
      if (medicationDrawerMode === "new" && !editingMedicationId) {
        const saved = await careService.residentMedications.simulateReceivedPrescription(
          selectedResidentId,
          {
            residentId: selectedResidentId,
            medicationName: medicationPayload.medicationName,
            genericName: medicationPayload.genericName,
            brandName: medicationPayload.brandName,
            dosage: medicationPayload.dosage,
            doseUnit: medicationPayload.doseUnit,
            strengthAmount: medicationPayload.strengthAmount,
            strengthUnit: medicationPayload.strengthUnit,
            strength: medicationPayload.strength,
            quantityAmount: medicationPayload.quantityAmount,
            quantityUnit: medicationPayload.quantityUnit,
            quantity: medicationPayload.quantity,
            maxDailyDoseAmount: medicationPayload.maxDailyDoseAmount,
            maxDailyDoseUnit: medicationPayload.maxDailyDoseUnit,
            maxDailyDose: medicationPayload.maxDailyDose,
            route: medicationPayload.route,
            expectedBodyPart: medicationPayload.expectedBodyPart,
            expectedBodyParts: medicationPayload.expectedBodyParts,
            expectedBodyPartOther: medicationPayload.expectedBodyPartOther,
            frequency: medicationPayload.frequency,
            withheld: medicationPayload.withheld,
            prescribedBy: medicationPayload.prescribedBy,
            prescriberPhone: medicationPayload.prescriberPhone,
            prescriberEmail: medicationPayload.prescriberEmail,
            hospitalSource: medicationPayload.hospitalSource,
            sourceType: medicationPayload.sourceType,
            sourceDocumentName: medicationPayload.sourceDocumentName,
            sourceDocumentDate: medicationPayload.sourceDocumentDate,
            sourceReferenceNumber: medicationPayload.sourceReferenceNumber,
            sourceContactName: medicationPayload.sourceContactName,
            sourceContactPhone: medicationPayload.sourceContactPhone,
            sourceContactEmail: medicationPayload.sourceContactEmail,
            instructions: medicationPayload.instructions,
            notes: medicationPayload.notes,
            receivedFromName: medicationPayload.sourceContactName,
            receivedFromFaxNumber: medicationPayload.sourceContactPhone,
            efaxNumber: "",
            receivedAt: new Date().toISOString(),
            prescriptionDocument: uploadedDocuments,
            extractedMedicationDetails: medicationPayload,
          },
          workspaceParams,
        );
        setNewMedications((current) => [saved, ...current.filter((item) => item.id !== saved.id)]);
        setMedicationDrawerOpen(false);
        setMedicationDrawerMode("medication");
        selectMedicationTab("new");
        message.success("New medication record saved for Oliveeb review.");
        return;
      }
      const saved = editingMedicationId
        ? await careService.residentMedications.update(editingMedicationId, medicationPayload, workspaceParams)
        : await careService.residentMedications.createForResident(
            selectedResidentId,
            medicationPayload,
            workspaceParams,
          );
      setMedications((current) => [saved, ...current.filter((item) => item.id !== saved.id)]);
      if (medicationExpectedScheduledOn(saved, selectedDate)) {
        setExpectedMedications((current) => [saved, ...current.filter((item) => item.id !== saved.id)]);
      } else {
        setExpectedMedications((current) => current.filter((item) => item.id !== saved.id));
      }
      setSelectedMedication(saved);
      setEditingMedicationId("");
      setMedicationDrawerOpen(false);
      setMedicationDrawerMode("medication");
      message.success("Resident medication saved.");
    } catch (err) {
      message.error(apiErrorMessage(err, "Unable to save resident medication."));
    } finally {
      setMedicationSaving(false);
    }
  };

  const expectedCountForDay = (key) => medications.filter((medication) => medicationExpectedScheduledOn(medication, key)).length;
  const actionCountsForDay = (key) => activeAdministrationLogs
    .filter((log) => logScheduledDate(log) === key)
    .reduce((counts, log) => {
      const action = String(log.adminAction || log.status || "").toUpperCase();
      if (action && action !== "MISSED") counts[action] = (counts[action] || 0) + 1;
      return counts;
    }, medications.reduce((counts, medication) => {
      const missedCount = computedMissedCountForMedicationDay(medication, key);
      if (missedCount > 0) counts.MISSED = (counts.MISSED || 0) + missedCount;
      return counts;
    }, {}));
  const medicationDetailGroups = selectedMedication ? [
    ["Prescription Details", [
      [["RX#", medicationRxNumber(selectedMedication)], ["Medication name", medicationName(selectedMedication)]],
      [["Generic name", selectedMedication.genericName], ["Brand name", selectedMedication.brandName]],
      [["Order date", formatDate(selectedMedication.orderDate)], ["Start date", formatDate(selectedMedication.startDate)], ["End date", formatDate(selectedMedication.endDate)]],
    ]],
    ["Clinical Details", [
      [
        ["Form", selectedMedication.medicationForm],
        ["Classification", selectedMedication.classification],
        ["Therapeutic indication", selectedMedication.indication],
        ["Route", selectedMedication.route],
        ["Expected body part/site", medicationBodyPartLabel(medicationExpectedBodyPart(selectedMedication), selectedMedication.route)],
      ],
    ]],
    ["Dose & Supply", [
      [
        ["Dose", [selectedMedication.dosage, medicationDoseUnitDisplay(selectedMedication)].filter(Boolean).join(" ")],
        ["Max daily dose", [medicationMaxDailyDoseAmount(selectedMedication), medicationMaxDailyDoseUnitDisplay(selectedMedication)].filter(Boolean).join(" ")],
        ["Strength", medicationStrength(selectedMedication)],
      ],
      [
        ["Quantity", [medicationQuantityAmount(selectedMedication), medicationQuantityUnitDisplay(selectedMedication)].filter(Boolean).join(" ")],
        ["Remaining balance", medicationRemainingQuantityDisplay(selectedMedication)],
        ["Refills", selectedMedication.refills],
      ],
    ]],
    ["Schedule & Instructions", [
      [["PRN / as needed", isMedicationPrn(selectedMedication) ? "PRN" : ""], ["Frequency", isMedicationPrn(selectedMedication) ? "" : selectedMedication.frequency]],
      [["Administration schedule", isMedicationPrn(selectedMedication) ? "As needed" : administrationScheduleDisplay(selectedMedication)], ["Administration time", isMedicationPrn(selectedMedication) ? "" : selectedMedication.administrationTime], ["Timezone", selectedMedication.medicationTimeZone || selectedMedication.administrationSchedule?.timeZone]],
      [["Directions / SIG", selectedMedication.instructions]],
      [["Food instructions", selectedMedication.foodInstructions], ["PRN reason", selectedMedication.prnReason]],
      [["Hold parameters", selectedMedication.holdParameters]],
      [["Missed dose instructions", selectedMedication.missedDoseInstructions], ["Refusal instructions", selectedMedication.refusalInstructions]],
    ]],
    ["Safety Flags", [
      [["Controlled substance", selectedMedication.controlledSubstance ? "Yes" : "No"], ["High-alert medication", selectedMedication.highAlertMedication ? "Yes" : "No"]],
      [["Requires vitals check", selectedMedication.requiresVitalsCheck ? "Yes" : "No"], ["Requires blood sugar check", selectedMedication.requiresBloodSugarCheck ? "Yes" : "No"]],
      [["Monitoring instructions", selectedMedication.monitoringInstructions]],
      [["Side effects to watch", selectedMedication.sideEffectsToWatch], ["Contraindications / precautions", selectedMedication.contraindications]],
      [["Storage", selectedMedication.storageInstructions], ["Special handling", selectedMedication.specialHandling]],
      [["Medication notes", selectedMedication.notes]],
    ]],
    ["Prescriber & Pharmacy", [
      [["Prescribed by", selectedMedication.prescribedBy], ["Pharmacy", selectedMedication.pharmacy]],
      [["Prescriber phone", selectedMedication.prescriberPhone], ["Prescriber email", selectedMedication.prescriberEmail]],
    ]],
    ["Prescription Source", [
      [["Source type", selectedMedication.sourceType], ["Source organization", selectedMedication.hospitalSource]],
      [["Source document", selectedMedication.sourceDocumentName], ["Source date", formatDate(selectedMedication.sourceDocumentDate)], ["Source reference #", selectedMedication.sourceReferenceNumber]],
      [["Source contact name", selectedMedication.sourceContactName], ["Source contact phone", selectedMedication.sourceContactPhone], ["Source contact email", selectedMedication.sourceContactEmail]],
      [["Source notes", selectedMedication.sourceNotes]],
    ]],
  ] : [];
  const selectedMedicationWithholdingRecords = selectedMedication ? medicationWithholdingRecords(selectedMedication) : [];
  const selectedMedicationDosageDisplay = selectedMedication
    ? [selectedMedication.dosage, medicationDoseUnitDisplay(selectedMedication)].filter(Boolean).join(" ")
    : "";
  const selectedMedicationQuantityDisplay = selectedMedication
    ? [medicationQuantityAmount(selectedMedication), medicationQuantityUnitDisplay(selectedMedication)].filter(Boolean).join(" ")
    : "";
  const selectedMedicationRemainingQuantityDisplay = selectedMedication
    ? medicationRemainingQuantityDisplay(selectedMedication)
    : "";
  const selectedMedicationMaxDailyDoseDisplay = selectedMedication
    ? [medicationMaxDailyDoseAmount(selectedMedication), medicationMaxDailyDoseUnitDisplay(selectedMedication)].filter(Boolean).join(" ")
    : "";
  const selectedMedicationFrequencyDisplay = selectedMedication
    ? (isMedicationPrn(selectedMedication) ? "PRN / as needed" : selectedMedication.frequency)
    : "";
  const selectedNewMedicationDocuments = useMemo(
    () => medicationDocumentsFromPayload(selectedNewMedication?.prescriptionDocument),
    [selectedNewMedication],
  );
  const selectedNewMedicationPreviewDocument = selectedNewMedicationDocuments[0] || null;
  const selectedNewMedicationPreviewUrl = medicationDocumentUrl(selectedNewMedicationPreviewDocument);
  const selectedNewMedicationExtractedDetails = selectedNewMedication?.extractedMedicationDetails
    && typeof selectedNewMedication.extractedMedicationDetails === "object"
    && !Array.isArray(selectedNewMedication.extractedMedicationDetails)
    ? selectedNewMedication.extractedMedicationDetails
    : {};
  const medicationPreviewDocument = medicationDocumentPreview.document || {};
  const medicationPreviewUrl = medicationDocumentUrl(medicationPreviewDocument);
  const medicationPreviewName = medicationDocumentName(medicationPreviewDocument, medicationDocumentPreview.index);
  const medicationPreviewDescription = displayValue(medicationPreviewDocument.description, "Medication document");
  const medicationPreviewIsImage = medicationDocumentIsImage(medicationPreviewDocument);
  const medicationPreviewIsPdf = medicationDocumentIsPdf(medicationPreviewDocument);
  const medicationFormDocuments = medicationForm.documents || [];
  const selectedMedicationFormDocumentIndex = medicationFormDocuments.findIndex(
    (document, index) => medicationDocumentKey(document, index) === selectedMedicationFormDocumentKey,
  );
  const medicationFormPreviewIndex = selectedMedicationFormDocumentIndex >= 0 ? selectedMedicationFormDocumentIndex : 0;
  const medicationFormPreviewDocument = medicationFormDocuments[medicationFormPreviewIndex] || null;
  const medicationFormPreviewUrl = medicationDocumentUrl(medicationFormPreviewDocument || {});
  const medicationFormPreviewName = medicationFormPreviewDocument
    ? medicationDocumentName(medicationFormPreviewDocument, medicationFormPreviewIndex)
    : "Prescription document";
  const selectedPharmacyRecord = selectedPharmacy?.record || selectedPharmacy || {};
  const pharmacyDetailGroups = selectedPharmacy ? [
    ["Pharmacy Information", [
      ["Name", pharmacyName(selectedPharmacyRecord)],
      ["Status", selectedPharmacyRecord.status],
      ["Preferred for resident", selectedPharmacy.preferred ? "Yes" : "No"],
      ["Resident notes", selectedPharmacy.notes],
    ]],
    ["Contact", [
      ["Phone", selectedPharmacyRecord.phone],
      ["Fax", selectedPharmacyRecord.fax],
      ["Email", selectedPharmacyRecord.email],
      ["Website", selectedPharmacyRecord.website],
      ["Contact name", selectedPharmacyRecord.contactName],
    ]],
    ["Address", [
      ["Address", pharmacyAddress(selectedPharmacyRecord)],
      ["Address line 1", selectedPharmacyRecord.addressLine1],
      ["Address line 2", selectedPharmacyRecord.addressLine2],
      ["City", selectedPharmacyRecord.city],
      ["State", selectedPharmacyRecord.state],
      ["Postal code", selectedPharmacyRecord.postalCode],
      ["Country", selectedPharmacyRecord.country],
    ]],
  ] : [];
  const vitalSignRows = useMemo(() =>
    [...vitalSigns].sort((firstRecord, secondRecord) => {
      const firstDate = new Date(vitalRecordedAt(firstRecord) || 0);
      const secondDate = new Date(vitalRecordedAt(secondRecord) || 0);
      return (Number.isNaN(secondDate.getTime()) ? 0 : secondDate.getTime())
        - (Number.isNaN(firstDate.getTime()) ? 0 : firstDate.getTime());
    }),
  [vitalSigns]);
  const filteredVitalSignChecklist = useMemo(() => {
    const term = vitalSignsSearch.trim().toLowerCase();
    if (!term) return vitalSignChecklist;
    return vitalSignChecklist.filter(([key, label, detail]) =>
      [key, label, detail].join(" ").toLowerCase().includes(term),
    );
  }, [vitalSignsSearch]);
  const selectedHealthTrackingLogs = useMemo(() =>
    [...careActivityLogs].sort((firstLog, secondLog) => {
      const firstDate = new Date(firstLog.recordedAt || firstLog.createdAt || firstLog.activityAt || 0);
      const secondDate = new Date(secondLog.recordedAt || secondLog.createdAt || secondLog.activityAt || 0);
      return (Number.isNaN(secondDate.getTime()) ? 0 : secondDate.getTime())
        - (Number.isNaN(firstDate.getTime()) ? 0 : firstDate.getTime());
    }),
  [careActivityLogs]);
  const careActivityTotalPages = Math.max(1, Math.ceil(selectedHealthTrackingLogs.length / careActivityPageSize));
  const careActivityPageStart = selectedHealthTrackingLogs.length
    ? ((careActivityPage - 1) * careActivityPageSize) + 1
    : 0;
  const careActivityPageEnd = Math.min(selectedHealthTrackingLogs.length, careActivityPage * careActivityPageSize);
  const paginatedHealthTrackingLogs = useMemo(
    () => selectedHealthTrackingLogs.slice(
      (careActivityPage - 1) * careActivityPageSize,
      careActivityPage * careActivityPageSize,
    ),
    [careActivityPage, careActivityPageSize, selectedHealthTrackingLogs],
  );

  useEffect(() => {
    setCareActivityPage(1);
  }, [selectedResidentId, careActivityPageSize]);

  useEffect(() => {
    if (careActivityPage > careActivityTotalPages) setCareActivityPage(careActivityTotalPages);
  }, [careActivityPage, careActivityTotalPages]);
  const selectedVitalThresholdOverrides = useMemo(() =>
    selectedResidentId ? (vitalThresholdOverridesByResident[selectedResidentId] || {}) : {},
  [selectedResidentId, vitalThresholdOverridesByResident]);
  const filteredVitalAlertThresholds = useMemo(() => {
    const term = vitalThresholdSearch.trim().toLowerCase();
    if (!term) return vitalAlertThresholds;
    return vitalAlertThresholds.filter((threshold) => {
      const values = thresholdValuesForResident(threshold, selectedVitalThresholdOverrides);
      return [
        threshold.key,
        threshold.title,
        threshold.summary,
        thresholdSummaryFromValues(threshold, values),
      ].join(" ").toLowerCase().includes(term);
    });
  }, [selectedVitalThresholdOverrides, vitalThresholdSearch]);
  const selectedVitalThreshold = selectedVitalThresholdKey ? vitalThresholdMap[selectedVitalThresholdKey] : null;
  const selectedVitalThresholdValues = selectedVitalThreshold
    ? thresholdValuesForResident(selectedVitalThreshold, selectedVitalThresholdOverrides)
    : {};
  const renderVitalThresholdField = (fieldDefinition) => {
    const [field, label, , unit, options, settings] = fieldDefinition;
    const scale = thresholdFieldScale(fieldDefinition);
    const scaleMax = thresholdFieldScaleMax(fieldDefinition);
    return (
      <label className={`ncp-form-field${field === "instruction" ? " ncp-form-field-wide" : ""}`} key={field}>
        <FieldLabel info={thresholdFieldInfo(selectedVitalThreshold.key, fieldDefinition)}>{label}</FieldLabel>
        {options?.length ? (
          settings?.multiple ? (
            <Select
              allowClear
              className="medication-multi-select vital-threshold-option-picker"
              mode="multiple"
              options={options.map(([value, optionLabel]) => ({ value, label: optionLabel }))}
              placeholder={`Select ${label.toLowerCase()}`}
              value={Array.isArray(vitalThresholdForm[field])
                ? vitalThresholdForm[field].map(String)
                : [vitalThresholdForm[field]].filter(hasEnteredValue).map(String)}
              onChange={(values) => updateVitalThresholdForm(field, values)}
            />
          ) : (
            <Select
              allowClear={options !== booleanThresholdOptions}
              className="medication-multi-select vital-threshold-option-picker"
              options={options.map(([value, optionLabel]) => ({ value: String(value), label: optionLabel }))}
              placeholder={`Select ${label.toLowerCase()}`}
              value={vitalThresholdForm[field] === undefined || vitalThresholdForm[field] === null ? "" : String(vitalThresholdForm[field])}
              onChange={(value) => updateVitalThresholdForm(field, options === booleanThresholdOptions ? value === "true" : value || "")}
            />
          )
        ) : field === "instruction" ? (
          <textarea
            rows={3}
            value={vitalThresholdForm[field] || ""}
            onChange={(event) => updateVitalThresholdForm(field, event.target.value)}
            placeholder={label}
          />
        ) : scaleMax === 10 ? (
          <ScaleSlider
            label={label}
            max={scaleMax}
            value={vitalThresholdForm[field]}
            onChange={(value) => updateVitalThresholdForm(field, value)}
          />
        ) : (
          <input
            inputMode="decimal"
            value={vitalThresholdForm[field] || ""}
            onChange={(event) => {
              const nextValue = numericInputValue(event.target.value);
              if (nextValue !== null) updateVitalThresholdForm(field, nextValue);
            }}
            placeholder={scale || unit || label}
          />
        )}
        {unit || scale ? <small>{[unit, scale].filter(Boolean).join(" | ")}</small> : null}
      </label>
    );
  };
  const renderVitalThresholdFields = () => {
    if (!selectedVitalThreshold) return null;
    if (selectedVitalThreshold.key !== "bloodPressure") {
      return selectedVitalThreshold.fields.map(renderVitalThresholdField);
    }
    const fieldByKey = Object.fromEntries(selectedVitalThreshold.fields.map((fieldDefinition) => [fieldDefinition[0], fieldDefinition]));
    return [
      ["systolicLow", "systolicHigh"],
      ["diastolicLow", "diastolicHigh"],
    ].map((row) => (
      <div className="vital-threshold-field-row" key={row.join("-")}>
        {row.map((fieldKey) => renderVitalThresholdField(fieldByKey[fieldKey]))}
      </div>
    ));
  };
  const selectedVitalSign = vitalSignChecklistMap[selectedVitalSignKey] || vitalSignChecklistMap.bloodPressure;
  const selectedVitalSignRows = useMemo(() =>
    vitalSignRows.filter((record) => vitalRecordMatchesType(record, selectedVitalSignKey)),
  [selectedVitalSignKey, vitalSignRows]);
  const selectedVitalTrend = useMemo(() => {
    const rows = selectedVitalSignRows
      .map((record) => ({
        record,
        value: numericVitalValueForType(record, selectedVitalSignKey),
        date: new Date(vitalRecordedAt(record) || 0),
      }))
      .filter((point) => point.value !== null && !Number.isNaN(point.date.getTime()))
      .sort((firstPoint, secondPoint) => firstPoint.date.getTime() - secondPoint.date.getTime())
      .slice(-12);
    if (!rows.length) return null;
    const values = rows.map((point) => point.value);
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    const range = maxValue - minValue || 1;
    const width = 320;
    const height = 180;
    const padding = 26;
    const points = rows.map((point, index) => {
      const x = rows.length === 1
        ? width / 2
        : padding + (index * (width - padding * 2)) / (rows.length - 1);
      const y = height - padding - ((point.value - minValue) / range) * (height - padding * 2);
      return {
        ...point,
        x,
        y,
        label: formatDate(vitalRecordedAt(point.record)),
      };
    });
    return {
      points,
      path: points.map((point) => `${point.x},${point.y}`).join(" "),
      minValue,
      maxValue,
      latestValue: rows[rows.length - 1]?.value,
      label: selectedVitalSignKey === "bloodPressure" ? "Systolic trend" : `${selectedVitalSign.label} trend`,
      width,
      height,
    };
  }, [selectedVitalSign.label, selectedVitalSignKey, selectedVitalSignRows]);
  const residentAllergiesSummary = residentSummaryText(firstText(
    selectedResident?.allergies,
    selectedResident?.careSnapshot?.allergies,
    selectedResident?.initialCareSnapshot?.allergies,
    selectedResident?.onboardingCareSnapshot?.allergies,
    selectedResident?.healthAndCoverage?.allergies,
  ));
  const residentDietSummary = residentSummaryText(firstText(
    selectedResident?.diet,
    selectedResident?.dietaryNeeds,
    selectedResident?.careSnapshot?.diet,
    selectedResident?.initialCareSnapshot?.diet,
    selectedResident?.onboardingCareSnapshot?.diet,
  ));
  const residentPersonalInformation = selectedResident?.personalInformation || {};
  const residentRace = firstText(residentPersonalInformation.race, selectedResident?.race);
  const residentRaceOther = firstText(residentPersonalInformation.raceOther, selectedResident?.raceOther);
  const residentRaceSummary = residentSummaryText(
    String(residentRace).toLowerCase() === "other" && residentRaceOther
      ? `Other: ${residentRaceOther}`
      : readableCode(residentRace),
  );
  const residentHeightFeet = residentPersonalInformation.heightFeet ?? selectedResident?.heightFeet;
  const residentHeightInches = residentPersonalInformation.heightInches ?? selectedResident?.heightInches;
  const residentHeightSummary = residentSummaryText(
    residentHeightFeet !== undefined && residentHeightFeet !== null && residentHeightFeet !== ""
      ? `${residentHeightFeet} ft${residentHeightInches !== undefined && residentHeightInches !== null && residentHeightInches !== "" ? ` ${residentHeightInches} in` : ""}`
      : "",
  );
  const residentWeight = residentPersonalInformation.weightLbs ?? selectedResident?.weightLbs;
  const residentWeightSummary = residentSummaryText(
    residentWeight !== undefined && residentWeight !== null && residentWeight !== "" ? `${residentWeight} lbs` : "",
  );
  const residentLanguageSummary = residentSummaryText(firstText(
    residentPersonalInformation.primaryLanguage,
    selectedResident?.primaryLanguage,
    selectedResident?.language,
    selectedResident?.preferredLanguage,
  ));
  const residentResidencyDetails = selectedResident?.residencyDetails || selectedResident?.location || {};
  const residentSinceValue = firstText(
    selectedResident?.moveInDate,
    selectedResident?.movedInDate,
    selectedResident?.admissionDate,
    selectedResident?.expectedAdmissionDate,
    residentResidencyDetails.moveInDate,
    residentResidencyDetails.admissionDate,
  );
  const residentSinceSummary = residentSinceValue ? formatDate(residentSinceValue) : "Not assigned";
  const residentFloorSummary = firstText(selectedResident?.floorNumber, selectedResident?.floor, residentResidencyDetails.floorNumber, residentResidencyDetails.floor, "Not assigned");
  const residentRoomSummary = firstText(selectedResident?.roomNumber, selectedResident?.room, residentResidencyDetails.roomNumber, residentResidencyDetails.room, "Not assigned");
  const residentBedSummary = firstText(selectedResident?.bedNumber, selectedResident?.bed, residentResidencyDetails.bedNumber, residentResidencyDetails.bed, "Not assigned");
  const residentMedicationNotesSummary = residentSummaryText(firstText(
    selectedResident?.medicationNotes,
    selectedResident?.careSnapshot?.medicationNotes,
    selectedResident?.initialCareSnapshot?.medicationNotes,
    selectedResident?.onboardingCareSnapshot?.medicationNotes,
  ));
  const currentVitalSignsFromLoadedTab = [
    ["BP", "bloodPressure"],
    ["Pulse", "pulse"],
    ["Temp", "temperature"],
    ["SpO2", "oxygenSaturation"],
    ["Weight", "weight"],
  ].map(([label, key]) => {
    const record = vitalSignRows.find((entry) => vitalRecordMatchesType(entry, key));
    return record ? `${label} ${vitalRecordValueForType(record, key)}` : "";
  }).filter(Boolean).join(" | ");
  const currentVitalSignsSummary = firstText(
    currentVitalSignsFromLoadedTab,
    selectedResident?.currentVitalSigns,
    "No current readings",
  );
  const residentProviderSummary = residentSummaryText(firstText(
    selectedResident?.primaryCareProviderName,
    selectedResident?.primaryProvider,
  ));
  const residentCareTeamSummary = residentSummaryText(selectedResident?.careTeamNames);
  const residentPharmaciesSummary = residentSummaryText(
    residentPharmacyNames.length ? residentPharmacyNames : selectedResident?.pharmacyNames,
  );
  const selectedNewMedicationNeedsAdfApproval = isAdfApprovalMedication(selectedNewMedication);
  const selectedNewMedicationNeedsOliveebApproval = isOliveebApprovalMedication(selectedNewMedication);
  const selectedNewMedicationFinalized = ["ACTIVE", "REJECTED"].includes(medicationStatus(selectedNewMedication));
  const selectedNewMedicationFilterLabel = NEW_MEDICATION_STATUS_FILTER_OPTIONS
    .find((option) => option.value === newMedicationStatusFilter)?.label?.toLowerCase() || "selected";

  return (
    <CareShell className="dashboard-content resident-medications-page">
      <div className="residents-page-head">
        <div className="page-title">
          <h1>{carePlansOnly ? "Care Plans" : vitalSignsOnly ? "Vital Signs" : "Resident eMAR"}</h1>
          <p>
            {carePlansOnly
              ? "Select a resident to review vital sign thresholds and capture care activity notes."
              : vitalSignsOnly
                ? "Select a resident to capture vital signs and review health monitoring trends."
                : "Select a resident to view medication orders, daily expectations, and eMAR administration logs."}
          </p>
        </div>
      </div>

      <div className="resident-medication-layout">
        <Panel title="Residents">
          {error ? <p className="form-error">{error}</p> : null}
          <label className="resident-search medication-resident-search">
            <i className="ti ti-search" />
            <input
              placeholder="Search residents"
              value={residentSearch}
              onChange={(event) => setResidentSearch(event.target.value)}
            />
          </label>
          <div className="medication-resident-list">
            {loadingResidents ? <p>Loading residents...</p> : null}
            {!loadingResidents && !filteredResidents.length ? <p>No residents found.</p> : null}
            {filteredResidents.map((resident) => {
              const id = residentId(resident);
              const active = id === selectedResidentId;
              return (
                <button
                  className={`medication-resident-row ${active ? "is-active" : ""}`}
                  key={id || residentName(resident)}
                  type="button"
                  onClick={() => selectMedicationResident(id)}
                >
                  <span>
                    <strong>{residentName(resident)}</strong>
                    <small>{residentRoom(resident) || "No room assigned"}</small>
                  </span>
                  <i className="ti ti-chevron-right" />
                </button>
              );
            })}
          </div>
        </Panel>

        <div className="resident-medication-main">
          {selectedResident ? (
            <ResidentInformationSummary
              resident={selectedResident}
              items={[
                { label: "Residency", entries: [["Bed", residentBedSummary], ["Room", residentRoomSummary], ["Floor", residentFloorSummary], ["Since", residentSinceSummary]] },
                { label: "Demographics", entries: [["Race", residentRaceSummary], ["Height", residentHeightSummary], ["Weight", residentWeightSummary], ["Language", residentLanguageSummary]] },
                { label: "Allergies", value: residentAllergiesSummary },
                { label: "Diet", value: residentDietSummary },
                { label: "Medication notes", value: residentMedicationNotesSummary },
                { label: "Current vital signs", value: currentVitalSignsSummary },
                { label: "Care network", entries: [["Provider", residentProviderSummary], ["Care team", residentCareTeamSummary], ["Pharmacies", residentPharmaciesSummary]] },
              ]}
            />
          ) : null}

          {!standaloneResidentCareView ? (
          <div className="medication-tabs" role="tablist" aria-label="Medication views">
            {medicationTabs.map(([tab, label, icon]) => {
              const isActive = Boolean(selectedResident) && activeMedicationTab === tab;
              return (
                <button
                  className={isActive ? "is-active" : ""}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  aria-controls={medicationTabHash[tab]}
                  onClick={() => selectMedicationTab(tab)}
                  key={tab}
                >
                  <i className={`ti ${icon}`} /> {label}
                </button>
              );
            })}
          </div>
          ) : null}

          {!selectedResident ? (
            <div className="medication-resident-empty-state">
              <span className="round-icon blue"><i className="ti ti-pill" /></span>
              <span className="page-eyebrow">Resident eMAR</span>
              <h2>Review and manage a resident&apos;s medications</h2>
              <p>Select a resident from the list to view medication orders, administration schedules, imports, approvals, and alerts.</p>
              <div className="medication-resident-welcome-steps">
                <div><i className="ti ti-user-search" /><span><strong>Choose a resident</strong><small>Use the resident list or search on the left.</small></span></div>
                <div><i className="ti ti-calendar-event" /><span><strong>Review eMAR</strong><small>Open schedules and administration records.</small></span></div>
                <div><i className="ti ti-pill" /><span><strong>Manage medications</strong><small>Review orders, imports, approvals, and alerts.</small></span></div>
              </div>
            </div>
          ) : !standaloneResidentCareView && !activeMedicationTab ? (
            <div className="medication-resident-empty-state medication-resident-overview">
              <span className="round-icon blue"><i className="ti ti-user" /></span>
              <span className="page-eyebrow">Selected resident</span>
              <h2>{residentName(selectedResident)}</h2>
              <div className="medication-resident-overview-grid">
                <div>
                  <small>Room</small>
                  <strong>{residentRoom(selectedResident) || "Not assigned"}</strong>
                </div>
                <div>
                  <small>Date of birth</small>
                  <strong>{formatDate(selectedResident.birthDate || selectedResident.dateOfBirth || selectedResident.dob)}</strong>
                </div>
                <div>
                  <small>Status</small>
                  <strong>{displayValue(selectedResident.status)}</strong>
                </div>
              </div>
              <p>Select a tab above to review or manage this resident&apos;s medications.</p>
            </div>
          ) : <>
          {activeMedicationTab === "admin" ? (
            <section className="resident-bookmark-section" id="med-admin">
              {medicationError ? <p className="form-error">{medicationError}</p> : null}
              {medAdminView === "calendar" ? <div className="medication-admin-view-bar">
                <div className="medication-admin-log-view-toggle" aria-label="Medication administration view">
                  <button className={medAdminView === "chart" ? "active" : ""} type="button" onClick={() => setMedAdminView("chart")}>
                    <i className="ti ti-calendar-stats" /> Chart
                  </button>
                  <button className={medAdminView === "calendar" ? "active" : ""} type="button" onClick={() => {
                    setAdminLogFullscreen(false);
                    setMedAdminView("calendar");
                  }}>
                    <i className="ti ti-calendar-month" /> Calendar
                  </button>
                </div>
              </div> : null}
              {medAdminView === "calendar" ? (
              <Panel title="Medication Administration Calendar">
              <div className="medication-calendar-shell medication-calendar-only">
                <div className="medication-calendar">
                  {loadingDay || loadingMedications ? (
                    <div className="medication-calendar-loading" role="status" aria-live="polite">
                      <i className="ti ti-loader-2" />
                      <span>Loading medication calendar...</span>
                    </div>
                  ) : null}
                  <div className="medication-calendar-head">
                    <button className="mini-btn" type="button" onClick={() => moveCalendarMonth(-1)}>
                      <i className="ti ti-chevron-left" /> Previous
                    </button>
                    <div className="medication-calendar-jump" aria-label={`Jump from ${monthLabel(calendarMonth)}`}>
                      <div>
                        <select
                          aria-label="Select medication administration month"
                          value={calendarMonth.getMonth()}
                          onChange={(event) => jumpCalendarMonth(Number(event.target.value), calendarMonth.getFullYear())}
                        >
                          {monthOptions.map((option) => (
                            <option value={option.value} key={option.value}>{option.label}</option>
                          ))}
                        </select>
                        <select
                          aria-label="Select medication administration year"
                          value={calendarMonth.getFullYear()}
                          onChange={(event) => jumpCalendarMonth(calendarMonth.getMonth(), Number(event.target.value))}
                        >
                          {yearOptions.map((year) => (
                            <option value={year} key={year}>{year}</option>
                          ))}
                        </select>
                      </div>
                      <strong>{monthLabel(calendarMonth)}</strong>
                    </div>
                    <button className="mini-btn" type="button" onClick={() => moveCalendarMonth(1)}>
                      Next <i className="ti ti-chevron-right" />
                    </button>
                  </div>
                  <div className="medication-calendar-weekdays">
                    {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((label) => <span key={label}>{label}</span>)}
                  </div>
                  <div className="medication-calendar-grid">
                    {calendarDays.map((day) => {
                      const expectedCount = expectedCountForDay(day.key);
                      const actionCounts = actionCountsForDay(day.key);
                      return (
                        <button
                          className={[
                            "medication-calendar-day",
                            day.currentMonth ? "" : "is-muted",
                            day.key === selectedDate ? "is-selected" : "",
                            actionCounts.MISSED ? "is-missed" : "",
                          ].filter(Boolean).join(" ")}
                          key={day.key}
                          type="button"
                          onClick={() => selectCalendarDate(day, true)}
                        >
                          <strong>{day.date.getDate()}</strong>
                          <span data-compact={`${expectedCount} exp`}>{expectedCount} expected</span>
                          {Object.entries(actionCounts).map(([action, count]) => (
                            <em
                              className={action === "ADMINISTERED" ? "" : "medication-calendar-alert"}
                              data-compact={`${count} ${action === "ADMINISTERED" ? "given" : action.toLowerCase().slice(0, 4)}`}
                              key={action}
                            >
                              {count} {adminActionLabel(action).toLowerCase()}
                            </em>
                          ))}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
              </Panel>
              ) : null}
            </section>
          ) : null}

          {activeMedicationTab === "adminLog" || (activeMedicationTab === "admin" && medAdminView === "chart") ? (
            <section className="resident-bookmark-section" id={activeMedicationTab === "admin" ? "med-admin-chart" : "med-admin-log"}>
            <Panel title={activeMedicationTab === "admin" ? "Medication Administration Chart" : "Medication Admin Log"}>
              {medicationError ? <p className="form-error">{medicationError}</p> : null}
              {activeMedicationTab === "adminLog" ? (
              <>
              <div className="medication-admin-log-toolbar">
                <div className="medication-admin-log-range">
                  <label>
                    <span>Start date</span>
                    <input type="date" value={adminLogRangeStart} onChange={(event) => setAdminLogRangeStart(event.target.value)} />
                  </label>
                  <label>
                    <span>End date</span>
                    <input type="date" value={adminLogRangeEnd} onChange={(event) => setAdminLogRangeEnd(event.target.value)} />
                  </label>
                </div>
                <div className="medication-admin-log-actions">
                  <div className="medication-admin-log-view-toggle" aria-label="Medication log view">
                    <button className={adminLogView === "matrix" ? "active" : ""} type="button" onClick={() => setAdminLogView("matrix")}>
                      <i className="ti ti-calendar-stats" /> Chart
                    </button>
                    <button className={adminLogView === "table" ? "active" : ""} type="button" onClick={() => setAdminLogView("table")}>
                      <i className="ti ti-list" /> List
                    </button>
                  </div>
                  <button className="mini-btn" type="button" onClick={() => printMedicationAdminLog()} disabled={!medicationAdminLogDisplayRows.length}>
                    <i className="ti ti-printer" /> Print
                  </button>
                  <button className="mini-btn" type="button" onClick={exportMedicationAdminLogExcel} disabled={!medicationAdminLogDisplayRows.length}>
                    <i className="ti ti-file-spreadsheet" /> Excel
                  </button>
                  <button className="mini-btn" type="button" onClick={() => printMedicationAdminLog({ pdf: true })} disabled={!medicationAdminLogDisplayRows.length}>
                    <i className="ti ti-file-type-pdf" /> PDF
                  </button>
                </div>
              </div>
              <div className="medication-summary-strip medication-list-summary-strip">
                <span><strong>{medicationAdminLogDisplayRows.length}</strong><small>Records in range</small></span>
                <span><strong>{selectedResident ? residentRoom(selectedResident) || "Not set" : "Not set"}</strong><small>Room</small></span>
                <span><strong>{medicationAdminLogRangeLabel}</strong><small>Log period</small></span>
              </div>
              </>
              ) : null}
              {activeMedicationTab === "admin" || adminLogView === "matrix" ? (
                <div className={`medication-med-log${adminLogFullscreen ? " is-fullscreen" : ""}`}>
                  <div className="medication-med-log-head">
                    <div className="medication-admin-log-view-toggle" aria-label="Medication schedule type">
                      <button className={adminLogMedicationMode === "scheduled" ? "active" : ""} type="button" onClick={() => setAdminLogMedicationMode("scheduled")}>Scheduled</button>
                      <button className={adminLogMedicationMode === "prn" ? "active" : ""} type="button" onClick={() => setAdminLogMedicationMode("prn")}>PRN</button>
                      <button className={adminLogMedicationMode === "both" ? "active" : ""} type="button" onClick={() => setAdminLogMedicationMode("both")}>Both</button>
                    </div>
                    <div className="medication-med-log-legend" aria-label="Medication administration status legend">
                      {[
                        ["administered", "ti-check", "Given"],
                        ["missed", "ti-clock-x", "Missed"],
                        ["refused", "ti-ban", "Refused"],
                        ["withheld", "ti-player-pause", "Withheld"],
                      ].map(([level, icon, label]) => (
                        <span className={`is-${level}`} key={level}><i className={`ti ${icon}`} /> {label}</span>
                      ))}
                    </div>
                    <div className="medication-med-log-head-actions">
                      <strong>{medicationChartRangeLabel}</strong>
                      <div className="medication-med-log-month-selectors" aria-label="Medication chart month and year">
                        <select
                          aria-label="Select medication chart month"
                          value={medicationChartMonth.getMonth()}
                          onChange={(event) => jumpMedicationChartMonth(Number(event.target.value), medicationChartMonth.getFullYear())}
                        >
                          {monthOptions.map((option) => <option value={option.value} key={option.value}>{option.label}</option>)}
                        </select>
                        <select
                          aria-label="Select medication chart year"
                          value={medicationChartMonth.getFullYear()}
                          onChange={(event) => jumpMedicationChartMonth(medicationChartMonth.getMonth(), Number(event.target.value))}
                        >
                          {yearOptions.map((year) => <option value={year} key={year}>{year}</option>)}
                        </select>
                      </div>
                      <Tooltip title="Print medication chart">
                        <button
                          aria-label="Print medication chart"
                          className="medication-med-log-fullscreen medication-med-log-print"
                          type="button"
                          onClick={printMedicationAdminLogChart}
                        >
                          <i className="ti ti-printer" />
                        </button>
                      </Tooltip>
                      <Tooltip title={adminLogFullscreen ? "Close full screen" : "View full screen"}>
                        <button
                          aria-label={adminLogFullscreen ? "Close full-screen medication log" : "View medication log full screen"}
                          className="medication-med-log-fullscreen"
                          type="button"
                          onClick={() => setAdminLogFullscreen((current) => !current)}
                        >
                          <i className={`ti ${adminLogFullscreen ? "ti-x" : "ti-maximize"}`} />
                        </button>
                      </Tooltip>
                      {activeMedicationTab === "admin" ? (
                        <div className="medication-admin-log-view-toggle" aria-label="Medication administration view">
                          <button className={medAdminView === "chart" ? "active" : ""} type="button" onClick={() => setMedAdminView("chart")}>
                            <i className="ti ti-calendar-stats" /> Chart
                          </button>
                          <button className={medAdminView === "calendar" ? "active" : ""} type="button" onClick={() => {
                            setAdminLogFullscreen(false);
                            setMedAdminView("calendar");
                          }}>
                            <i className="ti ti-calendar-month" /> Calendar
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className="medication-med-log-scroll">
                    <table className="medication-med-log-table">
                      <thead>
                        <tr>
                          <th className="medication-med-log-medication">Medication</th>
                          <th className="medication-med-log-time">Time</th>
                          {medicationAdminLogDays.map((key) => (
                            <th className={key === dateKey(todayDate()) ? "is-today" : ""} key={key} title={formatDate(key)}>
                              <div className="medication-med-log-day">
                                <span>{parseDateKey(key).toLocaleDateString(undefined, { weekday: "short" })}</span>
                                <strong>{parseDateKey(key).getDate()}</strong>
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {loadingDay || loadingMedications ? <tr><td colSpan={medicationAdminLogDays.length + 2}>Loading medication administration log...</td></tr> : null}
                        {!loadingDay && !loadingMedications && !medicationAdminLogMatrixRows.length ? (
                          <tr><td colSpan={medicationAdminLogDays.length + 2}>No {adminLogMedicationMode === "both" ? "" : adminLogMedicationMode === "prn" ? "PRN " : "scheduled "}medications found.</td></tr>
                        ) : null}
                        {!loadingDay && !loadingMedications && medicationAdminLogMatrixRows.map((row) => row.slots.map((slot, slotIndex) => (
                          <tr key={`${row.medicationId}-${slot.scheduledSlot}-${slotIndex}`}>
                            {slotIndex === 0 ? (
                              <td className="medication-med-log-medication" rowSpan={row.slots.length}>
                                <button type="button" onClick={() => openMedicationDetails(row.medication)}>{medicationName(row.medication)}</button>
                                {row.isPrn ? <em className="medication-med-log-prn-label">PRN</em> : null}
                                <strong>{medicationStrength(row.medication)}</strong>
                                <small>{medicationDoseSummary(row.medication)}</small>
                                <small>Start: {formatDate(row.medication.startDate || row.medication.effectiveDate)}</small>
                              </td>
                            ) : null}
                            <td className="medication-med-log-time">
                              {row.isPrn ? "As needed" : formatMedicationSlotTime(slot)}
                            </td>
                            {medicationAdminLogDays.map((key) => {
                              const dayLogs = row.logsByDay[key] || [];
                              const matchingLogs = row.isPrn
                                ? dayLogs.filter((item) => logScheduledSlot(item) === "PRN")
                                : [dayLogs.find((item) => logScheduledSlot(item) === slot.scheduledSlot)
                                  || (row.slots.length === 1 ? dayLogs[0] : null)].filter(Boolean);
                              const log = matchingLogs[0] || null;
                              const expected = medicationExpectedOn(row.medication, key);
                              const scheduledTime = firstText(
                                log?.scheduledTime,
                                row.isPrn ? "PRN" : formatMedicationSlotTime(slot),
                              );
                              const systemMissed = Boolean(log && isSystemMissedAdministrationLog(log));
                              const administrationWindowOpen = row.isPrn
                                ? prnAdministrationDateAllowed(key)
                                : medicationSlotAdministrationWindowOpen(key, slot, row.slots)
                                  || lateMedicationAdministrationDateAllowed(key);
                              const canOpenFocusedAdministration = activeMedicationTab === "admin"
                                && expected
                                && administrationWindowOpen
                                && (!log || systemMissed);
                              const openChartCell = () => {
                                if (log && (!systemMissed || !canOpenFocusedAdministration)) {
                                  setSelectedDate(key);
                                  openAdminLogDetails(log);
                                } else if (canOpenFocusedAdministration) {
                                  setSelectedDate(key);
                                  setFocusedAdministrationCell({
                                    medicationId: row.medicationId,
                                    scheduledSlot: row.isPrn ? "PRN" : slot.scheduledSlot,
                                  });
                                  if (row.isPrn) {
                                    setPrnAdminDrawerOpen(true);
                                  } else {
                                    setAdminDrawerOpen(true);
                                  }
                                }
                              };
                              return (
                                <td
                                  className={`${key === dateKey(todayDate()) ? "is-today" : ""} ${!expected ? "is-outside" : ""} ${log || canOpenFocusedAdministration ? "is-clickable" : ""}`}
                                  key={key}
                                  onClick={openChartCell}
                                  onKeyDown={(event) => {
                                    if ((event.key === "Enter" || event.key === " ") && (log || canOpenFocusedAdministration)) {
                                      event.preventDefault();
                                      openChartCell();
                                    }
                                  }}
                                  tabIndex={log || canOpenFocusedAdministration ? 0 : undefined}
                                >
                                  {log ? (
                                    <div className={`medication-med-log-status-group ${matchingLogs.length > 1 ? "has-multiple" : ""}`}>
                                      {matchingLogs.map((record) => {
                                        const recordAction = logAdministrationAction(record);
                                        const recordStatus = medicationMedLogStatus(recordAction);
                                        const recordActor = logActorName(record);
                                        const recordTime = formatTimeOnly(record.administrationAt || record.createdAt);
                                        const recordSystemMissed = isSystemMissedAdministrationLog(record);
                                        return (
                                          <Tooltip
                                            key={record.id || `${recordTime}-${recordActor}`}
                                            title={`${medicationAdminLogActionLabel(recordAction)} at ${recordTime}${recordActor ? ` by ${recordActor}` : ""}`}
                                            zIndex={2200}
                                          >
                                            <button
                                              aria-label={`${recordStatus.label} at ${recordTime}${recordActor ? ` by ${recordActor}` : ""}`}
                                              className={`medication-med-log-status is-${recordStatus.level}`}
                                              type="button"
                                              onClick={(event) => {
                                                event.stopPropagation();
                                                setSelectedDate(key);
                                                if (recordSystemMissed) openChartCell();
                                                else openAdminLogDetails(record);
                                              }}
                                            >
                                              <span className="medication-med-log-status-main">
                                                <i className={`ti ${recordStatus.icon}`} />
                                              </span>
                                              {recordActor ? <em className="medication-med-log-caregiver">{userInitials(recordActor)}</em> : null}
                                            </button>
                                          </Tooltip>
                                        );
                                      })}
                                    </div>
                                  ) : canOpenFocusedAdministration ? (
                                    <Tooltip
                                      title={`Open ${medicationName(row.medication)} administration at ${scheduledTime}`}
                                      zIndex={2200}
                                    >
                                      <button
                                        aria-label={`Administer ${medicationName(row.medication)} for ${formatDate(key)} at ${scheduledTime}`}
                                        className="medication-med-log-administer"
                                        type="button"
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          openChartCell();
                                        }}
                                      >
                                        <i className="ti ti-plus" />
                                      </button>
                                    </Tooltip>
                                  ) : expected ? <span className="medication-med-log-empty">-</span> : null}
                                </td>
                              );
                            })}
                          </tr>
                        )))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
              <div className="medication-table-wrap">
                <table className="medication-table medication-admin-log-table">
                  <thead>
                    <tr>
                      <th>Medication</th>
                      <th>Action</th>
                      <th>Quantity</th>
                      <th>Time</th>
                      <th>Timing</th>
                      <th>Administered by</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingDay || loadingMedications ? (
                      <tr><td colSpan={6}>Loading medication administration log...</td></tr>
                    ) : null}
                    {!loadingDay && !loadingMedications && !medicationAdminLogDisplayRows.length ? (
                      <tr><td colSpan={6}>No medication administration records found for this range.</td></tr>
                    ) : null}
                    {!loadingDay && !loadingMedications && medicationAdminLogDisplayRows.map((row) => {
                      const { log, medication } = row;
                      return (
                        <tr
                          className="medication-table-clickable-row"
                          key={log.id || `${logMedicationId(log)}-${log.administrationAt || log.createdAt}`}
                          onClick={() => openAdminLogDetails(log)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              openAdminLogDetails(log);
                            }
                          }}
                          tabIndex={0}
                        >
                          <td className="medication-table-name-cell">
                            <button
                              className="medication-admin-name-link"
                              onClick={(event) => {
                                event.stopPropagation();
                                openMedicationDetails(medication);
                              }}
                              type="button"
                            >
                              {row.medicationName}
                            </button>
                            <small>{row.strength}</small>
                            {row.isPrn ? <em className="pill medium medication-admin-log-prn">PRN</em> : null}
                          </td>
                          <td><em className={`pill medication-admin-log-action is-${row.actionLevel}`}>{row.action}</em></td>
                          <td>{displayValue(row.quantity, "-")}</td>
                          <td>{row.time}</td>
                          <td><em className={`pill medication-admin-log-timing is-${row.timing.level}`}>{row.timing.label}</em></td>
                          <td>
                            <Tooltip title={row.administeredBy}>
                              <span className="medication-admin-log-initials">{row.initials}</span>
                            </Tooltip>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              )}
            </Panel>
            </section>
          ) : null}

          {activeMedicationTab === "list" ? (
            <section className="resident-bookmark-section" id="med-list">
            <Panel
              title="Medication List"
              actionNode={(
                <button className="mini-btn success" type="button" onClick={openMedicationDrawer} disabled={!selectedResidentId}>
                  <i className="ti ti-plus" /> Add Medication
                </button>
              )}
            >
              {medicationError ? <p className="form-error">{medicationError}</p> : null}
              <div className="medication-summary-strip medication-list-summary-strip">
                <span><strong>{medications.length}</strong><small>Total medications</small></span>
                <span><strong>{prnMedicationCount}</strong><small>PRNs</small></span>
                <span><strong>{scheduledMedicationCount}</strong><small>Scheduled</small></span>
                <span><strong>{selectedResident ? residentRoom(selectedResident) || "Not set" : "Not set"}</strong><small>Room</small></span>
              </div>
              <label className="resident-search medication-list-search">
                <i className="ti ti-search" />
                <input
                  aria-label="Search medications"
                  placeholder="Search medications"
                  value={medicationSearch}
                  onChange={(event) => setMedicationSearch(event.target.value)}
                />
              </label>
              <div className="medication-table-wrap">
                <table className="medication-table">
                  <colgroup>
                    <col className="medication-table-col-name" />
                    <col className="medication-table-col-dose" />
                    <col className="medication-table-col-strength" />
                    <col className="medication-table-col-quantity" />
                    <col className="medication-table-col-remaining" />
                    <col className="medication-table-col-route" />
                    <col className="medication-table-col-frequency" />
                    <col className="medication-table-col-actions" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Medication</th>
                      <th>Dosage</th>
                      <th>Strength</th>
                      <th>Qty</th>
                      <th>Remaining Qty</th>
                      <th>Route</th>
                      <th>Frequency</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingMedications ? (
                      <tr><td colSpan={8}>Loading medications...</td></tr>
                    ) : null}
                    {!loadingMedications && !medications.length ? (
                      <tr><td colSpan={8}>No medications found for this resident.</td></tr>
                    ) : null}
                    {!loadingMedications && medications.length > 0 && !filteredMedicationList.length ? (
                      <tr><td colSpan={8}>No medications match your search.</td></tr>
                    ) : null}
                    {!loadingMedications && filteredMedicationList.map((medication) => (
                      <tr
                        className="medication-table-clickable-row"
                        key={medication.id || medicationName(medication)}
                        onClick={() => openMedicationDetails(medication)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            openMedicationDetails(medication);
                          }
                        }}
                        tabIndex={0}
                      >
                        <td className="medication-table-name-cell">
                          <strong>{medicationName(medication)}</strong>
                          <small>{displayValue(medication.prescribedBy, "")}</small>
                          <small className="medication-table-range">{medicationPeriod(medication)}</small>
                          <span className="medication-table-badges">
                            {isMedicationWithheld(medication) ? <em className="pill amber"><i className="ti ti-player-pause" /> Withheld</em> : null}
                            {isMedicationPrn(medication) ? <em className="pill medium"><i className="ti ti-clock-question" /> PRN</em> : null}
                          </span>
                        </td>
                        <td>{displayValue([medication.dosage, medicationDoseUnitDisplay(medication)].filter(Boolean).join(" "), "-")}</td>
                        <td>{displayValue(medicationStrength(medication), "-")}</td>
                        <td>{displayValue([medicationQuantityAmount(medication), medicationQuantityUnitDisplay(medication)].filter(Boolean).join(" "), "-")}</td>
                        <td>{displayValue(medicationRemainingQuantityDisplay(medication), "-")}</td>
                        <td>{displayValue(medication.route, "-")}</td>
                        <td>{displayValue(medication.frequency, "-")}</td>
                        <td>
                          <div className="medication-row-actions">
                            <Dropdown
                              menu={{
                                items: [
                                  { key: "view", icon: <i className="ti ti-eye" />, label: "View details" },
                                  {
                                    key: "refill",
                                    disabled: (() => {
                                      const { remaining } = medicationRefillBalance(medication);
                                      if (remaining === null) return false;
                                      const pending = refillRequests.filter((request) => request.medicationId === medicationRecordId(medication) && String(request.status || "").toUpperCase() === "REQUESTED").length;
                                      return remaining <= 0 || pending >= remaining;
                                    })(),
                                    icon: <i className="ti ti-prescription" />,
                                    label: "Request refill",
                                  },
                                  { key: "withhold", disabled: medicationWithholdingId === medication.id, icon: <i className={medicationWithholdingId === medication.id ? "ti ti-loader-2" : isMedicationWithheld(medication) ? "ti ti-player-play" : "ti ti-player-pause"} />, label: isMedicationWithheld(medication) ? "Resume medication" : "Withhold medication" },
                                  { key: "edit", icon: <i className="ti ti-edit" />, label: "Edit" },
                                  { key: "delete", danger: true, disabled: medicationDeletingId === medication.id, icon: <i className={medicationDeletingId === medication.id ? "ti ti-loader-2" : "ti ti-trash"} />, label: "Delete" },
                                ],
                                onClick: ({ key, domEvent }) => {
                                  domEvent.stopPropagation();
                                  if (key === "view") openMedicationDetails(medication);
                                  if (key === "refill") openRefillRequestDialog(medication);
                                  if (key === "withhold") openMedicationWithholdDialog(medication);
                                  if (key === "edit") openMedicationEdit(medication);
                                  if (key === "delete") deleteMedication(medication);
                                },
                              }}
                              overlayClassName="medication-row-actions-menu"
                              placement="bottomRight"
                              trigger={["click"]}
                            >
                              <button
                                aria-label={`Actions for ${medicationName(medication)}`}
                                className="resident-icon-button medication-row-actions-trigger"
                                onClick={(event) => event.stopPropagation()}
                                type="button"
                              >
                                <i className="ti ti-dots-vertical" />
                              </button>
                            </Dropdown>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
            </section>
          ) : null}

          {(vitalSignsOnly || activeMedicationTab === "vitals") ? (
            <section className="resident-bookmark-section" id="vital-signs">
            <Panel title="Vital Signs">
              {medicationError ? <p className="form-error">{medicationError}</p> : null}
              <label className="resident-search vital-signs-search">
                <i className="ti ti-search" />
                <input
                  placeholder="Search vital signs"
                  value={vitalSignsSearch}
                  onChange={(event) => setVitalSignsSearch(event.target.value)}
                />
                {vitalSignsSearch ? (
                  <button
                    aria-label="Clear vital signs search"
                    className="vital-signs-search-clear"
                    type="button"
                    onClick={() => setVitalSignsSearch("")}
                  >
                    <i className="ti ti-x" />
                  </button>
                ) : null}
              </label>
              <div className="vital-signs-catalog">
                {filteredVitalSignChecklist.map(([key, label, detail, icon]) => (
                  <button
                    aria-label={`Capture ${label}`}
                    className="vital-signs-catalog-row"
                    key={key}
                    type="button"
                    onClick={() => openVitalSignsCapture(key)}
                    disabled={!selectedResidentId}
                  >
                    <span className="vital-signs-catalog-icon">
                      <i className={`ti ${icon}`} />
                    </span>
                    <div>
                      <strong>{label}</strong>
                      <small>{detail}</small>
                    </div>
                    <i className="ti ti-chevron-right vital-signs-catalog-arrow" />
                  </button>
                ))}
              </div>
              {!filteredVitalSignChecklist.length ? (
                <p>No vital sign items match your search.</p>
              ) : null}
            </Panel>
            </section>
          ) : null}

          {carePlansOnly ? (
            <section className="resident-bookmark-section" id="resident-care-plans">
            <Panel title="Care Plans">
              <div className="medication-tabs vital-care-plan-tabs" role="tablist" aria-label="Care plan views">
                {vitalCarePlanTabs.map(([tab, label, icon]) => (
                  <button
                    className={activeVitalCarePlanTab === tab ? "is-active" : ""}
                    type="button"
                    role="tab"
                    aria-selected={activeVitalCarePlanTab === tab}
                    onClick={() => setActiveVitalCarePlanTab(tab)}
                    key={tab}
                  >
                    <i className={`ti ${icon}`} /> {label}
                  </button>
                ))}
              </div>
              {activeVitalCarePlanTab === "thresholds" ? (
                <>
                <label className="resident-search vital-signs-search vital-threshold-search">
                  <i className="ti ti-search" />
                  <input
                    placeholder="Search vital sign thresholds"
                    value={vitalThresholdSearch}
                    onChange={(event) => setVitalThresholdSearch(event.target.value)}
                  />
                  {vitalThresholdSearch ? (
                    <button
                      aria-label="Clear vital sign threshold search"
                      className="vital-signs-search-clear"
                      type="button"
                      onClick={() => setVitalThresholdSearch("")}
                    >
                      <i className="ti ti-x" />
                    </button>
                  ) : null}
                </label>
                {loadingVitalThresholds ? <p>Loading vital sign thresholds...</p> : null}
                <div className="vital-threshold-list">
                  {filteredVitalAlertThresholds.map((threshold) => {
                    const values = thresholdValuesForResident(threshold, selectedVitalThresholdOverrides);
                    return (
                    <button
                      aria-label={`View ${threshold.title} thresholds`}
                      className="vital-threshold-card"
                      key={threshold.key}
                      type="button"
                      onClick={() => openVitalThreshold(threshold.key)}
                    >
                      <span className="vital-signs-catalog-icon"><i className={`ti ${threshold.icon}`} /></span>
                      <div>
                        <strong>{threshold.title}</strong>
                        <small>{thresholdSummaryFromValues(threshold, values)}</small>
                      </div>
                      <i className="ti ti-chevron-right vital-signs-catalog-arrow" />
                    </button>
                    );
                  })}
                </div>
                {!filteredVitalAlertThresholds.length ? (
                  <p>No vital sign threshold items match your search.</p>
                ) : null}
                </>
              ) : null}
              {activeVitalCarePlanTab === "activity" ? (
                <div className="health-tracking-log">
                  <div className="medication-table-wrap health-tracking-table-wrap">
                    <table className="medication-table">
                      <thead>
                        <tr>
                          <th>Recorded</th>
                          <th>Focus</th>
                          <th>Summary</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loadingCareActivityLogs ? (
                          <tr><td colSpan={3}>Loading care activity logs...</td></tr>
                        ) : null}
                        {!loadingCareActivityLogs && !selectedHealthTrackingLogs.length ? (
                          <tr><td colSpan={3}>No care activity logs added for this resident.</td></tr>
                        ) : null}
                        {!loadingCareActivityLogs && paginatedHealthTrackingLogs.map((log) => (
                          <tr key={log.id}>
                            <td>{formatTimestamp(careActivityRecordedAt(log))}<small>{displayValue(log.recordedByName || log.performedByName || log.createdByName, "")}</small></td>
                            <td>{careActivityCategoryLabel(log)}</td>
                            <td>
                              {[
                                ["Care activity", careActivitySummary(log)],
                                ["Resident needs", log.residentNeeds],
                                ["Interventions", log.interventions],
                                ["Goals", log.goals],
                                ["ADLs", log.adls],
                                ["Monitoring instructions", log.monitoringInstructions],
                                ["Medication-related care instructions", log.medicationCareInstructions],
                              ]
                                .filter(([, value]) => String(value || "").trim())
                                .map(([label, value]) => (
                                  <small className="health-tracking-summary-line" key={label}><strong>{label}:</strong> {value}</small>
                                ))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {!loadingCareActivityLogs && selectedHealthTrackingLogs.length ? (
                    <div className="timesheet-pagination care-activity-pagination">
                      <span>
                        Showing <strong>{careActivityPageStart}-{careActivityPageEnd}</strong> of <strong>{selectedHealthTrackingLogs.length}</strong>
                      </span>
                      <label>
                        Rows
                        <select
                          value={careActivityPageSize}
                          onChange={(event) => setCareActivityPageSize(Number(event.target.value))}
                        >
                          {[10, 20, 50].map((size) => <option key={size} value={size}>{size}</option>)}
                        </select>
                      </label>
                      <div className="timesheet-pagination-actions">
                        <button className="mini-btn" type="button" disabled={careActivityPage <= 1} onClick={() => setCareActivityPage((page) => Math.max(1, page - 1))}>
                          <i className="ti ti-chevron-left" /> Previous
                        </button>
                        <strong>Page {careActivityPage} of {careActivityTotalPages}</strong>
                        <button className="mini-btn" type="button" disabled={careActivityPage >= careActivityTotalPages} onClick={() => setCareActivityPage((page) => Math.min(careActivityTotalPages, page + 1))}>
                          Next <i className="ti ti-chevron-right" />
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </Panel>
            </section>
          ) : null}

          {activeMedicationTab === "refills" ? (
            <section className="resident-bookmark-section" id="refills-orders">
            <Panel title="Refills & Orders">
              {medicationError ? <p className="form-error">{medicationError}</p> : null}
              <div className="medication-summary-strip medication-list-summary-strip">
                <span><strong>{refillRequests.length}</strong><small>Refill requests</small></span>
                <span><strong>{refillRequests.filter((request) => String(request.status || "").toUpperCase() === "REQUESTED").length}</strong><small>Requested</small></span>
                <span><strong>{refillRequests.filter((request) => request.pharmacyName || request.pharmacy).length}</strong><small>With pharmacy</small></span>
              </div>
              <div className="medication-table-wrap">
                <table className="medication-table">
                  <thead>
                    <tr>
                      <th>Medication</th>
                      <th>Status</th>
                      <th>Requested</th>
                      <th>Refills</th>
                      <th>Prescriber</th>
                      <th>Pharmacy / fax destination</th>
                      <th>Fax status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingRefills ? (
                      <tr><td colSpan={8}>Loading refills and orders...</td></tr>
                    ) : null}
                    {!loadingRefills && !refillRequests.length ? (
                      <tr><td colSpan={8}>No refill requests found for this resident.</td></tr>
                    ) : null}
                    {!loadingRefills && refillRequests.map((request) => (
                      <tr key={request.id || `${request.medicationId}-${request.requestedAt}`}>
                        <td>
                          <strong>{displayValue(refillRequestedMedication(request).medicationName || request.medicationName, "Medication")}</strong>
                          <small>{medicationDoseSummary(refillRequestedMedication(request))}</small>
                        </td>
                        <td>
                          <em className="pill low">{displayValue(request.status, "Requested")}</em>
                          {request.fulfilledAt ? <small>{formatTimestamp(request.fulfilledAt)}</small> : null}
                          {request.canceledAt ? <small>{formatTimestamp(request.canceledAt)}</small> : null}
                        </td>
                        <td>
                          {formatDate(request.requestedAt)}
                          <small>{displayValue(request.requestedByName, "")}</small>
                        </td>
                        <td>
                          {request.totalRefills != null
                            ? `${request.remainingRefills ?? 0} of ${request.totalRefills} remaining`
                            : displayValue(request.refills)}
                        </td>
                        <td>
                          {displayValue(request.prescribedBy)}
                          <small>{displayValue(request.prescriberEmail, "")}</small>
                        </td>
                        <td>
                          <div className="refill-pharmacy-destination">
                            <strong>{displayValue(request.pharmacyName, "Pharmacy not recorded")}</strong>
                            <span className="refill-pharmacy-fax"><i className="ti ti-printer" aria-hidden="true" />
                              <span><span className="refill-pharmacy-fax-label">Fax</span>{request.pharmacyFaxNumber || "Number not recorded"}</span>
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className="refill-fax-status">
                            {refillFaxReason(request) ? (
                              <Tooltip title={refillFaxReason(request)} placement="topLeft">
                                <span>
                                  <em className={`pill ${refillFaxDisplay(request).tone}`}>
                                    {refillFaxDisplay(request).label}
                                  </em>
                                </span>
                              </Tooltip>
                            ) : (
                              <em className={`pill ${refillFaxDisplay(request).tone}`}>
                                {refillFaxDisplay(request).label}
                              </em>
                            )}
                            {request.faxSentAt ? <small>{formatTimestamp(request.faxSentAt)}</small> : null}
                            {request.faxAttempts > 1 ? (
                              <small>{request.faxAttempts} attempts</small>
                            ) : null}
                          </div>
                        </td>
                        <td>
                          <RefillRowActions
                            request={request}
                            busy={Boolean(refillFaxSendingId)}
                            onSendFax={() => sendRefillRequestFax(request)}
                            onFulfill={() => openRefillFulfillmentDialog(request)}
                            onCancel={() => openRefillCancellationDialog(request)}
                            onPreview={() => openRefillFaxPreview(request)}
                            onRefreshFax={() => refreshRefillFaxStatus(request)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
            </section>
          ) : null}

          {activeMedicationTab === "pharmacy" ? (
            <section className="resident-bookmark-section" id="pharmacy">
            <Panel
              title="Pharmacy"
              actionNode={(
                <button className="mini-btn" type="button" onClick={() => navigate("/pharmacies")}>
                  <i className="ti ti-building-store" /> Manage Pharmacies
                </button>
              )}
            >
              {medicationError ? <p className="form-error">{medicationError}</p> : null}
              <div className="medication-summary-strip medication-list-summary-strip">
                <span><strong>{residentPharmacyLinks.length}</strong><small>Selected for resident</small></span>
                <span><strong>{workspacePharmacies.length}</strong><small>Workspace pharmacies</small></span>
                <span><strong>{medications.filter((medication) => medication.pharmacy).length}</strong><small>Meds linked</small></span>
              </div>
              <form className="medication-resident-pharmacy-form" onSubmit={addPharmacyToResident}>
                <label className="ncp-form-field">
                  <span>Add pharmacy to resident</span>
                  <Select
                    allowClear
                    className="medication-resident-pharmacy-select"
                    disabled={!selectedResidentId || loadingPharmacies || !availableResidentPharmacyOptions.length}
                    filterOption={(input, option) =>
                      String(option?.searchText || "").toLowerCase().includes(input.toLowerCase())
                    }
                    onChange={(value) => setResidentPharmacyForm((current) => ({ ...current, pharmacyId: value || "" }))}
                    optionLabelProp="label"
                    placeholder={loadingPharmacies ? "Loading pharmacies..." : "Select pharmacy"}
                    showSearch
                    value={residentPharmacyForm.pharmacyId}
                  >
                    {availableResidentPharmacyOptions.map((pharmacy) => (
                      <Select.Option
                        key={pharmacy.id}
                        label={pharmacy.displayName}
                        searchText={`${pharmacy.displayName} ${pharmacy.displayAddress}`}
                        value={pharmacy.id}
                      >
                        <span className="pharmacy-select-option">
                          <strong>{pharmacy.displayName}</strong>
                          <small>{pharmacy.displayAddress}</small>
                        </span>
                      </Select.Option>
                    ))}
                  </Select>
                </label>
                <label className="ncp-form-field">
                  <span>Notes</span>
                  <input
                    value={residentPharmacyForm.notes}
                    onChange={(event) => setResidentPharmacyForm((current) => ({ ...current, notes: event.target.value }))}
                    placeholder="Resident-specific pharmacy notes"
                  />
                </label>
                <label className="medication-inline-check">
                  <input
                    checked={residentPharmacyForm.preferred}
                    onChange={(event) => setResidentPharmacyForm((current) => ({ ...current, preferred: event.target.checked }))}
                    type="checkbox"
                  />
                  <span>Preferred</span>
                </label>
                <button className="mini-btn" type="submit" disabled={!residentPharmacyForm.pharmacyId || addingResidentPharmacy}>
                  <i className="ti ti-plus" /> {addingResidentPharmacy ? "Adding..." : "Add to Resident"}
                </button>
              </form>
              <div className="medication-table-wrap">
                <table className="medication-table">
                  <thead>
                    <tr>
                      <th>Pharmacy</th>
                      <th>Contact</th>
                      <th>Address</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingMedications || loadingPharmacies ? (
                      <tr><td colSpan={4}>Loading pharmacy details...</td></tr>
                    ) : null}
                    {!loadingMedications && !loadingPharmacies && !residentPharmacies.length ? (
                      <tr><td colSpan={4}>No pharmacies have been added to this resident.</td></tr>
                    ) : null}
                    {!loadingMedications && !loadingPharmacies && residentPharmacies.map((pharmacy) => (
                      <tr key={pharmacy.id || pharmacyName(pharmacy)}>
                        <td>
                          <strong>{pharmacyName(pharmacy)}</strong>
                          <small>{pharmacy.preferred ? "Preferred" : firstText(pharmacy.notes, "")}</small>
                        </td>
                        <td>
                          {firstText(pharmacy.record?.phone, pharmacy.record?.email, "Not added")}
                          <small>{firstText(pharmacy.record?.contactName, "")}</small>
                        </td>
                        <td>{firstText(pharmacyAddress(pharmacy.record || pharmacy), "Not added")}</td>
                        <td>
                          <div className="medication-row-actions">
                            <button
                              aria-label={`View ${pharmacyName(pharmacy)} details`}
                              className="resident-icon-button"
                              onClick={() => openPharmacyDetails(pharmacy)}
                              type="button"
                            >
                              <i className="ti ti-eye" />
                            </button>
                            <button
                              aria-label={`Remove ${pharmacyName(pharmacy)} from resident`}
                              className="resident-icon-button danger"
                              disabled={removingResidentPharmacyId === pharmacy.id}
                              onClick={() => requestRemovePharmacyFromResident(pharmacy)}
                              type="button"
                            >
                              <i className={removingResidentPharmacyId === pharmacy.id ? "ti ti-loader-2" : "ti ti-trash"} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
            </section>
          ) : null}

          {activeMedicationTab === "new" ? (
            <section className="resident-bookmark-section" id="new-meds">
            <Panel
              title="New Meds Approvals"
              actionNode={(
                <div className="new-medication-panel-actions">
                  <label className="new-medication-status-filter">
                    <span>Status</span>
                    <Select
                      aria-label="Filter new medications by status"
                      popupMatchSelectWidth={false}
                      style={{ minWidth: 190 }}
                      value={newMedicationStatusFilter}
                      options={NEW_MEDICATION_STATUS_FILTER_OPTIONS}
                      onChange={setNewMedicationStatusFilter}
                    />
                  </label>
                  <button className="mini-btn success" type="button" onClick={() => openMedicationDrawer("new")} disabled={!selectedResidentId}>
                    <i className="ti ti-plus" /> Add New Medication
                  </button>
                </div>
              )}
            >
              {medicationError ? <p className="form-error">{medicationError}</p> : null}
              <div className="medication-summary-strip medication-list-summary-strip">
                <span><strong>{newMedications.length}</strong><small>Records shown</small></span>
                <span><strong>{newMedications.filter(isMedicationApprovalVisible).length}</strong><small>Pending approval</small></span>
                <span><strong>{newMedications.filter((record) => medicationStatus(record) === "ACTIVE").length}</strong><small>Approved</small></span>
                <span><strong>{newMedications.filter((record) => medicationStatus(record) === "REJECTED").length}</strong><small>Rejected</small></span>
                <span><strong>{selectedResident ? residentName(selectedResident) : "No resident"}</strong><small>Resident</small></span>
              </div>
              <div className="medication-table-wrap">
                <table className="medication-table">
                  <thead>
                    <tr>
                      <th>Medication</th>
                      <th>Status</th>
                      <th>Source</th>
                      <th>Received</th>
                      <th>Prescriber</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingNewMedications ? (
                      <tr><td colSpan={6}>Loading new medication records...</td></tr>
                    ) : null}
                    {!loadingNewMedications && !newMedications.length ? (
                      <tr><td colSpan={6}>No {selectedNewMedicationFilterLabel} medication records found for this resident.</td></tr>
                    ) : null}
                    {!loadingNewMedications && newMedications.map((record) => (
                      <tr
                        className="is-clickable"
                        key={record.id || medicationName(record)}
                        onClick={() => openNewMedicationReview(record)}
                      >
                        <td>
                          <strong>{medicationName(record)}</strong>
                          <small>{displayValue(medicationDoseSummary(record), "")}</small>
                        </td>
                        <td><em className={`pill ${newMedicationStatusPillClass(record)}`}>{newMedicationStatusLabel(record)}</em></td>
                        <td>{displayValue(record.sourceType || record.receivedFromName)}</td>
                        <td>{formatTimestamp(record.receivedAt || record.createdAt)}</td>
                        <td>{displayValue(record.prescribedBy || record.prescriberEmail)}</td>
                        <td>{displayValue(record.rejectionReason || record.notes)}<br />
                          <button type="button" className="mini-btn" onClick={(event) => { event.stopPropagation(); openNewMedicationReview(record); }}>
                            {isMedicationApprovalVisible(record) ? "Review & approve" : "View details"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
            </section>
          ) : null}

          {activeMedicationTab === "import" ? (
            <section className="resident-bookmark-section" id="med-import">
              <Panel title="Medication Import">
                <MedicationImportWorkspace
                  key={`${selectedWorkspaceId}-${selectedResidentId}-${medicationImportRefresh}`}
                  residentId={selectedResidentId}
                  workspaceParams={workspaceParams}
                  actorId={userId(user)}
                  uploadFile={uploadFile}
                  onReviewMedication={(draft, reference) => {
                    openNewMedicationReview(draft, reference);
                  }}
                />
              </Panel>
            </section>
          ) : null}

          {activeMedicationTab === "logs" ? (
            <section className="resident-bookmark-section" id="resident-log">
            <Panel title="Resident Log">
              {medicationError ? <p className="form-error">{medicationError}</p> : null}
              <div className="medication-summary-strip medication-list-summary-strip">
                <span><strong>{residentAuditLogs.length}</strong><small>Recent actions</small></span>
                <span><strong>{selectedResident ? residentName(selectedResident) : "No resident"}</strong><small>Resident</small></span>
                <span><strong>{residentAuditLogs.filter((log) => String(log.actionName || "").includes("Medication")).length}</strong><small>Medication actions</small></span>
              </div>
              <div className="medication-table-wrap">
                <table className="medication-table">
                  <thead>
                    <tr>
                      <th>Action</th>
                      <th>Description</th>
                      <th>Actor</th>
                      <th>Timestamp</th>
                      <th>Action ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingResidentAuditLogs ? (
                      <tr><td colSpan={5}>Loading resident log...</td></tr>
                    ) : null}
                    {!loadingResidentAuditLogs && !residentAuditLogs.length ? (
                      <tr><td colSpan={5}>No resident log records found.</td></tr>
                    ) : null}
                    {!loadingResidentAuditLogs && residentAuditLogs.map((log) => (
                      <tr key={log.id || `${log.actionName}-${log.actionId}-${log.timestamp}`}>
                        <td>
                          <strong>{displayValue(log.actionName)}</strong>
                        </td>
                        <td>{displayValue(log.description)}</td>
                        <td>
                          {displayValue(log.actorName || log.actorId)}
                        </td>
                        <td>{formatTimestamp(log.timestamp)}</td>
                        <td className="text-muted">{displayValue(log.actionId)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
            </section>
          ) : null}

          {activeMedicationTab === "alerts" ? (
            <section className="resident-bookmark-section" id="medication-alerts">
            <Panel title="Medication Alerts">
              {medicationError ? <p className="form-error">{medicationError}</p> : null}
              <div className="medication-summary-strip medication-list-summary-strip">
                <span><strong>{medicationAlerts.length}</strong><small>Total alerts</small></span>
                <span><strong>{medicationAlerts.filter((alert) => alert.severity === "high").length}</strong><small>High priority</small></span>
                <span><strong>{medicationAlerts.filter((alert) => alert.title.includes("Refill")).length}</strong><small>Refill alerts</small></span>
              </div>
              {loadingMedications || loadingDay ? <p>Loading medication alerts...</p> : null}
              {!loadingMedications && !loadingDay && !medicationAlerts.length ? (
                <p>No medication alerts found for this resident.</p>
              ) : null}
              <div className="medication-alert-list">
                {medicationAlerts.map((alert) => (
                  <article className={`medication-alert-card is-${alert.severity}`} key={alert.id}>
                    <span><i className="ti ti-alert-triangle" /></span>
                    <div>
                      <strong>{alert.title}</strong>
                      <small>{alert.medicationName}</small>
                      <p>{alert.detail}</p>
                      <em>{alert.action}</em>
                    </div>
                    <b>{alert.severity}</b>
                  </article>
                ))}
              </div>
            </Panel>
            </section>
          ) : null}
          </>}
        </div>
      </div>

      <Drawer
        title={selectedVitalThreshold ? `${selectedVitalThreshold.title} Thresholds` : "Vital Sign Thresholds"}
        width="min(720px, 94vw)"
        open={vitalThresholdDrawerOpen}
        onClose={() => {
          setVitalThresholdDrawerOpen(false);
          setVitalThresholdEditing(false);
        }}
        className="resident-action-drawer medication-admin-drawer vital-threshold-drawer"
      >
        {selectedVitalThreshold ? (
          <div className="resident-drawer-content vital-threshold-drawer-content">
            <section className="assessment-drawer-section">
              <div className="vital-threshold-drawer-head">
                <div>
                  <h3>{selectedVitalThreshold.title}</h3>
                  <p>{thresholdSummaryFromValues(selectedVitalThreshold, selectedVitalThresholdValues)}</p>
                </div>
                {!vitalThresholdEditing ? (
                  <button
                    className="mini-btn vital-threshold-edit-link"
                    type="button"
                    onClick={() => {
                      setVitalThresholdForm(selectedVitalThresholdValues);
                      setVitalThresholdEditing(true);
                    }}
                  >
                    <i className="ti ti-edit" /> Edit
                  </button>
                ) : null}
              </div>
              {!vitalThresholdEditing ? (
                <dl className="vital-threshold-current-list">
                  {selectedVitalThreshold.fields.map(([field, label, , unit, options]) => (
                    <div key={field}>
                      <dt>{label}</dt>
                      <dd>{options?.length ? thresholdOptionLabel(selectedVitalThreshold, field, selectedVitalThresholdValues[field]) : displayValue(selectedVitalThresholdValues[field])}{unit ? ` ${unit}` : ""}</dd>
                    </div>
                  ))}
                </dl>
              ) : (
                <form className="assessment-drawer-grid medication-add-grid vital-threshold-edit-form">
                  {renderVitalThresholdFields()}
                </form>
              )}
            </section>
            {vitalThresholdEditing ? (
              <div className="assessment-drawer-actions vital-threshold-actions">
                <button
                  className="btn secondary"
                  type="button"
                  disabled={vitalThresholdSaving}
                  onClick={() => {
                    setVitalThresholdForm(selectedVitalThresholdValues);
                    setVitalThresholdEditing(false);
                  }}
                >
                  Cancel
                </button>
                <button className="btn primary" type="button" disabled={vitalThresholdSaving} onClick={saveVitalThreshold}>
                  {vitalThresholdSaving ? "Saving..." : "Save Thresholds"}
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
      </Drawer>

      <Drawer
        title={`Capture ${selectedVitalSign.label}`}
        width="min(760px, 94vw)"
        open={vitalSignsDrawerOpen}
        onClose={() => {
          if (!vitalSignsSaving) setVitalSignsDrawerOpen(false);
        }}
        className="resident-action-drawer medication-admin-drawer vital-signs-drawer"
      >
        <form className="resident-drawer-content vital-signs-form" onSubmit={submitVitalSigns}>
          <section className="assessment-drawer-section">
            <h3><FieldLabel info={vitalCaptureInfo[selectedVitalSignKey]}>{selectedVitalSign.label}</FieldLabel></h3>
            <div className="assessment-drawer-grid medication-add-grid vital-signs-grid">
              <label className="ncp-form-field">
                <span>Date</span>
                <input
                  type="date"
                  value={vitalSignsForm.recordedDate}
                  onChange={(event) => updateVitalSignsForm("recordedDate", event.target.value)}
                />
              </label>
              <label className="ncp-form-field">
                <span>Time</span>
                <input
                  type="time"
                  value={vitalSignsForm.recordedTime}
                  onChange={(event) => updateVitalSignsForm("recordedTime", event.target.value)}
                />
              </label>
              {selectedVitalSignKey === "bloodPressure" ? (
              <label className="ncp-form-field ncp-form-field-wide vital-signs-bp-field vital-signs-capture-field">
                <FieldLabel info={vitalCaptureInfo.bloodPressure}>Blood pressure</FieldLabel>
                <div className="vital-signs-bp-inputs">
                  <input
                    inputMode="numeric"
                    value={vitalSignsForm.systolicBloodPressure}
                    onChange={(event) => updateVitalSignsForm("systolicBloodPressure", event.target.value)}
                    placeholder="Systolic"
                  />
                  <b>/</b>
                  <input
                    inputMode="numeric"
                    value={vitalSignsForm.diastolicBloodPressure}
                    onChange={(event) => updateVitalSignsForm("diastolicBloodPressure", event.target.value)}
                    placeholder="Diastolic"
                  />
                </div>
              </label>
              ) : null}
              {selectedVitalSignKey === "pulse" ? (
              <label className="ncp-form-field ncp-form-field-wide vital-signs-capture-field">
                <FieldLabel info={vitalCaptureInfo.pulse}>Heart rate / pulse</FieldLabel>
                <input inputMode="numeric" value={vitalSignsForm.pulse} onChange={(event) => updateVitalSignsForm("pulse", event.target.value)} placeholder="bpm" />
              </label>
              ) : null}
              {selectedVitalSignKey === "temperature" ? (
              <label className="ncp-form-field ncp-form-field-wide vital-signs-capture-field">
                <FieldLabel info={vitalCaptureInfo.temperature}>Body temperature</FieldLabel>
                <div className="vital-signs-combo-input">
                  <input inputMode="decimal" value={vitalSignsForm.temperature} onChange={(event) => updateVitalSignsForm("temperature", event.target.value)} placeholder="Temp" />
                  <select value={vitalSignsForm.temperatureUnit} onChange={(event) => updateVitalSignsForm("temperatureUnit", event.target.value)}>
                    <option value="F">F</option>
                    <option value="C">C</option>
                  </select>
                </div>
              </label>
              ) : null}
              {selectedVitalSignKey === "respiratoryRate" ? (
              <label className="ncp-form-field ncp-form-field-wide vital-signs-capture-field">
                <FieldLabel info={vitalCaptureInfo.respiratoryRate}>Respiratory rate</FieldLabel>
                <input inputMode="numeric" value={vitalSignsForm.respiratoryRate} onChange={(event) => updateVitalSignsForm("respiratoryRate", event.target.value)} placeholder="per min" />
              </label>
              ) : null}
              {selectedVitalSignKey === "oxygenSaturation" ? (
              <label className="ncp-form-field ncp-form-field-wide vital-signs-capture-field">
                <FieldLabel info={vitalCaptureInfo.oxygenSaturation}>SpO2</FieldLabel>
                <input inputMode="numeric" value={vitalSignsForm.oxygenSaturation} onChange={(event) => updateVitalSignsForm("oxygenSaturation", event.target.value)} placeholder="%" />
              </label>
              ) : null}
              {selectedVitalSignKey === "bloodSugar" ? (
              <label className="ncp-form-field ncp-form-field-wide vital-signs-capture-field">
                <FieldLabel info={vitalCaptureInfo.bloodSugar}>Blood glucose</FieldLabel>
                <input inputMode="numeric" value={vitalSignsForm.bloodSugar} onChange={(event) => updateVitalSignsForm("bloodSugar", event.target.value)} placeholder="mg/dL" />
              </label>
              ) : null}
              {selectedVitalSignKey === "weight" ? (
              <label className="ncp-form-field ncp-form-field-wide vital-signs-capture-field">
                <FieldLabel info={vitalCaptureInfo.weight}>Weight monitoring</FieldLabel>
                <input inputMode="decimal" value={vitalSignsForm.weight} onChange={(event) => updateVitalSignsForm("weight", event.target.value)} placeholder="lb" />
              </label>
              ) : null}
              {selectedVitalSignKey === "painLevel" ? (
              <label className="ncp-form-field ncp-form-field-wide vital-signs-capture-field">
                <FieldLabel info={vitalCaptureInfo.painLevel}>Pain level tracking</FieldLabel>
                <ScaleSlider label="Pain level" value={vitalSignsForm.painLevel} onChange={(value) => updateVitalSignsForm("painLevel", value)} />
              </label>
              ) : null}
              {selectedVitalSignKey === "bowelBladder" ? (
              <label className="ncp-form-field ncp-form-field-wide vital-signs-capture-field">
                <FieldLabel info={vitalCaptureInfo.bowelBladder}>Bowel & bladder occurrences</FieldLabel>
                <div className="vital-signs-measure-pair">
                  <input inputMode="numeric" value={vitalSignsForm.bowelBladderEpisodes} onChange={(event) => updateVitalSignsForm("bowelBladderEpisodes", event.target.value)} placeholder="count" />
                  <select value={vitalSignsForm.bowelBladderConcernType} onChange={(event) => updateVitalSignsForm("bowelBladderConcernType", event.target.value)}>
                    <option value="">Concern type</option>
                    <option value="NO_BM">No bowel movement</option>
                    <option value="DIARRHEA">Diarrhea</option>
                    <option value="UTI_SIGNS">UTI signs</option>
                    <option value="URINARY_PAIN">Urinary pain</option>
                    <option value="BLOOD_PRESENT">Blood present</option>
                  </select>
                </div>
              </label>
              ) : null}
              {selectedVitalSignKey === "intakeOutput" ? (
              <label className="ncp-form-field ncp-form-field-wide vital-signs-capture-field">
                <FieldLabel info={vitalCaptureInfo.intakeOutput}>I&O balance</FieldLabel>
                <div className="vital-signs-measure-pair">
                  <input inputMode="decimal" value={vitalSignsForm.intakeOutputBalance} onChange={(event) => updateVitalSignsForm("intakeOutputBalance", event.target.value)} placeholder="mL" />
                  <ScaleSlider label="I&O concern level" value={vitalSignsForm.intakeOutputConcernLevel} onChange={(value) => updateVitalSignsForm("intakeOutputConcernLevel", value)} />
                </div>
              </label>
              ) : null}
              {selectedVitalSignKey === "skinWound" ? (
              <label className="ncp-form-field ncp-form-field-wide vital-signs-capture-field">
                <FieldLabel info={vitalCaptureInfo.skinWound}>Skin / wound severity</FieldLabel>
                <select value={vitalSignsForm.skinWoundSeverity} onChange={(event) => updateVitalSignsForm("skinWoundSeverity", event.target.value)}>
                  <option value="">Select severity</option>
                  {Array.from({ length: 6 }, (_, value) => (
                    <option value={value} key={value}>{value}</option>
                  ))}
                </select>
                <select
                  value={hasEnteredValue(vitalSignsForm.skinWoundDrainage) ? String(vitalSignsForm.skinWoundDrainage) : ""}
                  onChange={(event) => updateVitalSignsForm("skinWoundDrainage", event.target.value ? event.target.value === "true" : "")}
                >
                  <option value="">Drainage present?</option>
                  <option value="false">No</option>
                  <option value="true">Yes</option>
                </select>
              </label>
              ) : null}
              {selectedVitalSignKey === "edema" ? (
              <label className="ncp-form-field ncp-form-field-wide vital-signs-capture-field">
                <FieldLabel info={vitalCaptureInfo.edema}>Edema grade</FieldLabel>
                <div className="vital-signs-measure-pair">
                  <select value={vitalSignsForm.edemaGrade} onChange={(event) => updateVitalSignsForm("edemaGrade", event.target.value)}>
                    <option value="">Select grade</option>
                    {Array.from({ length: 5 }, (_, value) => (
                      <option value={value} key={value}>{value === 4 ? "4+" : value}</option>
                    ))}
                  </select>
                  <input inputMode="numeric" value={vitalSignsForm.edemaLocationCount} onChange={(event) => updateVitalSignsForm("edemaLocationCount", event.target.value)} placeholder="Swollen areas count" />
                </div>
              </label>
              ) : null}
              {selectedVitalSignKey === "sleepFatigue" ? (
              <div className="ncp-form-field ncp-form-field-wide vital-signs-capture-field">
                <FieldLabel info={vitalCaptureInfo.sleepFatigue}>Sleep & fatigue measurements</FieldLabel>
                <div className="vital-signs-measure-pair">
                  <input inputMode="decimal" value={vitalSignsForm.sleepHours} onChange={(event) => updateVitalSignsForm("sleepHours", event.target.value)} placeholder="Sleep hours" />
                  <ScaleSlider label="Fatigue level" value={vitalSignsForm.fatigueLevel} onChange={(value) => updateVitalSignsForm("fatigueLevel", value)} />
                </div>
                <input inputMode="numeric" value={vitalSignsForm.sleepAwakenings} onChange={(event) => updateVitalSignsForm("sleepAwakenings", event.target.value)} placeholder="Night awakenings count" />
              </div>
              ) : null}
              {selectedVitalSignKey === "mentalStatusMood" ? (
              <div className="ncp-form-field ncp-form-field-wide vital-signs-capture-field">
                <FieldLabel info={vitalCaptureInfo.mentalStatusMood}>Mental status & mood measurements</FieldLabel>
                <div className="vital-signs-measure-pair">
                  <ScaleSlider label="Mental status score" value={vitalSignsForm.mentalStatusScore} onChange={(value) => updateVitalSignsForm("mentalStatusScore", value)} />
                  <ScaleSlider label="Mood score" value={vitalSignsForm.moodScore} onChange={(value) => updateVitalSignsForm("moodScore", value)} />
                </div>
                <select value={vitalSignsForm.mentalStatusChangeType} onChange={(event) => updateVitalSignsForm("mentalStatusChangeType", event.target.value)}>
                  <option value="">Change type</option>
                  <option value="NONE">None</option>
                  <option value="CONFUSION">Confusion</option>
                  <option value="AGITATION">Agitation</option>
                  <option value="DEPRESSION_CONCERN">Depression concern</option>
                  <option value="ANXIETY">Anxiety</option>
                  <option value="SAFETY_RISK">Safety risk</option>
                </select>
              </div>
              ) : null}
              {selectedVitalSignKey === "fallMobility" ? (
              <label className="ncp-form-field ncp-form-field-wide vital-signs-capture-field">
                <FieldLabel info={vitalCaptureInfo.fallMobility}>Fall / mobility risk</FieldLabel>
                <div className="vital-signs-measure-pair">
                  <ScaleSlider label="Fall mobility risk" value={vitalSignsForm.fallMobilityRisk} onChange={(value) => updateVitalSignsForm("fallMobilityRisk", value)} />
                  <select
                    value={hasEnteredValue(vitalSignsForm.fallOccurred) ? String(vitalSignsForm.fallOccurred) : ""}
                    onChange={(event) => updateVitalSignsForm("fallOccurred", event.target.value ? event.target.value === "true" : "")}
                  >
                    <option value="">Fall occurred?</option>
                    <option value="false">No</option>
                    <option value="true">Yes</option>
                  </select>
                </div>
              </label>
              ) : null}
              {selectedVitalSignKey === "medicationSideEffects" ? (
              <label className="ncp-form-field ncp-form-field-wide vital-signs-capture-field">
                <FieldLabel info={vitalCaptureInfo.medicationSideEffects}>Side effect severity</FieldLabel>
                <div className="vital-signs-measure-pair">
                  <ScaleSlider label="Side effect severity" value={vitalSignsForm.medicationSideEffectSeverity} onChange={(value) => updateVitalSignsForm("medicationSideEffectSeverity", value)} />
                  <select value={vitalSignsForm.medicationSideEffectType} onChange={(event) => updateVitalSignsForm("medicationSideEffectType", event.target.value)}>
                    <option value="">Side effect type</option>
                    <option value="DIZZINESS">Dizziness</option>
                    <option value="SEDATION">Sedation</option>
                    <option value="APPETITE_CHANGE">Appetite change</option>
                    <option value="RASH">Rash</option>
                    <option value="BREATHING_CHANGE">Breathing change</option>
                    <option value="OTHER_REACTION">Other reaction</option>
                  </select>
                </div>
              </label>
              ) : null}
              {vitalSignsRequiringPosition.has(selectedVitalSignKey) ? (
              <label className="ncp-form-field">
                <FieldLabel info="Record the resident position used for this reading when it can affect the result.">Position</FieldLabel>
                <select value={vitalSignsForm.position} onChange={(event) => updateVitalSignsForm("position", event.target.value)}>
                  <option value="">Select position</option>
                  <option value="SITTING">Sitting</option>
                  <option value="STANDING">Standing</option>
                  <option value="LYING">Lying</option>
                </select>
              </label>
              ) : null}
              {vitalSignsRequiringSite.has(selectedVitalSignKey) ? (
              <label className="ncp-form-field">
                <FieldLabel info="Record the body site used for this reading, such as left arm, oral, temporal, ankle, or wound location.">Site</FieldLabel>
                <input value={vitalSignsForm.site} onChange={(event) => updateVitalSignsForm("site", event.target.value)} placeholder="Example: left arm" />
              </label>
              ) : null}
              <label className="ncp-form-field ncp-form-field-wide">
                <FieldLabel info="Add symptoms, context, device notes, resident condition, or follow-up needed.">
                  {vitalSignValueFields[selectedVitalSignKey] ? "Notes" : `${selectedVitalSign.label} details`}
                </FieldLabel>
                <textarea
                  rows={3}
                  value={vitalSignsForm.notes}
                  onChange={(event) => updateVitalSignsForm("notes", event.target.value)}
                  placeholder={vitalSignValueFields[selectedVitalSignKey] ? "Symptoms, resident condition, device used, or follow-up needed." : selectedVitalSign.detail}
                />
              </label>
            </div>
          </section>
          <div className="assessment-drawer-actions vital-signs-actions">
            <button className="btn secondary" type="button" onClick={() => setVitalSignsForm(defaultVitalSignsForm())} disabled={vitalSignsSaving}>
              Clear
            </button>
            <button className="btn primary" type="submit" disabled={!selectedResidentId || vitalSignsSaving}>
              {vitalSignsSaving ? "Saving..." : "Save Vital Signs"}
            </button>
          </div>
          <section className="assessment-drawer-section vital-signs-previous-section">
            <h3>Previously Captured</h3>
            {loadingVitalSigns ? <p>Loading captured records...</p> : null}
            {!loadingVitalSigns && !selectedVitalSignRows.length ? (
              <p>No previous {selectedVitalSign.label.toLowerCase()} records captured for this resident.</p>
            ) : null}
            {!loadingVitalSigns && selectedVitalSignRows.length ? (
              <div className="vital-signs-previous-layout">
                <div className="vital-signs-previous-list">
                  <div className="medication-table-wrap">
                    <table className="medication-table vital-signs-previous-table">
                      <thead>
                        <tr>
                          <th>Recorded</th>
                          <th>Value</th>
                          <th>Position</th>
                          <th>Site</th>
                          <th>Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedVitalSignRows.map((record, index) => (
                          <tr key={record.id || `${vitalRecordedAt(record)}-${selectedVitalSignKey}-${index}`}>
                            <td>
                              {formatTimestamp(vitalRecordedAt(record))}
                              <small>{displayValue(record.recordedByName || record.createdByName, "")}</small>
                            </td>
                            <td>{vitalRecordValueForType(record, selectedVitalSignKey)}</td>
                            <td>{displayValue(record.position)}</td>
                            <td>{displayValue(record.site)}</td>
                            <td>{displayValue(record.notes)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="vital-signs-trend-panel">
                  <div className="vital-signs-trend-head">
                    <strong>{selectedVitalTrend ? selectedVitalTrend.label : "Trend graph"}</strong>
                    <small>{selectedVitalTrend ? `${selectedVitalTrend.points.length} captured ${selectedVitalTrend.points.length === 1 ? "point" : "points"}` : "No measurable data"}</small>
                  </div>
                  {selectedVitalTrend ? (
                    <div className="vital-signs-chart" role="img" aria-label={`${selectedVitalTrend.label} over time`}>
                      <svg viewBox={`0 0 ${selectedVitalTrend.width} ${selectedVitalTrend.height}`} preserveAspectRatio="none">
                        <line x1="26" y1="154" x2="294" y2="154" />
                        <line x1="26" y1="26" x2="26" y2="154" />
                        <polyline points={selectedVitalTrend.path} />
                        {selectedVitalTrend.points.map((point) => (
                          <circle cx={point.x} cy={point.y} key={`${point.label}-${point.value}-${point.x}`} r="4" />
                        ))}
                      </svg>
                      <div className="vital-signs-chart-range">
                        <span>{selectedVitalTrend.maxValue}</span>
                        <span>{selectedVitalTrend.minValue}</span>
                      </div>
                      <div className="vital-signs-chart-footer">
                        <span>{selectedVitalTrend.points[0]?.label}</span>
                        <strong>{selectedVitalTrend.latestValue}</strong>
                        <span>{selectedVitalTrend.points[selectedVitalTrend.points.length - 1]?.label}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="vital-signs-chart-empty">
                      <i className="ti ti-chart-line" />
                      <strong>No graph available</strong>
                      <small>This item needs numeric captured values before a trend can be shown.</small>
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </section>
        </form>
      </Drawer>

      <Drawer
        title={selectedNewMedication ? `${selectedNewMedicationFinalized ? "View" : "Review"} ${medicationName(selectedNewMedication)}` : "Review New Medication"}
        width="100vw"
        open={newMedicationReviewOpen}
        onClose={() => {
          setNewMedicationReviewOpen(false);
          setMedicationImportReviewRef(null);
          setNewMedicationRejectDialog({ open: false, reason: "" });
          setExtractedMedicationDataOpen(false);
          setNewMedicationReviewRotation(0);
        }}
        className="resident-action-drawer medication-admin-drawer new-medication-review-drawer"
        rootClassName="new-medication-review-fullscreen-drawer"
      >
        {selectedNewMedication?.sourceType === "Imported prescription" ? (
          <div role="note" style={{ padding: 16, marginBottom: 16, background: "#fff8e6", borderRadius: 8 }}>
            <strong>Imported draft — verify against the prescription before approval.</strong>
            <p>Patient on document: {selectedNewMedicationExtractedDetails.patientName || "Not identified"}
              {selectedNewMedicationExtractedDetails.patientDateOfBirth ? ` · DOB: ${selectedNewMedicationExtractedDetails.patientDateOfBirth}` : " · DOB not identified"}.
              {" "}Selected resident: {selectedResident ? residentName(selectedResident) : "No resident"}.</p>
            {selectedNewMedicationExtractedDetails.extractionWarnings ? <p>{selectedNewMedicationExtractedDetails.extractionWarnings}</p> : null}
            <p>Confirm patient identity, dose, route, frequency and any missing details. Saving a draft does not activate it.</p>
          </div>
        ) : null}
        <div className="new-medication-review-content">
          <section className="assessment-drawer-section new-medication-review-document-pane">
            <h3>Prescription Document</h3>
            <div className="new-medication-document-live-preview">
              {selectedNewMedicationPreviewDocument && medicationDocumentIsImage(selectedNewMedicationPreviewDocument) && selectedNewMedicationPreviewUrl ? (
                <div className="new-medication-review-image-preview">
                  <div className="new-medication-review-rotate-controls" aria-label="Rotate prescription image">
                    <button type="button" onClick={() => setNewMedicationReviewRotation((value) => value - 90)} aria-label="Rotate image left" title="Rotate left"><i className="ti ti-rotate-2" /></button>
                    <button type="button" onClick={() => setNewMedicationReviewRotation((value) => value + 90)} aria-label="Rotate image right" title="Rotate right"><i className="ti ti-rotate-clockwise-2" /></button>
                  </div>
                  <div className="new-medication-review-image-canvas">
                    <img style={{ transform: `rotate(${newMedicationReviewRotation}deg)` }} src={selectedNewMedicationPreviewUrl} alt={medicationDocumentName(selectedNewMedicationPreviewDocument, 0)} />
                  </div>
                </div>
              ) : selectedNewMedicationPreviewDocument && medicationDocumentIsPdf(selectedNewMedicationPreviewDocument) && selectedNewMedicationPreviewUrl ? (
                <iframe src={selectedNewMedicationPreviewUrl} title={medicationDocumentName(selectedNewMedicationPreviewDocument, 0)} />
              ) : (
                <div className="new-medication-document-preview-empty">
                  <i className="ti ti-file-description" />
                  <strong>{selectedNewMedicationPreviewDocument ? medicationDocumentName(selectedNewMedicationPreviewDocument, 0) : "No prescription document"}</strong>
                  <small>{selectedNewMedicationPreviewDocument ? "Preview is available for image and PDF prescriptions." : "No document was attached to this record."}</small>
                </div>
              )}
            </div>
            {selectedNewMedicationDocuments.length ? (
              <div className="medication-document-list new-medication-document-preview-list">
                {selectedNewMedicationDocuments.map((document, index) => {
                  const documentUrl = medicationDocumentUrl(document);
                  return (
                    <article className="medication-document-row new-medication-document-card" key={document.id || document.fileId || document.fileName || index}>
                      <span className="medication-document-icon"><i className="ti ti-file-description" /></span>
                      <div className="medication-document-main">
                        <strong>{medicationDocumentName(document, index)}</strong>
                        <small>{[document.contentType || document.type || "File", formatFileSize(document.size), displayValue(document.description, "")].filter(Boolean).join(" | ")}</small>
                      </div>
                      {documentUrl ? (
                        <a className="mini-btn" href={documentUrl} target="_blank" rel="noreferrer" aria-label={`Open ${medicationDocumentName(document, index)} preview`}>
                          <i className="ti ti-eye" />
                        </a>
                      ) : null}
                    </article>
                  );
                })}
              </div>
            ) : null}
          </section>

          <div className="new-medication-review-data-pane">
            <section className="assessment-drawer-section">
              <h3>Review Status</h3>
              <div className="assessment-drawer-actions new-medication-review-context-actions">
                <button className="btn secondary" type="button" onClick={() => setExtractedMedicationDataOpen(true)}>
                  Extracted Medication Data
                </button>
              </div>
              <dl className="medication-admin-instructions medication-detail-grid">
                {[
                  ["Status", selectedNewMedication?.status],
                  ["Received", formatTimestamp(selectedNewMedication?.receivedAt || selectedNewMedication?.createdAt)],
                  ["Resident", selectedResident ? residentName(selectedResident) : selectedNewMedication?.residentId],
                  ["Reference medication", selectedNewMedication?.medicationId],
                ].map(([label, value]) => (
                  <div key={label}>
                    <dt>{label}</dt>
                    <dd>{displayValue(value)}</dd>
                  </div>
                ))}
              </dl>
            </section>
            <section className="assessment-drawer-section">
              <h3>Hospital Medication Details</h3>
              <div className="assessment-drawer-grid medication-add-grid new-medication-review-edit-grid">
                <label className="ncp-form-field">
                  <FieldLabel info="Enter the medication name exactly as shown on the hospital order, pharmacy label, or medication list. Example: Lisinopril 10 mg tablet.">
                    Medication name
                  </FieldLabel>
                  <input value={newMedicationReviewForm.medicationName} onChange={(event) => updateNewMedicationReviewForm("medicationName", event.target.value)} />
                </label>
                <label className="ncp-form-field">
                  <FieldLabel info="Use the non-brand drug name when available. Example: acetaminophen is the generic name for Tylenol.">
                    Generic name
                  </FieldLabel>
                  <input value={newMedicationReviewForm.genericName} onChange={(event) => updateNewMedicationReviewForm("genericName", event.target.value)} />
                </label>
                <label className="ncp-form-field">
                  <FieldLabel info="Use the manufacturer or trade name if shown. Example: Tylenol, Lipitor, Norvasc, or Glucophage.">
                    Brand name
                  </FieldLabel>
                  <input value={newMedicationReviewForm.brandName} onChange={(event) => updateNewMedicationReviewForm("brandName", event.target.value)} />
                </label>
                <label className="ncp-form-field">
                  <span>Form</span>
                  <select value={newMedicationReviewForm.medicationForm} onChange={(event) => updateNewMedicationReviewForm("medicationForm", event.target.value)}>
                    {medicationFormOptions.map(([value, label]) => <option value={value} key={value || label}>{label}</option>)}
                  </select>
                  {newMedicationReviewForm.medicationForm === "OTHER" ? (
                    <input value={newMedicationReviewForm.medicationFormOther} onChange={(event) => updateNewMedicationReviewForm("medicationFormOther", event.target.value)} placeholder="Enter medication form" />
                  ) : null}
                </label>
                <label className="ncp-form-field">
                  <span>Classification</span>
                  <Select
                    allowClear
                    className="medication-searchable-select"
                    filterOption={(input, option) =>
                      String(option?.searchText || option?.label || "").toLowerCase().includes(input.toLowerCase())
                    }
                    onChange={(value) => updateNewMedicationReviewForm("classification", value || "")}
                    optionLabelProp="label"
                    placeholder="Select classification"
                    showSearch
                    value={newMedicationReviewForm.classification || undefined}
                  >
                    {medicationClassificationOptions.filter(([value]) => value).map(([value, label]) => (
                      <Select.Option key={value} label={label} searchText={`${label} ${value}`} value={value}>
                        {label}
                      </Select.Option>
                    ))}
                  </Select>
                  {newMedicationReviewForm.classification === "OTHER" ? (
                    <input value={newMedicationReviewForm.classificationOther} onChange={(event) => updateNewMedicationReviewForm("classificationOther", event.target.value)} placeholder="Enter medication classification" />
                  ) : null}
                </label>
                <label className="ncp-form-field">
                  <FieldLabel info="The clinical reason this medication is prescribed, such as pain, hypertension, diabetes, infection, mood, or sleep.">
                    Therapeutic indication
                  </FieldLabel>
                  <select value={newMedicationReviewForm.indication} onChange={(event) => updateNewMedicationReviewForm("indication", event.target.value)}>
                    {medicationIndicationOptions.map(([value, label]) => <option value={value} key={value || label}>{label}</option>)}
                  </select>
                  {newMedicationReviewForm.indication === "OTHER" ? (
                    <input value={newMedicationReviewForm.indicationOther} onChange={(event) => updateNewMedicationReviewForm("indicationOther", event.target.value)} placeholder="Enter therapeutic indication" />
                  ) : null}
                </label>
                <label className="ncp-form-field medication-dose-amount-field">
                  <span>Dose amount</span>
                  <input
                    inputMode="decimal"
                    pattern="[0-9]+(?:\.[0-9]+)?"
                    value={newMedicationReviewForm.dosage}
                    onChange={(event) => updateNewMedicationReviewForm("dosage", numericDosageValue(event.target.value))}
                    placeholder="Example: 2"
                  />
                </label>
                <label className="ncp-form-field medication-dose-unit-field">
                  <span>Dose unit/form</span>
                  <select value={newMedicationReviewForm.doseUnit} onChange={(event) => updateNewMedicationReviewForm("doseUnit", event.target.value)}>
                    {medicationDoseUnitOptions.map(([value, label]) => <option value={value} key={value || label}>{label}</option>)}
                  </select>
                  {newMedicationReviewForm.doseUnit === "OTHER" ? (
                    <input value={newMedicationReviewForm.doseUnitOther} onChange={(event) => updateNewMedicationReviewForm("doseUnitOther", event.target.value)} placeholder="Specify dose unit/form" />
                  ) : null}
                </label>
                {newMedicationReviewUnitWarning ? (
                  <p className="medication-unit-warning">{newMedicationReviewUnitWarning}</p>
                ) : null}
                <label className="ncp-form-field medication-quantity-field">
                  <FieldLabel info="Maximum allowed amount in a 24-hour period, especially important for PRN medications. Select the unit/form using the same options as dose unit.">
                    Max daily dose
                  </FieldLabel>
                  <span className="medication-quantity-control">
                    <span className="medication-combo-part">
                      <small>Amount</small>
                      <input
                        aria-label="Max daily dose amount"
                        inputMode="decimal"
                        value={newMedicationReviewForm.maxDailyDoseAmount}
                        onChange={(event) => updateNewMedicationReviewForm("maxDailyDoseAmount", numericDecimalValue(event.target.value))}
                        placeholder="Example: 3"
                      />
                    </span>
                    <span className="medication-combo-part">
                      <small>Unit</small>
                      <select aria-label="Max daily dose unit" value={newMedicationReviewForm.maxDailyDoseUnit} onChange={(event) => updateNewMedicationReviewForm("maxDailyDoseUnit", event.target.value)}>
                        {medicationDoseUnitOptions.map(([value, label]) => <option value={value} key={value || label}>{label}</option>)}
                      </select>
                    </span>
                  </span>
                  {newMedicationReviewForm.maxDailyDoseUnit === "OTHER" ? (
                    <input value={newMedicationReviewForm.maxDailyDoseUnitOther} onChange={(event) => updateNewMedicationReviewForm("maxDailyDoseUnitOther", event.target.value)} placeholder="Specify max daily dose unit" />
                  ) : null}
                </label>
                {newMedicationReviewUnitWarning ? (
                  <p className="medication-unit-warning">{newMedicationReviewUnitWarning}</p>
                ) : null}
                <div className="medication-strength-row">
                  <label className="ncp-form-field medication-strength-amount-field">
                    <span>Strength amount</span>
                    <input
                      inputMode="decimal"
                      value={newMedicationReviewForm.strengthAmount}
                      onChange={(event) => updateNewMedicationReviewForm("strengthAmount", numericDecimalValue(event.target.value))}
                      placeholder="Example: 500"
                    />
                  </label>
                  <label className="ncp-form-field medication-strength-unit-field">
                    <span>Strength unit</span>
                    <select value={newMedicationReviewForm.strengthUnit} onChange={(event) => updateNewMedicationReviewForm("strengthUnit", event.target.value)}>
                      {medicationStrengthUnitOptions.map(([value, label]) => <option value={value} key={value || label}>{label}</option>)}
                    </select>
                    {newMedicationReviewForm.strengthUnit === "OTHER" ? (
                      <input value={newMedicationReviewForm.strengthUnitOther} onChange={(event) => updateNewMedicationReviewForm("strengthUnitOther", event.target.value)} placeholder="Specify strength unit" />
                    ) : null}
                  </label>
                </div>
                <label className="ncp-form-field medication-quantity-field">
                  <FieldLabel info="Enter the numeric amount dispensed or supplied and select the matching unit/form, such as 30 tablets or 10 mL.">
                    Quantity
                  </FieldLabel>
                  <span className="medication-quantity-control">
                    <input
                      aria-label="Quantity amount"
                      inputMode="decimal"
                      value={newMedicationReviewForm.quantityAmount}
                      onChange={(event) => updateNewMedicationReviewForm("quantityAmount", numericDecimalValue(event.target.value))}
                      placeholder="Example: 30"
                    />
                    <select aria-label="Quantity unit" value={newMedicationReviewForm.quantityUnit} onChange={(event) => updateNewMedicationReviewForm("quantityUnit", event.target.value)}>
                      {medicationDoseUnitOptions.map(([value, label]) => <option value={value} key={value || label}>{label}</option>)}
                    </select>
                  </span>
                  {newMedicationReviewForm.quantityUnit === "OTHER" ? (
                    <input value={newMedicationReviewForm.quantityUnitOther} onChange={(event) => updateNewMedicationReviewForm("quantityUnitOther", event.target.value)} placeholder="Specify quantity unit" />
                  ) : null}
                </label>
                <label className="ncp-form-field">
                  <FieldLabel info="Select the number of refills allowed or the refill status shown by the pharmacy, such as provider approval required or expired.">
                    Refills
                  </FieldLabel>
                  <select value={newMedicationReviewForm.refills} onChange={(event) => updateNewMedicationReviewForm("refills", event.target.value)}>
                    {medicationRefillOptions.map(([value, label]) => <option value={value} key={value || label}>{label}</option>)}
                  </select>
                  {newMedicationReviewForm.refills === "OTHER" ? (
                    <input value={newMedicationReviewForm.refillsOther} onChange={(event) => updateNewMedicationReviewForm("refillsOther", event.target.value)} placeholder="Enter refill instruction or status" />
                  ) : null}
                </label>
                {newMedicationReviewUnitWarning ? (
                  <p className="medication-unit-warning">{newMedicationReviewUnitWarning}</p>
                ) : null}
                <label className="ncp-form-field">
                  <span>Route</span>
                  <select value={newMedicationReviewForm.route} onChange={(event) => updateNewMedicationReviewForm("route", event.target.value)}>
                    {medicationRouteOptions.map(([value, label]) => <option value={value} key={value || label}>{label}</option>)}
                  </select>
                  {newMedicationReviewForm.route === "OTHER" ? (
                    <input value={newMedicationReviewForm.routeOther} onChange={(event) => updateNewMedicationReviewForm("routeOther", event.target.value)} placeholder="Enter administration route" />
                  ) : null}
                </label>
                <div className={`medication-prn-frequency-row${booleanValue(newMedicationReviewForm.prn) ? " is-prn-only" : ""}`}>
                  <label className="ncp-form-field">
                    <span>PRN / as needed</span>
                    <select value={booleanValue(newMedicationReviewForm.prn) ? "YES" : "NO"} onChange={(event) => updateNewMedicationReviewForm("prn", event.target.value === "YES")}>
                      <option value="NO">No</option>
                      <option value="YES">Yes</option>
                    </select>
                  </label>
                  {!booleanValue(newMedicationReviewForm.prn) ? (
                    <label className="ncp-form-field">
                      <span>Frequency</span>
                      <select value={newMedicationReviewForm.frequency} onChange={(event) => updateNewMedicationReviewForm("frequency", event.target.value)}>
                        {medicationFrequencyOptions.map(([value, label]) => <option value={value} key={value || label}>{label}</option>)}
                      </select>
                      {newMedicationReviewForm.frequency === "CUSTOM" ? (
                        <input value={newMedicationReviewForm.frequencyOther} onChange={(event) => updateNewMedicationReviewForm("frequencyOther", event.target.value)} placeholder="Enter medication frequency" />
                      ) : null}
                    </label>
                  ) : null}
                </div>
                {!booleanValue(newMedicationReviewForm.prn) ? (
                <label className="ncp-form-field ncp-form-field-wide medication-schedule-field">
                  <FieldLabel info="Choose the unique daily time slots when staff should administer this medication. The number of selections is based on the selected frequency.">
                    Administration schedule *
                  </FieldLabel>
                  {newMedicationReviewForm.frequency ? (
                    <>
                      {newMedicationReviewAdministrationTimePresets.length ? (
                        <div className="medication-schedule-presets" aria-label="Administration schedule options">
                          {newMedicationReviewAdministrationTimePresets.map(([label, times]) => (
                            <button
                              className="medication-schedule-preset"
                              key={label}
                              onClick={() => applyNewMedicationReviewAdministrationTimePreset(times)}
                              type="button"
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      ) : null}
                      <Select
                        allowClear
                        className="medication-multi-select"
                        mode="multiple"
                        options={newMedicationReviewAdministrationTimeOptions}
                        placeholder="Select administration time"
                        value={newMedicationReviewForm.administrationTime}
                        onChange={(values) => updateNewMedicationReviewForm("administrationTime", values)}
                      />
                      <small className="medication-field-help medication-schedule-help">{newMedicationReviewAdministrationTimeGuidance}</small>
                      {newMedicationReviewForm.administrationTime.some((time) => !["AS_NEEDED", "SPECIFIC_TIME"].includes(time)) ? (
                        <div className="medication-time-entry-list">
                          {newMedicationReviewForm.administrationTime
                            .filter((time) => !["AS_NEEDED", "SPECIFIC_TIME"].includes(time))
                            .map((time) => (
                              <div className="medication-time-entry" key={time}>
                                <span>{administrationTimeOptionLabel(time)}</span>
                                <input
                                  aria-label={`${administrationTimeOptionLabel(time)} administration time`}
                                  required
                                  type="time"
                                  value={newMedicationReviewForm.administrationTimeDetails?.[time] || ""}
                                  onChange={(event) => updateNewMedicationReviewForm("administrationTimeDetails", { [time]: event.target.value })}
                                />
                              </div>
                            ))}
                        </div>
                      ) : null}
                      {newMedicationReviewForm.administrationTime.includes("SPECIFIC_TIME") ? (
                        <input required value={newMedicationReviewForm.administrationTimeOther} onChange={(event) => updateNewMedicationReviewForm("administrationTimeOther", event.target.value)} placeholder="Enter administration time" />
                      ) : null}
                    </>
                  ) : (
                    <small className="medication-field-help medication-schedule-help">{newMedicationReviewAdministrationTimeGuidance}</small>
                  )}
                </label>
                ) : null}
                <label className="ncp-form-field ncp-form-field-wide">
                  <FieldLabel info="Enter the exact medication directions as written on the hospital order, pharmacy label, or MAR, including dose, route, timing, and frequency.">
                    Directions / SIG
                  </FieldLabel>
                  <textarea rows={3} value={newMedicationReviewForm.instructions} onChange={(event) => updateNewMedicationReviewForm("instructions", event.target.value)} placeholder="Exact prescription directions from the hospital, pharmacy label, or MAR." />
                </label>
                <label className="ncp-form-field">
                  <FieldLabel info="Use the prescription, Rx, or order number shown on the medication record or pharmacy label.">
                    Prescription / order number
                  </FieldLabel>
                  <input value={newMedicationReviewForm.orderNumber} onChange={(event) => updateNewMedicationReviewForm("orderNumber", event.target.value)} />
                </label>
                <label className="ncp-form-field">
                  <span>Order date</span>
                  <input type="date" value={newMedicationReviewForm.orderDate} onChange={(event) => updateNewMedicationReviewForm("orderDate", event.target.value)} />
                </label>
                <div className="medication-date-range-row">
                  <label className="ncp-form-field">
                    <span>Start date</span>
                    <input type="date" value={newMedicationReviewForm.startDate} onChange={(event) => updateNewMedicationReviewForm("startDate", event.target.value)} />
                  </label>
                  <label className="ncp-form-field">
                    <span>End date</span>
                    <input type="date" value={newMedicationReviewForm.endDate} onChange={(event) => updateNewMedicationReviewForm("endDate", event.target.value)} />
                  </label>
                </div>
              </div>
            </section>

            <section className="assessment-drawer-section">
              <h3>Prescription Administration Instructions</h3>
              <div className="medication-risk-grid">
                {[
                  ["controlledSubstance", "Controlled substance"],
                  ["highAlertMedication", "High-alert medication"],
                  ["requiresVitalsCheck", "Requires vitals check"],
                  ["requiresBloodSugarCheck", "Requires blood sugar check"],
                ].map(([field, label]) => (
                  <label className="medication-risk-toggle" key={field}>
                    <input
                      type="checkbox"
                      checked={Boolean(newMedicationReviewForm[field])}
                      onChange={(event) => updateNewMedicationReviewForm(field, event.target.checked)}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
              <div className="assessment-drawer-grid medication-add-grid new-medication-review-edit-grid">
                <label className="ncp-form-field">
                  <span>Food instructions</span>
                  <Select
                    allowClear
                    className="medication-multi-select"
                    mode="multiple"
                    options={multiSelectOptions(medicationFoodInstructionOptions)}
                    placeholder="Select food instructions"
                    value={newMedicationReviewForm.foodInstructions}
                    onChange={(values) => updateNewMedicationReviewForm("foodInstructions", values)}
                  />
                  {newMedicationReviewForm.foodInstructions.includes("CUSTOM") ? (
                    <input value={newMedicationReviewForm.foodInstructionsOther} onChange={(event) => updateNewMedicationReviewForm("foodInstructionsOther", event.target.value)} placeholder="Enter food instructions" />
                  ) : null}
                </label>
                <label className="ncp-form-field">
                  <span>Storage</span>
                  <Select
                    allowClear
                    className="medication-multi-select"
                    mode="multiple"
                    options={multiSelectOptions(medicationStorageOptions)}
                    placeholder="Select storage"
                    value={newMedicationReviewForm.storageInstructions}
                    onChange={(values) => updateNewMedicationReviewForm("storageInstructions", values)}
                  />
                  {newMedicationReviewForm.storageInstructions.includes("CUSTOM") ? (
                    <input value={newMedicationReviewForm.storageInstructionsOther} onChange={(event) => updateNewMedicationReviewForm("storageInstructionsOther", event.target.value)} placeholder="Enter storage instructions" />
                  ) : null}
                </label>
                <label className="ncp-form-field">
                  <span>Special handling</span>
                  <Select
                    allowClear
                    className="medication-multi-select"
                    mode="multiple"
                    options={multiSelectOptions(medicationSpecialHandlingOptions)}
                    placeholder="Select handling"
                    value={newMedicationReviewForm.specialHandling}
                    onChange={(values) => updateNewMedicationReviewForm("specialHandling", values)}
                  />
                  {newMedicationReviewForm.specialHandling.includes("CUSTOM") ? (
                    <input value={newMedicationReviewForm.specialHandlingOther} onChange={(event) => updateNewMedicationReviewForm("specialHandlingOther", event.target.value)} placeholder="Enter special handling instructions" />
                  ) : null}
                </label>
                <label className="ncp-form-field">
                  <FieldLabel info="Use only for as-needed medications. Select the symptom or situation that allows staff to give the PRN dose.">
                    PRN reason
                  </FieldLabel>
                  <Select
                    allowClear
                    className="medication-multi-select"
                    mode="multiple"
                    options={multiSelectOptions(medicationPrnReasonOptions)}
                    placeholder="Select PRN reason"
                    value={newMedicationReviewForm.prnReason}
                    onChange={(values) => updateNewMedicationReviewForm("prnReason", values)}
                  />
                  {newMedicationReviewForm.prnReason.includes("OTHER") ? (
                    <input value={newMedicationReviewForm.prnReasonOther} onChange={(event) => updateNewMedicationReviewForm("prnReasonOther", event.target.value)} placeholder="Enter PRN reason" />
                  ) : null}
                </label>
              </div>
              <label className="ncp-form-field">
                <FieldLabel info="Conditions where staff should not administer the medication, such as blood pressure, pulse, sedation, blood sugar, or provider instructions.">
                  Hold parameters
                </FieldLabel>
                <textarea rows={3} value={newMedicationReviewForm.holdParameters} onChange={(event) => updateNewMedicationReviewForm("holdParameters", event.target.value)} placeholder="Example: Hold if systolic BP below 100 or pulse below 60." />
              </label>
              <label className="ncp-form-field">
                <FieldLabel info="Checks required before or after administration, such as vitals, blood sugar, pain score, sedation, respirations, labs, or follow-up observation.">
                  Monitoring instructions
                </FieldLabel>
                <textarea rows={3} value={newMedicationReviewForm.monitoringInstructions} onChange={(event) => updateNewMedicationReviewForm("monitoringInstructions", event.target.value)} placeholder="Vitals, pain score, blood sugar, sedation, respiration, labs, or follow-up checks required before or after administration." />
              </label>
              <label className="ncp-form-field">
                <FieldLabel info="Symptoms staff should monitor and report, including expected side effects and serious adverse reactions.">
                  Side effects to watch
                </FieldLabel>
                <textarea rows={3} value={newMedicationReviewForm.sideEffectsToWatch} onChange={(event) => updateNewMedicationReviewForm("sideEffectsToWatch", event.target.value)} placeholder="Expected or serious adverse reactions staff should monitor and report." />
              </label>
              <label className="ncp-form-field">
                <FieldLabel info="Allergy warnings, interaction concerns, diagnosis-specific cautions, or other reasons administration may need extra review.">
                  Contraindications / precautions
                </FieldLabel>
                <textarea rows={3} value={newMedicationReviewForm.contraindications} onChange={(event) => updateNewMedicationReviewForm("contraindications", event.target.value)} placeholder="Allergy warnings, diagnosis-specific precautions, interaction concerns, or administration restrictions." />
              </label>
              <label className="ncp-form-field">
                <FieldLabel info="Directions for what staff should do if a scheduled dose is late or missed.">
                  Missed dose instructions
                </FieldLabel>
                <textarea rows={3} value={newMedicationReviewForm.missedDoseInstructions} onChange={(event) => updateNewMedicationReviewForm("missedDoseInstructions", event.target.value)} placeholder="Hospital or prescriber directions when a dose is late or missed." />
              </label>
              <label className="ncp-form-field">
                <FieldLabel info="Steps to follow when the resident refuses the medication, including documentation, notification, reassessment, or escalation.">
                  Refusal instructions
                </FieldLabel>
                <textarea rows={3} value={newMedicationReviewForm.refusalInstructions} onChange={(event) => updateNewMedicationReviewForm("refusalInstructions", event.target.value)} placeholder="Steps when resident refuses: documentation, notification, reassessment, or escalation guidance." />
              </label>
            </section>

            <section className="assessment-drawer-section">
              <h3>Prescriber & Pharmacy</h3>
              <div className="assessment-drawer-grid medication-add-grid medication-prescriber-grid new-medication-review-edit-grid">
                <label className="ncp-form-field">
                  <span>Prescribed by</span>
                  <input value={newMedicationReviewForm.prescribedBy} onChange={(event) => updateNewMedicationReviewForm("prescribedBy", event.target.value)} />
                </label>
                <label className="ncp-form-field">
                  <span>Prescriber phone</span>
                  <input value={newMedicationReviewForm.prescriberPhone} onChange={(event) => updateNewMedicationReviewForm("prescriberPhone", event.target.value)} />
                </label>
                <label className="ncp-form-field">
                  <span>Prescriber email</span>
                  <input type="email" value={newMedicationReviewForm.prescriberEmail} onChange={(event) => updateNewMedicationReviewForm("prescriberEmail", event.target.value)} />
                </label>
                <label className="ncp-form-field">
                  <span>Pharmacy</span>
                  <select value={newMedicationReviewForm.pharmacyId} onChange={(event) => updateNewMedicationReviewForm("pharmacyId", event.target.value)} disabled={loadingPharmacies && !pharmacyOptions.length}>
                    <option value="">{loadingPharmacies ? "Loading pharmacies..." : "Select pharmacy"}</option>
                    {pharmacyOptions.map((pharmacy) => (
                      <option value={pharmacy.id} key={pharmacy.id || pharmacy.name}>
                        {pharmacy.detail ? `${pharmacy.name} - ${pharmacy.detail}` : pharmacy.name}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              {!loadingPharmacies && !pharmacyOptions.length ? (
                <p className="medication-section-note">Add a pharmacy to this resident from the Pharmacy tab before selecting it here.</p>
              ) : null}
            </section>

            <section className="assessment-drawer-section">
              <h3>Prescription Source</h3>
              <div className="assessment-drawer-grid medication-add-grid new-medication-review-edit-grid">
                <label className="ncp-form-field">
                  <FieldLabel info="Where this medication information came from, such as a discharge summary, pharmacy medication list, MAR, clinic order, or family-provided document.">
                    Source type
                  </FieldLabel>
                  <select value={newMedicationReviewForm.sourceType} onChange={(event) => updateNewMedicationReviewForm("sourceType", event.target.value)}>
                    {medicationSourceTypeOptions.map(([value, label]) => <option value={value} key={value || label}>{label}</option>)}
                  </select>
                  {newMedicationReviewForm.sourceType === "OTHER" ? (
                    <input value={newMedicationReviewForm.sourceTypeOther} onChange={(event) => updateNewMedicationReviewForm("sourceTypeOther", event.target.value)} placeholder="Enter source type" />
                  ) : null}
                </label>
                <label className="ncp-form-field">
                  <FieldLabel info="Name of the hospital, clinic, pharmacy, provider group, or person that supplied the medication order.">
                    Source organization
                  </FieldLabel>
                  <input value={newMedicationReviewForm.hospitalSource} onChange={(event) => updateNewMedicationReviewForm("hospitalSource", event.target.value)} placeholder="Example: Kaiser Permanente" />
                </label>
                <label className="ncp-form-field">
                  <FieldLabel info="Name of the source document, such as discharge summary, after-visit summary, medication list, prescription label, or MAR.">
                    Source document
                  </FieldLabel>
                  <input value={newMedicationReviewForm.sourceDocumentName} onChange={(event) => updateNewMedicationReviewForm("sourceDocumentName", event.target.value)} />
                </label>
                <label className="ncp-form-field">
                  <FieldLabel info="Date shown on the source document or the date the medication source was received.">
                    Source date
                  </FieldLabel>
                  <input type="date" value={newMedicationReviewForm.sourceDocumentDate} onChange={(event) => updateNewMedicationReviewForm("sourceDocumentDate", event.target.value)} />
                </label>
                <label className="ncp-form-field">
                  <FieldLabel info="Any source reference number, encounter number, document ID, case number, or external prescription reference.">
                    Source reference #
                  </FieldLabel>
                  <input value={newMedicationReviewForm.sourceReferenceNumber} onChange={(event) => updateNewMedicationReviewForm("sourceReferenceNumber", event.target.value)} />
                </label>
                <label className="ncp-form-field">
                  <span>Source contact name</span>
                  <input value={newMedicationReviewForm.sourceContactName} onChange={(event) => updateNewMedicationReviewForm("sourceContactName", event.target.value)} />
                </label>
                <label className="ncp-form-field">
                  <span>Source contact phone</span>
                  <input value={newMedicationReviewForm.sourceContactPhone} onChange={(event) => updateNewMedicationReviewForm("sourceContactPhone", event.target.value)} />
                </label>
                <label className="ncp-form-field">
                  <span>Source contact email</span>
                  <input type="email" value={newMedicationReviewForm.sourceContactEmail} onChange={(event) => updateNewMedicationReviewForm("sourceContactEmail", event.target.value)} />
                </label>
                <label className="ncp-form-field">
                  <span>Oliveeb eFax number</span>
                  <input value={newMedicationReviewForm.efaxNumber} onChange={(event) => updateNewMedicationReviewForm("efaxNumber", event.target.value)} />
                </label>
                <label className="ncp-form-field ncp-form-field-wide">
                  <FieldLabel info="Use this for source-specific notes, such as verification status, pages reviewed, medication list conflicts, or follow-up needed with the source.">
                    Source notes
                  </FieldLabel>
                  <textarea rows={3} value={newMedicationReviewForm.sourceNotes} onChange={(event) => updateNewMedicationReviewForm("sourceNotes", event.target.value)} placeholder="Example: Verified against hospital discharge medication list; awaiting pharmacy clarification on refill status." />
                </label>
                <label className="ncp-form-field ncp-form-field-wide">
                  <span>Medication notes</span>
                  <textarea rows={4} value={newMedicationReviewForm.notes} onChange={(event) => updateNewMedicationReviewForm("notes", event.target.value)} placeholder="Additional hospital discharge medication details or internal care notes." />
                </label>
              </div>
            </section>
            <div className="assessment-drawer-actions new-medication-review-actions">
              {selectedNewMedicationFinalized ? (
                <button className="btn secondary" type="button" onClick={() => setNewMedicationReviewOpen(false)}>
                  Close
                </button>
              ) : (
                <>
              <button
                className="btn new-medication-reject-button"
                type="button"
                disabled={Boolean(newMedicationReviewSaving)}
                onClick={() => setNewMedicationRejectDialog({ open: true, reason: "" })}
              >
                Reject Medication
              </button>
              <button className="btn secondary" type="button" disabled={Boolean(newMedicationReviewSaving)} onClick={() => setNewMedicationReviewOpen(false)}>
                Cancel
              </button>
              <button className="btn primary" type="button" disabled={Boolean(newMedicationReviewSaving)} onClick={() => saveNewMedicationReview("save")}>
                {newMedicationReviewSaving === "save" ? "Saving..." : "Save"}
              </button>
              {selectedNewMedicationNeedsAdfApproval ? (
                <button className="btn primary" type="button" disabled={Boolean(newMedicationReviewSaving)} onClick={() => confirmNewMedicationApproval("approve")}>
                  {newMedicationReviewSaving === "approve" ? "Approving..." : "Approve"}
                </button>
              ) : null}
              {selectedNewMedicationNeedsOliveebApproval ? (
                <button className="btn primary" type="button" disabled={Boolean(newMedicationReviewSaving)} onClick={() => confirmNewMedicationApproval("adf")}>
                  {newMedicationReviewSaving === "adf"
                    ? "Saving..."
                    : medicationImportReviewRef
                      ? "Save to ADF Approval"
                      : "Approve"}
                </button>
              ) : null}
                </>
              )}
            </div>
          </div>
        </div>
      </Drawer>

      <Modal
        title="Reject Medication"
        zIndex={2200}
        open={newMedicationRejectDialog.open}
        okText={newMedicationReviewSaving === "reject" ? "Rejecting..." : "Reject Medication"}
        cancelText="Keep Reviewing"
        okButtonProps={{ danger: true, disabled: !String(newMedicationRejectDialog.reason || "").trim() }}
        confirmLoading={newMedicationReviewSaving === "reject"}
        onOk={rejectNewMedicationReview}
        onCancel={() => {
          if (newMedicationReviewSaving !== "reject") setNewMedicationRejectDialog({ open: false, reason: "" });
        }}
        destroyOnClose
      >
        <p>
          Reject <strong>{selectedNewMedication ? medicationName(selectedNewMedication) : "this medication"}</strong>? It will not be added to the resident's active medications.
        </p>
        <label className="ncp-form-field new-medication-reject-reason">
          <span>Reason for rejection</span>
          <textarea
            rows={4}
            maxLength={2000}
            value={newMedicationRejectDialog.reason}
            placeholder="Explain why this medication should not be approved."
            onChange={(event) => setNewMedicationRejectDialog((current) => ({ ...current, reason: event.target.value }))}
          />
        </label>
      </Modal>

      <Drawer
        title="Medication Administration Details"
        width="min(720px, 94vw)"
        zIndex={1800}
        open={adminLogDetailsOpen}
        onClose={() => setAdminLogDetailsOpen(false)}
        className="resident-action-drawer medication-admin-log-detail-drawer"
      >
        {selectedAdminLogDisplay ? (
          <div className="resident-drawer-content medication-admin-log-detail">
            <section className="medication-admin-log-detail-head">
              <div>
                <button
                  className="medication-admin-name-link"
                  onClick={() => openMedicationDetails(selectedAdminLogDisplay.medication)}
                  type="button"
                >
                  {selectedAdminLogDisplay.medicationName}
                </button>
                <span>
                  {selectedAdminLogDisplay.isPrn ? <em className="pill medium">PRN</em> : null}
                  <em className={`pill medication-admin-log-action is-${selectedAdminLogDisplay.actionLevel}`}>{selectedAdminLogDisplay.action}</em>
                </span>
              </div>
              <strong>{selectedAdminLogDisplay.initials}</strong>
            </section>
            <dl className="medication-admin-confirm-details medication-admin-log-detail-grid">
              <div><dt>Quantity</dt><dd>{selectedAdminLogDisplay.quantity}</dd></div>
              <div><dt>Time</dt><dd>{selectedAdminLogDisplay.time}</dd></div>
              <div><dt>Timing</dt><dd><em className={`pill medication-admin-log-timing is-${selectedAdminLogDisplay.timing.level}`}>{selectedAdminLogDisplay.timing.label}</em></dd></div>
              <div><dt>Administered by</dt><dd>{selectedAdminLogDisplay.administeredBy}</dd></div>
              <div><dt>Strength</dt><dd>{selectedAdminLogDisplay.strength}</dd></div>
              <div><dt>Route</dt><dd>{selectedAdminLogDisplay.route}</dd></div>
              <div><dt>Confirmed site</dt><dd>{displayValue(selectedAdminLogDisplay.bodyPart, "-")}</dd></div>
            </dl>
            {selectedAdminLogDisplay.notes ? (
              <section className="medication-admin-log-detail-notes">
                <h3>Notes</h3>
                <p>{selectedAdminLogDisplay.notes}</p>
              </section>
            ) : null}
            {selectedAdminLogDisplay.log.correctionReason ? (
              <section className="medication-admin-log-detail-notes">
                <h3>Correction history</h3>
                <p><strong>Previous time:</strong> {formatTimestamp(selectedAdminLogDisplay.log.previousAdministrationAt)}</p>
                <p><strong>Why changed:</strong> {selectedAdminLogDisplay.log.correctionReason}</p>
                {selectedAdminLogDisplay.log.lateAdministrationReason ? <p><strong>Late administration reason:</strong> {selectedAdminLogDisplay.log.lateAdministrationReason}</p> : null}
              </section>
            ) : null}
            {activeMedicationTab === "admin" && !selectedAdminLogDisplay.log.canceled && !selectedAdminLogDisplay.log.overridden ? (
              <div className="resident-drawer-actions medication-admin-record-actions">
                {selectedAdminLogDisplay.isPrn && prnAdministrationDateAllowed(logScheduledDate(selectedAdminLogDisplay.log)) ? (
                  <button className="btn success" type="button" onClick={() => {
                    const targetDate = logScheduledDate(selectedAdminLogDisplay.log);
                    setAdminLogDetailsOpen(false);
                    openAdministrationActionDialog(selectedAdminLogDisplay.medication, "ADMINISTERED", null, targetDate);
                  }}>
                    <i className="ti ti-plus" /> Administer PRN
                  </button>
                ) : null}
                <button className="btn primary" type="button" onClick={() => openEditAdministrationLog(selectedAdminLogDisplay.log)}>
                  <i className="ti ti-edit" /> Edit record
                </button>
                <button className="btn danger" type="button" onClick={() => {
                  setAdminLogDetailsOpen(false);
                  openCancelAdministrationLogDialog(selectedAdminLogDisplay.log);
                }}>
                  <i className="ti ti-trash" /> Delete record
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
      </Drawer>

      <Modal
        title="Update Medication Administration Record"
        open={adminEditDialog.open}
        width="min(680px, 94vw)"
        className="medication-admin-confirm-modal"
        okText={adminEditSaving ? "Updating..." : "Update record"}
        cancelText="Cancel"
        confirmLoading={adminEditSaving}
        onCancel={() => {
          if (!adminEditSaving) setAdminEditDialog({ open: false, log: null, administrationAt: "", adminAction: "", notes: "", changeReason: "", lateAdministrationReason: "" });
        }}
        onOk={submitEditAdministrationLog}
      >
        <div className="medication-admin-confirm">
          <p>Correct the administration record. The original time and correction details will remain in the audit history.</p>
          <label className="ncp-form-field">
            <span>Record type *</span>
            <select
              value={adminEditDialog.adminAction}
              onChange={(event) => setAdminEditDialog((current) => ({ ...current, adminAction: event.target.value }))}
              required
            >
              <option value="ADMINISTERED">Administered</option>
              <option value="REFUSED">Refused</option>
              <option value="HELD">Held</option>
              <option value="MISSED">Missed</option>
            </select>
          </label>
          <label className="ncp-form-field">
            <span>Actual administration date and time *</span>
            <input
              type="datetime-local"
              min={localDateTimeInputValue(medicationLateAdministrationEarliestDate())}
              max={localDateTimeInputValue()}
              value={adminEditDialog.administrationAt}
              onChange={(event) => setAdminEditDialog((current) => ({ ...current, administrationAt: event.target.value }))}
              required
            />
          </label>
          {adminEditDialog.log && administrationCorrectionOutsideExpectedRange(adminEditDialog.log, adminEditDialog.administrationAt) ? (
            <>
              <div className="medication-prn-dose-warning is-warning" role="alert">
                <i className="ti ti-alert-triangle" />
                <span>The corrected time is outside the expected administration range. A late medication administration reason is required.</span>
              </div>
              <label className="ncp-form-field">
                <span>Late medication administration reason *</span>
                <textarea
                  rows={3}
                  value={adminEditDialog.lateAdministrationReason}
                  onChange={(event) => setAdminEditDialog((current) => ({ ...current, lateAdministrationReason: event.target.value }))}
                  placeholder="Explain why the medication was administered outside the expected time range."
                />
              </label>
            </>
          ) : null}
          <label className="ncp-form-field">
            <span>Why is this record being changed? *</span>
            <textarea
              rows={3}
              value={adminEditDialog.changeReason}
              onChange={(event) => setAdminEditDialog((current) => ({ ...current, changeReason: event.target.value }))}
              placeholder="Explain the correction to this administration record."
            />
          </label>
          <label className="ncp-form-field">
            <span>Notes</span>
            <textarea
              rows={3}
              value={adminEditDialog.notes}
              onChange={(event) => setAdminEditDialog((current) => ({ ...current, notes: event.target.value }))}
            />
          </label>
        </div>
      </Modal>

      <Modal
        centered
        className="medication-admin-confirm-modal extracted-medication-data-modal"
        footer={[
          <button className="btn secondary" key="close" type="button" onClick={() => setExtractedMedicationDataOpen(false)}>
            Close
          </button>,
        ]}
        onCancel={() => setExtractedMedicationDataOpen(false)}
        open={extractedMedicationDataOpen}
        title="Extracted Medication Data"
        width={900}
      >
        {Object.keys(selectedNewMedicationExtractedDetails).length ? (
          <dl className="medication-admin-instructions medication-detail-grid">
            {Object.entries(selectedNewMedicationExtractedDetails)
              .filter(([, value]) => value !== null && value !== undefined && String(value).trim() !== "")
              .map(([key, value]) => (
                <div key={key}>
                  <dt>{key.replace(/([A-Z])/g, " $1").replace(/^./, (char) => char.toUpperCase())}</dt>
                  <dd>{typeof value === "object" ? JSON.stringify(value) : displayValue(value)}</dd>
                </div>
              ))}
          </dl>
        ) : (
          <p className="medication-section-note">No extracted medication data has been captured yet.</p>
        )}
      </Modal>

      <Drawer
        title={selectedPharmacy ? pharmacyName(selectedPharmacyRecord) : "Pharmacy Details"}
        width={720}
        open={pharmacyDetailsOpen}
        onClose={() => setPharmacyDetailsOpen(false)}
        className="resident-action-drawer medication-admin-drawer"
      >
        <div className="resident-drawer-content medication-detail-drawer-content">
          {pharmacyDetailGroups.map(([title, rows]) => (
            <section className="assessment-drawer-section" key={title}>
              <h3>{title}</h3>
              <dl className="medication-admin-instructions medication-detail-grid">
                {rows.map(([label, value]) => (
                  <div key={label}>
                    <dt>{label}</dt>
                    <dd>
                      {title === "Prescriber & Pharmacy" && label === "Pharmacy" && selectedMedicationPharmacy ? (
                        <button
                          className="medication-detail-link"
                          onClick={() => openPharmacyDetails(selectedMedicationPharmacy)}
                          type="button"
                        >
                          {displayValue(value)}
                        </button>
                      ) : displayValue(value)}
                    </dd>
                  </div>
                ))}
              </dl>
            </section>
          ))}
        </div>
      </Drawer>

      <Modal
        centered
        className="medication-admin-confirm-modal pharmacy-remove-modal"
        confirmLoading={Boolean(removingResidentPharmacyId)}
        okButtonProps={{ danger: true }}
        okText="Remove Pharmacy"
        onCancel={closePharmacyRemoveDialog}
        onOk={removePharmacyFromResident}
        open={pharmacyRemoveDialog.open}
        title="Remove Pharmacy"
      >
        <div className="pharmacy-remove-confirm">
          <span><i className="ti ti-building-store" /></span>
          <div>
            <strong>{pharmacyName(pharmacyRemoveDialog.pharmacy || {})}</strong>
            <small>{firstText(pharmacyAddress(pharmacyRemoveDialog.pharmacy?.record || pharmacyRemoveDialog.pharmacy || {}), "No address added")}</small>
            <p>This will remove the pharmacy from this resident. The pharmacy record will stay in the workspace pharmacy directory.</p>
          </div>
        </div>
      </Modal>

      <Modal
        centered
        className="medication-admin-confirm-modal medication-refill-request-modal"
        confirmLoading={refillFulfillmentSaving}
        okText={refillFulfillmentSaving ? "Fulfilling..." : "Fulfill Refill"}
        onCancel={closeRefillFulfillmentDialog}
        onOk={submitRefillFulfillment}
        open={refillFulfillmentDialog.open}
        title="Fulfill Refill Request"
      >
        <div className="medication-refill-request">
          <span><i className="ti ti-package-import" /></span>
          <div>
            <strong>{refillRequestedMedication(refillFulfillmentDialog.request).medicationName || refillFulfillmentDialog.request?.medicationName || "Medication"}</strong>
            <small>Enter the quantity received. This replaces the dispensed and remaining quantity.</small>
            <dl>
              <div><dt>Dose</dt><dd>{displayValue([refillRequestedMedication(refillFulfillmentDialog.request).dosage, medicationDoseUnitDisplay(refillRequestedMedication(refillFulfillmentDialog.request))].filter(Boolean).join(" "))}</dd></div>
              <div><dt>Form</dt><dd>{displayValue(refillRequestedMedication(refillFulfillmentDialog.request).medicationForm)}</dd></div>
              <div><dt>Prescription number</dt><dd>{displayValue(medicationRxNumber(refillRequestedMedication(refillFulfillmentDialog.request)))}</dd></div>
              <div><dt>Strength</dt><dd>{displayValue(medicationStrength(refillRequestedMedication(refillFulfillmentDialog.request)))}</dd></div>
              <div><dt>Requested quantity</dt><dd>{displayValue([refillRequestedMedication(refillFulfillmentDialog.request).quantityAmount, medicationQuantityUnitDisplay(refillRequestedMedication(refillFulfillmentDialog.request))].filter(Boolean).join(" "))}</dd></div>
              <div><dt>Remaining at request</dt><dd>{displayValue(medicationRemainingQuantityDisplay(refillRequestedMedication(refillFulfillmentDialog.request)))}</dd></div>
              <div><dt>Route</dt><dd>{displayValue(refillRequestedMedication(refillFulfillmentDialog.request).route)}</dd></div>
              <div><dt>Frequency</dt><dd>{displayValue(refillRequestedMedication(refillFulfillmentDialog.request).frequency)}</dd></div>
              <div><dt>Pharmacy</dt><dd>{displayValue(refillFulfillmentDialog.request?.pharmacyName)}</dd></div>
              <div><dt>Request note</dt><dd>{displayValue(refillFulfillmentDialog.request?.notes)}</dd></div>
            </dl>
            {medicationDocumentsFromPayload(refillRequestedMedication(refillFulfillmentDialog.request).documents).length ? (
              <div className="medication-document-list">
                {medicationDocumentsFromPayload(refillRequestedMedication(refillFulfillmentDialog.request).documents).map((document, index) => (
                  <button className="mini-btn" key={medicationDocumentKey(document, index)} onClick={() => openMedicationDocumentPreview(document, index)} type="button">
                    <i className="ti ti-file-description" /> {medicationDocumentName(document, index)}
                  </button>
                ))}
              </div>
            ) : null}
            <label className="ncp-form-field medication-quantity-field">
              <span>Quantity received</span>
              <span className="medication-quantity-control">
                <input
                  aria-label="Fulfilled quantity amount"
                  inputMode="decimal"
                  value={refillFulfillmentDialog.quantityAmount}
                  onChange={(event) => setRefillFulfillmentDialog((current) => ({ ...current, quantityAmount: numericDecimalValue(event.target.value) }))}
                  placeholder="Example: 30"
                />
                <input
                  aria-label="Fulfilled quantity unit"
                  readOnly
                  value={refillFulfillmentDialog.quantityUnit}
                />
              </span>
            </label>
            <label className="ncp-form-field">
              <span>Fulfillment note</span>
              <textarea
                value={refillFulfillmentDialog.notes}
                onChange={(event) => setRefillFulfillmentDialog((current) => ({ ...current, notes: event.target.value }))}
                placeholder="Optional delivery or fulfillment details"
              />
            </label>
          </div>
        </div>
      </Modal>

      <Modal
        centered
        className="medication-admin-confirm-modal medication-refill-request-modal"
        confirmLoading={refillCancellationSaving}
        okButtonProps={{ danger: true }}
        okText={refillCancellationSaving ? "Canceling..." : "Cancel Request"}
        onCancel={closeRefillCancellationDialog}
        onOk={submitRefillCancellation}
        open={refillCancellationDialog.open}
        title="Cancel Refill Request"
      >
        <div className="medication-refill-request">
          <span><i className="ti ti-x" /></span>
          <div>
            <strong>{refillCancellationDialog.request?.medicationName || "Medication"}</strong>
            <small>This status change does not alter the medication quantity.</small>
            <dl>
              <div><dt>Requested quantity</dt><dd>{displayValue([refillRequestedMedication(refillCancellationDialog.request).quantityAmount, medicationQuantityUnitDisplay(refillRequestedMedication(refillCancellationDialog.request))].filter(Boolean).join(" "))}</dd></div>
              <div><dt>Remaining at request</dt><dd>{displayValue(medicationRemainingQuantityDisplay(refillRequestedMedication(refillCancellationDialog.request)))}</dd></div>
              <div><dt>Dose</dt><dd>{displayValue(medicationDoseSummary(refillRequestedMedication(refillCancellationDialog.request)))}</dd></div>
              <div><dt>Prescription number</dt><dd>{displayValue(medicationRxNumber(refillRequestedMedication(refillCancellationDialog.request)))}</dd></div>
              <div><dt>Pharmacy</dt><dd>{displayValue(refillCancellationDialog.request?.pharmacyName)}</dd></div>
            </dl>
            <label className="ncp-form-field">
              <span>Cancellation reason</span>
              <textarea
                required
                value={refillCancellationDialog.cancelReason}
                onChange={(event) => setRefillCancellationDialog((current) => ({ ...current, cancelReason: event.target.value }))}
                placeholder="Explain why this refill request is being canceled"
              />
            </label>
          </div>
        </div>
      </Modal>

      <Modal
        centered
        className="medication-admin-confirm-modal medication-refill-fax-preview-modal"
        footer={(
          <button className="mini-btn" type="button" onClick={closeRefillFaxPreview}>
            Close
          </button>
        )}
        onCancel={closeRefillFaxPreview}
        open={refillFaxPreview.open}
        title="Pharmacy fax document"
        width={860}
      >
        <div className="refill-fax-recipient" aria-label="Pharmacy fax recipient">
          <div><small>Recipient pharmacy</small><strong>{refillFaxPreview.pharmacyName || "Pharmacy not recorded"}</strong></div>
          <div><small>Destination fax number</small><strong>{refillFaxPreview.faxNumber || "Number not recorded"}</strong></div>
          <div><small>Fax status</small><span>{refillFaxPreview.status || "Not faxed"}</span>
            {refillFaxPreview.sentAt ? <small>{formatTimestamp(refillFaxPreview.sentAt)}</small> : null}
          </div>
        </div>
        {refillFaxPreview.loading ? (
          <p className="refill-fax-preview-status">
            <i className="ti ti-loader-2" /> Opening the fax document...
          </p>
        ) : null}

        {refillFaxPreview.error ? (
          <p className="form-error refill-fax-preview-status">{refillFaxPreview.error}</p>
        ) : null}

        {refillFaxPreview.url ? (
          <>
            <div className="refill-fax-preview-meta">
              <span><i className="ti ti-file-text" /> {refillFaxPreview.fileName}</span>
              <a href={refillFaxPreview.url} target="_blank" rel="noopener noreferrer"><i className="ti ti-external-link" aria-hidden="true" /> Open document</a>
            </div>
            <iframe
              key={refillFaxPreview.url}
              className="refill-fax-preview-frame"
              src={refillFaxPreview.url}
              title="Pharmacy fax document"
            />
          </>
        ) : null}
      </Modal>

      <Modal
        centered
        className="medication-admin-confirm-modal medication-refill-request-modal"
        confirmLoading={refillRequestSaving}
        okText={refillRequestSaving ? "Requesting..." : "Request Refill"}
        onCancel={closeRefillRequestDialog}
        onOk={submitRefillRequest}
        open={refillRequestDialog.open}
        title="Request Refill"
      >
        <div className="medication-refill-request">
          <span><i className="ti ti-prescription" /></span>
          <div>
            <strong>{refillRequestDialog.medication ? medicationName(refillRequestDialog.medication) : "Medication"}</strong>
            <small>
              {medicationDoseSummary(refillRequestDialog.medication || {}) || "Medication details not added"}
            </small>
            <dl>
              <div>
                <dt>Refill balance</dt>
                <dd>
                  {refillRequestDialog.medication?.totalRefills != null
                    ? `${refillRequestDialog.medication.remainingRefills ?? 0} of ${refillRequestDialog.medication.totalRefills} remaining`
                    : displayValue(refillRequestDialog.medication?.refills)}
                </dd>
              </div>
              <div>
                <dt>Pharmacy</dt>
                <dd>{displayValue(refillRequestPharmacy ? pharmacyName(refillRequestPharmacy.record || refillRequestPharmacy) : refillRequestDialog.medication?.pharmacy)}</dd>
              </div>
              <div>
                <dt>Prescriber</dt>
                <dd>{displayValue(refillRequestDialog.medication?.prescribedBy)}</dd>
              </div>
            </dl>
            <label className="ncp-form-field">
              <span>Pharmacy for refill</span>
              <Select
                allowClear
                className="medication-resident-pharmacy-select"
                disabled={!residentPharmacies.length}
                filterOption={(input, option) =>
                  String(option?.searchText || "").toLowerCase().includes(input.toLowerCase())
                }
                onChange={(value) => setRefillRequestDialog((current) => ({ ...current, pharmacyId: value || "" }))}
                optionLabelProp="label"
                placeholder={residentPharmacies.length ? "Select refill pharmacy" : "No resident pharmacies added"}
                showSearch
                value={refillRequestDialog.pharmacyId || undefined}
              >
                {residentPharmacies.map((pharmacy) => {
                  const record = pharmacy.record || pharmacy;
                  const name = pharmacyName(record);
                  const address = firstText(pharmacyAddress(record), "No address added");
                  return (
                    <Select.Option
                      key={pharmacy.id || name}
                      label={name}
                      searchText={`${name} ${address}`}
                      value={pharmacy.id}
                    >
                      <span className="pharmacy-select-option">
                        <strong>{name}</strong>
                        <small>{address}</small>
                      </span>
                    </Select.Option>
                  );
                })}
              </Select>
              {!residentPharmacies.length ? (
                <small className="medication-field-help">Add a pharmacy to this resident from the Pharmacy tab before selecting it here.</small>
              ) : null}
            </label>
            <label className="ncp-form-field">
              <span>Request note</span>
              <textarea
                value={refillRequestDialog.notes}
                onChange={(event) => setRefillRequestDialog((current) => ({ ...current, notes: event.target.value }))}
                placeholder="Add refill request notes, pharmacy instructions, or urgency"
              />
            </label>
          </div>
        </div>
      </Modal>

      <Modal
        centered
        className="medication-admin-confirm-modal"
        confirmLoading={Boolean(medicationWithholdingId)}
        okButtonProps={{ danger: medicationWithholdDialog.nextWithheld }}
        okText={medicationWithholdingId ? "Saving..." : medicationWithholdDialog.nextWithheld ? "Withhold Medication" : "Resume Medication"}
        onCancel={closeMedicationWithholdDialog}
        onOk={submitMedicationWithholdDialog}
        open={medicationWithholdDialog.open}
        title={medicationWithholdDialog.nextWithheld ? "Withhold Medication" : "Resume Medication"}
      >
        <div className="medication-admin-confirm">
          <p>
            {medicationWithholdDialog.nextWithheld ? "Withhold" : "Resume"}{" "}
            <strong>{medicationWithholdDialog.medication ? medicationName(medicationWithholdDialog.medication) : "this medication"}</strong>.
          </p>
          <label className="ncp-form-field">
            <span>{medicationWithholdDialog.nextWithheld ? "Withhold reason" : "Resume reason"} *</span>
            <textarea
              rows={4}
              value={medicationWithholdDialog.reason}
              onChange={(event) => setMedicationWithholdDialog((current) => ({ ...current, reason: event.target.value }))}
              placeholder={medicationWithholdDialog.nextWithheld ? "Explain why this medication is being withheld." : "Explain why this medication is being resumed."}
            />
          </label>
        </div>
      </Modal>

      <Drawer
        title={(
          <div className="medication-detail-drawer-title">
            <div className="medication-detail-drawer-title-main">
              <span className="medication-detail-drawer-name">{selectedMedication ? medicationName(selectedMedication) : "Medication Details"}</span>
              <small>RX# {selectedMedication ? displayValue(medicationRxNumber(selectedMedication)) : "Not added"}</small>
            </div>
            {activeMedicationTab !== "adminLog" ? <div className="medication-detail-drawer-actions">
              <button
                className="mini-btn success"
                type="button"
                onClick={() => openMedicationAdministrationWindow(selectedMedication)}
                disabled={!selectedMedication || isMedicationWithheld(selectedMedication)}
              >
                <i className="ti ti-check" /> Administer
              </button>
              <button className="mini-btn refill" type="button" onClick={() => openRefillRequestDialog(selectedMedication)} disabled={!selectedMedication}>
                <i className="ti ti-prescription" /> Request Refill
              </button>
              <button className="mini-btn" type="button" onClick={() => openMedicationEdit(selectedMedication)} disabled={!selectedMedication}>
                <i className="ti ti-edit" /> Edit
              </button>
              <button className="mini-btn danger" type="button" onClick={() => deleteMedication(selectedMedication)} disabled={!selectedMedication || medicationDeletingId === selectedMedication?.id}>
                <i className={medicationDeletingId === selectedMedication?.id ? "ti ti-loader-2" : "ti ti-trash"} /> Delete
              </button>
            </div> : null}
          </div>
        )}
        width={1180}
        open={medicationDetailsOpen}
        onClose={() => setMedicationDetailsOpen(false)}
        className="resident-action-drawer medication-admin-drawer medication-detail-drawer"
      >
        <div className="resident-drawer-content medication-detail-drawer-content">
          {selectedMedication ? (
            <section className="medication-detail-overview" aria-label="Medication summary">
              <div className="medication-detail-overview-main">
                <div>
                  <span className="medication-detail-overview-label">Medication</span>
                  <strong>{medicationName(selectedMedication)}</strong>
                  <small>{[selectedMedication.genericName, selectedMedication.brandName].filter(Boolean).join(" / ") || "No generic or brand name added"}</small>
                </div>
                <div className="medication-detail-overview-badges">
                  <em className="pill low">{displayValue(selectedMedication.status, "Active")}</em>
                  {isMedicationWithheld(selectedMedication) ? <em className="pill amber">Withheld</em> : null}
                  {isMedicationPrn(selectedMedication) ? <em className="pill medium">PRN</em> : null}
                </div>
              </div>
              <div className="medication-detail-overview-metrics">
                <span>
                  <small>Dosage</small>
                  <strong>{displayValue(selectedMedicationDosageDisplay)}</strong>
                </span>
                <span>
                  <small>Strength</small>
                  <strong>{displayValue(medicationStrength(selectedMedication))}</strong>
                </span>
                <span>
                  <small>Balance</small>
                  <strong>{displayValue(selectedMedicationRemainingQuantityDisplay || selectedMedicationQuantityDisplay)}</strong>
                </span>
                <span>
                  <small>Route</small>
                  <strong>{displayValue(selectedMedication.route)}</strong>
                </span>
                <span>
                  <small>Frequency</small>
                  <strong>{displayValue(selectedMedicationFrequencyDisplay)}</strong>
                </span>
                <span>
                  <small>Max daily dose</small>
                  <strong>{displayValue(selectedMedicationMaxDailyDoseDisplay)}</strong>
                </span>
              </div>
            </section>
          ) : null}

          {medicationDetailGroups.map(([title, rows]) => {
            const visibleRows = rows
              .map((row) => row.filter(([, value]) => shouldShowMedicationDetailValue(value)))
              .filter((row) => row.length);
            if (!visibleRows.length) return null;
            return (
              <section className="assessment-drawer-section medication-detail-section" key={title}>
                <h3>{title}</h3>
                <dl className="medication-detail-grid">
                  {visibleRows.map((row) => (
                    <div className={`medication-detail-row columns-${Math.min(row.length, 5)}`} key={row.map(([label]) => label).join("-")}>
                      {row.map(([label, value]) => (
                        <div className="medication-detail-item" key={label}>
                          <dt>{label}</dt>
                          <dd>
                            {title === "Prescriber & Pharmacy" && label === "Pharmacy" && selectedMedicationPharmacy ? (
                              <button
                                className="medication-detail-link"
                                onClick={() => openPharmacyDetails(selectedMedicationPharmacy)}
                                type="button"
                              >
                                {displayValue(value)}
                              </button>
                            ) : displayValue(value)}
                          </dd>
                        </div>
                      ))}
                    </div>
                  ))}
                </dl>
              </section>
            );
          })}

          <section className="assessment-drawer-section medication-detail-section">
            <h3>Withholding Details</h3>
            {!selectedMedicationWithholdingRecords.length ? (
              <p className="medication-section-note">No withholding history has been recorded for this medication.</p>
            ) : (
              <div className="medication-table-wrap">
                <table className="medication-table">
                  <thead>
                    <tr>
                      <th>Started</th>
                      <th>Ended</th>
                      <th>Reason held</th>
                      <th>Reason resumed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedMedicationWithholdingRecords.map((record) => (
                      <tr key={record.id || `${record.startedAt}-${record.endedAt || "active"}`}>
                        <td>{formatTimestamp(record.startedAt)}</td>
                        <td>{record.endedAt ? formatTimestamp(record.endedAt) : <em className="pill amber">Active</em>}</td>
                        <td>{displayValue(record.startReason)}</td>
                        <td>{displayValue(record.endReason)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="assessment-drawer-section medication-detail-section">
            <h3>Uploaded Documents</h3>
            {Array.isArray(selectedMedication?.documents) && selectedMedication.documents.length ? (
              <div className="medication-document-gallery">
                {selectedMedication.documents.map((document, index) => {
                  const documentUrl = medicationDocumentUrl(document);
                  const documentName = medicationDocumentName(document, index);
                  const isImage = medicationDocumentIsImage(document);
                  return (
                  <article className="medication-document-card" key={document.id || document.fileId || `${documentName}-${index}`}>
                    <button
                      className={`medication-document-thumb${isImage && documentUrl ? " has-image" : ""}`}
                      type="button"
                      onClick={() => openMedicationDocumentPreview(document, index)}
                      disabled={!documentUrl}
                      aria-label={documentUrl ? `Preview ${documentName}` : `${documentName} preview unavailable`}
                    >
                      {isImage && documentUrl ? <img alt="" src={documentUrl} /> : <i className="ti ti-file-description" />}
                    </button>
                    <div className="medication-document-copy">
                      <p>{displayValue(document.description, "No description added")}</p>
                      {documentUrl ? (
                        <button className="mini-btn medication-document-preview" type="button" onClick={() => openMedicationDocumentPreview(document, index)} aria-label={`Preview ${documentName}`}>
                          <i className="ti ti-eye" /> Preview
                        </button>
                      ) : (
                        <button className="mini-btn medication-document-preview" type="button" disabled>
                          <i className="ti ti-eye-off" /> Preview unavailable
                        </button>
                      )}
                    </div>
                  </article>
                  );
                })}
              </div>
            ) : (
              <p>No documents uploaded for this medication.</p>
            )}
          </section>
        </div>
      </Drawer>

      <Modal
        title={medicationPreviewDescription}
        open={medicationDocumentPreview.open}
        onCancel={closeMedicationDocumentPreview}
        footer={null}
        width="96vw"
        className="medication-document-preview-modal"
        destroyOnClose
      >
        <div className="medication-document-preview-window">
          <div className="medication-document-preview-toolbar">
            <div>
              <strong>{medicationPreviewDescription}</strong>
              <small>{medicationPreviewIsPdf ? "PDF document" : medicationPreviewIsImage ? "Photo document" : "Medication document"}</small>
            </div>
          </div>
          <div className="medication-document-preview-body">
            {medicationPreviewUrl && medicationPreviewIsImage ? (
              <img src={medicationPreviewUrl} alt={medicationPreviewName} />
            ) : medicationPreviewUrl && medicationPreviewIsPdf ? (
              <iframe src={medicationPreviewUrl} title={medicationPreviewName} />
            ) : (
              <div className="medication-document-preview-empty">
                <i className="ti ti-file-description" />
                <strong>Preview unavailable</strong>
                <small>This file type cannot be previewed here.</small>
              </div>
            )}
          </div>
        </div>
      </Modal>

      <Drawer
        title={`${focusedAdministrationMedication ? medicationName(focusedAdministrationMedication) : "eMAR Administration"} - ${formatDate(selectedDate)}`}
        width="min(920px, 96vw)"
        zIndex={1800}
        open={adminDrawerOpen}
        onClose={() => {
          setAdminDrawerOpen(false);
          setPrnAdminDrawerOpen(false);
          setFocusedAdministrationCell(null);
        }}
        className="resident-action-drawer medication-admin-drawer"
      >
        <div className="resident-drawer-content medication-admin-drawer-content">
          <p>
            {selectedResident ? residentName(selectedResident) : "Selected resident"} medication administration,
            prescription instructions, and log for {formatDate(selectedDate)}.
          </p>
          {!focusedAdministrationCell ? <div className="medication-admin-drawer-tools">
            <button
              className="mini-btn medication-admin-prn-trigger"
              type="button"
              onClick={() => {
                if (!prnAdministrationDateAllowed(selectedDate)) {
                  message.warning("PRN administration cannot be recorded for a future date.");
                  return;
                }
                setFocusedAdministrationCell(null);
                setPrnAdminDrawerOpen(true);
              }}
              disabled={loadingMedications || !drawerPrnMedications.length || !prnAdministrationDateAllowed(selectedDate)}
            >
              <i className="ti ti-pill" /> Administer PRNs
            </button>
          </div> : null}

          <section className="assessment-drawer-section">
            <h3>Today's eMAR</h3>
            {loadingDay || loadingMedications ? <p>Loading medication details...</p> : null}
            {!loadingDay && !loadingMedications && !visibleDrawerAdministrations.length ? <p>No medications expected for this day.</p> : null}
            <div className="medication-admin-stack">
              {visibleDrawerAdministrations.map(({ medication, administrationSlot, key }) => {
                const medicationId = medicationRecordId(medication);
                const actionSlot = administrationSlot ? administrationSlotForMedicationAction(medication, administrationSlot) : null;
                const slotKey = actionSlot?.scheduledSlot || administrationSlot?.scheduledSlot || "";
                const recordedSlotLog = administrationSlot
                  ? administrationLogsForMedicationDay(medicationId, selectedDate)
                    .find((log) => logScheduledSlot(log) === String(slotKey || "").toUpperCase()
                      && ["ADMINISTERED", "REFUSED", "MISSED", "HELD", "WITHHELD"].includes(logAdministrationAction(log))
                      && !isSystemMissedAdministrationLog(log))
                  : null;
                const computedMissed = Boolean(administrationSlot && medicationSlotAdministrationLate(administrationSlot));
                const canAdministerMedication = Boolean(administrationSlot
                  && !recordedSlotLog
                  && (administrationSlotWindowOpen(administrationSlot) || lateMedicationAdministrationDateAllowed(selectedDate)));
                const recordedAction = recordedSlotLog ? logAdministrationAction(recordedSlotLog) : "";
                return (
                <article className="medication-admin-card" key={key}>
                  <div className="medication-admin-card-head">
                    <div>
                      <div className="medication-admin-title-row">
                        <button
                          className="medication-admin-name-link"
                          onClick={() => openMedicationDetails(medication)}
                          type="button"
                        >
                          {medicationName(medication)}
                        </button>
                        {administrationSlot ? (
                          <span className="medication-admin-time-badge">
                            {administrationSlot.scheduledLabel} {administrationSlot.scheduledTime}
                          </span>
                        ) : null}
                      </div>
                      <small>{medicationDoseSummary(medication) || "Dose details not added"}</small>
                    </div>
                    <div className="medication-admin-card-tools">
                      <button
                        aria-label={`View ${medicationName(medication)} details`}
                        className="medication-admin-view-details"
                        onClick={() => openMedicationDetails(medication)}
                        type="button"
                      >
                        <i className="ti ti-eye" />
                      </button>
                      <em className={`pill ${recordedSlotLog ? "neutral" : computedMissed ? "high" : "low"}`}>
                        {recordedSlotLog ? medicationAdminLogActionLabel(recordedAction) : computedMissed ? "Missed" : displayValue(medication.status, "Active")}
                      </em>
                    </div>
                  </div>
                  {recordedSlotLog ? (
                    <div className="medication-admin-recorded-action-wrap">
                      <span className={`medication-admin-recorded-action is-${recordedAction.toLowerCase()}`}>
                        <i className={`ti ${recordedAction === "ADMINISTERED" ? "ti-check" : recordedAction === "REFUSED" ? "ti-ban" : recordedAction === "HELD" || recordedAction === "WITHHELD" ? "ti-player-pause" : "ti-alert-triangle"}`} />
                        {medicationAdminLogActionLabel(recordedAction)}
                      </span>
                      <small>{formatTimestamp(recordedSlotLog.administrationAt || recordedSlotLog.createdAt)}{recordedSlotLog.administeredByName ? ` by ${recordedSlotLog.administeredByName}` : ""}</small>
                    </div>
                  ) : null}
                  {canAdministerMedication ? (
                    <div className="medication-admin-actions" aria-label={`Administration options for ${medicationName(medication)} ${administrationSlot.scheduledLabel}`}>
                      {adminOptionsForAdministrationSlot(actionSlot || administrationSlot).map(([status, label, icon]) => {
                        const saving = adminSavingKey === `${medicationId}:${administrationSlot.scheduledSlot}:${status}`;
                        return (
                          <button
                            className={`mini-btn medication-admin-action is-${status.toLowerCase()}`}
                            type="button"
                            key={status}
                            disabled={Boolean(adminSavingKey)}
                            onClick={() => openAdministrationActionDialog(medication, status, actionSlot || administrationSlot)}
                          >
                            <i className={`ti ${icon}`} /> {saving ? "Saving..." : label}
                          </button>
                        );
                      })}
                    </div>
                  ) : null}
                </article>
                );
              })}
            </div>
          </section>

          <section className="assessment-drawer-section">
            <h3>Administration Log</h3>
            {!loadingDay && !visibleDrawerDayLogs.length ? <p>No administration log records for this day.</p> : null}
            <div className="medication-admin-stack">
              {visibleDrawerDayLogs.map((log) => {
                const medication = medications.find((item) => medicationRecordId(item) === logMedicationId(log)) || {};
                return (
                  <article className="medication-log-card" key={log.id}>
                    <div className="medication-log-card-head">
                      <strong>{displayValue(log.adminAction || log.status, "Recorded")}</strong>
                      <button
                        aria-label={`Cancel ${medicationName(medication)} administration log`}
                        className="medication-log-cancel"
                        onClick={() => openCancelAdministrationLogDialog(log)}
                        type="button"
                      >
                        <i className="ti ti-edit" />
                      </button>
                    </div>
                    <p>
                      <button
                        className="medication-admin-name-link"
                        onClick={() => openMedicationDetails(medication)}
                        type="button"
                      >
                        {medicationName(medication)}
                      </button>
                      {log.doseGiven ? ` | ${log.doseGiven}` : ""}
                    </p>
                    <small>{formatTimestamp(log.administrationAt || log.createdAt)}{log.administeredByName ? ` by ${log.administeredByName}` : ""}</small>
                    {log.confirmedBodyPart ? (
                      <small>Confirmed site: {medicationBodyPartLabel(log.confirmedBodyPart, medication.route)}</small>
                    ) : null}
                    {log.notes ? <small>{log.notes}</small> : null}
                  </article>
                );
              })}
            </div>
          </section>
        </div>
      </Drawer>

      <Drawer
        title={`${focusedAdministrationMedication ? medicationName(focusedAdministrationMedication) : "Administer PRNs"} - ${formatDate(selectedDate)}`}
        width="min(760px, 94vw)"
        zIndex={1800}
        open={prnAdminDrawerOpen}
        onClose={() => {
          setPrnAdminDrawerOpen(false);
          setFocusedAdministrationCell(null);
        }}
        className="resident-action-drawer medication-admin-drawer medication-prn-admin-drawer"
      >
        <div className="resident-drawer-content medication-admin-drawer-content">
          <p>
            {selectedResident ? residentName(selectedResident) : "Selected resident"} PRN medications for {formatDate(selectedDate)}.
          </p>

          <section className="assessment-drawer-section">
            <h3>PRN List</h3>
            {loadingMedications ? <p>Loading PRNs...</p> : null}
            {!loadingMedications && !visibleDrawerPrnMedications.length ? <p>No active PRN medications found for this day.</p> : null}
            <div className="medication-admin-stack">
              {visibleDrawerPrnMedications.map((medication) => {
                const medicationId = medicationRecordId(medication);
                const previousPrnLogs = administrationLogsForMedicationDay(medicationId, selectedDate)
                  .filter((log) => logScheduledSlot(log) === "PRN")
                  .sort((firstLog, secondLog) =>
                    (Date.parse(secondLog.administrationAt || secondLog.createdAt || "") || 0)
                    - (Date.parse(firstLog.administrationAt || firstLog.createdAt || "") || 0));
                const prnDoseLimitStatus = medicationPrnDailyDoseLimitStatus(medication, previousPrnLogs);
                return (
                  <article className="medication-admin-card" key={medicationId || medicationName(medication)}>
                    <div className="medication-admin-card-head">
                      <div>
                        <div className="medication-admin-title-row">
                          <button
                            className="medication-admin-name-link"
                            onClick={() => openMedicationDetails(medication)}
                            type="button"
                          >
                            {medicationName(medication)}
                          </button>
                          <span className="medication-admin-time-badge">PRN</span>
                        </div>
                        <small>{medicationDoseSummary(medication) || "Dose details not added"}</small>
                      </div>
                      <div className="medication-admin-card-tools">
                        <button
                          aria-label={`View ${medicationName(medication)} details`}
                          className="medication-admin-view-details"
                          onClick={() => openMedicationDetails(medication)}
                          type="button"
                        >
                          <i className="ti ti-eye" />
                        </button>
                        <em className="pill low">{displayValue(medication.status, "Active")}</em>
                      </div>
                    </div>
                    <dl className="medication-admin-instructions medication-prn-summary">
                      <div>
                        <dt>Dosage</dt>
                        <dd>{displayValue([medication.dosage, medicationDoseUnitDisplay(medication)].filter(Boolean).join(" "), "-")}</dd>
                      </div>
                      <div>
                        <dt>Strength</dt>
                        <dd>{displayValue(medicationStrength(medication), "-")}</dd>
                      </div>
                      <div>
                        <dt>Quantity</dt>
                        <dd>{displayValue([medicationQuantityAmount(medication), medicationQuantityUnitDisplay(medication)].filter(Boolean).join(" "), "-")}</dd>
                      </div>
                      <div>
                        <dt>Remaining quantity</dt>
                        <dd>{displayValue(medicationRemainingQuantityDisplay(medication), "-")}</dd>
                      </div>
                      <div>
                        <dt>Max daily dose</dt>
                        <dd>{displayValue([medicationMaxDailyDoseAmount(medication), medicationMaxDailyDoseUnitDisplay(medication)].filter(Boolean).join(" "), "-")}</dd>
                      </div>
                      <div>
                        <dt>PRN reason</dt>
                        <dd>{displayValue(medication.prnReason, "-")}</dd>
                      </div>
                    </dl>
                    <div className="medication-prn-admin-history">
                      <div className="medication-prn-admin-history-head">
                        <strong>Previous PRNs today</strong>
                        <span>{previousPrnLogs.length}</span>
                      </div>
                      {loadingDay ? (
                        <p>Loading previous PRNs...</p>
                      ) : previousPrnLogs.length ? (
                        <div className="medication-prn-admin-history-list">
                          {previousPrnLogs.map((log) => {
                            const action = logAdministrationAction(log);
                            return (
                              <article className="medication-prn-admin-history-item" key={log.id || `${medicationId}-${log.administrationAt || log.createdAt}`}>
                                <span className={`medication-prn-admin-history-status is-${action.toLowerCase()}`}>
                                  {adminActionLabel(action)}
                                </span>
                                <span>{formatTimestamp(log.administrationAt || log.createdAt)}</span>
                                {log.administeredByName ? <span>{log.administeredByName}</span> : null}
                                {log.doseGiven ? <span>{log.doseGiven}</span> : null}
                                {log.notes ? <p>{log.notes}</p> : null}
                              </article>
                            );
                          })}
                        </div>
                      ) : (
                        <p>No previous PRNs today.</p>
                      )}
                    </div>
                    {prnDoseLimitStatus ? (
                      <div className={`medication-prn-dose-warning is-${prnDoseLimitStatus.level}`} role="alert">
                        <i className="ti ti-alert-triangle" />
                        <span>{prnDoseLimitStatus.message}</span>
                      </div>
                    ) : null}
                    <div className="medication-admin-actions" aria-label={`PRN administration options for ${medicationName(medication)}`}>
                      {prnAdminOptions.map(([status, label, icon]) => {
                        const saving = adminSavingKey === `${medicationId}:PRN:${status}`;
                        return (
                          <button
                            className={`mini-btn medication-admin-action is-${status.toLowerCase()}`}
                            type="button"
                            key={status}
                            disabled={Boolean(adminSavingKey) || !prnAdministrationDateAllowed(selectedDate)}
                            onClick={() => openAdministrationActionDialog(medication, status)}
                          >
                            <i className={`ti ${icon}`} /> {saving ? "Saving..." : label}
                          </button>
                        );
                      })}
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        </div>
      </Drawer>

      <Modal
        title={`Confirm ${adminActionLabel(adminActionDialog.status)}`}
        open={adminActionDialog.open}
        width="min(720px, 94vw)"
        zIndex={2400}
        className="medication-admin-confirm-modal"
        okText={adminSavingKey ? "Saving..." : adminActionConflictRecords.length ? "Review overwrite" : adminActionPrnDoseLimitStatus && adminActionDialog.status === "ADMINISTERED" ? "Confirm Override" : `Confirm ${adminActionLabel(adminActionDialog.status)}`}
        cancelText="Cancel"
        confirmLoading={Boolean(adminSavingKey)}
        footer={(_, { OkBtn, CancelBtn }) => (
          <div className="medication-admin-confirm-footer">
            <CancelBtn />
            {adminActionDialog.status === "ADMINISTERED"
              && adminActionDialog.medication
              && isMedicationPrn(adminActionDialog.medication) ? (
                <button
                  className="mini-btn medication-admin-action is-refused"
                  disabled={Boolean(adminSavingKey)}
                  type="button"
                  onClick={() => setAdminActionDialog((current) => ({
                    ...current,
                    status: "REFUSED",
                    notes: "",
                    doseEditing: false,
                  }))}
                >
                  <i className="ti ti-ban" /> Refused
                </button>
              ) : null}
            <OkBtn />
          </div>
        )}
        onCancel={() => {
          if (!adminSavingKey) {
            setAdminActionDialog({ open: false, medication: null, status: "", notes: "", confirmedBodyPart: "", confirmedBodyPartOther: "", requiresLateReason: false, administrationAt: "", administrationSlot: null, existingRecords: [], doseGiven: "", originalDoseGiven: "", doseEditing: false });
          }
        }}
        onOk={submitAdministrationAction}
      >
        <div className="medication-admin-confirm">
          <p>
            {adminActionLabel(adminActionDialog.status)}{" "}
            <strong>{adminActionDialog.medication ? medicationName(adminActionDialog.medication) : "this medication"}</strong>{" "}
            for {formatDate(selectedDate)}.
          </p>
          {adminActionPrnDoseLimitStatus && adminActionDialog.status === "ADMINISTERED" ? (
            <div className={`medication-prn-dose-warning is-${adminActionPrnDoseLimitStatus.level}`} role="alert">
              <i className="ti ti-alert-triangle" />
              <span>{adminActionPrnDoseLimitStatus.message}</span>
            </div>
          ) : null}
          {adminActionDayPrnRecords.length ? (
            <section className="medication-admin-existing-prns">
              <h3>PRN records already added for this day</h3>
              <div>
                {adminActionDayPrnRecords.map((record) => {
                  const actor = logActorName(record);
                  return (
                    <span key={record.id}>
                      <strong>{formatTimeOnly(record.administrationAt || record.createdAt)}</strong>
                      <em>{medicationAdminLogActionLabel(logAdministrationAction(record))}</em>
                      {actor ? <small>{userInitials(actor)} · {actor}</small> : null}
                    </span>
                  );
                })}
              </div>
            </section>
          ) : null}
          {adminActionConflictRecords.length ? (
            <div className="medication-prn-dose-warning is-warning" role="alert">
              <i className="ti ti-alert-triangle" />
              <span>
                {adminActionConflictRecords.some((log) => !isSystemMissedAdministrationLog(log))
                  ? `${adminActionDialog.medication && isMedicationPrn(adminActionDialog.medication) ? "A PRN record conflicts with the selected administration time." : "An administration record already exists for this date and scheduled time."} Saving will mark the existing staff-entered record as overridden and retain it in the audit history.`
                  : "A system-generated missed record already exists for this date and scheduled time. Saving this late record will permanently remove the system-generated record."}
                {adminActionDialog.medication && isMedicationPrn(adminActionDialog.medication) ? (
                  <small>
                    Conflicting {adminActionConflictRecords.length === 1 ? "record" : "records"}: {adminActionConflictRecords
                      .map((record) => formatTimeOnly(record.administrationAt || record.createdAt))
                      .join(", ")}
                  </small>
                ) : null}
              </span>
            </div>
          ) : null}
          {adminActionDialog.medication ? (
            <dl className="medication-admin-confirm-details">
              <div>
                <dt>Dosage</dt>
                <dd className="medication-admin-dose-value">
                  {adminActionDialog.doseEditing ? (
                    <input
                      aria-label="Administered dose amount"
                      autoFocus
                      type="text"
                      value={adminActionDialog.doseGiven}
                      onChange={(event) => setAdminActionDialog((current) => ({ ...current, doseGiven: event.target.value }))}
                    />
                  ) : (
                    <span>{displayValue([adminActionDialog.doseGiven, medicationDoseUnitDisplay(adminActionDialog.medication)].filter(Boolean).join(" "), "-")}</span>
                  )}
                  {adminActionDialog.status === "ADMINISTERED" ? (
                    <Tooltip title={adminActionDialog.doseEditing ? "Finish editing dose amount" : "Edit dose amount"}>
                      <button
                        aria-label={adminActionDialog.doseEditing ? "Finish editing dose amount" : "Edit dose amount"}
                        className="resident-icon-button"
                        type="button"
                        onClick={() => setAdminActionDialog((current) => ({ ...current, doseEditing: !current.doseEditing }))}
                      >
                        <i className={`ti ${adminActionDialog.doseEditing ? "ti-check" : "ti-edit"}`} />
                      </button>
                    </Tooltip>
                  ) : null}
                </dd>
              </div>
              <div>
                <dt>Max daily dose</dt>
                <dd>{displayValue([medicationMaxDailyDoseAmount(adminActionDialog.medication), medicationMaxDailyDoseUnitDisplay(adminActionDialog.medication)].filter(Boolean).join(" "), "-")}</dd>
              </div>
              <div>
                <dt>Route</dt>
                <dd>{displayValue(adminActionDialog.medication.route, "-")}</dd>
              </div>
            </dl>
          ) : null}
          {adminActionDoseChanged ? (
            <div className="medication-prn-dose-warning is-warning" role="alert">
              <i className="ti ti-alert-triangle" />
              <span>The administered dose amount differs from the ordered amount. A reason is required.</span>
            </div>
          ) : null}
          {adminActionDialog.administrationSlot ? (
            <div className="medication-admin-confirm-slot">
              <span>
                <strong>{adminActionDialog.administrationSlot.scheduledSlot === "PRN" ? "Administration type" : "Administration time"}</strong>
                {adminActionDialog.administrationSlot.scheduledLabel}
              </span>
              {adminActionDialog.administrationSlot.scheduledTime ? (
                <span>
                  <strong>Scheduled time</strong>
                  {adminActionDialog.administrationSlot.scheduledTime}
                </span>
              ) : null}
            </div>
          ) : null}
          {adminActionDialog.medication && medicationRouteNeedsBodyPart(optionValueFromStored(medicationRouteOptions, adminActionDialog.medication.route)) ? (
            <div className="ncp-form-field medication-body-part-field medication-admin-body-part-field">
              <FieldLabel info="Confirm the actual body part or administration site used before saving the eMAR record.">
                Confirm body part/site{adminActionDialog.status === "ADMINISTERED" ? " *" : ""}
              </FieldLabel>
              {medicationExpectedBodyPart(adminActionDialog.medication) ? (
                <small className="medication-field-help">
                  Expected: {medicationBodyPartLabel(medicationExpectedBodyPart(adminActionDialog.medication), adminActionDialog.medication.route)}
                </small>
              ) : null}
              <MedicationBodyPartPicker
                otherValue={adminActionDialog.confirmedBodyPartOther}
                options={medicationBodyPartOptionsForRoute(optionValueFromStored(medicationRouteOptions, adminActionDialog.medication.route))}
                route={adminActionDialog.medication.route}
                value={adminActionDialog.confirmedBodyPart}
                onChange={(nextValue) => setAdminActionDialog((current) => ({ ...current, confirmedBodyPart: nextValue }))}
                onOtherChange={(nextValue) => setAdminActionDialog((current) => ({ ...current, confirmedBodyPartOther: nextValue }))}
              />
            </div>
          ) : null}
          {adminActionDialog.status ? (
            <label className="ncp-form-field">
              <span>Actual administration date and time *</span>
              <input
                type="datetime-local"
                min={localDateTimeInputValue(medicationLateAdministrationEarliestDate())}
                max={localDateTimeInputValue()}
                value={adminActionDialog.administrationAt}
                onChange={(event) => setAdminActionDialog((current) => ({ ...current, administrationAt: event.target.value }))}
                required
              />
            </label>
          ) : null}
          <label className="ncp-form-field">
            <span>
              {adminActionPrnDoseLimitStatus && adminActionDialog.status === "ADMINISTERED"
                ? "Max daily dose override reason"
                : adminActionDoseChanged
                ? "Dose amount change reason"
                : adminActionRequiresLateReason
                ? "Late administration or record add reason"
                : adminActionNoteLabel(adminActionDialog.status)}
              {adminActionsRequiringReason.has(adminActionDialog.status) || adminActionRequiresLateReason || adminActionDoseChanged || (adminActionPrnDoseLimitStatus && adminActionDialog.status === "ADMINISTERED") ? " *" : ""}
            </span>
            <textarea
              rows={4}
              value={adminActionDialog.notes}
              onChange={(event) => setAdminActionDialog((current) => ({ ...current, notes: event.target.value }))}
              placeholder={adminActionPrnDoseLimitStatus && adminActionDialog.status === "ADMINISTERED"
                ? "Explain why this medication should be administered even though the max daily dose has been reached."
                : adminActionDoseChanged
                ? "Explain why the administered dose amount differs from the ordered amount."
                : adminActionRequiresLateReason
                ? "Explain why this medication is being administered more than 1 hour after the scheduled time."
                : adminActionNotePlaceholder(adminActionDialog.status)}
            />
          </label>
        </div>
      </Modal>

      <Modal
        title="Delete Administration Record"
        open={adminCancelDialog.open}
        className="medication-admin-confirm-modal"
        okText={adminCancelSaving ? "Deleting..." : "Delete record"}
        cancelText="Keep record"
        confirmLoading={adminCancelSaving}
        okButtonProps={{ danger: true }}
        onCancel={() => {
          if (!adminCancelSaving) {
            setAdminCancelDialog({ open: false, log: null, reason: "" });
          }
        }}
        onOk={submitCancelAdministrationLog}
      >
        <div className="medication-admin-confirm">
          <p>{adminCancelDialog.log?.systemGenerated
            ? "This system-generated record will be permanently removed."
            : "This record will be removed from active charts and retained as overridden in the audit history."}</p>
          <label className="ncp-form-field">
            <span>Delete reason *</span>
            <textarea
              rows={4}
              value={adminCancelDialog.reason}
              onChange={(event) => setAdminCancelDialog((current) => ({ ...current, reason: event.target.value }))}
              placeholder="Explain why this administration record should be deleted."
            />
          </label>
        </div>
      </Modal>

      <Drawer
        title={medicationDrawerMode === "new" ? "Add New Medication" : editingMedicationId ? "Edit Medication" : "Add Medication"}
        width="82vw"
        open={medicationDrawerOpen}
        onClose={() => {
          setMedicationDrawerOpen(false);
          if (!editingMedicationId) setMedicationDrawerMode("medication");
        }}
        className={`resident-action-drawer medication-admin-drawer medication-add-drawer medication-document-preview-drawer ${medicationDrawerMode === "new" ? "new-medication-intake-drawer" : ""}`}
      >
        <form
          className={`assessment-drawer-form medication-add-form medication-document-preview-form ${medicationDrawerMode === "new" ? "new-medication-intake-form" : ""}`}
          noValidate={medicationDrawerMode === "new"}
          onSubmit={saveMedication}
        >
          <section className="assessment-drawer-section medication-documents-section new-medication-prescription-pane">
              <h3>Prescription Document</h3>
              {(medicationForm.documents || []).length ? (
                <>
                  <div className="new-medication-document-live-preview">
                    {medicationDocumentIsImage(medicationFormPreviewDocument || {}) && medicationFormPreviewUrl ? (
                      <img src={medicationFormPreviewUrl} alt={medicationFormPreviewName} />
                    ) : medicationDocumentIsPdf(medicationFormPreviewDocument || {}) && medicationFormPreviewUrl ? (
                      <iframe src={medicationFormPreviewUrl} title={medicationFormPreviewName} />
                    ) : (
                      <div className="new-medication-document-preview-empty">
                        <i className="ti ti-file-description" />
                        <strong>{medicationFormPreviewName}</strong>
                        <small>{medicationFormPreviewUrl ? "This file type cannot be previewed here." : "Preview will show for image and PDF prescriptions."}</small>
                      </div>
                    )}
                  </div>
                  {medicationForm.documents.length > 1 ? (
                  <div className="new-medication-document-thumbnail-strip" aria-label="Prescription document thumbnails">
                    {medicationForm.documents.map((document, index) => {
                    const documentKey = medicationDocumentKey(document, index);
                    const previewUrl = medicationDocumentUrl(document);
                    const isSelected = documentKey === medicationDocumentKey(medicationFormPreviewDocument || {}, medicationFormPreviewIndex);
                    const selectDocumentPreview = () => setSelectedMedicationFormDocumentKey(documentKey);
                    return (
                      <button
                        aria-label={`Show ${medicationDocumentName(document, index)} preview`}
                        className={`new-medication-document-preview-thumb ${isSelected ? "is-selected" : ""}`}
                        disabled={!previewUrl}
                        key={document.id || document.fileId || document.fileName}
                        onClick={selectDocumentPreview}
                        title={medicationDocumentName(document, index)}
                        type="button"
                      >
                        {medicationDocumentIsImage(document) && previewUrl ? (
                          <img src={previewUrl} alt="" />
                        ) : (
                          <i className="ti ti-file-description" />
                        )}
                        <span>{index + 1}</span>
                      </button>
                    );
                    })}
                  </div>
                  ) : null}
                </>
              ) : (
                <div className="new-medication-document-live-preview">
                  <div className="new-medication-document-preview-empty">
                    <i className="ti ti-file-description" />
                    <strong>No prescription document</strong>
                    <small>Preview will show for image and PDF prescriptions.</small>
                  </div>
                </div>
              )}
              <label className="assessment-upload assessment-upload-compact medication-document-upload">
                <input
                  accept=".pdf,.docx,.png,.jpg,.jpeg,.jpe,.jfif,.webp,.heic,.heif,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/png,image/jpeg,image/webp,image/heic,image/heif"
                  className="assessment-file-input"
                  multiple
                  onChange={addMedicationDocuments}
                  type="file"
                />
                <i className="ti ti-file-upload" />
                <strong>Upload prescription</strong>
                <small>Attach the prescription document received from eFax or the hospital.</small>
              </label>
            </section>
          <section className="assessment-drawer-section">
            <h3>Hospital Medication Details</h3>
            <div className="assessment-drawer-grid medication-add-grid">
              <label className="ncp-form-field">
                <FieldLabel info="Enter the medication name exactly as shown on the hospital order, pharmacy label, or medication list. Example: Lisinopril 10 mg tablet.">
                  Medication name
                </FieldLabel>
                <input
                  value={medicationForm.medicationName}
                  onChange={(event) => updateMedicationForm("medicationName", event.target.value)}
                  required={medicationDrawerMode !== "new"}
                />
              </label>
              <label className="ncp-form-field">
                <FieldLabel info="Use the non-brand drug name when available. Example: acetaminophen is the generic name for Tylenol.">
                  Generic name
                </FieldLabel>
                <input value={medicationForm.genericName} onChange={(event) => updateMedicationForm("genericName", event.target.value)} />
              </label>
              <label className="ncp-form-field">
                <FieldLabel info="Use the manufacturer or trade name if shown. Example: Tylenol, Lipitor, Norvasc, or Glucophage.">
                  Brand name
                </FieldLabel>
                <input value={medicationForm.brandName} onChange={(event) => updateMedicationForm("brandName", event.target.value)} />
              </label>
              <label className="ncp-form-field">
                <span>Form</span>
                <select value={medicationForm.medicationForm} onChange={(event) => updateMedicationForm("medicationForm", event.target.value)}>
                  {medicationFormOptions.map(([value, label]) => <option value={value} key={value || label}>{label}</option>)}
                </select>
                {medicationForm.medicationForm === "OTHER" ? (
                  <input value={medicationForm.medicationFormOther} onChange={(event) => updateMedicationForm("medicationFormOther", event.target.value)} placeholder="Enter medication form" />
                ) : null}
              </label>
              <label className="ncp-form-field">
                <span>Classification</span>
                <Select
                  allowClear
                  className="medication-searchable-select"
                  filterOption={(input, option) =>
                    String(option?.searchText || option?.label || "").toLowerCase().includes(input.toLowerCase())
                  }
                  onChange={(value) => updateMedicationForm("classification", value || "")}
                  optionLabelProp="label"
                  placeholder="Select classification"
                  showSearch
                  value={medicationForm.classification || undefined}
                >
                  {medicationClassificationOptions.filter(([value]) => value).map(([value, label]) => (
                    <Select.Option key={value} label={label} searchText={`${label} ${value}`} value={value}>
                      {label}
                    </Select.Option>
                  ))}
                </Select>
                {medicationForm.classification === "OTHER" ? (
                  <input value={medicationForm.classificationOther} onChange={(event) => updateMedicationForm("classificationOther", event.target.value)} placeholder="Enter medication classification" />
                ) : null}
              </label>
              <label className="ncp-form-field">
                <FieldLabel info="The clinical reason this medication is prescribed, such as pain, hypertension, diabetes, infection, mood, or sleep.">
                  Therapeutic indication
                </FieldLabel>
                <select value={medicationForm.indication} onChange={(event) => updateMedicationForm("indication", event.target.value)}>
                  {medicationIndicationOptions.map(([value, label]) => <option value={value} key={value || label}>{label}</option>)}
                </select>
                {medicationForm.indication === "OTHER" ? (
                  <input value={medicationForm.indicationOther} onChange={(event) => updateMedicationForm("indicationOther", event.target.value)} placeholder="Enter therapeutic indication" />
                ) : null}
              </label>
              <label className="ncp-form-field medication-dose-amount-field">
                <span>Dose amount</span>
                <input
                  inputMode="decimal"
                  pattern="[0-9]+(?:\.[0-9]+)?"
                  value={medicationForm.dosage}
                  onChange={(event) => updateMedicationForm("dosage", numericDosageValue(event.target.value))}
                  placeholder="Example: 2"
                />
              </label>
              <label className="ncp-form-field medication-dose-unit-field">
                <span>Dose unit/form</span>
                <select value={medicationForm.doseUnit} onChange={(event) => updateMedicationForm("doseUnit", event.target.value)}>
                  {medicationDoseUnitOptions.map(([value, label]) => <option value={value} key={value || label}>{label}</option>)}
                </select>
                {medicationForm.doseUnit === "OTHER" ? (
                  <input value={medicationForm.doseUnitOther} onChange={(event) => updateMedicationForm("doseUnitOther", event.target.value)} placeholder="Specify dose unit/form" />
                ) : null}
              </label>
              {medicationUnitWarning ? (
                <p className="medication-unit-warning">{medicationUnitWarning}</p>
              ) : null}
              <label className="ncp-form-field medication-quantity-field">
                <FieldLabel info="Maximum allowed amount in a 24-hour period, especially important for PRN medications. Select the unit/form using the same options as dose unit.">
                  Max daily dose
                </FieldLabel>
                <span className="medication-quantity-control">
                  <span className="medication-combo-part">
                    <small>Amount</small>
                    <input
                      aria-label="Max daily dose amount"
                      inputMode="decimal"
                      value={medicationForm.maxDailyDoseAmount}
                      onChange={(event) => updateMedicationForm("maxDailyDoseAmount", numericDecimalValue(event.target.value))}
                      placeholder="Example: 3"
                    />
                  </span>
                  <span className="medication-combo-part">
                    <small>Unit</small>
                    <select aria-label="Max daily dose unit" value={medicationForm.maxDailyDoseUnit} onChange={(event) => updateMedicationForm("maxDailyDoseUnit", event.target.value)}>
                      {medicationDoseUnitOptions.map(([value, label]) => <option value={value} key={value || label}>{label}</option>)}
                    </select>
                  </span>
                </span>
                {medicationForm.maxDailyDoseUnit === "OTHER" ? (
                  <input value={medicationForm.maxDailyDoseUnitOther} onChange={(event) => updateMedicationForm("maxDailyDoseUnitOther", event.target.value)} placeholder="Specify max daily dose unit" />
                ) : null}
              </label>
              {medicationUnitWarning ? (
                <p className="medication-unit-warning">{medicationUnitWarning}</p>
              ) : null}
              <div className="medication-strength-row">
                <label className="ncp-form-field medication-strength-amount-field">
                  <span>Strength amount</span>
                  <input
                    inputMode="decimal"
                    value={medicationForm.strengthAmount}
                    onChange={(event) => updateMedicationForm("strengthAmount", numericDecimalValue(event.target.value))}
                    placeholder="Example: 500"
                  />
                </label>
                <label className="ncp-form-field medication-strength-unit-field">
                  <span>Strength unit</span>
                  <select value={medicationForm.strengthUnit} onChange={(event) => updateMedicationForm("strengthUnit", event.target.value)}>
                    {medicationStrengthUnitOptions.map(([value, label]) => <option value={value} key={value || label}>{label}</option>)}
                  </select>
                  {medicationForm.strengthUnit === "OTHER" ? (
                    <input value={medicationForm.strengthUnitOther} onChange={(event) => updateMedicationForm("strengthUnitOther", event.target.value)} placeholder="Specify strength unit" />
                  ) : null}
                </label>
              </div>
              <label className="ncp-form-field medication-quantity-field">
                <FieldLabel info="Enter the numeric amount dispensed or supplied and select the matching unit/form, such as 30 tablets or 10 mL.">
                  Quantity
                </FieldLabel>
                <span className="medication-quantity-control">
                  <input
                    aria-label="Quantity amount"
                    inputMode="decimal"
                    value={medicationForm.quantityAmount}
                    onChange={(event) => updateMedicationForm("quantityAmount", numericDecimalValue(event.target.value))}
                    placeholder="Example: 30"
                    disabled={Boolean(editingMedicationId)}
                  />
                  <select aria-label="Quantity unit" value={medicationForm.quantityUnit} onChange={(event) => updateMedicationForm("quantityUnit", event.target.value)} disabled={Boolean(editingMedicationId)}>
                    {medicationDoseUnitOptions.map(([value, label]) => <option value={value} key={value || label}>{label}</option>)}
                  </select>
                </span>
                {medicationForm.quantityUnit === "OTHER" ? (
                  <input value={medicationForm.quantityUnitOther} onChange={(event) => updateMedicationForm("quantityUnitOther", event.target.value)} placeholder="Specify quantity unit" disabled={Boolean(editingMedicationId)} />
                ) : null}
                {editingMedicationId ? <small>Quantity is updated only when a refill request is fulfilled.</small> : null}
              </label>
              <label className="ncp-form-field">
                <FieldLabel info="Select the number of refills allowed or the refill status shown by the pharmacy, such as provider approval required or expired.">
                  Refills
                </FieldLabel>
                <select value={medicationForm.refills} onChange={(event) => updateMedicationForm("refills", event.target.value)} disabled={Boolean(editingMedicationId)}>
                  {medicationRefillOptions.map(([value, label]) => <option value={value} key={value || label}>{label}</option>)}
                </select>
                {medicationForm.refills === "OTHER" ? (
                  <input value={medicationForm.refillsOther} onChange={(event) => updateMedicationForm("refillsOther", event.target.value)} placeholder="Enter refill instruction or status" disabled={Boolean(editingMedicationId)} />
                ) : null}
                {editingMedicationId ? <small>Refill allowance is set by the prescription and decreases when a refill is fulfilled.</small> : null}
              </label>
              {medicationUnitWarning ? (
                <p className="medication-unit-warning">{medicationUnitWarning}</p>
              ) : null}
              <label className="ncp-form-field">
                <span>Route</span>
                <select value={medicationForm.route} onChange={(event) => updateMedicationForm("route", event.target.value)}>
                  {medicationRouteOptions.map(([value, label]) => <option value={value} key={value || label}>{label}</option>)}
                </select>
                {medicationForm.route === "OTHER" ? (
                  <input value={medicationForm.routeOther} onChange={(event) => updateMedicationForm("routeOther", event.target.value)} placeholder="Enter administration route" />
                ) : null}
              </label>
              {medicationRouteNeedsBodyPart(medicationForm.route) ? (
                <div className="ncp-form-field ncp-form-field-wide medication-body-part-field">
                  <FieldLabel info="Select the expected body part or administration site staff should use for topical/body-area medication or injection routes.">
                    BODY PARTS FOR TOPICAL MEDICATION
                  </FieldLabel>
                  <MedicationBodyPartPicker
                    otherValue={medicationForm.expectedBodyPartOther}
                    options={medicationBodyPartOptionsForRoute(medicationForm.route)}
                    route={medicationForm.route}
                    value={medicationForm.expectedBodyPart}
                    onChange={(nextValue) => updateMedicationForm("expectedBodyPart", nextValue)}
                    onOtherChange={(nextValue) => updateMedicationForm("expectedBodyPartOther", nextValue)}
                  />
                </div>
              ) : null}
              <div className={`medication-prn-frequency-row${booleanValue(medicationForm.prn) ? " is-prn-only" : ""}`}>
                <label className="ncp-form-field">
                  <span>PRN / as needed</span>
                  <select value={booleanValue(medicationForm.prn) ? "YES" : "NO"} onChange={(event) => updateMedicationForm("prn", event.target.value === "YES")}>
                    <option value="NO">No</option>
                    <option value="YES">Yes</option>
                  </select>
                </label>
                {!booleanValue(medicationForm.prn) ? (
                  <label className="ncp-form-field">
                    <span>Frequency</span>
                    <select value={medicationForm.frequency} onChange={(event) => updateMedicationForm("frequency", event.target.value)}>
                      {medicationFrequencyOptions.map(([value, label]) => <option value={value} key={value || label}>{label}</option>)}
                    </select>
                    {medicationForm.frequency === "CUSTOM" ? (
                      <input value={medicationForm.frequencyOther} onChange={(event) => updateMedicationForm("frequencyOther", event.target.value)} placeholder="Enter medication frequency" />
                    ) : null}
                  </label>
                ) : null}
              </div>
              {!booleanValue(medicationForm.prn) ? (
              <label className="ncp-form-field ncp-form-field-wide medication-schedule-field">
                  <FieldLabel info="Choose the unique daily time slots when staff should administer this medication. The number of selections is based on the selected frequency.">
                  Administration schedule *
                </FieldLabel>
                {medicationForm.frequency ? (
                  <>
                    {administrationTimePresets.length ? (
                      <div className="medication-schedule-presets" aria-label="Administration schedule options">
                        {administrationTimePresets.map(([label, times]) => (
                          <button
                            className="medication-schedule-preset"
                            key={label}
                            onClick={() => applyAdministrationTimePreset(times)}
                            type="button"
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    ) : null}
                    <Select
                      allowClear
                      className="medication-multi-select"
                      mode="multiple"
                      options={administrationTimeOptions}
                      placeholder="Select administration time"
                      value={medicationForm.administrationTime}
                      onChange={(values) => updateMedicationForm("administrationTime", values)}
                    />
                    <small className="medication-field-help medication-schedule-help">{administrationTimeGuidance}</small>
                    {medicationForm.administrationTime.some((time) => !["AS_NEEDED", "SPECIFIC_TIME"].includes(time)) ? (
                      <div className="medication-time-entry-list">
                        {medicationForm.administrationTime
                          .filter((time) => !["AS_NEEDED", "SPECIFIC_TIME"].includes(time))
                          .map((time) => (
                            <div className="medication-time-entry" key={time}>
                              <span>{administrationTimeOptionLabel(time)}</span>
                              <input
                                aria-label={`${administrationTimeOptionLabel(time)} administration time`}
                                required
                                type="time"
                                value={medicationForm.administrationTimeDetails?.[time] || ""}
                                onChange={(event) => updateMedicationForm("administrationTimeDetails", { [time]: event.target.value })}
                              />
                            </div>
                          ))}
                      </div>
                    ) : null}
                    {medicationForm.administrationTime.includes("SPECIFIC_TIME") ? (
                      <input required value={medicationForm.administrationTimeOther} onChange={(event) => updateMedicationForm("administrationTimeOther", event.target.value)} placeholder="Enter administration time" />
                    ) : null}
                  </>
                ) : (
                  <small className="medication-field-help medication-schedule-help">{administrationTimeGuidance}</small>
                )}
              </label>
              ) : null}
              <label className="ncp-form-field ncp-form-field-wide">
                <FieldLabel info="Enter the exact medication directions as written on the hospital order, pharmacy label, or MAR, including dose, route, timing, and frequency.">
                  Directions / SIG
                </FieldLabel>
                <textarea
                  rows={3}
                  value={medicationForm.instructions}
                  onChange={(event) => updateMedicationForm("instructions", event.target.value)}
                  placeholder="Exact prescription directions from the hospital, pharmacy label, or MAR."
                />
              </label>
              <label className="ncp-form-field">
                <FieldLabel info="Use the prescription, Rx, or order number shown on the medication record or pharmacy label.">
                  Prescription / order number
                </FieldLabel>
                <input value={medicationForm.orderNumber} onChange={(event) => updateMedicationForm("orderNumber", event.target.value)} />
              </label>
              <label className="ncp-form-field">
                <span>Order date</span>
                <input type="date" value={medicationForm.orderDate} onChange={(event) => updateMedicationForm("orderDate", event.target.value)} />
              </label>
              <div className="medication-date-range-row">
                <label className="ncp-form-field">
                  <span>Start date</span>
                  <input type="date" value={medicationForm.startDate} onChange={(event) => updateMedicationForm("startDate", event.target.value)} />
                </label>
                <label className="ncp-form-field">
                  <span>End date</span>
                  <input type="date" value={medicationForm.endDate} onChange={(event) => updateMedicationForm("endDate", event.target.value)} />
                </label>
              </div>
            </div>
          </section>

          <section className="assessment-drawer-section">
            <h3>Prescription Administration Instructions</h3>
            <div className="medication-risk-grid">
              {[
                ["controlledSubstance", "Controlled substance"],
                ["highAlertMedication", "High-alert medication"],
                ["requiresVitalsCheck", "Requires vitals check"],
                ["requiresBloodSugarCheck", "Requires blood sugar check"],
              ].map(([field, label]) => (
                <label className="medication-risk-toggle" key={field}>
                  <input
                    type="checkbox"
                    checked={Boolean(medicationForm[field])}
                    onChange={(event) => updateMedicationForm(field, event.target.checked)}
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>
            <div className="assessment-drawer-grid medication-add-grid">
              <label className="ncp-form-field">
                <span>Food instructions</span>
                <Select
                  allowClear
                  className="medication-multi-select"
                  mode="multiple"
                  options={multiSelectOptions(medicationFoodInstructionOptions)}
                  placeholder="Select food instructions"
                  value={medicationForm.foodInstructions}
                  onChange={(values) => updateMedicationForm("foodInstructions", values)}
                />
                {medicationForm.foodInstructions.includes("CUSTOM") ? (
                  <input value={medicationForm.foodInstructionsOther} onChange={(event) => updateMedicationForm("foodInstructionsOther", event.target.value)} placeholder="Enter food instructions" />
                ) : null}
              </label>
              <label className="ncp-form-field">
                <span>Storage</span>
                <Select
                  allowClear
                  className="medication-multi-select"
                  mode="multiple"
                  options={multiSelectOptions(medicationStorageOptions)}
                  placeholder="Select storage"
                  value={medicationForm.storageInstructions}
                  onChange={(values) => updateMedicationForm("storageInstructions", values)}
                />
                {medicationForm.storageInstructions.includes("CUSTOM") ? (
                  <input value={medicationForm.storageInstructionsOther} onChange={(event) => updateMedicationForm("storageInstructionsOther", event.target.value)} placeholder="Enter storage instructions" />
                ) : null}
              </label>
              <label className="ncp-form-field">
                <span>Special handling</span>
                <Select
                  allowClear
                  className="medication-multi-select"
                  mode="multiple"
                  options={multiSelectOptions(medicationSpecialHandlingOptions)}
                  placeholder="Select handling"
                  value={medicationForm.specialHandling}
                  onChange={(values) => updateMedicationForm("specialHandling", values)}
                />
                {medicationForm.specialHandling.includes("CUSTOM") ? (
                  <input value={medicationForm.specialHandlingOther} onChange={(event) => updateMedicationForm("specialHandlingOther", event.target.value)} placeholder="Enter special handling instructions" />
                ) : null}
              </label>
              <label className="ncp-form-field">
                <FieldLabel info="Use only for as-needed medications. Select the symptom or situation that allows staff to give the PRN dose.">
                  PRN reason
                </FieldLabel>
                <Select
                  allowClear
                  className="medication-multi-select"
                  mode="multiple"
                  options={multiSelectOptions(medicationPrnReasonOptions)}
                  placeholder="Select PRN reason"
                  value={medicationForm.prnReason}
                  onChange={(values) => updateMedicationForm("prnReason", values)}
                />
                {medicationForm.prnReason.includes("OTHER") ? (
                  <input value={medicationForm.prnReasonOther} onChange={(event) => updateMedicationForm("prnReasonOther", event.target.value)} placeholder="Enter PRN reason" />
                ) : null}
              </label>
            </div>
            <label className="ncp-form-field">
              <FieldLabel info="Conditions where staff should not administer the medication, such as blood pressure, pulse, sedation, blood sugar, or provider instructions.">
                Hold parameters
              </FieldLabel>
              <textarea
                rows={3}
                value={medicationForm.holdParameters}
                onChange={(event) => updateMedicationForm("holdParameters", event.target.value)}
                placeholder="Example: Hold if systolic BP below 100 or pulse below 60."
              />
            </label>
            <label className="ncp-form-field">
              <FieldLabel info="Checks required before or after administration, such as vitals, blood sugar, pain score, sedation, respirations, labs, or follow-up observation.">
                Monitoring instructions
              </FieldLabel>
              <textarea
                rows={3}
                value={medicationForm.monitoringInstructions}
                onChange={(event) => updateMedicationForm("monitoringInstructions", event.target.value)}
                placeholder="Vitals, pain score, blood sugar, sedation, respiration, labs, or follow-up checks required before or after administration."
              />
            </label>
            <label className="ncp-form-field">
              <FieldLabel info="Symptoms staff should monitor and report, including expected side effects and serious adverse reactions.">
                Side effects to watch
              </FieldLabel>
              <textarea
                rows={3}
                value={medicationForm.sideEffectsToWatch}
                onChange={(event) => updateMedicationForm("sideEffectsToWatch", event.target.value)}
                placeholder="Expected or serious adverse reactions staff should monitor and report."
              />
            </label>
            <label className="ncp-form-field">
              <FieldLabel info="Allergy warnings, interaction concerns, diagnosis-specific cautions, or other reasons administration may need extra review.">
                Contraindications / precautions
              </FieldLabel>
              <textarea
                rows={3}
                value={medicationForm.contraindications}
                onChange={(event) => updateMedicationForm("contraindications", event.target.value)}
                placeholder="Allergy warnings, diagnosis-specific precautions, interaction concerns, or administration restrictions."
              />
            </label>
            <label className="ncp-form-field">
              <FieldLabel info="Directions for what staff should do if a scheduled dose is late or missed.">
                Missed dose instructions
              </FieldLabel>
              <textarea
                rows={3}
                value={medicationForm.missedDoseInstructions}
                onChange={(event) => updateMedicationForm("missedDoseInstructions", event.target.value)}
                placeholder="Hospital or prescriber directions when a dose is late or missed."
              />
            </label>
            <label className="ncp-form-field">
              <FieldLabel info="Steps to follow when the resident refuses the medication, including documentation, notification, reassessment, or escalation.">
                Refusal instructions
              </FieldLabel>
              <textarea
                rows={3}
                value={medicationForm.refusalInstructions}
                onChange={(event) => updateMedicationForm("refusalInstructions", event.target.value)}
                placeholder="Steps when resident refuses: documentation, notification, reassessment, or escalation guidance."
              />
            </label>
            <label className="ncp-form-field">
              <span>Medication notes</span>
              <textarea
                rows={4}
                value={medicationForm.notes}
                onChange={(event) => updateMedicationForm("notes", event.target.value)}
                placeholder="Additional hospital discharge medication details or internal care notes."
              />
            </label>
          </section>

          <section className="assessment-drawer-section">
            <h3>Prescriber & Pharmacy</h3>
            <div className="assessment-drawer-grid medication-add-grid medication-prescriber-grid">
              <label className="ncp-form-field">
                <span>Prescribed by</span>
                <input value={medicationForm.prescribedBy} onChange={(event) => updateMedicationForm("prescribedBy", event.target.value)} />
              </label>
              <label className="ncp-form-field">
                <span>Prescriber phone</span>
                <input value={medicationForm.prescriberPhone} onChange={(event) => updateMedicationForm("prescriberPhone", event.target.value)} />
              </label>
              <label className="ncp-form-field">
                <span>Prescriber email</span>
                <input type="email" value={medicationForm.prescriberEmail} onChange={(event) => updateMedicationForm("prescriberEmail", event.target.value)} />
              </label>
              <label className="ncp-form-field">
                <span>Pharmacy</span>
                <select value={medicationForm.pharmacyId} onChange={(event) => updateMedicationForm("pharmacyId", event.target.value)} disabled={loadingPharmacies && !pharmacyOptions.length}>
                  <option value="">{loadingPharmacies ? "Loading pharmacies..." : "Select pharmacy"}</option>
                  {pharmacyOptions.map((pharmacy) => (
                    <option value={pharmacy.id} key={pharmacy.id || pharmacy.name}>
                      {pharmacy.detail ? `${pharmacy.name} - ${pharmacy.detail}` : pharmacy.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {!loadingPharmacies && !pharmacyOptions.length ? (
              <p className="medication-section-note">Add a pharmacy to this resident from the Pharmacy tab before selecting it here.</p>
            ) : null}
          </section>

          <section className="assessment-drawer-section">
            <h3>Prescription Source</h3>
            <div className="assessment-drawer-grid medication-add-grid">
              <label className="ncp-form-field">
                <FieldLabel info="Where this medication information came from, such as a discharge summary, pharmacy medication list, MAR, clinic order, or family-provided document.">
                  Source type
                </FieldLabel>
                <select value={medicationForm.sourceType} onChange={(event) => updateMedicationForm("sourceType", event.target.value)}>
                  {medicationSourceTypeOptions.map(([value, label]) => <option value={value} key={value || label}>{label}</option>)}
                </select>
                {medicationForm.sourceType === "OTHER" ? (
                  <input value={medicationForm.sourceTypeOther} onChange={(event) => updateMedicationForm("sourceTypeOther", event.target.value)} placeholder="Enter source type" />
                ) : null}
              </label>
              <label className="ncp-form-field">
                <FieldLabel info="Name of the hospital, clinic, pharmacy, provider group, or person that supplied the medication order.">
                  Source organization
                </FieldLabel>
                <input value={medicationForm.hospitalSource} onChange={(event) => updateMedicationForm("hospitalSource", event.target.value)} placeholder="Example: Kaiser Permanente" />
              </label>
              <label className="ncp-form-field">
                <FieldLabel info="Name of the source document, such as discharge summary, after-visit summary, medication list, prescription label, or MAR.">
                  Source document
                </FieldLabel>
                <input value={medicationForm.sourceDocumentName} onChange={(event) => updateMedicationForm("sourceDocumentName", event.target.value)} />
              </label>
              <label className="ncp-form-field">
                <FieldLabel info="Date shown on the source document or the date the medication source was received.">
                  Source date
                </FieldLabel>
                <input type="date" value={medicationForm.sourceDocumentDate} onChange={(event) => updateMedicationForm("sourceDocumentDate", event.target.value)} />
              </label>
              <label className="ncp-form-field">
                <FieldLabel info="Any source reference number, encounter number, document ID, case number, or external prescription reference.">
                  Source reference #
                </FieldLabel>
                <input value={medicationForm.sourceReferenceNumber} onChange={(event) => updateMedicationForm("sourceReferenceNumber", event.target.value)} />
              </label>
              <label className="ncp-form-field">
                <span>Source contact name</span>
                <input value={medicationForm.sourceContactName} onChange={(event) => updateMedicationForm("sourceContactName", event.target.value)} />
              </label>
              <label className="ncp-form-field">
                <span>Source contact phone</span>
                <input value={medicationForm.sourceContactPhone} onChange={(event) => updateMedicationForm("sourceContactPhone", event.target.value)} />
              </label>
              <label className="ncp-form-field">
                <span>Source contact email</span>
                <input type="email" value={medicationForm.sourceContactEmail} onChange={(event) => updateMedicationForm("sourceContactEmail", event.target.value)} />
              </label>
              <label className="ncp-form-field ncp-form-field-wide">
                <FieldLabel info="Use this for source-specific notes, such as verification status, pages reviewed, medication list conflicts, or follow-up needed with the source.">
                  Source notes
                </FieldLabel>
                <textarea
                  rows={3}
                  value={medicationForm.sourceNotes}
                  onChange={(event) => updateMedicationForm("sourceNotes", event.target.value)}
                  placeholder="Example: Verified against hospital discharge medication list; awaiting pharmacy clarification on refill status."
                />
              </label>
            </div>
          </section>

          {(medicationForm.documents || []).length ? (
            <section className="assessment-drawer-section medication-documents-section">
              <h3>Medication Documents</h3>
              <div className="medication-document-list medication-form-document-list">
                {medicationForm.documents.map((document, index) => {
                  const documentKey = medicationDocumentKey(document, index);
                  const previewUrl = medicationDocumentUrl(document);
                  const isSelected = documentKey === medicationDocumentKey(medicationFormPreviewDocument || {}, medicationFormPreviewIndex);
                  const selectDocumentPreview = () => setSelectedMedicationFormDocumentKey(documentKey);

                  return (
                    <article
                      className={`medication-document-row new-medication-document-card ${isSelected ? "is-selected" : ""}`}
                      key={document.id || document.fileId || document.fileName || index}
                    >
                      <button
                        aria-label={`Show ${medicationDocumentName(document, index)} preview`}
                        className="new-medication-document-thumb"
                        disabled={!previewUrl}
                        onClick={selectDocumentPreview}
                        type="button"
                      >
                        {medicationDocumentIsImage(document) && previewUrl ? (
                          <img src={previewUrl} alt="" />
                        ) : (
                          <i className="ti ti-file-description" />
                        )}
                      </button>
                      <div className="medication-document-main">
                        <strong>{document.fileName || document.originalFileName || "Prescription document"}</strong>
                        <small>
                          {[document.contentType || document.type || "File", formatFileSize(document.size), document.fileId ? "Uploaded" : "Pending upload"].filter(Boolean).join(" | ")}
                        </small>
                        <label className="ncp-form-field">
                          <FieldLabel info="Describe the received prescription, such as source, pages, resident identifier, or medication order note.">
                            Description
                          </FieldLabel>
                          <input
                            value={document.description || ""}
                            onChange={(event) => updateMedicationDocument(document.id, "description", event.target.value)}
                            placeholder="Example: eFax prescription received from Kaiser"
                          />
                        </label>
                      </div>
                      {previewUrl ? (
                        <button
                          aria-label={`Show ${medicationDocumentName(document, index)} preview`}
                          className="mini-btn"
                          onClick={selectDocumentPreview}
                          type="button"
                        >
                          <i className="ti ti-eye" />
                        </button>
                      ) : null}
                      <button
                        aria-label={`Remove ${document.fileName || "document"}`}
                        className="mini-btn danger"
                        disabled={medicationSaving || documentUploading}
                        onClick={() => removeMedicationDocument(document.id, index)}
                        type="button"
                      >
                        <i className="ti ti-trash" />
                      </button>
                    </article>
                  );
                })}
              </div>
            </section>
          ) : null}

          <div className="assessment-drawer-actions">
            <button className="btn secondary" type="button" disabled={medicationSaving || documentUploading} onClick={() => {
              setMedicationDrawerOpen(false);
              if (!editingMedicationId) setMedicationDrawerMode("medication");
            }}>Cancel</button>
            <button className="btn primary" type="submit" disabled={medicationSaving || documentUploading}>
              {medicationSaving || documentUploading ? "Saving..." : medicationDrawerMode === "new" ? "Save for Oliveeb Review" : editingMedicationId ? "Save Changes" : "Save Medication"}
            </button>
          </div>
        </form>
      </Drawer>
    </CareShell>
  );
}
